import React, { useState, useEffect, useRef, useMemo } from "react";
import QRCode from "qrcode";

function QRCanvas({ value, size = 72 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !value) return;
    QRCode.toCanvas(ref.current, value, {
      width: size,
      margin: 1,
      color: { dark: '#000000', light: '#ffffff' }
    }).catch(() => {});
  }, [value, size]);
  return <canvas ref={ref} style={{ borderRadius: 6, display: 'block' }} />;
}
const O = "#EF801A",
  DK = "#1A1A1B",
  DK2 = "#555555",
  MG = "#999999",
  BG = "#F6F5F3",
  OL = "#FFF8F0",
  LG = "#F0EFED",
  WH = "#FFFFFF",
  SH = "0 1px 8px rgba(0,0,0,0.04)";

// v1.3.58 — Ημερομηνίες Ορθόδοξου Πάσχα (για εποχιακά «moments» του Ορφέα).
const ORTHODOX_EASTER = { 2026: "2026-04-12", 2027: "2027-05-02", 2028: "2028-04-16", 2029: "2029-04-08", 2030: "2030-04-28", 2031: "2031-04-13", 2032: "2032-05-02" };

// ─── Branded loader (v1.6) — Earmedical sound-wave equalizer ───
// Original take *inspired by* (not copied from) the Soundz loader: a clean audiogram /
// equalizer — rounded orange bars rising & falling like a hearing-test sweep — on
// Earmedical's light/white theme. No drawn ear icon, no Soundz-style rings/orbit.
// Pure CSS, 60fps in WebView. Keyframe `earEq` lives in the root <style> and inline
// in the boot splash (which returns before the root mounts).
const EarPulse = ({ size = 72, color = O, bars = 5 }) => {
  const h = Math.round(size * 0.62);
  const barW = Math.max(3, Math.round(size * 0.075));
  const gap = Math.max(3, Math.round(size * 0.06));
  return (
    <div role="status" aria-label="Φόρτωση" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap, height: h }}>
      {Array.from({ length: bars }).map((_, i) => (
        <span key={i} aria-hidden style={{
          width: barW,
          height: h,
          borderRadius: barW,
          background: `linear-gradient(180deg, #F6A24A 0%, ${color} 100%)`,
          transformOrigin: "center",
          animation: "earEq 1.1s ease-in-out infinite",
          animationDelay: `${i * 0.11}s`,
          willChange: "transform",
        }} />
      ))}
    </div>
  );
};
const EarLoading = ({ text = "Φόρτωση", size = 64, pad = 24 }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: pad, color: MG, fontSize: 12.5, fontWeight: 600, letterSpacing: "0.03em" }}>
    <EarPulse size={size} />
    {text ? <span>{text}</span> : null}
  </div>
);

const EarLogo = ({ h = 28 }) => (
  <svg
    height={h}
    viewBox="0 0 15000 5000"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: "block" }}
  >
    <polygon fill="#EF7F1A" points="462,3799 3049,3799 3049,1201 462,1201" />
    <path
      fill="#FEFEFE"
      d="M2123 2535c0-67-54-121-121-121s-121 54-121 121 54 121 121 121 121-54 121-121z"
    />
    <path
      fill="#FEFEFE"
      d="M1644 1600c-378 0-574 366-512 891 63 524 420 949 798 949 182 0 337-102 440-268-91 103-211 165-349 165-345 0-671-376-729-838-57-463 123-787 468-787 148 0 280 70 395 182-144-177-315-294-511-294z"
    />
    <path
      fill="#FEFEFE"
      d="M1728 1870c-267 0-406 259-361 630 44 370 295 670 563 670 129 0 238-73 310-190-63 74-75 116-246 116-171 0-447-265-487-592-41-326 59-555 302-555 105 0 198 50 280 128-102-125-223-207-361-207z"
    />
    <path
      fill="#FEFEFE"
      d="M1797 2140c-154 0-236 152-210 367 26 217 173 393 329 393 75 0 139-43 181-112-37 43-43 69-144 69-99 0-253-153-277-344-23-190 28-326 170-326 61 0 115 29 163 74-60-72-130-121-212-121z"
    />
    <path
      fill="#898989"
      d="M13956 3512c-81 0-151-21-207-62-57-42-85-114-85-217V1620h141v1607c0 56 14 96 42 120 29 25 67 37 115 37h74v128h-80z"
    />
    <path
      fill="#898989"
      d="M13422 3509V2877h-418c-45 0-87 5-126 15s-74 26-103 46c-30 21-53 48-71 80-17 33-26 70-26 114 0 83 33 148 97 196 64 46 148 70 251 70 55 0 106-7 152-21v142c-18 3-36 5-55 6-36 2-74 3-116 3-69 0-132-10-190-30-58-19-108-46-150-80-40-33-73-73-97-119-24-46-35-96-35-151 0-56 11-108 32-157 22-49 53-91 92-127 40-37 89-65 145-85 58-21 122-32 192-32h426v-176c0-55-10-100-30-137-20-38-46-68-80-90-204-137-443-65-600 86l-98-95c205-206 568-255 811-82 93 67 141 178 141 334v922h-144z"
    />
    <path
      fill="#898989"
      d="M12509 3374c-45 41-97 75-153 103-70 34-147 51-232 51-74 0-147-12-217-36-71-22-133-61-189-115-55-52-99-123-132-211-34-88-51-196-51-324s17-236 51-325c33-87 77-158 132-212 56-55 118-93 189-117 70-23 143-35 217-35 85 0 162 17 232 50 70 33 132 77 186 132l-98 95c-40-39-85-72-138-102-53-30-108-45-168-45-145 0-258 47-338 140-81 92-121 232-121 419s40 323 121 416c80 93 193 140 338 140 60 0 114-14 165-43 51-27 96-60 136-98l3 2c4 14 10 27 17 40 14 27 31 52 50 75z"
    />
    <polygon
      fill="#898989"
      points="11305,3512 11305,2150 11446,2150 11446,3512"
    />
    <polygon
      fill="#898989"
      points="11305,1970 11305,1830 11446,1830 11446,1970"
    />
    <path
      fill="#898989"
      d="M6153 3512V2165h483v157c34-114 109-171 227-171 22 0 45 3 70 9v535c-38-42-62-66-70-73-30-23-67-35-110-35-77 0-117 53-117 157v768h-483z"
    />
    <path
      fill="#898989"
      d="M6074 3512h-484v-117c-16 43-53 77-112 101-47 20-98 29-152 29-131 0-240-35-326-106-95-75-141-177-141-303 0-120 43-218 130-295 94-81 222-122 386-122h215v-77c0-82-49-122-146-122-68 0-118 7-149 23-32 16-68 49-107 99l-297-221c126-169 311-252 558-252 181 0 326 37 436 111 126 84 189 205 189 365v887zm-484-518h-146c-94 0-141 30-141 90 0 62 48 94 146 94 55 0 92-11 112-31 20-21 29-72 29-153z"
    />
    <path
      fill="#898989"
      d="M4545 3021l314 255c-149 167-346 249-590 249-171 0-314-47-431-143-151-121-225-302-225-542 0-228 68-407 206-534 115-105 253-157 412-157 169 0 314 56 436 167 122 112 184 250 184 415v181h-755c0 54 13 104 40 149 36 58 86 87 154 87 115 0 200-43 255-127zm-299-534c-100 0-150 68-150 207h303c0-139-51-207-153-207z"
    />
    <path
      fill="#898989"
      d="M11036 3512v-790c-2-69-12-129-30-181-20-58-47-107-81-145-35-38-76-66-122-86-48-19-99-28-153-28s-105 9-151 27c-47 18-87 47-120 84-33 38-60 88-81 147-20 60-31 131-33 213h1v122h-1c0 94 11 175 33 241 22 67 52 120 90 162 36 42 81 72 133 91 51 19 107 29 166 29 35 0 70-4 105-12v135c-32 5-65 7-99 7-67 0-135-9-202-26-69-16-130-50-186-100-54-51-100-120-136-209-35-91-53-207-53-351 0-138 16-251 49-342 33-89 75-159 127-211 52-53 109-88 173-109 64-21 125-32 185-32 70 0 138 13 202 37 65 25 121 60 169 106l15 15-1-476h144l1 1682h-144z"
    />
    <path
      fill="#898989"
      d="M9119 2875c0 94 10 175 32 241 22 67 52 120 89 162 37 42 82 72 134 91 51 19 107 29 167 29 56 0 113-11 170-32 23-8 45-18 67-29v149l-8 2c-72 27-146 40-224 40-67 0-135-9-202-26-69-16-130-50-186-100-55-51-100-120-136-209-36-91-52-207-52-351 0-138 15-251 48-342 33-89 75-159 126-211 52-53 111-88 173-109 64-21 126-32 186-32 70 0 138 13 202 37 65 25 122 60 169 106 48 47 87 104 114 173 29 69 43 147 43 234v177h-912zm770-122c0-82-10-152-30-212-20-58-47-107-81-145-35-38-76-66-123-86-47-19-98-28-152-28s-105 9-151 27c-47 18-87 47-120 84-34 38-61 88-81 147-20 60-30 131-32 213h770z"
    />
    <path
      fill="#898989"
      d="M8736 3512v-850c0-59-9-113-28-162-18-47-44-88-76-121-32-32-70-57-113-74-44-17-90-26-139-26s-96 10-138 29c-44 19-82 46-113 80-32 34-58 77-77 126-19 50-29 106-29 165v833h-144v-850c0-59-10-113-28-162-19-47-45-88-77-121-32-32-68-57-112-74-44-17-91-26-139-26s-95 10-139 29c-44 19-82 46-113 80-32 34-57 77-76 126-20 50-29 106-29 165v833h-144V2165h144v193c29-53 63-93 101-122 38-27 76-48 114-62 39-13 74-21 109-23 35-2 63-3 84-3 82 0 158 21 228 63 69 41 123 106 159 193 89-171 246-256 470-256 48 0 98 9 149 28 52 18 101 45 146 82 45 38 83 85 112 142 28 57 43 125 43 203v909h-145z"
    />
    <path
      fill="#898989"
      d="M14538 1640c0 24-4 47-14 69-9 22-22 41-39 58-15 16-34 28-56 37-22 10-46 15-71 15-24 0-47-5-69-15-22-9-41-21-58-37-16-17-29-36-38-58-9-22-14-45-14-69 0-25 5-49 14-71 9-22 22-41 38-56 17-17 36-30 58-39 22-10 45-14 69-14s49 4 71 14c22 9 41 22 56 39 17 15 30 34 39 56 10 22 14 46 14 71zm-39 0c0-21-3-41-10-59-8-18-17-33-29-46-13-13-28-22-45-29-18-7-36-10-57-10-19 0-38 3-55 10-18 7-33 16-45 29-13 13-23 28-30 46-7 18-11 38-11 59s4 39 11 57c7 18 17 33 30 46 12 13 27 23 45 30 17 7 36 11 55 11 21 0 39-4 57-11 17-7 32-17 45-30 12-13 21-28 29-46 7-18 10-37 10-57zm-92 99l-61-85h-19v85h-36v-210h79c24 0 42 5 54 15 12 10 18 26 18 48 0 10-1 19-4 27-3 7-7 14-12 18-5 5-10 9-17 11-6 3-13 5-21 6l61 85h-42zm-1-147c0-6-1-11-4-15-2-4-6-7-10-9-5-2-10-4-16-5-5-1-11-1-17-1h-32v60h24c7 0 14-1 21-1 6-1 12-2 18-4 5-2 9-5 12-9 3-4 4-9 4-16z"
    />
  </svg>
);
const CLINICS = [
  {
    id: "athens",
    c: "Earmedical Αθήνας",
    a: "Σολωνός 47, Κολωνάκι",
    t: "2103608005",
    f: "210 360 8005",
    rv: "https://g.page/r/CeC1pdUp2960EBE/review",
    map: "https://www.google.com/maps/dir/?api=1&destination=Earmedical,+Solonos+47,+Athens",
    appointmentEmail: "appointments.earmedical@gmail.com",
    hoursText: "Δευτέρα έως Παρασκευή 09:00 - 18:00",
    schedule: {
      1: [["09:00", "18:00"]],
      2: [["09:00", "18:00"]],
      3: [["09:00", "18:00"]],
      4: [["09:00", "18:00"]],
      5: [["09:00", "18:00"]],
    },
  },
  {
    id: "larisa",
    c: "Earmedical Λάρισας",
    a: "Μεγάλου Αλεξάνδρου 21",
    t: "2410250230",
    f: "2410 250230",
    rv: "https://g.page/r/CUu2R0RRPm2EEBE/review",
    map: "https://www.google.com/maps/dir/?api=1&destination=Earmedical,+Megalou+Alexandrou+21,+Larisa",
    appointmentEmail: "appointments.earmedical@gmail.com",
    hoursText: "Δε Τε Πα 09:00-14:00 & 17:30-20:30 • Τρ Πε 09:00-14:00 • Σα 10:00-14:00",
    schedule: {
      1: [["09:00", "14:00"], ["17:30", "20:30"]],
      2: [["09:00", "14:00"]],
      3: [["09:00", "14:00"], ["17:30", "20:30"]],
      4: [["09:00", "14:00"]],
      5: [["09:00", "14:00"], ["17:30", "20:30"]],
      6: [["10:00", "14:00"]],
    },
  },
  {
    id: "katerini",
    c: "Earmedical Κατερίνης",
    a: "Δημ. Δημάδη 10",
    t: "2351604242",
    f: "2351 604242",
    rv: "https://g.page/r/CWzT9qsiwDFKEBE/review",
    map: "https://www.google.com/maps/dir/?api=1&destination=Earmedical,+Dimitriou+Dimadi+10,+Katerini",
    appointmentEmail: "appointments.earmedical@gmail.com",
    hoursText: "Τρ Πε Πα 09:00-14:00 & 17:30-20:30 • Δε Τε 09:00-14:00 • Σα 10:00-14:00",
    schedule: {
      1: [["09:00", "14:00"]],
      2: [["09:00", "14:00"], ["17:30", "20:30"]],
      3: [["09:00", "14:00"]],
      4: [["09:00", "14:00"], ["17:30", "20:30"]],
      5: [["09:00", "14:00"], ["17:30", "20:30"]],
      6: [["10:00", "14:00"]],
    },
  },
  {
    id: "farsala",
    c: "Earmedical Φαρσάλων",
    a: "Λαρίσης 3",
    t: "2491022900",
    f: "2491 022900",
    rv: "https://g.page/r/CT6VFIGfhcJvEBE/review",
    map: "https://www.google.com/maps/dir/?api=1&destination=Earmedical,+Larisis+3,+Farsala",
    appointmentEmail: "appointments.earmedical@gmail.com",
    hoursText: "Δε Τε Πα 09:00-14:00 & 17:30-20:30 • Τρ Πε 09:00-14:00 • Σα 10:00-14:00",
    schedule: {
      1: [["09:00", "14:00"], ["17:30", "20:30"]],
      2: [["09:00", "14:00"]],
      3: [["09:00", "14:00"], ["17:30", "20:30"]],
      4: [["09:00", "14:00"]],
      5: [["09:00", "14:00"], ["17:30", "20:30"]],
      6: [["10:00", "14:00"]],
    },
  },
];
const LOCS = CLINICS;
const getClinicById = (clinicId) =>
  CLINICS.find((clinic) => clinic.id === clinicId) || CLINICS[0];

// Check if clinic is currently open
function isClinicOpen(cl) {
  if (!cl || !cl.schedule) return { open: false, text: "" };
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const hhmm = now.getHours() * 100 + now.getMinutes();
  const slots = cl.schedule[day];
  if (!slots) return { open: false, next: getNextOpenTime(cl, now), text: "Κλειστό" };
  for (const [start, end] of slots) {
    const s = parseInt(start.replace(":", ""));
    const e = parseInt(end.replace(":", ""));
    if (hhmm >= s && hhmm < e) return { open: true, text: "Ανοιχτό τώρα — έως " + end };
  }
  // Check if there's a later slot today
  for (const [start, end] of slots) {
    const s = parseInt(start.replace(":", ""));
    if (hhmm < s) return { open: false, text: "Ανοίγει σήμερα στις " + start };
  }
  return { open: false, next: getNextOpenTime(cl, now), text: "Κλειστό" };
}

function getNextOpenTime(cl, now) {
  const days = ["Κυριακή", "Δευτέρα", "Τρίτη", "Τετάρτη", "Πέμπτη", "Παρασκευή", "Σάββατο"];
  for (let i = 1; i <= 7; i++) {
    const d = (now.getDay() + i) % 7;
    const slots = cl.schedule[d];
    if (slots && slots.length) return days[d] + " " + slots[0][0];
  }
  return "Δευτέρα 09:00";
}

// ─── Audiologists per clinic ──────────────────────────────────
const AUDIOLOGISTS = {
  athens:   ["Γιώργος", "Κώστας", "Ιφιγένεια", "Αλέξης"],
  larisa:   ["Όλγα", "Άννα", "Αλέξης"],
  katerini: ["Ελευθερία", "Αλέξης"],
  farsala:  ["Εύα", "Αλέξης"],
};
const getAudiologistsForClinic = (clinicId) => AUDIOLOGISTS[clinicId] || [];
const fmtTime = (d) =>
  d.toLocaleTimeString("el-GR", {
    hour: "2-digit",
    minute: "2-digit",
  });
const formatDaysLabel = (days) => {
  if (days <= 0) return "Σήμερα";
  if (days === 1) return "Αύριο";
  return `Σε ${days} ημέρες`;
};
const getTodayDateStr = () => {
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffset).toISOString().split("T")[0];
};
const DAY_NAMES = ["Κυριακή", "Δευτέρα", "Τρίτη", "Τετάρτη", "Πέμπτη", "Παρασκευή", "Σάββατο"];
const DAY_NAMES_SHORT = ["Κυ", "Δε", "Τρ", "Τε", "Πε", "Πα", "Σα"];
const MONTH_NAMES = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];
const pad2 = (n) => String(n).padStart(2, "0");
const minutesToSlot = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
const slotToMinutes = (slot) => {
  const [h, m] = String(slot || "00:00").split(":").map(Number);
  return h * 60 + m;
};
const getClinicRangesForDate = (clinic, dateStr) => {
  if (!clinic || !dateStr) return [];
  const date = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(date.getTime())) return [];
  return clinic.schedule?.[date.getDay()] || [];
};
const getClinicTimeSlots = (clinic, dateStr) => {
  const ranges = getClinicRangesForDate(clinic, dateStr);
  const slots = [];
  ranges.forEach(([start, end]) => {
    const startMin = slotToMinutes(start);
    const endMin = slotToMinutes(end);
    for (let mins = startMin; mins < endMin; mins += 15) {
      slots.push(minutesToSlot(mins));
    }
  });
  return slots;
};

/**
 * Επιστρέφει την πρώτη διαθέσιμη ώρα έναρξης λειτουργίας του κέντρου για δεδομένη ημέρα.
 * Αν το κέντρο είναι κλειστό, επιστρέφει null.
 */
const getFirstOpeningTime = (clinic, dateStr) => {
  const ranges = getClinicRangesForDate(clinic, dateStr);
  if (!ranges.length) return null;
  return ranges[0][0]; // π.χ. "09:00"
};

/**
 * Βρίσκει τη σωστή ημερομηνία για το ραντεβού λήξης δοκιμαστικής.
 * Ξεκινά από trialStart + trialDays και αν το κέντρο είναι κλειστό,
 * πηγαίνει πίσω μέχρι να βρει ανοιχτή μέρα (max 7 μέρες).
 * Επιστρέφει { date: "YYYY-MM-DD", time: "HH:MM" } ή null.
 */
const computeTrialEndAppointment = (clinic, trialStartStr, trialDays) => {
  if (!clinic || !trialStartStr || !trialDays) return null;

  // Parse trial start (accept YYYY-MM-DD ή YYYYMMDD)
  const s = trialStartStr.length === 8
    ? `${trialStartStr.substring(0,4)}-${trialStartStr.substring(4,6)}-${trialStartStr.substring(6,8)}`
    : trialStartStr;
  const startDate = new Date(`${s}T00:00:00`);
  if (Number.isNaN(startDate.getTime())) return null;

  // Ημέρα λήξης = trialStart + trialDays
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + trialDays);

  // Βρες ανοιχτή ημέρα (προχώρα πίσω αν κλειστό — μέχρι 7 μέρες)
  for (let i = 0; i < 8; i++) {
    const d = new Date(endDate);
    d.setDate(d.getDate() - i);
    const dateStr = `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
    const firstOpen = getFirstOpeningTime(clinic, dateStr);
    if (firstOpen) {
      return { date: dateStr, time: firstOpen };
    }
  }
  return null;
};
const getClinicHoursSummaryForDate = (clinic, dateStr) => {
  const ranges = getClinicRangesForDate(clinic, dateStr);
  if (!ranges.length) return "Το κέντρο είναι κλειστό την επιλεγμένη ημέρα.";
  return `${DAY_NAMES[new Date(`${dateStr}T00:00:00`).getDay()]}: ` + ranges.map(([start, end]) => `${start} - ${end}`).join(" • ");
};
const toDateOnlyTime = (dateStr) => new Date(`${dateStr}T00:00:00`);
const formatDateInputValue = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const isSelectableClinicDate = (clinic, dateStr) => {
  if (!clinic || !dateStr) return false;
  return toDateOnlyTime(dateStr) >= toDateOnlyTime(getTodayDateStr()) && getClinicRangesForDate(clinic, dateStr).length > 0;
};
const getCalendarGrid = (monthDate, clinic, selectedDateStr) => {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = addDays(firstDay, -firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const dayDate = addDays(gridStart, index);
    const dateStr = formatDateInputValue(dayDate);
    return {
      date: dayDate,
      dateStr,
      label: dayDate.getDate(),
      inMonth: dayDate.getMonth() === monthDate.getMonth(),
      isToday: dateStr === getTodayDateStr(),
      isSelected: dateStr === selectedDateStr,
      isSelectable: isSelectableClinicDate(clinic, dateStr),
    };
  });
};
// ─── Status Helpers ───────────────────────────────────────────

/**
 * Computed display status — NEVER persists "completed" to DB.
 * cancelled > past (completed) > pending > confirmed
 */
const getAppointmentDisplayStatus = (appointment) => {
  if (!appointment) return "pending";
  const stored = appointment.status || "pending";
  if (stored === "cancelled") return "cancelled";
  const past = appointment.past ?? false;
  if (past && stored !== "cancelled") return "completed";
  return stored; // pending | confirmed
};

const isCompletedAppointment = (appointment) => {
  if (!appointment) return false;
  if (appointment.status === "cancelled") return false;
  return appointment.past === true;
};

const mapGoogleStatusToWpStatus = (googleStatus) => {
  const map = { tentative: "pending", confirmed: "confirmed", cancelled: "cancelled" };
  return map[googleStatus] || "pending";
};

const mapWpStatusToGoogleStatus = (wpStatus) => {
  const map = { pending: "tentative", confirmed: "confirmed", cancelled: "cancelled", completed: "confirmed" };
  return map[wpStatus] || "tentative";
};

/**
 * Returns display metadata (bg, color, label) based on computed status.
 */
const getStatusMeta = (statusOrAppointment) => {
  const map = {
    pending:   { bg: "#FFF3E0", color: "#E65100", label: "Σε αναμονή" },
    confirmed: { bg: "#E8F5E9", color: "#2E7D32", label: "Επιβεβαιωμένο" },
    completed: { bg: LG,        color: MG,        label: "Ολοκληρώθηκε" },
    cancelled: { bg: "#FFEBEE", color: "#C62828", label: "Ακυρωμένο" },
    scheduled: { bg: LG,        color: DK,        label: "Προγραμματισμένο" },
  };
  // Αν περαστεί ολόκληρο appointment object → υπολόγισε computed status
  if (typeof statusOrAppointment === "object" && statusOrAppointment !== null) {
    return map[getAppointmentDisplayStatus(statusOrAppointment)] || map.pending;
  }
  return map[statusOrAppointment] || map.pending;
};

const normalizeWpAppointment = (a) => {
  const rawDt = a?.appointment_date
    ? new Date(a.appointment_date + (a.appointment_time ? `T${a.appointment_time}` : "T09:00"))
    : null;
  const validDt = rawDt && !Number.isNaN(rawDt.getTime()) ? rawDt : null;
  const now     = new Date();
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const apptDay  = validDt ? new Date(validDt.getFullYear(), validDt.getMonth(), validDt.getDate()) : null;
  const days     = apptDay ? Math.ceil((apptDay - dayStart) / 86400000) : 0;
  return {
    ...a,
    dt:   validDt,
    dtF:  validDt
      ? validDt.toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" })
      : "",
    timeF: validDt ? fmtTime(validDt) : (a.appointment_time || ""),
    past:  validDt ? validDt < now : false,
    days,
  };
};

const getReasonLabel = (reason, fallback) => {
  const reasonLabels = {
    followup: "Ρύθμιση / Follow-up",
    service: "Καθαρισμός / Service",
    hearing_test: "Επανέλεγχος ακοής",
    urgent: "Νέο θέμα / Επείγον",
    trial_end: "Επιστροφή ακουστικών & αξιολόγηση",
  };
  // Καθαρισμός του &amp; που εμφανίζεται λανθασμένα
  const label = reasonLabels[reason] || fallback || reason || "Ραντεβού";
  return label.replace(/&amp;/g, "&");
};

// v1.3.31 — Υπηρεσίες για prospects (Ενδιαφερόμενοι). v1.3.35 — emojis αντικαταστάθηκαν με
// inline SVG line icons (stroke μόνο, no fill) για consistent professional look.
// Κάθε iconSvg(size, color) επιστρέφει <svg> element έτοιμο για render.
const PIcon = {
  // Hearing assessment — audio waveform icon (stylised ear + waves)
  ear: (size = 24, color = "currentColor") => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8.5C6 5.46 8.46 3 11.5 3S17 5.46 17 8.5c0 2.3-1.5 3.5-2.5 5-1.05 1.58-1 4 0 5"/>
      <path d="M11.5 13a2 2 0 0 0 2-2"/>
      <path d="M14 18c-1.5 2-4 2.5-6 1"/>
    </svg>
  ),
  // Hearing aid trial — small device icon
  device: (size = 24, color = "currentColor") => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 4c-2 0-4 2-4 5v4c0 2 1 3 2 4l3 3c1 1 3 1 4 0l1-1c2-2 3-4 3-7V8c0-2-2-4-4-4"/>
      <circle cx="12" cy="10" r="1.5"/>
      <path d="M9 14h2"/>
    </svg>
  ),
  // Earplugs — shield icon (protection)
  shield: (size = 24, color = "currentColor") => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L4 5v7c0 5 3 8 8 10 5-2 8-5 8-10V5l-8-3z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  ),
  // IEM Soundz — musical note + headphones
  music: (size = 24, color = "currentColor") => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13"/>
      <circle cx="6" cy="18" r="3"/>
      <circle cx="18" cy="16" r="3"/>
    </svg>
  ),
  // Tinnitus — bell with sound waves
  bell: (size = 24, color = "currentColor") => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  // Calendar — for "δες ραντεβού" CTAs
  cal: (size = 24, color = "currentColor") => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
};

const PROSPECT_SERVICES = [
  {
    id: "hearing_assessment",
    title: "Αξιολόγηση ακοής",
    short: "Δωρεάν εξέταση ~45'",
    desc: "Πλήρης ακοομετρικός έλεγχος από εξειδικευμένο ακοοπροθετιστή. Περιλαμβάνει τονικό ακοόγραμμα, ομιλητική ακοομετρία και προσωπική αξιολόγηση των αναγκών σας.",
    Icon: PIcon.ear,
  },
  {
    id: "hearing_aid_trial_info",
    title: "Δοκιμή ακουστικών βαρηκοΐας",
    short: "Δοκιμάστε δωρεάν στο σπίτι",
    desc: "Επιλέγουμε μαζί τα κατάλληλα ακουστικά, τα ρυθμίζουμε στις ανάγκες σας και τα δοκιμάζετε στο πραγματικό σας περιβάλλον (σπίτι, εργασία, παρέες) πριν αποφασίσετε.",
    Icon: PIcon.device,
  },
  {
    id: "earplugs_info",
    title: "Ωτοασπίδες",
    short: "Custom ωτοασπίδες · προστασία ακοής",
    desc: "Εξατομικευμένες ωτοασπίδες για ύπνο, κολύμβηση, μουσικούς, εργασιακό θόρυβο. Καλούπι σε λεπτά, παράδοση σε 5-10 ημέρες.",
    Icon: PIcon.shield,
  },
  {
    id: "soundz_iem_info",
    title: "Soundz In-Ear Monitors",
    short: "Custom IEMs για μουσικούς & επαγγελματίες",
    desc: "Επαγγελματικά custom IEMs Soundz — απομόνωση, ποιότητα στούντιο, εφαρμογή που κρατάει ώρες χωρίς ενόχληση. Για live, στούντιο, podcasts.",
    Icon: PIcon.music,
  },
  {
    id: "tinnitus_info",
    title: "Αντιμετώπιση Εμβοών",
    short: "Συμβουλευτική για βουητά / εμβοές",
    desc: "Εκτίμηση των εμβοών (βουητά), διερεύνηση αιτιών και ενημέρωση για διαθέσιμες θεραπείες — sound therapy, ακουστικά με tinnitus masker, συμβουλευτική.",
    Icon: PIcon.bell,
  },
];
const PROSPECT_REASONS_BY_ID = Object.fromEntries(PROSPECT_SERVICES.map(s => [s.id, s.title]));
const QZ = [
  {
    q: "Πώς ακούτε την τηλεόραση;",
    o: ["Πολύ καλύτερα", "Λίγο καλύτερα", "Το ίδιο", "Χειρότερα"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Πώς παρακολουθείτε συζήτηση στο τραπέζι;",
    o: ["Πολύ καλύτερα", "Λίγο καλύτερα", "Το ίδιο", "Χειρότερα"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Πώς ακούτε στο τηλέφωνο;",
    o: ["Πολύ καλύτερα", "Λίγο καλύτερα", "Το ίδιο", "Χειρότερα"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Πώς τα πάτε σε θορυβώδη μέρη;",
    o: ["Πολύ καλύτερα", "Λίγο καλύτερα", "Το ίδιο", "Χειρότερα"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Πώς ακούτε στο αυτοκίνητο;",
    o: ["Πολύ καλύτερα", "Λίγο καλύτερα", "Το ίδιο", "Χειρότερα"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Η φωνή σας ακούγεται φυσιολογική;",
    o: ["Ναι", "Σχεδόν", "Ακόμα αλλιώτικη"],
    sc: [3, 1, 0],
  },
  {
    q: "Σας ενοχλεί κάποιος θόρυβος;",
    o: ["Όχι", "Μέσα στο σπίτι", "Έξω", "Παντού"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Πόσες ώρες/μέρα φοράτε τα ακουστικά;",
    o: ["10+", "6-10", "3-6", "<3"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Πόσο άνετα είναι;",
    o: ["Πολύ", "Αρκετά", "Λίγο ενοχλητικά", "Πονάνε"],
    sc: [3, 2, 1, 0],
  },
  {
    q: "Θα συστήνατε τα ακουστικά σε κάποιον δικό σας;",
    o: ["Σίγουρα ναι!", "Πιθανόν", "Όχι ακόμα"],
    sc: [3, 1, 0],
  },
];
const SUPPORT_BASE = [
  {
    s: "Δεν ακούω καλά",
    icon: (c, sz) => Ic.mute(c, sz),
    reason: "Πρόβλημα ακοής",
    t: [
      "Ελέγξτε αν είναι ενεργοποιημένα",
      "Ελέγξτε αν εφαρμόζουν σωστά στα αυτιά",
      "Ελέγξτε αν βούλωσαν τα φίλτρα ή τα ανοίγματα",
      "Αν δεν λυθεί, προγραμματίστε ραντεβού",
    ],
  },
  {
    s: "Δεν λειτουργεί η συσκευή",
    icon: (c, sz) => Ic.zap(c, sz),
    reason: "Βλάβη συσκευής",
    t: [],
  },
  {
    s: "Σφύριγμα / μικροφωνισμός",
    icon: (c, sz) => Ic.vol(c, sz),
    reason: "Σφύριγμα",
    t: [
      "Ελέγξτε αν εφαρμόζουν σωστά",
      "Βγάλτε και ξαναφορέστε τα",
      "Χαμηλώστε προσωρινά την ένταση",
      "Αν συνεχίζεται, χρειάζεται ρύθμιση",
    ],
  },
  {
    s: "Μπαταρία / φόρτιση",
    icon: (c, sz) => Ic.batt(c, sz),
    reason: "Πρόβλημα τροφοδοσίας",
    t: [],
  },
  {
    s: "Bluetooth δεν συνδέεται",
    icon: (c, sz) => Ic.mobile(c, sz),
    reason: "Πρόβλημα Bluetooth",
    t: [
      "Κλείστε και ανοίξτε ξανά το Bluetooth",
      "Βγάλτε και ξαναβάλτε τα ακουστικά",
      "Διαγράψτε τη σύνδεση και κάντε ξανά pairing",
      "Δείτε τις οδηγίες ή επικοινωνήστε μαζί μας",
    ],
  },
];
const getPowerSourceLabel = (power) =>
  power === "battery" ? "Μπαταρίες" : power === "rechargeable" ? "Επαναφορτιζόμενα" : "—";
const DEVICE_TYPES = [
  { id: "CIC",      label: "CIC",        battery: "10",  batteryColor: "#FFD700", batteryColorName: "Κίτρινο" },
  { id: "IIC",      label: "IIC",        battery: "10",  batteryColor: "#FFD700", batteryColorName: "Κίτρινο" },
  { id: "IF CIC",   label: "IF CIC",     battery: "10",  batteryColor: "#FFD700", batteryColorName: "Κίτρινο" },
  { id: "CIC XP",   label: "CIC XP",     battery: "312", batteryColor: "#8B4513", batteryColorName: "Καφέ" },
  { id: "RIC",      label: "RIC",        battery: "312", batteryColor: "#8B4513", batteryColorName: "Καφέ" },
  { id: "BTE312",   label: "BTE 312",    battery: "312", batteryColor: "#8B4513", batteryColorName: "Καφέ" },
  { id: "BTE",      label: "BTE",        battery: "13",  batteryColor: "#EF801A", batteryColorName: "Πορτοκαλί" },
  { id: "SP BTE",   label: "SP BTE",     battery: "13",  batteryColor: "#EF801A", batteryColorName: "Πορτοκαλί" },
  { id: "UP BTE",   label: "UP BTE",     battery: "675", batteryColor: "#1565C0", batteryColorName: "Μπλε" },
];

const getBatteryProfile = (type, powerSource, serialLeft, serialRight) => {
  if (powerSource !== "battery") return null;
  const deviceCount = [serialLeft, serialRight].filter(Boolean).length || 1;
  const deviceInfo = DEVICE_TYPES.find(d => d.id === type);
  const batteryType = deviceInfo?.battery || "312";
  const batteryColor = deviceInfo?.batteryColor || "#8B4513";
  const batteryColorName = deviceInfo?.batteryColorName || "Καφέ";
  let daysPerBattery = 5;
  if (batteryType === "13")  daysPerBattery = 12;
  if (batteryType === "312") daysPerBattery = 8;
  if (batteryType === "675") daysPerBattery = 20;
  const batteriesPerPack = 6;
  const packDays = Math.max(1, Math.floor((batteriesPerPack * daysPerBattery) / deviceCount));
  return {
    batteryType,
    batteryColor,
    batteryColorName,
    daysPerBattery,
    batteriesPerPack,
    deviceCount,
    packDays,
    pushEveryDays: packDays,
  };
};
const getSupportOptions = (powerSource = "rechargeable") => {
  const batteryTroubleshooting = [
    {
      s: "Δεν λειτουργεί η συσκευή",
      icon: (c, sz) => Ic.zap(c, sz),
      reason: "Βλάβη συσκευής",
      t: [
        "Αλλάξτε καινούργια μπαταρία",
        "Περιμένετε 1 λεπτό αφού αφαιρέσετε το αυτοκόλλητο της μπαταρίας",
        "Ελέγξτε αν η μπαταρία τοποθετήθηκε σωστά",
        "Αν δεν λυθεί, προγραμματίστε ραντεβού",
      ],
    },
    {
      s: "Μπαταρία / φόρτιση",
      icon: (c, sz) => Ic.batt(c, sz),
      reason: "Πρόβλημα μπαταρίας",
      t: [
        "Βάλτε καινούργια μπαταρία",
        "Ελέγξτε ότι η μπαταριοθήκη κλείνει σωστά",
        "Καθαρίστε απαλά τις επαφές της μπαταρίας",
        "Αν συνεχίζεται, επικοινωνήστε μαζί μας",
      ],
    },
  ];
  const rechargeableTroubleshooting = [
    {
      s: "Δεν λειτουργεί η συσκευή",
      icon: (c, sz) => Ic.zap(c, sz),
      reason: "Βλάβη συσκευής",
      t: [
        "Τοποθετήστε ξανά σωστά τα ακουστικά στον φορτιστή",
        "Δοκιμάστε άλλη πρίζα ή άλλο μπριζάκι",
        "Ελέγξτε το καλώδιο και τη σωστή τροφοδοσία",
        "Αν δεν λυθεί, προγραμματίστε ραντεβού",
      ],
    },
    {
      s: "Μπαταρία / φόρτιση",
      icon: (c, sz) => Ic.batt(c, sz),
      reason: "Πρόβλημα φόρτισης",
      t: [
        "Ελέγξτε ότι ο φορτιστής είναι σωστά συνδεδεμένος",
        "Δοκιμάστε άλλη πρίζα ή άλλο αντάπτορα",
        "Αφήστε τα να φορτίσουν επαρκώς και ελέγξτε ξανά",
        "Αν συνεχίζεται, επικοινωνήστε μαζί μας",
      ],
    },
  ];
  const mapped = SUPPORT_BASE.map((item) => {
    if (item.s === "Δεν λειτουργεί η συσκευή") return powerSource === "battery" ? batteryTroubleshooting[0] : rechargeableTroubleshooting[0];
    if (item.s === "Μπαταρία / φόρτιση") return powerSource === "battery" ? batteryTroubleshooting[1] : rechargeableTroubleshooting[1];
    return item;
  });
  return mapped;
};
const SHOP = [
  {
    l: "Μπαταρίες",
    sub: "Για όλα τα μοντέλα ακουστικών",
    url: "https://www.earmedical.gr/product/bataries-mpataries-akoustikwn/",
    icon: (c, sz) => Ic.batt(c, sz),
    hint: "Αλλαγή κάθε 5-7 ημέρες",
    badge: "Συχνή αγορά",
    badgeColor: "#2E7D32",
    when: "Όταν η μπαταρία χάνει δύναμη γρήγορα",
  },
  {
    l: "Φίλτρα κεριού",
    sub: "Προστατεύουν τη συσκευή σας",
    url: "https://www.earmedical.gr/product/wax-protection-filters/",
    icon: (c, sz) => Ic.shield(c, sz),
    hint: "Αλλαγή κάθε μήνα",
    badge: "Αλλαγή τώρα",
    badgeColor: "#E65100",
    when: "Κάθε μήνα ή όταν ο ήχος γίνει θαμπός",
  },
  {
    l: "Σπρέι καθαρισμού",
    sub: "Για σωστή υγιεινή και φροντίδα",
    url: "https://www.earmedical.gr/product/soundz-spray/",
    icon: (c, sz) => Ic.sparkle(c, sz),
    hint: "Καθημερινή χρήση",
    badge: "Προτεινόμενο",
    badgeColor: "#1565C0",
    when: "Καθημερινά για μακροζωία της συσκευής",
  },
];
const PS = [
  {
    w: "Εβδομάδα 1",
    t: "Σταθερή έναρξη",
    d: "Στόχος είναι να τα φοράτε το μεγαλύτερο μέρος της ημέρας από τις πρώτες κιόλας ημέρες. Αν κουράζεστε, κάντε σύντομα διαλείμματα και συνεχίστε.",
    m: "Η σταθερή καθημερινή χρήση βοηθά τον εγκέφαλο να προσαρμοστεί πιο φυσικά στους νέους ήχους.",
  },
  {
    w: "Εβδομάδα 2",
    t: "Χρήση σε καθημερινά περιβάλλοντα",
    d: "Φοράτε τα στο σπίτι, στην τηλεόραση, στο τηλέφωνο και σε ήρεμες εξόδους. Σημειώστε ό,τι σας δυσκολεύει για να το ρυθμίσουμε μαζί.",
    m: "Οι πρώτες δυσκολίες είναι συχνές και συνήθως βελτιώνονται με σωστή καθοδήγηση.",
  },
  {
    w: "Εβδομάδα 3-4",
    t: "Άνεση και αυτοπεποίθηση",
    d: "Φοράτε τα σταθερά μέσα στη μέρα και δοκιμάστε τα και σε πιο απαιτητικά περιβάλλοντα, όπως παρέες ή εξωτερικούς χώρους.",
    m: "Η προσαρμογή δεν χρειάζεται πίεση· χρειάζεται συνέπεια, άνεση και σωστές ρυθμίσεις.",
  },
  {
    w: "Μήνας 2+",
    t: "Μακροχρόνια βελτιστοποίηση",
    d: "Τα ακουστικά γίνονται μέρος της καθημερινότητάς σας. Κρατήστε επαφή μαζί μας για βελτιώσεις, service και επανελέγχους.",
    m: "Ο στόχος δεν είναι μόνο να ακούτε περισσότερο, αλλά να ακούτε πιο άνετα και πιο φυσικά.",
  },
];
function downloadICS(t, d, s, e, l, attendeeEmail) {
  const p = (x) =>
    x
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const attendeeLine = attendeeEmail
    ? `\r\nATTENDEE;CN=Earmedical:mailto:${attendeeEmail}`
    : "";
  const i =
    "BEGIN:VCALENDAR\r\nVERSION:2.0\r\nPRODID:-//Earmedical//App//EN\r\nBEGIN:VEVENT\r\nDTSTART:" +
    p(s) +
    "\r\nDTEND:" +
    p(e) +
    "\r\nSUMMARY:" +
    t +
    "\r\nDESCRIPTION:" +
    d.replace(/\n/g, "\\n") +
    "\r\nLOCATION:" +
    l +
    attendeeLine +
    "\r\nBEGIN:VALARM\r\nTRIGGER:-P1D\r\nACTION:DISPLAY\r\nDESCRIPTION:" +
    t +
    " αύριο\r\nEND:VALARM\r\nBEGIN:VALARM\r\nTRIGGER:-PT2H\r\nACTION:DISPLAY\r\nDESCRIPTION:" +
    t +
    " σε 2 ώρες\r\nEND:VALARM\r\nEND:VEVENT\r\nEND:VCALENDAR";
  const b = new Blob([i], { type: "text/calendar" });
  const u = URL.createObjectURL(b);
  const a = document.createElement("a");
  a.href = u;
  a.download = t.replace(/[^a-zA-Zα-ωΑ-Ω0-9]/g, "_") + ".ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(u);
}
function mkA(sd, name, email, phone, clinic, profId, warrantyYrs) {
  const activeClinic = clinic || CLINICS[0];
  const wYears = warrantyYrs || 2;
  const wMonths = wYears * 12;
  const s = sd && !isNaN(new Date(sd)) ? new Date(sd) : new Date(),
    ad = (d, n) => {
      const r = new Date(d);
      r.setDate(r.getDate() + n);
      r.setHours(8, 0, 0);
      return r;
    },
    am = (d, m) => {
      const r = new Date(d);
      r.setMonth(r.getMonth() + m);
      r.setHours(8, 0, 0);
      return r;
    },
    fm = (d) =>
      d.toLocaleDateString("el-GR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
  const a = [
    {
      dt: ad(s, 15),
      l: "2η Ρύθμιση",
      d: "Fine-tuning & αύξηση έντασης",
      status: "Προγραμματισμένο",
    },
    {
      dt: ad(s, 45),
      l: "3η Ρύθμιση",
      d: "Τελικά fine-tunings",
      status: "Προγραμματισμένο",
    },
  ];
  for (let m = 3; m <= wMonths; m += 3) {
    const y = m % 12 === 0;
    a.push({
      dt: am(s, m),
      l: y ? "Ετήσιος Επανέλεγχος" : "Συντήρηση & Service",
      d: y ? "Πλήρης ακοομέτρηση" : "Καθαρισμός & έλεγχος",
      status: "Προγραμματισμένο",
    });
  }
  a.push(
    {
      dt: am(s, wMonths - 1),
      l: "Εγγύηση λήγει σύντομα",
      d: "Υπάρχει διαθέσιμο πρόγραμμα επέκτασης εγγύησης για την προστασία των ακουστικών σας. Επικοινωνήστε μαζί μας!",
      status: "Υπενθύμιση",
    },
    {
      dt: am(s, wMonths),
      l: "Λήξη Εγγύησης",
      d: "Η εγγύηση " + wYears + " ετών έληξε. Φροντίστε για επέκταση εγγύησης ώστε να διατηρήσετε την προστασία των ακουστικών σας.",
      status: "Υπενθύμιση",
    },
  );
  a.sort((x, y) => x.dt - y.dt);
  const nm = name || "Πελάτης";
  return a.map((x) => {
    const e = new Date(x.dt);
    e.setTime(x.dt.getTime() + 30 * 60 * 1000); // 30 λεπτά διάρκεια
    const f = (d) =>
      d
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}/, "");
    const ttl = nm + " — " + x.l;
    const det =
      x.d +
      "\nΠελάτης: " +
      nm +
      (phone ? "\nΚινητό: " + phone : "") +
      (profId ? "\nΚωδικός Prof: " + profId : "") +
      `\nΚέντρο: ${activeClinic.c}` +
      `\nΤηλέφωνο: ${activeClinic.f}` +
      `\nΔιεύθυνση: ${activeClinic.a}` +
      "\nearmedical.gr";
    const days = Math.ceil((x.dt - new Date()) / 86400000);
    return {
      ...x,
      dtF: fm(x.dt),
      timeF: fmtTime(x.dt),
      past: x.dt < new Date(),
      days,
      gc:
        `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ttl)}&details=${encodeURIComponent(det)}&location=${encodeURIComponent(activeClinic.c + ", " + activeClinic.a)}&dates=${f(x.dt)}/${f(e)}&add=${encodeURIComponent(activeClinic.appointmentEmail)}` +
        (email ? `&add=${encodeURIComponent(email)}` : ""),
      ics: () =>
        downloadICS(
          ttl,
          det,
          x.dt,
          e,
          `${activeClinic.c}, ${activeClinic.a}`,
          activeClinic.appointmentEmail,
        ),
    };
  });
}
function mkGcal(name, reason, dateStr, email, phone, clinic, profId) {
  const activeClinic = clinic || CLINICS[0];
  const dt = new Date(dateStr);
  dt.setHours(8, 0, 0);
  const e = new Date(dt.getTime() + 30 * 60 * 1000); // 30 λεπτά διάρκεια
  const f = (d) =>
    d
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const nm = name || "Πελάτης";
  const details =
    "Πελάτης: " +
    nm +
    (phone ? "\nΚινητό: " + phone : "") +
    (profId ? "\nΚωδικός Prof: " + profId : "") +
    "\n" +
    reason +
    `\nΚέντρο: ${activeClinic.c}` +
    `\nΤηλέφωνο: ${activeClinic.f}` +
    `\nΔιεύθυνση: ${activeClinic.a}` +
    "\nearmedical.gr";
  return (
    `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(nm + " — " + reason)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(activeClinic.c + ", " + activeClinic.a)}&dates=${f(dt)}/${f(e)}&add=${encodeURIComponent(activeClinic.appointmentEmail)}` +
    (email ? `&add=${encodeURIComponent(email)}` : "")
  );
}
const Ic = {
  ear: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <path d="M6 8.5c0-3.6 2.9-6.5 6.5-6.5S19 4.9 19 8.5c0 2.8-1.2 4.2-2.5 5.8-1.2 1.5-2 3.2-2 5.2 0 1.4-1.1 2.5-2.5 2.5S9.5 20.9 9.5 19.5" />
      <path d="M12 12a2 2 0 1 0 0-4" />
    </svg>
  ),
  cal: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  chat: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  ph: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2A19.8 19.8 0 0 1 3.1 4.1 2 2 0 0 1 5.1 2h3a2 2 0 0 1 2 1.7l.7 2.8a2 2 0 0 1-.5 2.1L9.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4l2.8.7a2 2 0 0 1 1.7 2z" />
    </svg>
  ),
  chk: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  ),
  bk: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  rt: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  hm: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  ),
  pin: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  plus: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  snd: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  ),
  dot: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill={c}>
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  ),
  al: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  star: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill={c}
      stroke={c}
      strokeWidth="1"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  bag: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  ),
  prog: (c, s = 20) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="1.8"
    >
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  mute: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  ),
  vol: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </svg>
  ),
  zap: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  batt: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round">
      <rect x="1" y="6" width="18" height="12" rx="2" /><line x1="23" y1="10" x2="23" y2="14" />
    </svg>
  ),
  mobile: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round">
      <rect x="5" y="2" width="14" height="20" rx="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
  shield: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  sparkle: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8L12 2z" />
    </svg>
  ),
  smile: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
  smileSoft: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14h8" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
  meh: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="15" x2="16" y2="15" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
  frown: (c, s = 20) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M16 16s-1.5-2-4-2-4 2-4 2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
};
const C = ({ children, onClick, s = {} }) => {
  const T = onClick ? "button" : "div";
  return (
    <T
      onClick={onClick}
      style={{
        background: WH,
        borderRadius: 18,
        padding: "18px 20px",
        border: "none",
        textAlign: "left",
        width: "100%",
        boxSizing: "border-box", // v1.3.62 — αλλιώς width:100% + padding ξεχειλίζει δεξιά (content-box)
        cursor: onClick ? "pointer" : "default",
        boxShadow: SH,
        display: "block",
        marginBottom: 12,
        transition: "transform 0.1s, box-shadow 0.15s",
        ...s,
      }}
    >
      {children}
    </T>
  );
};
const SH2 = ({ title, onBack }) => (
  // v1.3.30 — Shared sub-screen header for ALL full-screen views (Profile,
  // SOS, Προγραμμάτισε Ραντεβού, Notifs, AI Agent, κλπ.). Bulletproof safe-area
  // top inset with max() fallback so Capacitor WebViews που δεν δίνουν σωστά
  // WindowInsets παίρνουν τουλάχιστον 32px breathing room από το status bar.
  <div
    style={{
      padding: "var(--em-header-pad-top) 20px 16px",
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}
  >
    <button
      onClick={onBack}
      style={{
        background: "none",
        border: "none",
        width: 36,
        height: 36,
        borderRadius: 12,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        color: DK,
      }}
    >
      {Ic.bk(DK, 16)}
    </button>
    <span style={{ fontSize: 17, fontWeight: 700, color: DK }}>{title}</span>
  </div>
);
const Btn = ({ children, onClick, primary = true, s = {} }) => (
  <button
    onClick={onClick}
    style={{
      width: "100%",
      padding: "15px 20px",
      borderRadius: 16,
      border: primary ? "none" : `1.5px solid ${O}40`,
      background: primary ? O : WH,
      color: primary ? WH : O,
      fontWeight: 700,
      fontSize: 14,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      transition: "opacity 0.15s",
      letterSpacing: 0.2,
      ...s,
    }}
  >
    {children}
  </button>
);
const CLINICS_DEFAULT_ID = CLINICS[0].id;
const API_URL = "https://admin.earmedical.gr/wp-json/earmedical/v1";
const GCAL_SLOTS_URL = "https://script.google.com/macros/s/AKfycbwA46A-riw6nyExH5vIKoggPH-VvzYchavKzzwlf30k-QvFMh5yADQ1_HUsi-tuDCG5QQ/exec";

// ─── OneSignal Push Notifications ───
// 1. Δημιούργησε δωρεάν λογαριασμό: https://onesignal.com
// 2. Βάλε το App ID εδώ:
const ONESIGNAL_APP_ID = "10a75f78-d31f-465c-9b28-b14d821e459f";
// 3. Πρόσθεσε αρχείο OneSignalSDKWorker.js στο /public (βλ. οδηγίες κάτω)
function applyOneSignalUser(OneSignal, userData) {
  if (!userData) return;
  // In-flight guard — αποφεύγει 409 Conflicts από concurrent PATCH calls
  if (window.__earmedicalOSBusy) return;
  window.__earmedicalOSBusy = true;
  setTimeout(() => { window.__earmedicalOSBusy = false; }, 1500);

  const tags = {};
  if (userData.nm) tags.name = userData.nm;
  if (userData.clinicId) tags.clinic = userData.clinicId;
  if (userData.sd) tags.fitting_date = userData.sd;
  if (userData.dv?.b) tags.brand = userData.dv.b;
  if (userData.dv?.m) tags.model = userData.dv.m;
  if (userData.dv?.p) tags.power_source = userData.dv.p;
  if (userData.profId) tags.prof_id = userData.profId;
  if (userData.birthday) {
    // Στέλνουμε μόνο MM-DD για να μη χρειαστεί ανανέωση κάθε χρόνο
    const bd = userData.birthday; // format YYYY-MM-DD
    if (bd && bd.length === 10) tags.birthday = bd.substring(5); // MM-DD
  }
  const batteryProfile = getBatteryProfile(userData.dv?.t, userData.dv?.p, userData.sL, userData.sR);
  if (batteryProfile) {
    tags.battery_type = batteryProfile.batteryType;
    tags.battery_days_per_cell = String(batteryProfile.daysPerBattery);
    tags.device_count = String(batteryProfile.deviceCount);
    tags.battery_pack_days = String(batteryProfile.packDays);
  }
  // Προσθήκη email ως tag για targeting
  if (userData.em) tags.email = userData.em;
  if (userData.ph2) tags.phone = userData.ph2;
  if (userData.patientStatus) tags.status = userData.patientStatus;
  if (userData.trialDays) tags.trial_days = String(userData.trialDays);
  // v1.3.25 — App version tag ώστε να μπορούμε να σκοπεύουμε broadcast
  // pushes σε συγκεκριμένες εκδόσεις (π.χ. 'στείλε push μόνο σε όσους
  // είναι σε <1.3.30 για να ενημερωθούν'). Διαβάζεται από το build-time
  // env var που γεμίζει το CRA από το .env.
  tags.app_version = process.env.REACT_APP_VERSION || "unknown";
  // Platform tag επίσης χρήσιμο για segment-based targeting (π.χ.
  // 'στείλε μόνο σε Android χρήστες' αν χρειαστεί native-only feature).
  tags.platform = (window.Capacitor && window.Capacitor.getPlatform)
    ? window.Capacitor.getPlatform()
    : "web";

  if (Object.keys(tags).length) {
    // Check if tags have changed since last send (avoid 409 conflicts)
    try {
      const lastTagsKey = 'earmedical_last_os_tags';
      const lastTags = localStorage.getItem(lastTagsKey);
      const currentTagsStr = JSON.stringify(tags);
      if (lastTags !== currentTagsStr) {
        OneSignal.User.addTags(tags);
        localStorage.setItem(lastTagsKey, currentTagsStr);
      }
    } catch(e) {
      OneSignal.User.addTags(tags);
    }
  }
  if (userData.em) {
    try {
      const lastEmailKey = 'earmedical_last_os_email';
      if (localStorage.getItem(lastEmailKey) !== userData.em) {
        OneSignal.User.addEmail(userData.em);
        localStorage.setItem(lastEmailKey, userData.em);
      }
    } catch(e) {
      OneSignal.User.addEmail(userData.em);
    }
  }
  // v1.3.37 — Login με profId (existing customers) ή PH_<phone> (prospects
  // χωρίς ανατεθειμένο prof_id). Όταν αργότερα ο audiologist βάλει prof_id,
  // το app θα κάνει OneSignal.login(profId) στο επόμενο launch και το
  // external_id θα μεταναστεύσει αυτόματα (OneSignal merge).
  const safeProfId = userData.profId ? String(userData.profId).trim() : "";
  const isValidProfId = safeProfId.length > 0 && safeProfId !== '00000000' && safeProfId !== '0' && !/^0+$/.test(safeProfId);
  const phoneClean = userData.ph2 ? String(userData.ph2).replace(/\D/g, '') : "";
  const phoneId = phoneClean.length === 10 ? `PH_${phoneClean}` : "";
  const externalId = isValidProfId ? safeProfId : phoneId;
  if (externalId) {
    try {
      const lastLoginKey = 'earmedical_last_os_login';
      if (localStorage.getItem(lastLoginKey) !== externalId) {
        OneSignal.login(externalId);
        localStorage.setItem(lastLoginKey, externalId);
      }
    } catch(e) {}
  }
}
function initOneSignal(userData) {
  if (!ONESIGNAL_APP_ID || ONESIGNAL_APP_ID.length < 10) return;

  // ─── NATIVE PATH (Capacitor app) ───
  const isCapacitor = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
  if (isCapacitor) {
    if (!window.plugins || !window.plugins.OneSignal) {
      console.log('[Native] OneSignal plugin not yet loaded — retry in 500ms');
      setTimeout(() => initOneSignal(userData), 500);
      return;
    }
    if (!window.__earmedicalOneSignalNativeInit) {
      window.__earmedicalOneSignalNativeInit = true;
      try {
        console.log('[Native] Initializing OneSignal Cordova plugin v5');
        // v1.3.16 — Verbose log level so we can capture init failures
        // via `adb logcat` or Chrome DevTools when a device is USB-attached.
        // 6 = VERBOSE. Drop to 3 (INFO) once the flow is stable.
        if (window.plugins.OneSignal.Debug && window.plugins.OneSignal.Debug.setLogLevel) {
          window.plugins.OneSignal.Debug.setLogLevel(6);
        }
        window.plugins.OneSignal.initialize(ONESIGNAL_APP_ID);

        // v1.3.16 — Subscription change listener. The PREVIOUS code
        // called `pushSubscription.getIdAsync()` which doesn't exist in
        // the v5 Cordova SDK (that was a v3 API). subId came back
        // undefined and the WP backend never got the registration. With
        // the listener we capture the ID the moment Android assigns one
        // and POST it to admin.earmedical.gr.
        const pushSub = window.plugins.OneSignal.User.pushSubscription;
        if (pushSub && pushSub.addEventListener) {
          pushSub.addEventListener('change', function (event) {
            try {
              const cur  = event && event.current ? event.current : null;
              const sid  = cur && cur.id ? String(cur.id) : '';
              const prof = window.__earmedicalProfId || (userData && userData.profId) || '';
              console.log('[Native] pushSubscription change:', cur);
              if (sid && prof) {
                fetch('https://admin.earmedical.gr/wp-json/earmedical/v1/subscription', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ prof_id: prof, subscription_id: sid }),
                }).catch(e => console.log('[Native] Sub POST error:', e));
              }
            } catch (e) { console.log('[Native] Sub change handler error:', e); }
          });
        } else {
          console.log('[Native] pushSubscription.addEventListener missing — SDK shape unexpected');
        }

        window.plugins.OneSignal.Notifications.requestPermission(true).then(accepted => {
          console.log('[Native] Notification permission:', accepted);
          // v1.3.16 — Defensive optIn() after the OS dialog. On some
          // Android 13+ ROMs the subscription stays opted-out even
          // after permission is granted; explicit optIn() guarantees
          // OneSignal will actually deliver notifications.
          if (accepted) {
            try {
              if (pushSub && pushSub.optIn) pushSub.optIn();
            } catch (e) { console.log('[Native] optIn error:', e); }
          }
        }).catch(e => console.log('[Native] Permission error:', e));
      } catch(e) { console.log('[Native] Init error:', e); }
    }
    // Apply user data (login + tags)
    if (userData) {
      try {
        // v1.3.37 — Compute external_id: profId for existing customers,
        // PH_<phone> for prospects without an assigned Coselgi serial.
        // Stash on window so the pushSubscription change listener (fires
        // AFTER this block) can post the subscription to the WP backend.
        const safeProfId = userData.profId ? String(userData.profId).trim() : "";
        const isValidProfId = safeProfId.length > 0 && safeProfId !== '00000000' && safeProfId !== '0' && !/^0+$/.test(safeProfId);
        const phoneClean = userData.ph2 ? String(userData.ph2).replace(/\D/g, '') : "";
        const phoneId = phoneClean.length === 10 ? `PH_${phoneClean}` : "";
        const externalId = isValidProfId ? safeProfId : phoneId;
        if (externalId) window.__earmedicalExternalId = externalId;
        // Backward compat: το παλιό window.__earmedicalProfId κρατείται όπου
        // υπήρχε αναφορά. Όταν δεν έχουμε profId, βάζουμε το phone-id για να
        // συνεχίσει να δουλεύει το subscription POST handler.
        if (externalId) window.__earmedicalProfId = externalId;

        if (externalId) {
          window.plugins.OneSignal.login(externalId);
        }
        const tags = {};
        if (userData.nm) tags.name = userData.nm;
        if (userData.em) tags.email = userData.em;
        if (userData.ph2) tags.phone = userData.ph2;
        if (userData.profId) tags.prof_id = String(userData.profId);
        if (!isValidProfId && phoneId) tags.identity_kind = 'prospect_phone';
        if (userData.clinicId) tags.clinic = userData.clinicId;
        if (userData.patientStatus) tags.status = userData.patientStatus;
        if (userData.trialDays) tags.trial_days = String(userData.trialDays);
        // v1.3.32 — Native path was missing app_version + platform tags.
        // Web (PWA) path already had them via applyOneSignalUser(). This
        // caused OneSignal dashboard to show "App version: -" for every
        // Capacitor install. Fixed so we can segment broadcasts by version.
        tags.app_version = process.env.REACT_APP_VERSION || "unknown";
        tags.platform = (window.Capacitor && window.Capacitor.getPlatform)
          ? window.Capacitor.getPlatform()
          : "android";
        if (Object.keys(tags).length > 0) {
          window.plugins.OneSignal.User.addTags(tags);
        }
        // v1.3.35 — Re-opt-in AFTER login(). Bug που είδαμε: prospect
        // sign-up ολοκληρώθηκε, ο user πάτησε Allow στο notification
        // permission, αλλά το OneSignal dashboard έδειχνε "Unsubscribed".
        // Αιτία: η subscription δημιουργήθηκε στον anonymous user (πριν
        // το login()) και το merge στο external_id user δεν μεταβιβάζει
        // πάντα το subscription state. Forced optIn() μετά το login() το
        // διορθώνει επιθετικά. Αν δεν έχει permission ακόμα, ζητάμε ξανά.
        setTimeout(() => {
          try {
            const sub = window.plugins.OneSignal.User.pushSubscription;
            if (sub && sub.optIn) sub.optIn();
            console.log('[Native] forced optIn after login');
            if (window.plugins.OneSignal.Notifications && window.plugins.OneSignal.Notifications.canRequestPermission) {
              window.plugins.OneSignal.Notifications.permission().then(p => {
                console.log('[Native] notification permission state:', p);
                if (!p) {
                  // Permission denied or not yet granted — try again
                  window.plugins.OneSignal.Notifications.requestPermission(true)
                    .then(a => { console.log('[Native] retry permission:', a); if (a && sub && sub.optIn) sub.optIn(); })
                    .catch(() => {});
                }
              }).catch(() => {});
            }
          } catch(e) { console.log('[Native] post-login optIn error:', e); }
        }, 2000);
        // v1.3.16 — Belt-and-braces sync read after 5s for cases where
        // the change listener doesn't fire (unlikely but covers a few
        // odd Android ROMs). Sync `pushSubscription.id` returns the
        // current value or null. Listener path is still the primary.
        setTimeout(() => {
          try {
            const sub = window.plugins.OneSignal.User.pushSubscription;
            const sid = sub && sub.id ? String(sub.id) : '';
            console.log('[Native] sync subId after 5s:', sid || '(none)');
            if (sid && userData.profId) {
              fetch('https://admin.earmedical.gr/wp-json/earmedical/v1/subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prof_id: userData.profId, subscription_id: sid }),
              }).catch(e => console.log('[Native] Sub POST (sync) error:', e));
            }
          } catch (e) { console.log('[Native] Sync sub read error:', e); }
        }, 5000);
      } catch(e) { console.log('[Native] User apply error:', e); }
    }
    return;
  }

  // ─── WEB PATH (PWA browser, αμετάβλητο) ───
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  if (!window.__earmedicalOneSignalLoaded) {
    const s = document.createElement("script");
    s.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
    s.defer = true;
    document.head.appendChild(s);
    window.__earmedicalOneSignalLoaded = true;
    window.OneSignalDeferred.push(async function(OneSignal) {
      await OneSignal.init({
        appId: ONESIGNAL_APP_ID,
        notifyButton: { enable: false },
        welcomeNotification: {
          title: "Earmedical",
          message: "Λάβετε υπενθυμίσεις ραντεβού και χρήσιμες ενημερώσεις για τα ακουστικά σας."
        },
      });
      applyOneSignalUser(OneSignal, userData);

      // Αποθήκευση Subscription ID στο WordPress
      try {
        const subId = await OneSignal.User.PushSubscription.id;
        if (subId && userData.profId) {
          await fetch('https://admin.earmedical.gr/wp-json/earmedical/v1/subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prof_id: userData.profId, subscription_id: subId }),
          });
        }
      } catch(e) {}
    });
    return;
  }
  if (userData) {
    window.OneSignalDeferred.push(async function(OneSignal) {
      applyOneSignalUser(OneSignal, userData);
    });
  }
}

// ─── Soft-ask ειδοποιήσεων (web/PWA) ────────────────────────────────────────
// Ανεβάζει το opt-in: δείχνει branded οθόνη με την ΑΞΙΑ ΠΡΙΝ το browser prompt.
// Το πραγματικό prompt καλείται ΜΟΝΟ στο «Ενεργοποίηση» → δεν «καίμε» το one-shot
// prompt σε αδιάφορους. Στο NATIVE app δεν εμφανίζεται (έχει δικό του flow· μπαίνει
// στο επόμενο store release). iOS Safari χωρίς install → οδηγίες «Add to Home Screen».
function emWebPushState() {
  try {
    if (typeof Notification === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission; // 'granted' | 'denied' | 'default'
  } catch (e) { return 'unsupported'; }
}
function emIsIOS() {
  try { return /iP(hone|od|ad)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); } catch (e) { return false; }
}
function emIsStandalonePWA() {
  try { return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true; } catch (e) { return false; }
}
function emRequestWebPush() {
  return new Promise((resolve) => {
    try {
      if (!window.OneSignalDeferred) { resolve(false); return; }
      window.OneSignalDeferred.push(async function (OneSignal) {
        try {
          await OneSignal.Notifications.requestPermission();
          let granted = false;
          try { granted = !!OneSignal.Notifications.permission; } catch (e) { granted = (emWebPushState() === 'granted'); }
          try { if (granted && OneSignal.User && OneSignal.User.PushSubscription && OneSignal.User.PushSubscription.optIn) OneSignal.User.PushSubscription.optIn(); } catch (e) {}
          resolve(granted);
        } catch (e) { resolve(false); }
      });
    } catch (e) { resolve(false); }
  });
}
const EM_PUSH_SNOOZE_KEY = 'em_push_softask_snooze';
function PushSoftAsk({ active }) {
  const [view, setView] = useState(null); // null | 'ask' | 'ios' | 'ok'
  useEffect(() => {
    if (!active) return undefined;
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    if (isNative) return undefined; // native app: δικό του flow
    let snoozeTs = 0; try { snoozeTs = parseInt(localStorage.getItem(EM_PUSH_SNOOZE_KEY) || '0', 10) || 0; } catch (e) {}
    if (snoozeTs && Date.now() < snoozeTs) return undefined;
    const state = emWebPushState();
    if (state === 'granted' || state === 'unsupported' || state === 'denied') return undefined; // ήδη ναι / αδύνατο / μπλοκαρισμένο
    const iosNotInstalled = emIsIOS() && !emIsStandalonePWA();
    const t = setTimeout(() => setView(iosNotInstalled ? 'ios' : 'ask'), 3500);
    return () => clearTimeout(t);
  }, [active]);

  if (!view) return null;

  const doSnooze = (days) => {
    try { localStorage.setItem(EM_PUSH_SNOOZE_KEY, String(Date.now() + days * 86400000)); } catch (e) {}
    setView(null);
  };
  const onEnable = async () => {
    const ok = await emRequestWebPush();
    if (ok) { setView('ok'); setTimeout(() => setView(null), 2200); }
    else { doSnooze(6); }
  };

  const overlay = { position: 'fixed', inset: 0, zIndex: 2147483000, background: 'rgba(20,28,40,.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16 };
  const card = { width: '100%', maxWidth: 382, background: WH, borderRadius: 22, padding: '26px 22px 20px', boxShadow: '0 20px 60px rgba(20,30,50,.3)', textAlign: 'center' };
  const primaryBtn = { width: '100%', border: 'none', borderRadius: 13, padding: 15, background: 'linear-gradient(135deg,#f28c28,' + O + ')', color: WH, fontSize: 15.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 6px 16px rgba(226,119,48,.32)' };
  const ghostBtn = { width: '100%', border: 'none', background: 'none', color: MG, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 12, padding: 6 };
  const headIcon = { width: 72, height: 72, margin: '0 auto 18px', borderRadius: 20, background: 'linear-gradient(135deg,#f28c28,' + O + ')', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const ic = (path) => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{path}</svg>);
  const Bullet = ({ icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, textAlign: 'left' }}>
      <span style={{ flex: '0 0 auto', width: 30, height: 30, borderRadius: 9, background: OL, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
      <span style={{ fontSize: 13.5, lineHeight: 1.4, color: DK, paddingTop: 4 }}>{children}</span>
    </div>
  );

  if (view === 'ok') {
    return (
      <div style={overlay}>
        <div style={{ ...card, paddingBottom: 26 }}>
          <div style={{ ...headIcon, background: '#19a463' }}>
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, color: DK }}>Έτοιμο</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: DK2, marginTop: 6 }}>Θα λαμβάνεις ειδοποιήσεις για τα ραντεβού και τις ενημερώσεις σου.</div>
        </div>
      </div>
    );
  }

  if (view === 'ios') {
    return (
      <div style={overlay}>
        <div style={card}>
          <div style={headIcon}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17V3" /><polyline points="6 9 12 3 18 9" /><path d="M4 13v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" /></svg>
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, color: DK, marginBottom: 8 }}>Πρόσθεσε την Earmedical στην Αρχική οθόνη</div>
          <div style={{ fontSize: 13.5, lineHeight: 1.55, color: DK2, marginBottom: 18 }}>Για να λαμβάνεις ειδοποιήσεις στο iPhone, η εφαρμογή πρέπει να μπει στην Αρχική οθόνη:</div>
          <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 22 }}>
            <Bullet icon={<span style={{ fontWeight: 800, color: O, fontSize: 13 }}>1</span>}>Πάτα το κουμπί <strong>Κοινή χρήση</strong> (τετράγωνο με βέλος προς τα πάνω) στο κάτω μέρος του Safari.</Bullet>
            <Bullet icon={<span style={{ fontWeight: 800, color: O, fontSize: 13 }}>2</span>}>Διάλεξε <strong>«Προσθήκη στην οθόνη Αφετηρίας»</strong>.</Bullet>
            <Bullet icon={<span style={{ fontWeight: 800, color: O, fontSize: 13 }}>3</span>}>Άνοιξε την Earmedical από το νέο εικονίδιο και ενεργοποίησε τις ειδοποιήσεις.</Bullet>
          </div>
          <button style={primaryBtn} onClick={() => doSnooze(3)}>Κατάλαβα</button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={headIcon}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: DK, marginBottom: 8 }}>Μην χάνεις τίποτα σημαντικό</div>
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: DK2, marginBottom: 20 }}>Ενεργοποίησε τις ειδοποιήσεις για να σε κρατάμε ενήμερο για την ακοή σου.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 13, marginBottom: 24 }}>
          <Bullet icon={ic(<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>)}>Υπενθυμίσεις για τα <strong>ραντεβού</strong> σου</Bullet>
          <Bullet icon={ic(<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />)}><strong>Follow-up</strong> από τον ειδικό σου</Bullet>
          <Bullet icon={ic(<><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></>)}>Προσφορές για <strong>μπαταρίες</strong> και αξεσουάρ</Bullet>
        </div>
        <button style={primaryBtn} onClick={onEnable}>Ενεργοποίηση ειδοποιήσεων</button>
        <button style={ghostBtn} onClick={() => doSnooze(6)}>Όχι τώρα</button>
      </div>
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState("home"),
    [ans, setAns] = useState({}),
    [qD, setQD] = useState(false),
    [qSending, setQSending] = useState(false),
    [qScore, setQScore] = useState(null),
    // v1.3.41 — Smart quiz prompt card. Tracks last questionnaire date in
    // localStorage so we can adapt UI (prominent/subtle/hidden) based on
    // how long ago the patient filled the questionnaire. Reload-safe.
    [lastQuizDate, setLastQuizDate] = useState(() => {
      try { return localStorage.getItem('em_last_quiz_date') || ''; } catch(e) { return ''; }
    }),
    [sp, setSp] = useState(true),
    // ─── AUTH STATES ───
    [authStep, setAuthStep] = useState("booting"), // 'booting' | 'phone' | 'otp' | 'set-pin' | 'enter-pin' | 'prospect-signup' | 'logged-in'
    [authPhone, setAuthPhone] = useState(""),
    [authOtp, setAuthOtp] = useState(""),
    [authPin, setAuthPin] = useState(""),
    [authPinConfirm, setAuthPinConfirm] = useState(""),
    [authToken, setAuthToken] = useState(""),
    [authError, setAuthError] = useState(""),
    [authLoading, setAuthLoading] = useState(false),
    [authPhoneMasked, setAuthPhoneMasked] = useState(""),
    [authResendTimer, setAuthResendTimer] = useState(0),
    // ─── PROSPECT SIGNUP FORM ─── (v1.3.33)
    [signupName, setSignupName] = useState(""),
    [signupEmail, setSignupEmail] = useState(""),
    [signupClinic, setSignupClinic] = useState("athens"),
    // v1.3.36 — Flag: prospect μόλις έκανε signup, χρειάζεται να ορίσει PIN
    // μετά το OTP verify. Διαφορά από το existing flow όπου ο prof_id είναι
    // ο ίδιος ο PIN (Coselgi number για customer). Prospects έχουν PR-prefixed
    // prof_id, οπότε πρέπει να ορίσουν δικό τους 4ψήφιο PIN.
    [needsPinSetup, setNeedsPinSetup] = useState(false),
    [showDeleteModal, setShowDeleteModal] = useState(false),
    [deleteConfirmText, setDeleteConfirmText] = useState(""),
    [deleteLoading, setDeleteLoading] = useState(false),
    [deleteError, setDeleteError] = useState(""),
    [expandedGuideSection, setExpandedGuideSection] = useState(0),
    [optins, setOptins] = useState({ offers: true, news: true, reminders: true, nps: true }),
    [optinsLoading, setOptinsLoading] = useState(false),
    [optinsSaving, setOptinsSaving] = useState(false),
    [optinsSaved, setOptinsSaved] = useState(false),
    [sd, setSd] = useState(() => new Date().toISOString().split("T")[0]),
    [dv, setDv] = useState({ b: "", m: "", t: "RIC", p: "rechargeable" }),
    [clinicId, setClinicId] = useState(CLINICS_DEFAULT_ID),
    [nm, setNm] = useState(""),
    [ph2, setPh2] = useState(""),
    [em, setEm] = useState(""),
    [profId, setProfId] = useState(""),
    [birthday, setBirthday] = useState(""),
    [sL, setSL] = useState(""),
    [sR, setSR] = useState(""),
    [sC, setSC] = useState(""),
    [warrantyYears, setWarrantyYears] = useState(2),
    [patientStatus, setPatientStatus] = useState("purchased"),
    [trialDays, setTrialDays] = useState(14),
    [trialStart, setTrialStart] = useState(""),
    [dailyMood, setDailyMood] = useState(null),
    [dailyComment, setDailyComment] = useState(""),
    [dailySent, setDailySent] = useState(false),
    [loyaltyStamps, setLoyaltyStamps] = useState(0),
    // ─── OFFERS STATES ───
    [offers, setOffers] = useState([]),
    [offerSeen, setOfferSeen] = useState(() => {
      try { return JSON.parse(localStorage.getItem("ear_offer_seen") || "[]"); } catch(e) { return []; }
    }),
    [offerScreen, setOfferScreen] = useState(false),
    [offerSelectedItem, setOfferSelectedItem] = useState(null),
    [offerSubmitting, setOfferSubmitting] = useState(false),
    [offerThankYou, setOfferThankYou] = useState(false),
    [offerAiModal, setOfferAiModal] = useState(null), // { offerId, itemId, brand, model } or null
    [offerAiLoading, setOfferAiLoading] = useState(false),
    [offerAiAnswer, setOfferAiAnswer] = useState(""),
    [offerExpandedItem, setOfferExpandedItem] = useState(null), // itemId when "Δείτε περισσότερα" is tapped

    [fs, setFs] = useState(0),
    [sub, setSub] = useState(null),
    [si, setSi] = useState(null),
    [ac, setAc] = useState({}),
    // ─── Notifications Inbox state ───
    [notifs, setNotifs] = useState([]),
    [notifsLoading, setNotifsLoading] = useState(false),
    [notifsUnread, setNotifsUnread] = useState(0),
    // ─── v1.3.44 — Admin Announcements (banner on home + detail screen) ───
    [announcements, setAnnouncements] = useState([]),
    [selectedAnnouncement, setSelectedAnnouncement] = useState(null),
    [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState(() => {
      // Σώζεται locally — αν ο admin αρχειοθετήσει την ανακοίνωση, εξαφανίζεται ούτως ή
      // άλλως. Αυτή η λίστα κρατάει user-side dismissals ώστε αν την κλείσει ένας πελάτης
      // να μην του ξαναεμφανιστεί όσο είναι ακόμα active server-side.
      try { return JSON.parse(localStorage.getItem("ear_announcement_dismissed") || "[]"); } catch(e) { return []; }
    }),
    [calFilter, setCalFilter] = useState("upcoming"),
    [newADate, setNewADate] = useState(
      () => new Date().toISOString().split("T")[0],
    ),
    [newAReason, setNewAReason] = useState(""),
    [newATime, setNewATime] = useState(""),
    [liveSlots, setLiveSlots] = useState(null),  // null=δεν φορτώθηκε, []= κλειστό/άδειο
    [loadingSlots, setLoadingSlots] = useState(false),
    [rescheduleAppt, setRescheduleAppt] = useState(null), // ραντεβού που αλλάζουμε
    [rescheduleDate, setRescheduleDate] = useState(""),
    [rescheduleTime, setRescheduleTime] = useState(""),
    [rescheduleLiveSlots, setRescheduleLiveSlots] = useState(null),
    [rescheduleLoadingSlots, setRescheduleLoadingSlots] = useState(false),
    [rescheduling, setRescheduling] = useState(false),
    [rescheduleSuccess, setRescheduleSuccess] = useState(false),
    [cancelConfirm, setCancelConfirm] = useState(false),
    [cancelling, setCancelling] = useState(false),
    [showBookingCalendar, setShowBookingCalendar] = useState(false),
    [bookingCalendarMonth, setBookingCalendarMonth] = useState(() => {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }),
    [booking, setBooking] = useState(null),
    [wpAppts, setWpAppts] = useState([]),
    [loadingAppts, setLoadingAppts] = useState(false),
    [formError, setFormError] = useState(""),
    [checkin, setCheckin] = useState(null),
    [termsAccepted, setTermsAccepted] = useState(false),
    [audiologist, setAudiologist] = useState(""),
    [showPurchaseModal, setShowPurchaseModal] = useState(false),
    [purchaseSubmitting, setPurchaseSubmitting] = useState(false),
    [purchaseSent, setPurchaseSent] = useState(false),
    [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null),
    [isAppInstalled, setIsAppInstalled] = useState(false),
    [showIosInstall, setShowIosInstall] = useState(false),
    [installBannerDismissed, setInstallBannerDismissed] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatPatientId, setChatPatientId] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  // v1.3.34 — Chat attachment state
  const [chatAttachment, setChatAttachment] = useState(null); // { url, type, name, size, previewUrl }
  const [chatUploading, setChatUploading] = useState(false);
  const chatFileInputRef = useRef(null);

  // AI Agent state
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [resolutionFeedback, setResolutionFeedback] = useState(null); // null | 'resolved' | 'needs_human'
  const [aiLoading, setAiLoading] = useState(false);
  // v1.3.57 — Εικόνα προς αξιολόγηση (ακοόγραμμα/βλάβη) + φωνή→κείμενο στο chat του Ορφέα
  const [aiImage, setAiImage] = useState(null);          // { url: dataURL, name }
  const [aiRecording, setAiRecording] = useState(false);
  const [aiTranscribing, setAiTranscribing] = useState(false);
  const aiFileInputRef = useRef(null);
  const aiMediaRec = useRef(null);
  const aiAudioChunks = useRef([]);
  const aiTextRef = useRef(null);
  // v1.3.61 — Auto-grow του πλαισίου εισαγωγής του Ορφέα ώστε να φαίνεται ΟΛΟ το κείμενο
  // (αντί να κρύβεται/κόβεται σε μονή γραμμή). Μεγαλώνει με το περιεχόμενο μέχρι 120px, μετά scroll.
  useEffect(() => {
    const el = aiTextRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }, [aiInput]);
  // v1.3.58 — Proactive διαδραστικός Ορφέας ("moments")
  const [momentRefresh, setMomentRefresh] = useState(0);
  const [momentOverlayOpen, setMomentOverlayOpen] = useState(false);
  const [momentReplyView, setMomentReplyView] = useState(null); // { id, text, actions:[{label,value,primary}] }
  const [updateInfo, setUpdateInfo] = useState(null); // v1.3.64 — in-app «νέα έκδοση» banner: { latest, native, url }
  // v1.3.63 — Store-download banner στο PWA/web (όχι native): προτείνει App Store/Google Play.
  const [storeBannerDismissed, setStoreBannerDismissed] = useState(() => { try { return localStorage.getItem('em_store_banner_dismissed') === '1'; } catch(e) { return false; } });
  const isNativeApp = !!(typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

  // Online/offline detection
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Cache version check - purge caches if server version changed
  useEffect(() => {
    const checkCacheVersion = async () => {
      try {
        const res = await fetch(`${API_URL}/cache-version`);
        const data = await res.json();
        if (!data.success) return;
        const serverVersion = String(data.version);
        const localVersion = localStorage.getItem('earmedical_cache_version');
        if (localVersion && localVersion !== serverVersion) {
          // Purge all app caches (keep essential user data)
          const keysToKeep = ['earmedical_data', 'earmedical_install_home_dismissed', 'earmedical_purchase_intent_sent'];
          const allKeys = Object.keys(localStorage);
          allKeys.forEach(k => {
            if (k.startsWith('earmedical_') && !keysToKeep.includes(k)) {
              localStorage.removeItem(k);
            }
          });
          // Also clear session storage (knowledge base cache)
          try { sessionStorage.clear(); } catch(e) {}
          console.log('[Cache] Purged stale cache, new version:', serverVersion);
        }
        localStorage.setItem('earmedical_cache_version', serverVersion);
      } catch(e) {}
    };
    checkCacheVersion();
  }, []);

  // ═══════════════════════════════════════════════════════════════
  //  NOTIFICATIONS INBOX
  // ═══════════════════════════════════════════════════════════════
  const NOTIFS_API = "https://admin.earmedical.gr/notifications-api.php";

  const fetchNotifs = async () => {
    if (!profId || !ph2) return;
    setNotifsLoading(true);
    try {
      const url = `${NOTIFS_API}?action=list&prof_id=${encodeURIComponent(profId)}&phone=${encodeURIComponent(ph2)}&limit=50`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success && Array.isArray(data.notifications)) {
        setNotifs(data.notifications);
        const unread = data.notifications.filter(n => !n.is_read).length;
        setNotifsUnread(unread);
      }
    } catch (e) {
      console.warn("[Notifs] fetch failed:", e.message);
    } finally {
      setNotifsLoading(false);
    }
  };

  const fetchNotifsUnreadCount = async () => {
    if (!profId || !ph2) return;
    try {
      const url = `${NOTIFS_API}?action=unread_count&prof_id=${encodeURIComponent(profId)}&phone=${encodeURIComponent(ph2)}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success && typeof data.unread_count === 'number') {
        setNotifsUnread(data.unread_count);
      }
    } catch (e) {
      // Silent — polling, don't spam logs
    }
  };

  const markNotifRead = async (id) => {
    if (!profId || !ph2 || !id) return;
    // Optimistic UI update
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
    setNotifsUnread(prev => Math.max(0, prev - 1));
    try {
      await fetch(`${NOTIFS_API}?action=mark_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, prof_id: profId, phone: ph2 }),
      });
    } catch (e) {
      console.warn("[Notifs] mark read failed:", e.message);
    }
  };

  const markAllNotifsRead = async () => {
    if (!profId || !ph2) return;
    // Optimistic UI update
    setNotifs(prev => prev.map(n => ({ ...n, is_read: 1 })));
    setNotifsUnread(0);
    try {
      await fetch(`${NOTIFS_API}?action=mark_all_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prof_id: profId, phone: ph2 }),
      });
    } catch (e) {
      console.warn("[Notifs] mark all read failed:", e.message);
    }
  };

  // Poll unread count when logged in
  // - Every 30 seconds (lightweight) while app is active
  // - Immediately when app returns to foreground (visibility change)
  // - Immediately when window regains focus
  useEffect(() => {
    if (authStep !== "logged-in" || !profId || !ph2) return;

    // Initial fetch
    fetchNotifsUnreadCount();

    // Periodic poll while app is in foreground
    const intervalId = setInterval(() => {
      // Only fetch if document is visible (saves battery when minimized)
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchNotifsUnreadCount();
      }
    }, 30000);

    // Refresh immediately when user returns to the app
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchNotifsUnreadCount();
      }
    };
    const onFocus = () => {
      fetchNotifsUnreadCount();
    };
    const onResume = () => {
      // Capacitor native resume event (Android/iOS)
      fetchNotifsUnreadCount();
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
      // Capacitor App resume event (if Capacitor is available)
      if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App) {
        try {
          window.Capacitor.Plugins.App.addListener('resume', onResume);
          window.Capacitor.Plugins.App.addListener('appStateChange', (state) => {
            if (state && state.isActive) onResume();
          });
        } catch (e) { /* ignore */ }
      }
    }

    return () => {
      clearInterval(intervalId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
    };
  }, [authStep, profId, ph2]);

  // Refresh notifications list whenever the user opens the notifs screen
  useEffect(() => {
    if (sub === "notifs" && authStep === "logged-in" && profId && ph2) {
      fetchNotifs();
    }
  }, [sub]);

  // ═══════════════════════════════════════════════════════════════
  //  v1.3.44 — ADMIN ANNOUNCEMENTS (banner on home + detail screen)
  // ═══════════════════════════════════════════════════════════════
  const ANNOUNCEMENTS_URL = "https://admin.earmedical.gr/wp-json/earmedical/v1/announcements/active";

  const fetchAnnouncements = async () => {
    if (!profId || !ph2) return;
    try {
      const url = `${ANNOUNCEMENTS_URL}?prof_id=${encodeURIComponent(profId)}&phone=${encodeURIComponent(ph2)}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.announcements)) {
        setAnnouncements(data.announcements);
      }
    } catch (e) {
      // Silent — polling, don't spam console
    }
  };

  // Fetch on login + poll every 60s while visible
  useEffect(() => {
    if (authStep !== "logged-in" || !profId || !ph2) return;
    fetchAnnouncements();
    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fetchAnnouncements();
      }
    }, 60000);
    return () => clearInterval(intervalId);
  }, [authStep, profId, ph2]);

  const dismissAnnouncement = (id) => {
    const next = Array.from(new Set([...(dismissedAnnouncementIds || []), id]));
    setDismissedAnnouncementIds(next);
    try { localStorage.setItem("ear_announcement_dismissed", JSON.stringify(next)); } catch(e) {}
    logAnnouncementEvent(id, 'dismissed');
  };

  // v1.3.48 — Log analytics event for an announcement. Fire-and-forget POST
  // to the patient-auth endpoint. Silent on failure (don't disrupt UX).
  const logAnnouncementEvent = (annId, type, extra) => {
    if (!annId || !type || !profId || !ph2) return;
    try {
      fetch(`https://admin.earmedical.gr/wp-json/earmedical/v1/announcements/${encodeURIComponent(annId)}/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: type,
          prof_id: profId,
          phone: ph2,
          data: extra || null,
        }),
      }).catch(() => {});
    } catch (e) { /* silent */ }
  };

  // v1.3.46 — Deep link handler for announcement push notifications.
  // Called from 3 entry points:
  //   1. OneSignal native click handler (Capacitor) — additionalData.announcement_id
  //   2. OneSignal web click handler (PWA browser) — same
  //   3. URL query param ?announcement=ID on initial load (web push fallback)
  // Fetches the single announcement (works even if no longer in active list)
  // and navigates straight to the detail screen.
  const handleAnnouncementDeepLink = async (annId) => {
    if (!annId || !profId || !ph2) return;
    try {
      const url = `https://admin.earmedical.gr/wp-json/earmedical/v1/announcements/by-id/${encodeURIComponent(annId)}?prof_id=${encodeURIComponent(profId)}&phone=${encodeURIComponent(ph2)}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.announcement) {
        // v1.3.48 — log "opened" from push deep link path
        logAnnouncementEvent(annId, 'opened', { source: 'push' });
        setSelectedAnnouncement(data.announcement);
        setSub("announcement-detail");
      }
    } catch (e) {
      console.log('[Deep link] fetch error:', e);
    }
  };

  // Pending deep-link ID (set BEFORE auth completes). Once user is
  // logged in, the useEffect below picks it up and navigates.
  const [pendingAnnouncementId, setPendingAnnouncementId] = useState(null);

  // 1. Parse ?announcement=ID from URL on initial mount (web push path)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const annId = params.get('announcement');
      if (annId && /^\d+$/.test(annId)) {
        setPendingAnnouncementId(parseInt(annId, 10));
        // Strip the query param so refresh doesn't re-trigger
        if (window.history && window.history.replaceState) {
          const cleanUrl = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', cleanUrl);
        }
      }
    } catch (e) { /* silent */ }
  }, []);

  // 2. OneSignal click handlers — native (Capacitor) + web (PWA browser)
  useEffect(() => {
    const handleClick = (event) => {
      try {
        const notification = (event && event.notification) || event;
        const data = (notification && (notification.additionalData || (notification.payload && notification.payload.additionalData))) || {};
        if (data && data.type === 'announcement' && data.announcement_id) {
          setPendingAnnouncementId(parseInt(data.announcement_id, 10));
        }
      } catch (e) { console.log('[OneSignal click] parse error:', e); }
    };

    let nativeRegistered = false;
    let webRegistered = false;

    // Native (Capacitor + OneSignal Cordova v5)
    const tryNative = () => {
      if (window.plugins && window.plugins.OneSignal && window.plugins.OneSignal.Notifications && window.plugins.OneSignal.Notifications.addEventListener) {
        try {
          window.plugins.OneSignal.Notifications.addEventListener('click', handleClick);
          nativeRegistered = true;
        } catch (e) { /* silent */ }
      }
    };
    tryNative();
    if (!nativeRegistered) {
      // Retry after OneSignal init finishes
      setTimeout(tryNative, 2000);
    }

    // Web (OneSignal Web SDK v16)
    if (window.OneSignalDeferred) {
      window.OneSignalDeferred.push((OneSignal) => {
        try {
          if (OneSignal.Notifications && OneSignal.Notifications.addEventListener) {
            OneSignal.Notifications.addEventListener('click', handleClick);
            webRegistered = true;
          }
        } catch (e) { /* silent */ }
      });
    }

    // No reliable removal API across both SDKs; rely on app-lifetime listener.
    return () => {};
  }, []);

  // 3. When user logs in AND there's a pending deep link → navigate
  useEffect(() => {
    if (authStep === "logged-in" && pendingAnnouncementId && profId && ph2) {
      handleAnnouncementDeepLink(pendingAnnouncementId);
      setPendingAnnouncementId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStep, pendingAnnouncementId, profId, ph2]);

  // Compute the top-1 announcement to show as banner (priority + recency)
  // Excludes locally-dismissed ones. Returns null if none.
  const topAnnouncement = (() => {
    if (!announcements || announcements.length === 0) return null;
    const dismissedSet = new Set(dismissedAnnouncementIds || []);
    const visible = announcements.filter(a => !dismissedSet.has(a.id));
    return visible.length > 0 ? visible[0] : null; // backend already sorts by priority+recency
  })();

  // Priority badge meta — drives COLOR only on patient side.
  // The user-facing LABEL comes from getAnnouncementLabel(category, priority).
  const ANN_PRIORITY_META = {
    low:    { label: "Χαμηλή",    color: "#666",    bg: "#F0F0F0" },
    medium: { label: "Μεσαία",    color: "#1976D2", bg: "#E6F1FB" },
    high:   { label: "Υψηλή",     color: "#EF801A", bg: "#FAEEDA" },
    urgent: { label: "Επείγουσα", color: "#C62828", bg: "#FFEBEE" },
  };

  // v1.3.49 — Patient-facing label derived from category (primary) with
  // priority fallback for 'general'. Categories: general/offer/appointment/service.
  // This is what appears in the banner pill instead of the raw priority.
  const ANN_PATIENT_LABELS = {
    offer:       "Νέα προσφορά",
    appointment: "Υπενθύμιση ραντεβού",
    service:     "Ενημέρωση υποστήριξης",
    general: {
      low:    "Ενημέρωση",
      medium: "Νέα ανακοίνωση",
      high:   "Σημαντική ενημέρωση",
      urgent: "Επείγουσα ενημέρωση",
    },
  };
  const getAnnouncementLabel = (a) => {
    const cat = (a && a.category) || "general";
    if (cat !== "general" && ANN_PATIENT_LABELS[cat]) return ANN_PATIENT_LABELS[cat];
    const map = ANN_PATIENT_LABELS.general;
    return map[(a && a.priority) || "medium"] || "Ενημέρωση";
  };

  // Fetch opt-in preferences when user opens Settings screen
  useEffect(() => {
    if (sub !== "settings" || authStep !== "logged-in" || !profId) return;
    let cancelled = false;
    (async () => {
      setOptinsLoading(true);
      try {
        const tk = sessionStorage.getItem("authToken") || localStorage.getItem("authToken") || "";
        const res = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/auth/optins", {
          headers: { Authorization: tk ? `Bearer ${tk}` : "" },
        });
        const data = await res.json();
        if (!cancelled && data && typeof data === "object" && !data.code) {
          setOptins({
            offers: data.offers !== false,
            news: data.news !== false,
            reminders: data.reminders !== false,
            nps: data.nps !== false,
          });
        }
      } catch (e) {
        console.warn("[Optins] Fetch failed:", e.message);
      } finally {
        if (!cancelled) setOptinsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sub, authStep, profId]);

  // Helper: relative time formatter (Greek)
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const then = new Date(dateStr.replace(' ', 'T'));
      const now = new Date();
      const diffSec = Math.floor((now - then) / 1000);
      if (diffSec < 60) return "τώρα";
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}λ πριν`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}ω πριν`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay === 1) return "χθες";
      if (diffDay < 7) return `${diffDay} ημέρες πριν`;
      return then.toLocaleDateString("el-GR", { day: "numeric", month: "short" });
    } catch (e) {
      return "";
    }
  };

  // Notification category styling
  const NOTIF_CATEGORIES = {
    message:     { color: "#1976D2", bg: "#E6F1FB", textColor: "#0C447C", label: "Συνομιλία με ειδικό" },
    appointment: { color: "#2E7D32", bg: "#E1F5EE", textColor: "#0F6E56", label: "Ραντεβού" },
    trial:       { color: "#F9A825", bg: "#FAEEDA", textColor: "#854F0B", label: "Δοκιμή" },
    birthday:    { color: "#E91E63", bg: "#FBEAF0", textColor: "#72243E", label: "Γενέθλια" },
    service:     { color: "#EF801A", bg: "#FAEEDA", textColor: "#854F0B", label: "Συντήρηση" },
    reward:      { color: "#EF801A", bg: "#FAEEDA", textColor: "#854F0B", label: "Ανταμοιβή" },
    system:      { color: "#888", bg: "#F0F0F0", textColor: "#5F5E5A", label: "Ενημέρωση" },
  };

  // Open a notification: mark read + navigate based on link
  const openNotif = (n) => {
    if (!n.is_read) markNotifRead(n.id);
    const link = n.link || "";
    if (link.includes("screen=chat")) {
      setSub("chat");
    } else if (link.includes("screen=appointments")) {
      setSub(null);
      setTab("cal");
    } else if (link.includes("screen=shop")) {
      setSub(null);
      setTab("shop");
    } else {
      // Default: stay on notifications screen
    }
  };

  // ─── Share helper ───
  // Tries Capacitor Share plugin first (native sheet on Android+iOS), then Web Share API,
  // then falls back to clipboard. Always gives the user clear feedback.
  const shareContent = async (text, title) => {
    // Strategy 1: Capacitor Share plugin (best UX on native platforms)
    if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Share) {
      try {
        await window.Capacitor.Plugins.Share.share({
          title: title || "Earmedical",
          text: text,
          dialogTitle: "Κοινοποίηση",
        });
        return; // success
      } catch(e) {
        // User cancelled or plugin failed — fall through to next strategy
        if (e && e.message && e.message.toLowerCase().includes('cancel')) {
          return; // user explicitly cancelled — don't show fallback
        }
      }
    }

    // Strategy 2: Web Share API (works in mobile browsers + iOS Safari)
    if (navigator.share) {
      try {
        await navigator.share({ text: text, title: title });
        return; // success
      } catch(e) {
        // User cancelled or share failed
        if (e && e.name === 'AbortError') return; // user cancelled
        // Other error — fall through to clipboard fallback
      }
    }

    // Strategy 3: Clipboard fallback (Android WebView without share plugin)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        alert("Το μήνυμα αντιγράφηκε!\n\nΕπικολλήστε το όπου θέλετε (WhatsApp, SMS, Email).");
        return;
      }
    } catch(e) {}

    // Strategy 4: Last resort — old execCommand
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert("Το μήνυμα αντιγράφηκε!\n\nΕπικολλήστε το όπου θέλετε.");
    } catch(e) {
      alert("Δεν ήταν δυνατή η κοινοποίηση. Δοκιμάστε ξανά.");
    }
  };

  const ref = useRef(null),
    nR = useRef(null),
    pR = useRef(null),
    eR = useRef(null),
    mR = useRef(null),
    sLR = useRef(null),
    sRR = useRef(null),
    sCR = useRef(null),
    prevWpStatusesRef = useRef({});
  useEffect(() => {
    const loadData = async () => {
      try {
        // ─── Έλεγχος αυθεντικοποίησης ───
        const savedToken = localStorage.getItem("earmedical_auth_token");
        const savedPhone = localStorage.getItem("earmedical_auth_phone");

        if (savedToken && savedPhone) {
          // Verify token μέσω /auth/me
          try {
            const meRes = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/auth/me", {
              headers: { "X-Auth-Token": savedToken },
            });
            if (meRes.ok) {
              const meData = await meRes.json();
              if (meData.success) {
                setAuthToken(savedToken);
                setAuthPhone(meData.phone || savedPhone);
                setAuthStep("logged-in");
                setPh2(meData.phone || savedPhone);
                setProfId(meData.prof_id || "");
                setNm(meData.patient_name || "");
                // Save patient_id for offers & other features
                if (meData.patient_id) {
                  setChatPatientId(meData.patient_id);
                }
                // Συνέχεια με loadData από ph2/em όπως πριν
              } else {
                // Invalid token — go to phone step
                localStorage.removeItem("earmedical_auth_token");
                setAuthStep("phone");
                return;
              }
            } else {
              localStorage.removeItem("earmedical_auth_token");
              setAuthStep("phone");
              return;
            }
          } catch(e) {
            // Δεν κάνει logout σε network error — μπορεί να είναι offline
            console.log('[Auth] Could not verify token, staying logged in:', e);
            setAuthToken(savedToken);
            setAuthPhone(savedPhone);
            setAuthStep("logged-in");
            setPh2(savedPhone);
          }
        } else {
          // ─── AUTO-MIGRATION για υπάρχοντες χρήστες ───
          // Αν υπάρχουν ήδη δεδομένα στο localStorage (ph2 + profId), δοκίμασε auto-migration
          try {
            const s = localStorage.getItem("earmedical_data");
            if (s) {
              const d = JSON.parse(s);
              if (d.ph2 && d.profId) {
                console.log('[Auth] Found existing user, attempting auto-migrate:', d.ph2, d.profId);
                const migrateRes = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/auth/auto-migrate", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ phone: d.ph2, prof_id: d.profId }),
                });
                if (migrateRes.ok) {
                  const mData = await migrateRes.json();
                  if (mData.success && mData.token) {
                    console.log('[Auth] ✅ Auto-migration successful');
                    localStorage.setItem("earmedical_auth_token", mData.token);
                    localStorage.setItem("earmedical_auth_phone", mData.phone);
                    setAuthToken(mData.token);
                    setAuthPhone(mData.phone);
                    setProfId(mData.prof_id || "");
                    setNm(mData.patient_name || "");
                    setPh2(mData.phone);
                    if (mData.patient_id) setChatPatientId(mData.patient_id);
                    setAuthStep("logged-in");
                  } else {
                    setAuthStep("phone");
                  }
                } else {
                  setAuthStep("phone");
                }
              } else {
                setAuthStep("phone");
              }
            } else {
              setAuthStep("phone");
            }
          } catch(e) {
            console.error('[Auth] Auto-migrate error:', e);
            setAuthStep("phone");
          }
        }

        const s = localStorage.getItem("earmedical_data");
        if (s) {
          const d = JSON.parse(s);
          if (d.nm) setNm(d.nm);
          if (d.em) setEm(d.em);
          if (d.ph2) setPh2(d.ph2);
          if (d.profId) setProfId(d.profId);
          if (d.birthday) setBirthday(d.birthday);
          if (d.sd) setSd(d.sd);
          if (d.dv) setDv(d.dv);
          if (d.clinicId) setClinicId(d.clinicId);
          if (d.sL) setSL(d.sL);
          if (d.sR) setSR(d.sR);
          if (d.sC) setSC(d.sC);
          if (d.warrantyYears) setWarrantyYears(d.warrantyYears);
          if (d.patientStatus) setPatientStatus(d.patientStatus);
          if (d.trialDays) setTrialDays(d.trialDays);
          if (d.trialStart) setTrialStart(d.trialStart);
          if (d.audiologist) setAudiologist(d.audiologist);
          setSp(false);

          // Sync στοιχείων από WordPress στο background
          if (d.ph2 || d.em) {
            try {
              const param = d.ph2
                ? `phone=${encodeURIComponent(d.ph2)}`
                : `email=${encodeURIComponent(d.em)}`;
              const res  = await fetch(`${API_URL}/patients?${param}`);
              const data = await res.json();
              if (data.found) {
                if (data.patient_name)  setNm(data.patient_name);
                if (data.patient_email) setEm(data.patient_email);
                if (data.patient_phone) setPh2(data.patient_phone);
                if (data.prof_id)       setProfId(data.prof_id);
                if (data.birthday)      setBirthday(data.birthday);
                if (data.fitting_date) {
                  // ACF Date Picker επιστρέφει Ymd (π.χ. 20260414) → το μετατρέπουμε σε YYYY-MM-DD
                  let fd = data.fitting_date;
                  if (fd && fd.length === 8 && !fd.includes('-')) {
                    fd = fd.substring(0,4) + '-' + fd.substring(4,6) + '-' + fd.substring(6,8);
                  }
                  if (fd && !isNaN(new Date(fd))) setSd(fd);
                }
                if (data.clinic)        setClinicId(data.clinic);
                if (data.power_source)  setDv(prev => ({ ...prev, p: data.power_source }));
                if (data.device_type)   setDv(prev => ({ ...prev, t: data.device_type }));
                if (data.device_brand)  setDv(prev => ({ ...prev, b: data.device_brand }));
                if (data.device_model)  setDv(prev => ({ ...prev, m: data.device_model }));
                if (data.serial_left)   setSL(data.serial_left);
                if (data.serial_right)  setSR(data.serial_right);
                if (data.serial_charger) setSC(data.serial_charger);
                if (data.patient_status) setPatientStatus(data.patient_status);
                if (data.trial_days) setTrialDays(data.trial_days);
                if (data.trial_start) setTrialStart(data.trial_start);
                if (data.audiologist) setAudiologist(data.audiologist);
                if (data.warranty_years) setWarrantyYears(data.warranty_years);
              }
            } catch(e) {}
          }
        }
      } catch (e) {}
    };
    loadData();

    // Safety timeout: if loadData hangs for >10s (very rare), force exit booting state
    // so the user can at least see a login screen instead of being stuck on splash.
    const bootSafetyTimer = setTimeout(() => {
      setAuthStep((current) => current === "booting" ? "phone" : current);
    }, 10000);
    return () => clearTimeout(bootSafetyTimer);
  }, []);

  // ─── Periodic sync: fetch patient data every 2 minutes and on window focus ───
  // Αυτό πιάνει αλλαγές που κάνει ο ακοοπροθετιστής από το admin (π.χ. trial → purchased)
  useEffect(() => {
    if (sp || (!ph2 && !em)) return;

    const syncPatientData = async () => {
      try {
        const param = ph2 ? `phone=${encodeURIComponent(ph2)}` : `email=${encodeURIComponent(em)}`;
        const res = await fetch(`${API_URL}/patients?${param}`);
        const data = await res.json();
        if (!data.found) return;

        // Normalize new fitting date
        let newFittingDate = data.fitting_date || '';
        if (newFittingDate && newFittingDate.length === 8 && !newFittingDate.includes('-')) {
          newFittingDate = `${newFittingDate.substring(0,4)}-${newFittingDate.substring(4,6)}-${newFittingDate.substring(6,8)}`;
        }

        const oldFitting = sd || '';
        const statusChangedToPurchased = patientStatus === 'trial' && data.patient_status === 'purchased';
        const statusChangedToTrial = patientStatus === 'purchased' && data.patient_status === 'trial';
        const fittingDateChanged = data.patient_status === 'purchased' && newFittingDate && newFittingDate !== oldFitting;

        // 🎯 PURCHASED → TRIAL: σβήσε όλα τα purchased ραντεβού + δημιούργησε trial_end
        if (statusChangedToTrial) {
          // Lock - prevent parallel runs
          const lockKey = `earmedical_reverting_${profId || ph2}`;
          if (localStorage.getItem(lockKey)) {
            console.log('[Sync] Revert already in progress — skip');
            return;
          }
          localStorage.setItem(lockKey, String(Date.now()));

          console.log('[Sync] Status changed: purchased → trial');

          // 1. Delete all future non-trial_end appointments from WP + Google Calendar
          try {
            await fetch(`${API_URL}/revert-to-trial`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: ph2, prof_id: profId }),
            });
          } catch(e) { console.error('Could not revert appts:', e); }

          // 2. Clear flags so trial_end appointment can be booked
          localStorage.removeItem('earmedical_scheduled_v1');
          localStorage.removeItem(`earmedical_trial_end_${profId || ph2}`);

          // 3. Book trial_end appointment
          let trialStartValue = data.trial_start;
          if (trialStartValue && trialStartValue.length === 8 && !trialStartValue.includes('-')) {
            trialStartValue = `${trialStartValue.substring(0,4)}-${trialStartValue.substring(4,6)}-${trialStartValue.substring(6,8)}`;
          }
          if (!trialStartValue) trialStartValue = new Date().toISOString().substring(0, 10);

          setTimeout(() => {
            bookTrialEndAppointment(
              nm, ph2, em, profId,
              data.clinic || clinicId,
              trialStartValue,
              data.trial_days || 14,
              birthday
            );
            // Release lock after 30 seconds
            setTimeout(() => localStorage.removeItem(lockKey), 30000);
          }, 1500);
        }

        // 🎯 Trigger re-booking in 2 cases:
        // 1. Status changed trial → purchased
        // 2. Patient is purchased AND fitting_date changed (admin corrected date)
        if (statusChangedToPurchased || fittingDateChanged) {
          console.log('[Sync] Triggering re-booking:', statusChangedToPurchased ? 'status→purchased' : 'fitting_date changed');

          // 1. Delete trial_end appointments (αν υπάρχουν)
          try {
            await fetch(`${API_URL}/convert-to-purchased`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: ph2, prof_id: profId }),
            });
          } catch(e) { console.error('Could not delete trial-end appts:', e); }

          // 2. Clear ALL booking flags so re-booking happens
          localStorage.removeItem('earmedical_scheduled_v1');
          localStorage.removeItem(`earmedical_trial_end_${profId || ph2}`);

          // 3. Prepare fitting date (prefer server, fallback to today)
          const fittingDateForBooking = newFittingDate || new Date().toISOString().substring(0, 10);

          // 4. Book new purchased appointments
          setTimeout(() => {
            bookScheduledAppointments(
              nm, ph2, em, profId, fittingDateForBooking, data.clinic || clinicId,
              data.power_source || dv?.p,
              birthday,
              data.warranty_years || warrantyYears || 2
            );
          }, 1500);
        }

        // Update only fields that changed
        if (data.patient_status && data.patient_status !== patientStatus) setPatientStatus(data.patient_status);
        if (data.trial_days && data.trial_days !== trialDays) setTrialDays(data.trial_days);
        if (data.trial_start && data.trial_start !== trialStart) setTrialStart(data.trial_start);
        if (newFittingDate && !isNaN(new Date(newFittingDate)) && newFittingDate !== oldFitting) setSd(newFittingDate);
        if (data.device_brand && data.device_brand !== dv?.b) setDv(prev => ({ ...prev, b: data.device_brand }));
        if (data.device_model && data.device_model !== dv?.m) setDv(prev => ({ ...prev, m: data.device_model }));
        if (data.device_type  && data.device_type  !== dv?.t) setDv(prev => ({ ...prev, t: data.device_type }));
        if (data.power_source && data.power_source !== dv?.p) setDv(prev => ({ ...prev, p: data.power_source }));
        if (data.serial_left  && data.serial_left  !== sL) setSL(data.serial_left);
        if (data.serial_right && data.serial_right !== sR) setSR(data.serial_right);
        if (data.serial_charger && data.serial_charger !== sC) setSC(data.serial_charger);
        if (data.audiologist && data.audiologist !== audiologist) setAudiologist(data.audiologist);
        if (data.warranty_years && data.warranty_years !== warrantyYears) setWarrantyYears(data.warranty_years);

        // 🩹 SELF-HEALING: Αν ο πελάτης είναι purchased + έχει fitting_date αλλά δεν έχει μελλοντικά ραντεβού → auto-book
        if (data.patient_status === 'purchased' && newFittingDate && !statusChangedToPurchased && !fittingDateChanged) {
          try {
            const apptRes = await fetch(`${API_URL}/appointments?phone=${encodeURIComponent(ph2)}`);
            const apptData = await apptRes.json();
            const today = new Date().toISOString().substring(0, 10);
            const futureAppts = Array.isArray(apptData)
              ? apptData.filter(a => {
                  const isTrialEnd = (a.reason === 'trial_end');
                  const isFuture = a.appointment_date && a.appointment_date >= today;
                  return !isTrialEnd && isFuture;
                })
              : [];

            if (futureAppts.length === 0) {
              console.log('[Self-heal] No future purchased appts found — auto-booking');
              localStorage.removeItem('earmedical_scheduled_v1');
              setTimeout(() => {
                bookScheduledAppointments(
                  nm, ph2, em, profId, newFittingDate, data.clinic || clinicId,
                  data.power_source || dv?.p,
                  birthday,
                  data.warranty_years || warrantyYears || 2
                );
              }, 1500);
            }
          } catch(e) { console.error('Self-heal check error:', e); }
        }

        // 🩹 SELF-HEALING: Αν ο πελάτης είναι trial + έχει trial_start αλλά δεν έχει trial_end ραντεβού → auto-book
        if (data.patient_status === 'trial' && data.trial_start && !statusChangedToTrial) {
          try {
            const apptRes = await fetch(`${API_URL}/appointments?phone=${encodeURIComponent(ph2)}`);
            const apptData = await apptRes.json();
            const today = new Date().toISOString().substring(0, 10);
            const hasTrialEnd = Array.isArray(apptData) && apptData.some(a =>
              a.reason === 'trial_end' && a.appointment_date >= today && a.status !== 'cancelled'
            );

            if (!hasTrialEnd) {
              console.log('[Self-heal] No trial_end appt found — auto-booking');
              localStorage.removeItem(`earmedical_trial_end_${profId || ph2}`);

              const trialStartValue = (data.trial_start.length === 8 && !data.trial_start.includes('-'))
                ? `${data.trial_start.substring(0,4)}-${data.trial_start.substring(4,6)}-${data.trial_start.substring(6,8)}`
                : data.trial_start;

              setTimeout(() => {
                bookTrialEndAppointment(
                  nm, ph2, em, profId,
                  data.clinic || clinicId,
                  trialStartValue,
                  data.trial_days || 14,
                  birthday
                );
              }, 1500);
            }
          } catch(e) { console.error('Self-heal trial check error:', e); }
        }
      } catch(e) {}
    };

    // Every 2 minutes while app is open
    const interval = setInterval(syncPatientData, 120000);

    // On window focus (user returns to app)
    const onFocus = () => syncPatientData();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncPatientData();
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [sp, ph2, em, patientStatus, trialDays, trialStart, sd, dv, sL, sR, sC, audiologist, warrantyYears]);

  useEffect(() => {
    if (!sp)
      try {
        localStorage.setItem(
          "earmedical_data",
          JSON.stringify({ nm, em, ph2, profId, birthday, sd, dv, clinicId, sL, sR, sC, warrantyYears, patientStatus, trialDays, trialStart, audiologist }),
        );
      } catch (e) {}
  }, [nm, em, ph2, profId, birthday, sd, dv, clinicId, sL, sR, sC, warrantyYears, patientStatus, trialDays, trialStart, audiologist, sp]);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, [tab, sub]);
  // OneSignal: init after user data is loaded
  // Single debounced effect — αποφεύγει 409 Conflicts από concurrent PATCH calls
  useEffect(() => {
    if (sp) return;
    if (!nm && !em && !ph2) return;
    const timeoutId = setTimeout(() => {
      initOneSignal({ nm, em, ph2, clinicId, sd, dv, profId, birthday, sL, sR, patientStatus, trialDays });
    }, 500); // 500ms debounce για να batcharoume rapid state changes
    return () => clearTimeout(timeoutId);
  }, [nm, em, ph2, clinicId, sd, dv, profId, birthday, sL, sR, patientStatus, trialDays, sp]);
  const clinic = getClinicById(clinicId);
  const supportOptions = getSupportOptions(dv.p);
  const batteryProfile = getBatteryProfile(dv.t, dv.p, sL, sR);
  const todayDateStr = getTodayDateStr();
  const ap = mkA(sd, nm, em, ph2, clinic, profId, warrantyYears),
    nx = ap.find((a) => !a.past),
    wD = new Date(new Date(sd).setFullYear(new Date(sd).getFullYear() + warrantyYears)),
    wF = wD.toLocaleDateString("el-GR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    ds = Math.floor((Date.now() - new Date(sd)) / 86400000),
    wk = Math.min(Math.floor(ds / 7) + 1, 99),
    ps = ds < 7 ? 0 : ds < 14 ? 1 : ds < 28 ? 2 : 3;
  const isTrial = patientStatus === "trial";

  // v1.3.44 — Format hearing-aid duration for hero card.
  // Thresholds:
  //   < 30 days  → "X ημέρες με τα ακουστικά σας"
  //   30-364     → big = months, sub = remaining days  ("1 μήνας" + "8 ημέρες")
  //   ≥ 365      → big = years,  sub = months + days   ("2 χρόνια" + "3 μήνες, 12 ημέρες")
  //
  // Pure function — keep at module scope or right here for readability.
  const formatHearingAidDuration = (totalDays) => {
    if (totalDays < 30) {
      return {
        big: totalDays,
        unit: totalDays === 1 ? "ημέρα με τα ακουστικά σας" : "ημέρες με τα ακουστικά σας",
        sub: "",
      };
    }
    const years = Math.floor(totalDays / 365);
    const afterYears = totalDays - (years * 365);
    const months = Math.floor(afterYears / 30);
    const remDays = afterYears - (months * 30);

    if (years >= 1) {
      const subParts = [];
      if (months > 0)  subParts.push(months === 1 ? "1 μήνας" : months + " μήνες");
      if (remDays > 0) subParts.push(remDays === 1 ? "1 ημέρα" : remDays + " ημέρες");
      return {
        big: years,
        unit: years === 1 ? "χρόνος με τα ακουστικά σας" : "χρόνια με τα ακουστικά σας",
        sub: subParts.join(", "),
      };
    }
    // 30-364 days → months bucket
    return {
      big: months,
      unit: months === 1 ? "μήνας με τα ακουστικά σας" : "μήνες με τα ακουστικά σας",
      sub: remDays > 0 ? (remDays === 1 ? "1 ημέρα" : remDays + " ημέρες") : "",
    };
  };
  const dur = formatHearingAidDuration(ds + 1);

  // v1.3.41 — Smart quiz prompt: compute one of 5 states based on how
  // long ago the patient last filled the questionnaire.
  //   'first'     — never filled → big orange CTA
  //   'overdue'   — >14 days ago  → solid orange "Είναι ώρα..."
  //   'available' — 7-14 days ago → outline orange "Διαθέσιμη νέα"
  //   'recent'    — 1-6 days ago  → subtle gray with small refill link
  //   'today'     — <24h ago      → hidden (rendered as null)
  // Trial/purchased share the same card; quiz content adapts via setSub("quiz").
  const quizPromptState = (() => {
    if (!lastQuizDate) return 'first';
    const parsed = new Date(lastQuizDate + 'T00:00:00');
    if (isNaN(parsed)) return 'first';
    const days = Math.floor((Date.now() - parsed.getTime()) / 86400000);
    if (days < 1)  return 'today';
    if (days < 7)  return 'recent';
    if (days < 14) return 'available';
    return 'overdue';
  })();
  const daysSinceQuiz = (() => {
    if (!lastQuizDate) return null;
    const parsed = new Date(lastQuizDate + 'T00:00:00');
    if (isNaN(parsed)) return null;
    return Math.floor((Date.now() - parsed.getTime()) / 86400000);
  })();

  // v1.3.41 — Smart quiz prompt card renderer. Shared between trial and
  // purchased home pages so audiologist gets consistent feedback signal
  // regardless of patient status. Designed to be visible-but-not-pushy:
  // only loud when the patient hasn't given recent feedback.
  const renderQuizPromptCard = () => {
    if (quizPromptState === 'today') return null;

    const openQuiz = () => setSub("quiz");

    if (quizPromptState === 'first') {
      // FIRST visit — big orange gradient with strong CTA
      return (
        <div onClick={openQuiz} style={{
          background: `linear-gradient(135deg, ${O}, #D06A0E)`,
          borderRadius: 16,
          padding: "18px 20px",
          marginBottom: 16,
          cursor: "pointer",
          boxShadow: "0 4px 14px rgba(239,128,26,0.28)",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}>
          <div style={{
            width: 46, height: 46, borderRadius: 12, flexShrink: 0,
            background: "rgba(255,255,255,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: "0 0 3px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: 1.2 }}>
              ΣΥΝΤΟΜΗ ΑΞΙΟΛΟΓΗΣΗ
            </p>
            <p style={{ margin: "0 0 2px", fontSize: 16, fontWeight: 800, color: WH, letterSpacing: -0.2 }}>
              Πες μας πώς ακούς
            </p>
            <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>
              2 λεπτά · βοηθάει τον ειδικό να σε υποστηρίξει καλύτερα
            </p>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.85 }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      );
    }

    if (quizPromptState === 'overdue') {
      // OVERDUE — solid orange, urgency in copy
      return (
        <div onClick={openQuiz} style={{
          background: O,
          borderRadius: 16,
          padding: "16px 18px",
          marginBottom: 16,
          cursor: "pointer",
          boxShadow: "0 3px 10px rgba(239,128,26,0.22)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11, flexShrink: 0,
            background: "rgba(255,255,255,0.20)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: "0 0 2px", fontSize: 15, fontWeight: 800, color: WH, letterSpacing: -0.2 }}>
              Ώρα για νέα αξιολόγηση
            </p>
            <p style={{ margin: 0, fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
              Πέρασαν {daysSinceQuiz} ημέρες — βοήθησέ μας με ένα γρήγορο update
            </p>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      );
    }

    if (quizPromptState === 'available') {
      // AVAILABLE — outline orange, polite
      return (
        <div onClick={openQuiz} style={{
          background: "#FFF8F0",
          border: `1.5px solid ${O}`,
          borderRadius: 16,
          padding: "14px 16px",
          marginBottom: 16,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            background: OL,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 700, color: DK }}>
              Διαθέσιμη νέα αξιολόγηση
            </p>
            <p style={{ margin: 0, fontSize: 12, color: MG }}>
              Τελευταία πριν {daysSinceQuiz} {daysSinceQuiz === 1 ? "ημέρα" : "ημέρες"}
            </p>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      );
    }

    // RECENT (1-6 days) — subtle confirmation strip with small refill link
    return (
      <div style={{
        background: "#F5F5F5",
        borderRadius: 12,
        padding: "10px 14px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}>
        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#E8F5E9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: "0 0 1px", fontSize: 12, fontWeight: 700, color: DK }}>
            Αξιολόγηση συμπληρωμένη
          </p>
          <p style={{ margin: 0, fontSize: 11, color: MG }}>
            Πριν {daysSinceQuiz} {daysSinceQuiz === 1 ? "ημέρα" : "ημέρες"} · Επόμενη σε {Math.max(1, 14 - daysSinceQuiz)} {Math.max(1, 14 - daysSinceQuiz) === 1 ? "ημέρα" : "ημέρες"}
          </p>
        </div>
        <button onClick={openQuiz} style={{
          background: "transparent", border: "none", color: O,
          fontSize: 11, fontWeight: 700, cursor: "pointer", padding: "4px 6px",
          fontFamily: "inherit", textDecoration: "underline",
          flexShrink: 0,
        }}>
          Νέα τώρα
        </button>
      </div>
    );
  };
  const isSupport = patientStatus === "support";
  const isPurchased = patientStatus === "purchased";
  // v1.3.31 — Νέο status: Ενδιαφερόμενος (prospect). Άτομα που ΔΕΝ φοράνε
  // ακόμα ακουστικά και ΔΕΝ έχουν αγοράσει — θέλουν να κλείσουν ραντεβού
  // για υπηρεσία γνωριμίας (αξιολόγηση ακοής, δοκιμή ακουστικών, ωτοασπίδες,
  // IEM Soundz, εμβοές). Reduced UI: όχι Κατάστημα, όχι trial countdown,
  // όχι warranty/fitting/mood tracking. Έχει Home/Chat/Ραντεβού/Υπηρεσίες/Μενού.
  const isProspect = patientStatus === "prospect";
  const trialStartDate = trialStart ? new Date(`${trialStart.length === 8 ? trialStart.substring(0,4)+'-'+trialStart.substring(4,6)+'-'+trialStart.substring(6,8) : trialStart}T00:00:00`) : new Date();
  const trialEndDate = new Date(trialStartDate.getTime() + trialDays * 86400000);
  const trialDaysUsed = Math.max(0, Math.floor((Date.now() - trialStartDate) / 86400000));
  const trialDay = trialDaysUsed + 1; // 1-indexed day (day 1 = πρώτη μέρα)
  const trialDaysLeft = Math.max(0, trialDays - trialDaysUsed);
  const trialExpired = isTrial && trialDaysLeft <= 0;
  const trialProgress = Math.min(100, Math.round((trialDaysUsed / trialDays) * 100));

  // ─── Checkpoints για Purchased & Trial ─────────────────────────
  const PURCHASED_CHECKPOINTS = [3, 7, 14, 30];
  const TRIAL_CHECKPOINT_MAP = {
    7:  [3, 6],
    14: [3, 7, 12],
    21: [3, 7, 14, 18],
    28: [3, 7, 14, 21, 24],
  };
  const trialCheckpoints = TRIAL_CHECKPOINT_MAP[trialDays] || [3, Math.floor(trialDays * 0.5), Math.floor(trialDays * 0.85)];
  const activeCheckpoints = isTrial ? trialCheckpoints : PURCHASED_CHECKPOINTS;
  const currentDay = isTrial ? trialDay : (ds + 1);
  const isCheckpoint = activeCheckpoints.includes(currentDay);
  const nextCheckpoint = activeCheckpoints.find(d => d > currentDay) || null;

  // ─── Ερωτηματολόγια ─────────────────────────────────────────────
  // PURCHASED — 10 ερωτήσεις εμπειρίας χρήσης
  const QZ_PURCHASED = [
    { q: "Πώς ακούτε τις καθημερινές συζητήσεις στο σπίτι;",
      o: ["Πολύ καλύτερα","Αρκετά καλύτερα","Λίγο καλύτερα","Χωρίς διαφορά"], sc: [3,2,1,0] },
    { q: "Πόσο εύκολα καταλαβαίνετε την τηλεόραση τώρα;",
      o: ["Πολύ πιο εύκολα","Καλύτερα από πριν","Περίπου το ίδιο","Δυσκολεύομαι ακόμα"], sc: [3,2,1,0] },
    { q: "Σε χώρο με θόρυβο, πώς τα πάτε;",
      o: ["Πολύ καλά","Αρκετά καλά","Μέτρια","Δυσκολεύομαι πολύ"], sc: [3,2,1,0] },
    { q: "Πόσο φυσικός σας φαίνεται ο ήχος;",
      o: ["Πολύ φυσικός","Αρκετά φυσικός","Κάπως περίεργος ακόμα","Με ενοχλεί"], sc: [3,2,1,0] },
    { q: "Η δική σας φωνή πώς ακούγεται;",
      o: ["Φυσιολογική","Λίγο διαφορετική","Αρκετά διαφορετική","Με ενοχλεί πολύ"], sc: [3,2,1,0] },
    { q: "Πόσες ώρες την ημέρα φοράτε τα ακουστικά σας;",
      o: ["Πάνω από 8 ώρες","5–8 ώρες","2–5 ώρες","Λιγότερο από 2 ώρες"], sc: [3,2,1,0] },
    { q: "Πόσο άνετα είναι στα αυτιά σας;",
      o: ["Πολύ άνετα","Αρκετά άνετα","Λίγο ενοχλητικά","Με πιέζουν / πονάνε"], sc: [3,2,1,0] },
    { q: "Έχετε παρατηρήσει κάποιο από τα παρακάτω;",
      o: ["Κανένα θέμα","Σφύριγμα","Χαμηλή ένταση","Ενόχληση σε θορύβους","Δυσκολία στην εφαρμογή"], sc: [3,1,1,1,1] },
    { q: "Πόσο σίγουροι νιώθετε ότι σας βοηθούν στην καθημερινότητα;",
      o: ["Πολύ","Αρκετά","Λίγο","Καθόλου ακόμα"], sc: [3,2,1,0] },
    { q: "Τι θα θέλατε περισσότερο αυτή τη στιγμή;",
      o: ["Να συνεχίσω όπως είμαι","Μικρή επιπλέον καθοδήγηση","Ρύθμιση στο κέντρο","Να μιλήσω άμεσα με ειδικό"], sc: [3,2,1,0] },
  ];

  // TRIAL — 10 ερωτήσεις με commercial focus
  const QZ_TRIAL = [
    { q: "Πόσο καλύτερα ακούτε σε σχέση με πριν;",
      o: ["Πολύ καλύτερα","Αρκετά καλύτερα","Λίγο καλύτερα","Δεν βλέπω διαφορά"], sc: [3,2,1,0] },
    { q: "Σε ποιες καταστάσεις σας βοηθούν περισσότερο;",
      o: ["Στις καθημερινές συζητήσεις","Στην τηλεόραση","Στο τηλέφωνο","Σε εξωτερικούς χώρους / παρέες","Δεν έχω δει ακόμα ουσιαστική βοήθεια"], sc: [3,3,3,3,0] },
    { q: "Πόσο άνετα είναι όταν τα φοράτε;",
      o: ["Πολύ άνετα","Αρκετά άνετα","Λίγο ενοχλητικά","Με δυσκολεύουν"], sc: [3,2,1,0] },
    { q: "Πόσο φυσικός σας φαίνεται ο ήχος;",
      o: ["Πολύ φυσικός","Αρκετά φυσικός","Θέλει λίγη συνήθεια","Δεν μου αρέσει ακόμα"], sc: [3,2,1,0] },
    { q: "Πόσο εύκολη είναι η καθημερινή χρήση τους;",
      o: ["Πολύ εύκολη","Αρκετά εύκολη","Θέλει λίγη βοήθεια","Με δυσκολεύει"], sc: [3,2,1,0] },
    { q: "Πόσες ώρες την ημέρα τα φοράτε συνήθως;",
      o: ["Πάνω από 8 ώρες","5–8 ώρες","2–5 ώρες","Λιγότερο από 2 ώρες"], sc: [3,2,1,0] },
    { q: "Υπάρχει κάτι που σας προβληματίζει αυτή τη στιγμή;",
      o: ["Όχι, είμαι αρκετά ικανοποιημένος/η","Θέλω καλύτερη ρύθμιση","Με δυσκολεύει η εφαρμογή / άνεση","Δεν είμαι σίγουρος/η για το αποτέλεσμα","Με προβληματίζει το κόστος"], sc: [3,1,1,1,1] },
    { q: "Πιστεύετε ότι τα ακουστικά βοηθούν ουσιαστικά την καθημερινότητά σας;",
      o: ["Ναι, πολύ","Ναι, αρκετά","Λίγο","Όχι ακόμα"], sc: [3,2,1,0] },
    { q: "Πώς νιώθετε μέχρι τώρα με τη δοκιμή σας;",
      o: ["Είμαι έτοιμος/η να προχωρήσω","Θέλω λίγο ακόμα χρόνο δοκιμής","Ίσως χρειαστούν κάποιες ρυθμίσεις","Θέλω να το συζητήσω με το κέντρο"], sc: [3,2,1,1] },
  ];

  const activeQZ = isTrial ? QZ_TRIAL : QZ_PURCHASED;

  // ─── Trial result helper (συνδυασμός score + feeling) ──
  const getTrialResult = (answers, scorePct) => {
    // Απάντηση στην ερώτηση 9 (index 8 — τελευταία): "Πώς νιώθετε με τη δοκιμή;"
    // 0=έτοιμος, 1=λίγο ακόμα χρόνο, 2=ρυθμίσεις, 3=συζήτηση με κέντρο
    const feeling = answers[8];

    // Πολύ θετικό: έτοιμος + καλό score
    if (feeling === 0 && scorePct >= 65) {
      return {
        level: "positive",
        title: "Πολύ θετική εμπειρία",
        text: "Βλέπουμε πολύ καλή προσαρμογή και ουσιαστικό όφελος. Είστε κοντά σε απόφαση ολοκλήρωσης.",
        cta: { label: "Κλείστε ραντεβού αγοράς", action: "appointment" },
      };
    }
    // Θέλει να συζητήσει με κέντρο → direct call CTA
    if (feeling === 3) {
      return {
        level: "medium",
        title: "Ας τα συζητήσουμε μαζί",
        text: "Είμαστε εδώ για να σας βοηθήσουμε να πάρετε τη σωστή απόφαση με την ησυχία σας.",
        cta: { label: "Κάλεσέ μας", action: "call" },
      };
    }
    // Ρυθμίσεις
    if (feeling === 2) {
      return {
        level: "medium",
        title: "Λίγες ρυθμίσεις θα κάνουν τη διαφορά",
        text: "Μπορούμε να βελτιώσουμε την εμπειρία σας με μικρές ρυθμίσεις στο κέντρο.",
        cta: { label: "Κλείστε ραντεβού ρύθμισης", action: "appointment" },
      };
    }
    // Θέλει λίγο ακόμα χρόνο
    if (feeling === 1) {
      return {
        level: "medium",
        title: "Πάρτε τον χρόνο σας",
        text: "Είναι σημαντικό να νιώθετε σίγουροι για την απόφασή σας. Συνεχίστε τη δοκιμή και είμαστε εδώ για ό,τι χρειαστείτε.",
        cta: { label: "Κλείστε ραντεβού ρύθμισης", action: "appointment" },
      };
    }
    // Χαμηλή ικανοποίηση (fallback — δεν απάντησε στη 9 ή χαμηλό score)
    if (scorePct < 40) {
      return {
        level: "low",
        title: "Χρειάζεται επανεκτίμηση",
        text: "Φαίνεται ότι υπάρχουν ακόμη εμπόδια στη χρήση ή στο αποτέλεσμα. Χρειάζεται νέα αξιολόγηση με το κέντρο.",
        cta: { label: "Κάλεσέ μας", action: "call" },
      };
    }
    // Default θετικό
    return {
      level: "medium",
      title: "Καλή πορεία",
      text: "Κρατήστε σταθερή τη χρήση και ενημερώστε μας αν προκύψει κάτι που θέλετε να συζητήσουμε.",
      cta: { label: "Κλείστε ραντεβού", action: "appointment" },
    };
  };
  const visibleAppointments = ap.filter((a) => {
    if (calFilter === "all") return true;
    // Ολοκληρωμένα = παρελθόν ΚΑΙ όχι cancelled
    if (calFilter === "completed") return a.past && a.status !== "cancelled";
    // Επερχόμενα = μέλλον ΚΑΙ όχι cancelled
    return !a.past && a.status !== "cancelled";
  });
  const upcomingWpAppointments = wpAppts
    .filter((a) => !a.past && a.status !== "cancelled")
    .sort((a, b) => (a.dt || 0) - (b.dt || 0));
  const homeNextAppointment = upcomingWpAppointments[0] || nx;

  // ─── v1.3.58 — Proactive διαδραστικός Ορφέας ("moments") ─────────────
  // Ο Ορφέας ξεκινά ο ίδιος μια σύντομη συνομιλία στη σωστή στιγμή, διακριτικά:
  // ερωτηματολόγιο ανάλογα με το πόσο καιρό φοράει τα ακουστικά, συντήρηση/
  // καθαρισμός, ετήσιος επανέλεγχος, και εποχιακά (Χριστούγεννα/Πάσχα/διακοπές).
  // Υπολογίζεται client-side· suppression/snooze στο localStorage· οι απαντήσεις
  // καταγράφονται (orfeas-moment) ώστε να τις βλέπει ο ακοοπροθετιστής.
  const orfeasMoment = useMemo(() => {
    if (patientStatus !== "purchased" && patientStatus !== "support") return null;
    const now = new Date();
    const nowMs = now.getTime();
    let st = {}; try { st = JSON.parse(localStorage.getItem("earmedical_orfeas_moments") || "{}"); } catch (e) {}
    const ok = (id) => { const s = st[id]; return !(s && s.until && s.until > nowMs); };

    const firstName = nm ? String(nm).trim().split(" ")[0] : "";
    const hi = firstName ? `${firstName}, ` : "";
    const power = dv && dv.p;
    const hasUpcoming = upcomingWpAppointments.length > 0;

    let tenure = null;
    if (sd) { const fd = new Date(sd); if (!isNaN(fd.getTime())) tenure = Math.floor((nowMs - fd.getTime()) / 86400000); }
    const lastAppt = wpAppts
      .filter((a) => a.past && a.status !== "cancelled")
      .sort((a, b) => ((b.dt && b.dt.getTime ? b.dt.getTime() : 0) - (a.dt && a.dt.getTime ? a.dt.getTime() : 0)))[0];
    let monthsSvc = null;
    const refD = (lastAppt && lastAppt.dt) ? new Date(lastAppt.dt) : (sd ? new Date(sd) : null);
    if (refD && !isNaN(refD.getTime())) monthsSvc = (nowMs - refD.getTime()) / (86400000 * 30.44);

    const scale = (extra = []) => ([
      { label: "Πολύ καλά", value: "ans:great" },
      { label: "Καλά", value: "ans:good" },
      { label: "Με δυσκολεύει κάτι", value: "ans:struggle", warn: true },
      ...extra,
    ]);
    const c = [];
    const M = now.getMonth() + 1, D = now.getDate();

    // Εποχιακά — Χριστούγεννα
    if (M === 12 && D >= 10 && D <= 26) c.push({ id: `xmas_${now.getFullYear()}`, critical: true, title: "Καλές γιορτές", cooldown: 220,
      text: `${hi}σε λίγες μέρες έρχονται τα Χριστούγεννα και όμορφες ώρες με αγαπημένους. Ας σιγουρευτούμε ότι θα τους ακούτε καθαρά — θέλετε να κλείσουμε έναν γρήγορο έλεγχο ακουστικών & ακοής;`,
      replies: [{ label: "Ναι, κλείσε ραντεβού", value: "book", primary: true }, { label: "Θύμισέ μου αργότερα", value: "later" }, { label: "Όχι ευχαριστώ", value: "no" }] });
    // Πάσχα
    const eas = ORTHODOX_EASTER[now.getFullYear()];
    if (eas) { const ed = new Date(eas + "T00:00:00"); const dd = (ed.getTime() - nowMs) / 86400000; if (dd >= 0 && dd <= 16) c.push({ id: `easter_${now.getFullYear()}`, critical: true, title: "Καλό Πάσχα", cooldown: 120,
      text: `${hi}πλησιάζει το Πάσχα και οι οικογενειακές στιγμές. Ας βεβαιωθούμε ότι τα ακουστικά σας είναι έτοιμα να τις απολαύσετε — να κλείσουμε έναν έλεγχο;`,
      replies: [{ label: "Ναι, κλείσε ραντεβού", value: "book", primary: true }, { label: "Αργότερα", value: "later" }, { label: "Όχι ευχαριστώ", value: "no" }] }); }
    // Καλοκαίρι / ταξίδι
    if ((M === 6 && D >= 15) || M === 7) c.push({ id: `summer_${now.getFullYear()}`, critical: false, title: "Καλοκαίρι & διακοπές", cooldown: 140, seed: "Ετοιμάζομαι για διακοπές/ταξίδι — δώσε μου οδηγίες για τα ακουστικά μου (καθαρισμός, μπαταρίες/φόρτιση, προστασία από νερό & ζέστη).",
      text: `${hi}αν ετοιμάζεστε για διακοπές ή ταξίδι, ας προετοιμάσουμε τα ακουστικά σας: καθαρισμός, ${power === "rechargeable" ? "πλήρης φόρτιση & φορτιστής ταξιδιού" : "αρκετές εφεδρικές μπαταρίες"}, προστασία από νερό/ζέστη. Θέλετε σύντομες οδηγίες ή έναν έλεγχο πριν φύγετε;`,
      replies: [{ label: "Δώσε μου οδηγίες", value: "chat", primary: true }, { label: "Κλείσε έλεγχο", value: "book" }, { label: "Αργότερα", value: "later" }] });

    // Ετήσιος επανέλεγχος
    if (monthsSvc !== null && monthsSvc >= 11.5 && !hasUpcoming) c.push({ id: "annual_recheck", critical: true, title: "Ετήσιος επανέλεγχος", cooldown: 300,
      text: `${hi}πέρασε σχεδόν ένας χρόνος από τον τελευταίο σας έλεγχο. Ένας ετήσιος επανέλεγχος ακοής μάς βοηθά να δούμε αν χρειάζεται κάποια ρύθμιση, ώστε να ακούτε στο καλύτερο. Να κλείσουμε ραντεβού;`,
      replies: [{ label: "Ναι, κλείσε ραντεβού", value: "book", primary: true }, { label: "Αργότερα", value: "later" }, { label: "Όχι τώρα", value: "no" }] });

    // Tenure ερωτηματολόγιο (one-time milestones)
    const tq = (id, lo, hiD, title, text, replies) => { if (tenure !== null && tenure >= lo && tenure <= hiD) c.push({ id, critical: false, cooldown: 3650, title, text, replies, seed: "Έχω μια απορία για τα ακουστικά μου." }); };
    tq("tenure_d7", 5, 10, "Η πρώτη εβδομάδα", `${hi}πέρασαν οι πρώτες μέρες με τα νέα σας ακουστικά. Πώς νιώθετε μέχρι τώρα;`, scale());
    tq("tenure_d14", 12, 20, "Δύο εβδομάδες μαζί", `${hi}είστε στην περίοδο προσαρμογής. Πόσο άνετα τα φοράτε — σας ενοχλεί κάτι (σφύριγμα, η δική σας φωνή, θόρυβος);`, scale());
    tq("tenure_d30", 27, 38, "Ένας μήνας", `${hi}συμπληρώθηκε ένας μήνας. Είστε ευχαριστημένοι με το πώς ακούτε στις καθημερινές σας καταστάσεις;`, scale());
    tq("tenure_d90", 85, 100, "Τρεις μήνες", `${hi}τρεις μήνες μαζί! Χρειάζεται κάποια ρύθμιση ή έχετε απορίες; Καλή στιγμή και για έναν καθαρισμό.`, scale([{ label: "Κλείσε καθαρισμό", value: "book" }]));
    tq("tenure_d180", 175, 200, "Έξι μήνες", `${hi}πέρασαν έξι μήνες — πώς νιώθετε γενικά με την ακοή σας; Να προγραμματίσουμε έναν έλεγχο & καθαρισμό;`, scale([{ label: "Κλείσε έλεγχο", value: "book" }]));

    // Συντήρηση (fallback)
    if (monthsSvc !== null && monthsSvc >= 4 && !hasUpcoming) c.push({ id: "maintenance", critical: false, title: "Καιρός για συντήρηση", cooldown: 150, seed: "Δώσε μου οδηγίες καθαρισμού/συντήρησης για τα ακουστικά μου.",
      text: `${hi}πέρασαν μερικοί μήνες από τον τελευταίο έλεγχο. Ένας καθαρισμός και αλλαγή ${power === "rechargeable" ? "θηλών/φίλτρων" : "φίλτρων κεριού"} κρατά τα ακουστικά σας σε άριστη κατάσταση. Θέλετε οδηγίες ή ραντεβού;`,
      replies: [{ label: "Δώσε μου οδηγίες", value: "chat", primary: true }, { label: "Κλείσε ραντεβού", value: "book" }, { label: "Αργότερα", value: "later" }] });

    return c.find((x) => ok(x.id)) || null;
  }, [patientStatus, sd, dv, upcomingWpAppointments, wpAppts, nm, momentRefresh]);

  // Critical moments → ένα διακριτικό overlay μία φορά τη μέρα.
  useEffect(() => {
    if (!orfeasMoment || !orfeasMoment.critical) return;
    let day = ""; try { day = localStorage.getItem("earmedical_orfeas_overlay_day") || ""; } catch (e) {}
    const today = new Date().toISOString().slice(0, 10);
    if (day !== today) { setMomentOverlayOpen(true); try { localStorage.setItem("earmedical_orfeas_overlay_day", today); } catch (e) {} }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orfeasMoment]);

  // ─── v1.3.64 — In-app «νέα έκδοση διαθέσιμη» ─────────────────────────
  // Ελέγχει το app-version endpoint και, αν υπάρχει νεότερη έκδοση, δείχνει banner:
  // native → ανοίγει App Store/Play· PWA → ανανεώνει (καθαρίζει cache + reload).
  useEffect(() => {
    const cur = String(process.env.REACT_APP_VERSION || "0.0.0");
    const cmp = (a, b) => { const pa = String(a).split(".").map(n => parseInt(n, 10) || 0), pb = String(b).split(".").map(n => parseInt(n, 10) || 0); for (let i = 0; i < 3; i++) { if ((pa[i] || 0) !== (pb[i] || 0)) return (pa[i] || 0) - (pb[i] || 0); } return 0; };
    (async () => {
      try {
        const r = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/app-version?_cb=" + Date.now(), { cache: "no-store" });
        const d = await r.json();
        if (!d || !d.latest || cmp(d.latest, cur) <= 0) return;
        let sn = {}; try { sn = JSON.parse(localStorage.getItem("em_update_snooze") || "{}"); } catch (e) {}
        if (sn.v === d.latest && sn.until && sn.until > Date.now()) return; // «Αργότερα» ενεργό
        const isNat = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
        const plat = (window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform()) || "";
        const url = isNat ? (plat === "ios" ? d.ios_url : d.android_url) : null;
        setUpdateInfo({ latest: d.latest, native: isNat, url });
      } catch (e) {}
    })();
  }, []);
  const doUpdate = () => {
    if (updateInfo && updateInfo.url) { try { window.open(updateInfo.url, "_blank"); } catch (e) { window.location.href = updateInfo.url; } }
    else {
      try { if (window.caches && caches.keys) caches.keys().then(ks => ks.forEach(k => caches.delete(k))); } catch (e) {}
      setTimeout(() => window.location.reload(), 200);
    }
  };
  const snoozeUpdate = () => {
    try { if (updateInfo) localStorage.setItem("em_update_snooze", JSON.stringify({ v: updateInfo.latest, until: Date.now() + 2 * 86400000 })); } catch (e) {}
    setUpdateInfo(null);
  };
  const updateBannerEl = updateInfo ? (
    <div style={{ margin: "12px 20px 0", padding: "14px 16px", background: "linear-gradient(135deg,#FB923C 0%,#EA580C 100%)", borderRadius: 16, boxShadow: SH, display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7"/><polyline points="8 12 12 16 16 12"/><line x1="12" y1="16" x2="12" y2="3"/></svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: WH }}>Νέα έκδοση διαθέσιμη</p>
        <p style={{ margin: "2px 0 0", fontSize: 12, color: "rgba(255,255,255,0.92)", lineHeight: 1.4 }}>{updateInfo.native ? "Ενημερώστε για τις πιο πρόσφατες βελτιώσεις." : "Ανανεώστε για τη νεότερη έκδοση."}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
        <button onClick={doUpdate} style={{ background: WH, color: "#EA580C", border: "none", borderRadius: 10, padding: "8px 16px", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{updateInfo.native ? "Ενημέρωση" : "Ανανέωση"}</button>
        <button onClick={snoozeUpdate} style={{ background: "transparent", color: "rgba(255,255,255,0.88)", border: "none", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Αργότερα</button>
      </div>
    </div>
  ) : null;
  const persistMoment = (moment, reply) => {
    let st = {}; try { st = JSON.parse(localStorage.getItem("earmedical_orfeas_moments") || "{}"); } catch (e) {}
    const days = reply === "later" ? (moment.laterDays || 4) : (moment.cooldown || 120);
    st[moment.id] = { until: Date.now() + days * 86400000 };
    try { localStorage.setItem("earmedical_orfeas_moments", JSON.stringify(st)); } catch (e) {}
  };
  const recordMomentBackend = (moment, reply, answer) => {
    if (reply === "later") return; // το "θύμισέ μου" δεν καταγράφεται
    try {
      fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/orfeas-moment", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Auth-Token": authToken || "" },
        body: JSON.stringify({ prof_id: profId || "", phone: ph2 || "", moment_id: moment.id, moment_title: moment.title || "", response: reply, answer: answer || "" }),
      }).catch(() => {});
    } catch (e) {}
  };
  const handleMomentReply = (moment, r) => {
    const v = r.value || "";
    if (v.indexOf("ans:") === 0) {
      const a = v.slice(4);
      persistMoment(moment, "answer"); recordMomentBackend(moment, "answer", a);
      if (a === "struggle") setMomentReplyView({ id: moment.id, text: "Λυπάμαι που σας δυσκολεύει — ας το λύσουμε μαζί. Θέλετε να μου περιγράψετε τι συμβαίνει ή να κλείσουμε ραντεβού με τον ειδικό σας;", actions: [{ label: "Μίλα με τον Ορφέα", value: "go_chat", primary: true }, { label: "Κλείσε ραντεβού", value: "go_book" }] });
      else setMomentReplyView({ id: moment.id, text: "Χαίρομαι που τα πάτε καλά! Είμαι πάντα εδώ — για έλεγχο, καθαρισμό ή οποιαδήποτε απορία.", actions: [{ label: "Ευχαριστώ", value: "close", primary: true }, { label: "Ρώτησε κάτι", value: "go_chat" }] });
      return;
    }
    if (v === "book") { persistMoment(moment, "book"); recordMomentBackend(moment, "book"); setMomentOverlayOpen(false); setMomentRefresh((x) => x + 1); setNewAReason(moment.title || "Έλεγχος / Συντήρηση"); setSub("newappt"); return; }
    if (v === "chat") { persistMoment(moment, "chat"); recordMomentBackend(moment, "chat"); setMomentOverlayOpen(false); setMomentRefresh((x) => x + 1); setSub("ai"); setTimeout(() => sendAiMessageDirect(moment.seed || (moment.title ? `Σχετικά με: ${moment.title}. Δώσε μου οδηγίες.` : "Δώσε μου οδηγίες.")), 200); return; }
    if (v === "later") { persistMoment(moment, "later"); setMomentOverlayOpen(false); setMomentRefresh((x) => x + 1); return; }
    if (v === "no") { persistMoment(moment, "no"); recordMomentBackend(moment, "no"); setMomentOverlayOpen(false); setMomentRefresh((x) => x + 1); return; }
  };
  const handleMomentFollowup = (moment, r) => {
    const v = r.value || "";
    setMomentReplyView(null); setMomentOverlayOpen(false); setMomentRefresh((x) => x + 1);
    if (v === "go_chat") { setSub("ai"); setTimeout(() => sendAiMessageDirect(moment.seed || "Θέλω βοήθεια με τα ακουστικά μου."), 200); }
    else if (v === "go_book") { setNewAReason(moment.title || "Ρύθμιση / Έλεγχος"); setSub("newappt"); }
  };
  const momentBody = (moment) => {
    const rv = (momentReplyView && momentReplyView.id === moment.id) ? momentReplyView : null;
    const items = rv ? rv.actions : moment.replies;
    return (
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: OL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: "0 0 3px", fontSize: 11.5, fontWeight: 800, color: O, letterSpacing: 0.3, textTransform: "uppercase" }}>Ορφέας{moment.title ? " · " + moment.title : ""}</p>
          <p style={{ margin: "0 0 12px", fontSize: 14, color: DK, lineHeight: 1.55 }}>{rv ? rv.text : moment.text}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {items.map((r, i) => (
              <button key={i} onClick={() => (rv ? handleMomentFollowup(moment, r) : handleMomentReply(moment, r))}
                style={{ padding: "9px 14px", borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  border: r.primary ? "none" : `1.5px solid ${r.warn ? "#E6C0C0" : "#e6e4e1"}`,
                  background: r.primary ? O : WH, color: r.primary ? WH : (r.warn ? "#C62828" : DK) }}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };
  const availableTimeSlots = liveSlots !== null ? liveSlots : getClinicTimeSlots(clinic, newADate);
  const clinicHoursSummary = getClinicHoursSummaryForDate(clinic, newADate);
  const bookingCalendarDays = getCalendarGrid(bookingCalendarMonth, clinic, newADate);
  const ch = () =>
    window.open(
      "https://salesiq.zohopublic.com/signaturesupport.ls?widgetcode=siq157b90aae19f0944db6748faf8ca279cf5222f4f87c80eb0bdcfd38c9feba517",
      "_blank",
    );
  useEffect(() => {
    if (!newADate) return;
    const selected = toDateOnlyTime(newADate);
    if (Number.isNaN(selected.getTime())) return;
    setBookingCalendarMonth((prev) => {
      if (
        prev.getFullYear() === selected.getFullYear() &&
        prev.getMonth() === selected.getMonth()
      ) {
        return prev;
      }
      return new Date(selected.getFullYear(), selected.getMonth(), 1);
    });
  }, [newADate]);
  useEffect(() => {
    if (!clinicId || !newADate) return;
    setNewATime("");
    setLiveSlots(null);    setLoadingSlots(true);

    fetch(`${GCAL_SLOTS_URL}?clinic=${clinicId}&date=${newADate}`)
      .then(r => r.json())
      .then(data => {
        if (data.closed || !data.slots || data.slots.length === 0) {
          setLiveSlots([]);
        } else {
          setLiveSlots(data.slots.map(s => s.start));
        }
      })
      .catch(() => {
        // Fallback σε static slots αν αποτύχει το API
        const fallback = getClinicTimeSlots(clinic, newADate);
        setLiveSlots(fallback);
      })
      .finally(() => setLoadingSlots(false));
  }, [clinicId, newADate]);

  // Φόρτωση slots για reschedule
  useEffect(() => {
    if (!clinicId || !rescheduleDate) return;
    setRescheduleTime("");
    setRescheduleLiveSlots(null);
    setRescheduleLoadingSlots(true);
    fetch(`${GCAL_SLOTS_URL}?clinic=${clinicId}&date=${rescheduleDate}`)
      .then(r => r.json())
      .then(data => {
        if (data.closed || !data.slots || data.slots.length === 0) {
          setRescheduleLiveSlots([]);
        } else {
          setRescheduleLiveSlots(data.slots.map(s => s.start));
        }
      })
      .catch(() => {
        setRescheduleLiveSlots(getClinicTimeSlots(clinic, rescheduleDate));
      })
      .finally(() => setRescheduleLoadingSlots(false));
  }, [clinicId, rescheduleDate]);
  // ─── Αυτόματη δημιουργία προτεινόμενων ραντεβού κατά το onboarding ───
  // ─── Αλλαγή ραντεβού από πελάτη ───
  const doReschedule = async () => {
    if (!rescheduleAppt || !rescheduleDate || !rescheduleTime) return;
    setRescheduling(true);
    try {
      const res = await fetch(`${API_URL}/appointments/${rescheduleAppt.id}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointment_date: rescheduleDate,
          appointment_time: rescheduleTime,
          status: 'pending',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setRescheduleSuccess(true);
        fetchAppointments();
        setTimeout(() => {
          setRescheduleAppt(null);
          setRescheduleSuccess(false);
          setRescheduleDate("");
          setRescheduleTime("");
        }, 2500);
      }
    } catch(err) {
      console.error('Reschedule error:', err);
    } finally {
      setRescheduling(false);
    }
  };

  // ─── Ακύρωση ραντεβού από πελάτη ───
  const cancelAppointment = async () => {
    if (!rescheduleAppt) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/appointments/${rescheduleAppt.id}`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled' }),
      });
      const data = await res.json();
      if (data.success) {
        setCancelling(false);
        setCancelConfirm("done");
        fetchAppointments();
        setTimeout(() => {
          setRescheduleAppt(null);
          setCancelConfirm(false);
        }, 2000);
      }
    } catch(err) {
      console.error('Cancel error:', err);
      setCancelling(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  //  AUTH FUNCTIONS — OTP + PIN
  // ═══════════════════════════════════════════════════════════════
  const AUTH_API = "https://admin.earmedical.gr/wp-json/earmedical/v1/auth";

  const authRequestOtp = async () => {
    const phone = authPhone.replace(/\D/g, "");
    if (phone.length !== 10 || !phone.startsWith("6")) {
      setAuthError("Μη έγκυρο κινητό (πρέπει να είναι 10 ψηφία και να ξεκινά από 6)");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await fetch(AUTH_API + "/request-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.message || "Σφάλμα. Δοκιμάστε ξανά.");
        setAuthLoading(false);
        return;
      }
      setAuthPhoneMasked(data.phone_masked || "");
      setAuthStep("otp");
      setAuthOtp("");
      // Countdown για resend
      setAuthResendTimer(60);
      const interval = setInterval(() => {
        setAuthResendTimer((prev) => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      setAuthError("Σφάλμα δικτύου. Δοκιμάστε ξανά.");
    }
    setAuthLoading(false);
  };

  const authVerifyOtp = async () => {
    const otp = authOtp.replace(/\D/g, "");
    if (otp.length !== 4) {
      setAuthError("Εισάγετε 4ψήφιο κωδικό");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await fetch(AUTH_API + "/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: authPhone.replace(/\D/g, ""), otp }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.message || "Λάθος κωδικός");
        setAuthLoading(false);
        return;
      }
      // ✅ Αποθήκευση token — καθαρίζουμε παλιά δεδομένα άλλου πελάτη
      localStorage.removeItem("earmedical_data");
      localStorage.setItem("earmedical_auth_token", data.token);
      localStorage.setItem("earmedical_auth_phone", data.phone);
      setAuthToken(data.token);
      setProfId(data.prof_id || "");
      setNm(data.patient_name || "");
      setPh2(data.phone || "");
      // Save patient_id for offers & other features
      if (data.patient_id) setChatPatientId(data.patient_id);

      // Reset παλιά στοιχεία άλλου πελάτη
      setAudiologist(""); setEm(""); setBirthday("");
      setSL(""); setSR(""); setSC("");
      setDv({ b: "", m: "", t: "RIC", p: "rechargeable" });

      // Φόρτωση πλήρων στοιχείων πελάτη από WP (audiologist, clinic, κλπ)
      try {
        const fullRes = await fetch(`${API_URL}/patients?phone=${encodeURIComponent(data.phone)}`);
        const full = await fullRes.json();
        if (full.found) {
          if (full.patient_name)   setNm(full.patient_name);
          if (full.audiologist)    setAudiologist(full.audiologist);
          if (full.clinic)         setClinicId(full.clinic);
          if (full.patient_email)  setEm(full.patient_email);
          if (full.birthday)       setBirthday(full.birthday);
          if (full.device_brand)   setDv(prev => ({ ...prev, b: full.device_brand }));
          if (full.device_model)   setDv(prev => ({ ...prev, m: full.device_model }));
          if (full.device_type)    setDv(prev => ({ ...prev, t: full.device_type }));
          if (full.power_source)   setDv(prev => ({ ...prev, p: full.power_source }));
          if (full.serial_left)    setSL(full.serial_left);
          if (full.serial_right)   setSR(full.serial_right);
          if (full.serial_charger) setSC(full.serial_charger);
          if (full.patient_status) setPatientStatus(full.patient_status);
          if (full.trial_days)     setTrialDays(full.trial_days);
          if (full.trial_start)    setTrialStart(full.trial_start);
          if (full.warranty_years) setWarrantyYears(full.warranty_years);
          if (full.fitting_date) {
            let fd = full.fitting_date;
            if (fd && fd.length === 8 && !fd.includes('-')) {
              fd = fd.substring(0,4) + '-' + fd.substring(4,6) + '-' + fd.substring(6,8);
            }
            if (fd && !isNaN(new Date(fd))) setSd(fd);
          }
          // ✅ Skip onboarding — ο πελάτης έχει ήδη στοιχεία στο WP
          setSp(false);
          setTermsAccepted(true);
        }
      } catch(e) { console.log('[Auth] Full patient fetch failed:', e); }

      // v1.3.42 — PIN setup routing:
      //   (a) prospect signup → setNeedsPinSetup(true) earlier in flow
      //   (b) existing customer whose PIN was wiped by the v1.6.13
      //       prof_id migration (server returns has_pin:false)
      //   (c) brand-new customer who never had any PIN
      // Any of these requires the user to set a fresh 4-digit PIN before
      // we mark them "logged-in". Previously only (a) was handled, so
      // wiped customers were silently logged in with no PIN configured —
      // breaking the password-reset path.
      const requiresPinSetup = needsPinSetup || data.has_pin === false;
      if (requiresPinSetup) {
        setAuthStep("set-pin");
        setAuthPin("");
        setAuthPinConfirm("");
      } else {
        setAuthStep("logged-in");
      }
      setAuthOtp("");
    } catch (e) {
      setAuthError("Σφάλμα δικτύου. Δοκιμάστε ξανά.");
    }
    setAuthLoading(false);
  };

  const authSetPin = async () => {
    if (authPin.length !== 4) {
      setAuthError("Το PIN πρέπει να είναι 4 ψηφία");
      return;
    }
    if (authPin !== authPinConfirm) {
      setAuthError("Τα PIN δεν ταιριάζουν");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await fetch(AUTH_API + "/set-pin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: authToken, pin: authPin }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAuthError(data.message || "Σφάλμα αποθήκευσης PIN");
        setAuthLoading(false);
        return;
      }
      localStorage.setItem("earmedical_has_pin", "1");
      setAuthStep("logged-in");
      setAuthPin("");
      setAuthPinConfirm("");
      setNeedsPinSetup(false); // v1.3.36 — clear prospect flag after PIN setup
    } catch (e) {
      setAuthError("Σφάλμα δικτύου");
    }
    setAuthLoading(false);
  };

  const authLoginWithPin = async () => {
    const phone = authPhone.replace(/\D/g, "");
    const pin = authPin.replace(/\D/g, "");
    if (phone.length !== 10 || pin.length !== 4) {
      setAuthError("Συμπληρώστε κινητό και PIN");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await fetch(AUTH_API + "/login-pin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, pin }),
      });
      const data = await res.json();
      if (!res.ok) {
        // v1.3.43 — Auto-route to OTP if patient never set a PIN
        // (or it was wiped by v1.6.13 migration). Previously the user
        // saw a confusing "no_pin" error and had to manually tap
        // "Ξέχασα τον κωδικό" to recover. Now we hand them off to the
        // OTP flow seamlessly — first-time customers get the SMS,
        // verify, and set their own PIN without an interim error.
        if (data.code === 'no_pin') {
          setAuthError("");
          setAuthLoading(false);
          await authRequestOtp();
          return;
        }
        setAuthError(data.message || "Λάθος PIN");
        setAuthLoading(false);
        return;
      }
      // Καθαρίζουμε παλιά δεδομένα άλλου πελάτη
      localStorage.removeItem("earmedical_data");
      localStorage.setItem("earmedical_auth_token", data.token);
      localStorage.setItem("earmedical_auth_phone", data.phone);
      setAuthToken(data.token);
      setProfId(data.prof_id || "");
      setNm(data.patient_name || "");
      setPh2(data.phone || "");
      // Save patient_id for offers & other features
      if (data.patient_id) setChatPatientId(data.patient_id);

      // Reset παλιά στοιχεία άλλου πελάτη
      setAudiologist(""); setEm(""); setBirthday("");
      setSL(""); setSR(""); setSC("");
      setDv({ b: "", m: "", t: "RIC", p: "rechargeable" });

      // Φόρτωση πλήρων στοιχείων πελάτη από WP (audiologist, clinic, κλπ)
      try {
        const fullRes = await fetch(`${API_URL}/patients?phone=${encodeURIComponent(data.phone)}`);
        const full = await fullRes.json();
        if (full.found) {
          if (full.patient_name)   setNm(full.patient_name);
          if (full.audiologist)    setAudiologist(full.audiologist);
          if (full.clinic)         setClinicId(full.clinic);
          if (full.patient_email)  setEm(full.patient_email);
          if (full.birthday)       setBirthday(full.birthday);
          if (full.device_brand)   setDv(prev => ({ ...prev, b: full.device_brand }));
          if (full.device_model)   setDv(prev => ({ ...prev, m: full.device_model }));
          if (full.device_type)    setDv(prev => ({ ...prev, t: full.device_type }));
          if (full.power_source)   setDv(prev => ({ ...prev, p: full.power_source }));
          if (full.serial_left)    setSL(full.serial_left);
          if (full.serial_right)   setSR(full.serial_right);
          if (full.serial_charger) setSC(full.serial_charger);
          if (full.patient_status) setPatientStatus(full.patient_status);
          if (full.trial_days)     setTrialDays(full.trial_days);
          if (full.trial_start)    setTrialStart(full.trial_start);
          if (full.warranty_years) setWarrantyYears(full.warranty_years);
          if (full.fitting_date) {
            let fd = full.fitting_date;
            if (fd && fd.length === 8 && !fd.includes('-')) {
              fd = fd.substring(0,4) + '-' + fd.substring(4,6) + '-' + fd.substring(6,8);
            }
            if (fd && !isNaN(new Date(fd))) setSd(fd);
          }
          // ✅ Skip onboarding — ο πελάτης έχει ήδη στοιχεία στο WP
          setSp(false);
          setTermsAccepted(true);
        }
      } catch(e) { console.log('[Auth] Full patient fetch failed:', e); }

      setAuthStep("logged-in");
      setAuthPin("");
    } catch (e) {
      setAuthError("Σφάλμα δικτύου");
    }
    setAuthLoading(false);
  };

  // v1.3.33 — Prospect self-signup: POST σε νέο WP endpoint, στο success
  // αυτο-login σαν prospect status (5-tab UI). Στο 409 (ήδη εγγεγραμμένος)
  // πάμε back σε phone screen με το ίδιο τηλέφωνο και error message.
  const authProspectSignup = async () => {
    const phone = authPhone.replace(/\D/g, "");
    const name  = signupName.trim();
    const email = signupEmail.trim();
    if (!name || !email || phone.length !== 10 || phone[0] !== "6") {
      setAuthError("Συμπλήρωσε όνομα, email, και έγκυρο ελληνικό κινητό (10 ψηφία, αρχίζει με 6)");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAuthError("Μη έγκυρο email");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const res = await fetch(`${API_URL}/prospect-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, clinic: signupClinic }),
      });
      const data = await res.json();
      if (res.status === 409 || data.code === "already_registered") {
        // v1.3.55 — Ήδη εγγεγραμμένος: ΟΔΗΓΟΥΜΕ στην οθόνη σύνδεσης (το τηλέφωνο μένει
        // συμπληρωμένο) αντί να μείνει στην εγγραφή. Καθαρό μήνυμα + αυτόματη μετάβαση.
        setAuthError(data.message || "Είσαι ήδη εγγεγραμμένος. Συνδέσου με τον κωδικό σου.");
        setSignupName(""); setSignupEmail("");
        setAuthStep("phone");   // πίσω στην οθόνη login — authPhone ήδη έχει το τηλέφωνο
        setAuthLoading(false);
        return;
      }
      if (!res.ok || !data.success) {
        setAuthError(data.message || "Σφάλμα εγγραφής. Δοκίμασε ξανά.");
        setAuthLoading(false);
        return;
      }
      // ── v1.3.36 — Δεν κάνουμε αυτόματο login. Αντί για αυτό:
      //   1. Αποθηκεύουμε τα στοιχεία του νέου prospect (name/email/clinic/profId)
      //   2. Σηκώνουμε το flag needsPinSetup ώστε μετά το OTP verify να πάει σε set-pin
      //   3. Ζητάμε OTP via SMS (παίρνει το authPhone που έχει ήδη τιμή)
      //   4. Μεταβαίνουμε στο authStep="otp" — ο χρήστης βάζει 4ψήφιο OTP
      //   5. authVerifyOtp θα δει το needsPinSetup και θα οδηγήσει σε set-pin
      localStorage.removeItem("earmedical_data");
      setProfId(data.prof_id || "");
      setNm(name);
      setEm(email);
      setPh2(phone);
      setClinicId(signupClinic);
      setPatientStatus("prospect");
      setAudiologist("");
      setSp(false);
      setTermsAccepted(true);
      setNeedsPinSetup(true);

      // Reset form before requesting OTP
      setSignupName(""); setSignupEmail("");
      setAuthLoading(false);

      // Trigger OTP send via existing helper (handles screen transition + countdown)
      await authRequestOtp();
      return;
    } catch (e) {
      setAuthError("Σφάλμα δικτύου. Δοκίμασε ξανά.");
    }
    setAuthLoading(false);
  };

  const authLogout = async () => {
    try {
      await fetch(AUTH_API + "/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: authToken }),
      });
    } catch (e) {}
    localStorage.removeItem("earmedical_auth_token");
    localStorage.removeItem("earmedical_auth_phone");
    localStorage.removeItem("earmedical_has_pin");
    localStorage.removeItem("earmedical_data");
    setAuthToken("");
    setAuthPhone("");
    setAuthOtp("");
    setAuthPin("");
    setAuthStep("phone");
    // Clear user data
    setNm(""); setEm(""); setPh2(""); setProfId("");
    window.location.reload();
  };

  // ─── Διαγραφή Λογαριασμού (Google Play / Apple compliance) ───
  const authDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError("");
    try {
      const res = await fetch(AUTH_API + "/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: authToken,
          confirmation: "ΔΙΑΓΡΑΦΗ",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setDeleteError(data.error || "Σφάλμα. Δοκιμάστε ξανά.");
        setDeleteLoading(false);
        return;
      }
      // Success — clear everything and redirect
      localStorage.clear();
      setShowDeleteModal(false);
      setDeleteConfirmText("");
      alert("Ο λογαριασμός σας διαγράφηκε με επιτυχία.\n\nΣας ευχαριστούμε που χρησιμοποιήσατε το Earmedical.");
      window.location.reload();
    } catch (e) {
      setDeleteError("Σφάλμα δικτύου. Δοκιμάστε ξανά.");
      setDeleteLoading(false);
    }
  };


  // ─── Υποβολή ερωτηματολογίου στο WordPress ───
  const submitQuiz = async () => {
    if (Object.keys(ans).length < activeQZ.length) return;
    setQSending(true);

    // Υπολογισμός score
    const maxScore = activeQZ.reduce((sum, q) => sum + Math.max(...q.sc), 0);
    const userScore = activeQZ.reduce((sum, q, i) => sum + (q.sc[ans[i]] ?? 0), 0);
    const pct = Math.round((userScore / maxScore) * 100);
    setQScore(pct);

    // Κατασκευή απαντήσεων
    const answers = activeQZ.map((q, i) => ({
      question: q.q,
      answer: q.o[ans[i]] || "—",
      score: q.sc[ans[i]] ?? 0,
    }));

    try {
      await fetch(API_URL + '/questionnaire', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name:  nm || 'Πελάτης',
          patient_phone: ph2,
          patient_email: em,
          prof_id:       profId,
          clinic:        clinicId,
          device:        (dv.b || '') + ' ' + (dv.m || ''),
          day:           ds + 1,
          score:         pct,
          answers:       answers,
        }),
      });
    } catch(e) {
      console.error('Quiz submit error:', e);
    }

    // v1.3.41 — Persist last-fill date for the smart prompt card on home.
    try {
      const isoDate = new Date().toISOString().slice(0, 10);
      localStorage.setItem('em_last_quiz_date', isoDate);
      setLastQuizDate(isoDate);
    } catch(e) { /* private mode / quota — non-fatal */ }

    setQSending(false);
    setQD(true);
  };

  // ─── Daily Check-in (Trial) ───
  const submitDailyCheckin = async (mood) => {
    setDailyMood(mood);
    setDailySent(true);
    try {
      await fetch(API_URL + '/daily-checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prof_id: profId,
          patient_phone: ph2,
          mood: mood,
          comment: dailyComment,
          day: trialDaysUsed + 1,
        }),
      });
    } catch(e) {
      console.error('Daily checkin error:', e);
    }
  };

  // ─── Daily check-in για purchased patients (απλό good/issue) ───
  const submitPurchasedCheckin = async (moodKey) => {
    // Map purchased 2-option UI → standard mood values in backend
    // "ok" → "good"  (όλα καλά)
    // "issue" → "struggle"  (triggers email alert to audiologist)
    const mood = moodKey === 'ok' ? 'good' : 'struggle';
    // Calculate day since fitting_date (for purchased context)
    let dayNum = 0;
    if (sd) {
      try {
        const fd = new Date(sd);
        if (!isNaN(fd)) {
          dayNum = Math.max(1, Math.floor((Date.now() - fd.getTime()) / 86400000) + 1);
        }
      } catch(e) {}
    }
    try {
      await fetch(API_URL + '/daily-checkin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prof_id: profId,
          patient_phone: ph2,
          mood: mood,
          comment: '',
          day: dayNum,
        }),
      });
    } catch(e) {
      console.error('Purchased checkin error:', e);
    }
  };

  // ─── Αποθήκευση πελάτη στο WordPress ───
  const savePatientToWp = async (patientData) => {
    try {
      console.log('[WP] Saving patient:', patientData.patient_name, patientData.patient_phone);
      const res = await fetch(API_URL + '/patients', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(patientData),
      });
      const data = await res.json();
      console.log('[WP] Save result:', res.status, data);
      if (!data.success) {
        console.error('[WP] Save failed:', data);
      }
      return data;
    } catch (err) {
      console.error('[WP] savePatientToWp error:', err);
      return null;
    }
  };

  // ─── Αποστολή purchase intent ("Αγορά τώρα") ───
  const submitPurchaseIntent = async () => {
    setPurchaseSubmitting(true);
    try {
      console.log('[PurchaseIntent] Sending:', { nm, ph2, em, profId, trialDay });

      const res = await fetch('https://admin.earmedical.gr/purchase-intent.php', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_name:  nm,
          patient_phone: ph2,
          patient_email: em,
          prof_id:       profId,
          trial_day:     trialDay,
        }),
      });

      console.log('[PurchaseIntent] Response status:', res.status);

      const data = await res.json();
      console.log('[PurchaseIntent] Response data:', data);

      if (data.success) {
        setPurchaseSent(true);
        setPurchaseIntentSent(true);
        try { localStorage.setItem('earmedical_purchase_intent_sent', '1'); } catch(e) {}
      } else {
        alert('Κάτι πήγε στραβά. ' + (data.error || 'Παρακαλώ δοκιμάστε ξανά ή καλέστε μας.'));
      }
    } catch (err) {
      console.error('[PurchaseIntent] Error:', err);
      alert('Πρόβλημα σύνδεσης. Παρακαλώ δοκιμάστε ξανά ή καλέστε μας.');
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  // Check if purchase intent has been sent already
  const [purchaseIntentSent, setPurchaseIntentSent] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem('earmedical_purchase_intent_sent') === '1') {
        setPurchaseIntentSent(true);
      }
    } catch(e) {}
  }, []);

  // Check if install banner was previously dismissed (7-day cooldown)
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem('earmedical_install_home_dismissed');
      if (dismissed) {
        const days = (Date.now() - parseInt(dismissed, 10)) / 86400000;
        if (days < 7) setInstallBannerDismissed(true);
      }
    } catch(e) {}
  }, []);

  const dismissInstallBanner = () => {
    try { localStorage.setItem('earmedical_install_home_dismissed', Date.now().toString()); } catch(e) {}
    setInstallBannerDismissed(true);
  };

  // ─── Chat: Fetch messages (polling) ──────────────────────────
  const chatApiUrl = 'https://admin.earmedical.gr/chat-api.php';

  const fetchChatMessages = async (useCache = true) => {
    if (!profId && !ph2) return;

    const cacheKey = `earmedical_chat_${profId || ph2}`;

    // 1. Load from cache first (instant) - only on first load
    if (useCache) {
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed.messages)) {
            setChatMessages(parsed.messages);
            if (parsed.patient_id) setChatPatientId(parsed.patient_id);
          }
        }
      } catch(e) {}
    }

    try {
      const params = new URLSearchParams({
        action: 'patient_messages',
        prof_id: profId || '',
        phone: ph2 || '',
      });
      const res = await fetch(`${chatApiUrl}?${params}`);
      const data = await res.json();
      if (data.success) {
        setChatMessages(data.messages || []);
        if (data.patient_id) setChatPatientId(data.patient_id);
        // Count unread audiologist messages
        const unread = (data.messages || []).filter(m => m.sender === 'audiologist' && !m.is_read).length;
        setChatUnread(unread);
        // Save to cache (keep only last 100 messages)
        try {
          const toCache = (data.messages || []).slice(-100);
          localStorage.setItem(cacheKey, JSON.stringify({
            messages: toCache,
            patient_id: data.patient_id,
            timestamp: Date.now(),
          }));
        } catch(e) {}
      }
    } catch (err) {
      console.error('[Chat] Fetch error:', err);
    }
  };

  // Poll chat messages every 5 seconds when chat is open
  const chatPollRef = React.useRef(null);

  useEffect(() => {
    if (sub === 'chat') {
      fetchChatMessages(true);  // First load with cache
      chatPollRef.current = setInterval(() => fetchChatMessages(false), 5000); // Polls without cache reload
      return () => clearInterval(chatPollRef.current);
    } else {
      if (chatPollRef.current) clearInterval(chatPollRef.current);
    }
  }, [sub, profId, ph2]);

  // ─── OFFERS: Fetch visible offers for patient ─────────────
  const fetchOffers = async () => {
    if (!chatPatientId) return;
    try {
      const res = await fetch(`${API_URL}/offers/visible?patient_id=${chatPatientId}`);
      const data = await res.json();
      if (Array.isArray(data.offers)) {
        setOffers(data.offers);
      }
    } catch (err) {
      console.error('[Offers] Fetch error:', err);
    }
  };

  // Load offers when chatPatientId becomes available OR when entering offers screen
  useEffect(() => {
    if (chatPatientId) {
      fetchOffers();
      // Poll every 60s for fresh offers (e.g. audiologist just sent a new one)
      const interval = setInterval(fetchOffers, 60000);
      return () => clearInterval(interval);
    }
  }, [chatPatientId]);

  // Mark offer as viewed when user opens the screen
  const markOfferViewed = async (offerId) => {
    if (!chatPatientId || !offerId) return;
    try {
      await fetch(`${API_URL}/offers/mark-viewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: chatPatientId, offer_id: offerId }),
      });
    } catch (err) {
      console.error('[Offers] Mark viewed error:', err);
    }
  };

  // Patient declares intent — 'interested' | 'questions' | 'not_ready'
  const chooseOfferItem = async (offerId, itemId, action) => {
    if (!chatPatientId || offerSubmitting) return;
    setOfferSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/offers/patient-choose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: chatPatientId,
          offer_id: offerId,
          item_id: itemId || '',
          action: action,
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (action === 'interested') {
          setOfferThankYou(true);
        } else if (action === 'questions') {
          setOfferScreen(false);
          setSub('chat');
        } else if (action === 'not_ready') {
          // Soft exit — close screen, offer stays available to revisit
          setOfferScreen(false);
        }
        await fetchOffers();
      } else {
        alert('Δεν ήταν δυνατή η αποστολή. ' + (data.error || 'Δοκιμάστε ξανά.'));
      }
    } catch (err) {
      alert('Πρόβλημα σύνδεσης. Δοκιμάστε ξανά.');
    } finally {
      setOfferSubmitting(false);
    }
  };

  // ─── AI explanation: "Τι διαφορά έχει αυτό το μοντέλο;" ─────
  const askAiAboutModel = async (offerId, itemId, brand, model, currentItem, allItems) => {
    setOfferAiModal({ offerId, itemId, brand, model });
    setOfferAiLoading(true);
    setOfferAiAnswer("");

    // Find other items to compare against
    const otherItems = (allItems || []).filter(it => it.id !== itemId);
    const otherModelsStr = otherItems.map(it => `${it.brand} ${it.model}`).join(', ') || 'κανένα άλλο';

    // Build features context
    const featuresList = (currentItem.features || []).map(f => `- ${f}`).join('\n');

    const systemPrompt = `Είσαι ο φιλικός AI βοηθός της Earmedical, που βοηθά ηλικιωμένους να κατανοήσουν τα ακουστικά τους.

Ο χρήστης ρωτά για το μοντέλο ${brand} ${model}.

ΠΛΗΡΗ ΧΑΡΑΚΤΗΡΙΣΤΙΚΑ:
${featuresList}

Άλλα μοντέλα στην προσφορά: ${otherModelsStr}

Κανόνες απάντησης:
1. 3-5 προτάσεις συνολικά
2. Απλή γλώσσα, ΧΩΡΙΣ τεχνικούς όρους
3. Εστίασε σε πρακτικό όφελος (τι βιώνει ο χρήστης στην καθημερινότητα)
4. Αν υπάρχουν άλλα μοντέλα, κάνε σύντομη σύγκριση (ποιο είναι πιο αναβαθμισμένο και γιατί)
5. Μην υποσχεθείς ότι είναι κατάλληλο για όλους — πες ότι ο ειδικός είναι αυτός που θα προτείνει
6. Μη χρησιμοποιείς emojis παρά μόνο αν ταιριάζει φυσικά`;

    try {
      const response = await fetch(`${API_URL}/ai-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 600,
          system: systemPrompt,
          messages: [{ role: 'user', content: `Τι διαφορά έχει αυτό το μοντέλο; Εξήγησέ μου απλά τι κερδίζω με το ${brand} ${model}.` }],
        }),
      });
      const data = await response.json();
      if (data.error) {
        setOfferAiAnswer('Δεν μπόρεσα να απαντήσω αυτή τη στιγμή. Δοκιμάστε ξανά ή μιλήστε με τον ειδικό σας.');
      } else {
        const text = data.content?.map(c => c.text || '').join('') || '';
        setOfferAiAnswer(text || 'Δεν υπάρχει απάντηση.');
      }
    } catch (err) {
      setOfferAiAnswer('Σφάλμα σύνδεσης. Δοκιμάστε ξανά.');
    } finally {
      setOfferAiLoading(false);
    }
  };

  // Helper: do we have a new unseen offer?
  const hasUnseenOffer = () => {
    return offers.some(o => o.status === 'sent' && !offerSeen.includes(o.id));
  };

  // Helper: sent offers (active — not yet decided)
  const activeSentOffers = offers.filter(o => o.status === 'sent');
  const decidedOffers = offers.filter(o => o.status === 'decided');

  // Open offers screen + mark all sent offers as seen
  const openOffersScreen = () => {
    setOfferScreen(true);
    setOfferThankYou(false);
    // Mark newly-visible sent offers as viewed (backend)
    activeSentOffers.forEach(o => markOfferViewed(o.id));
    // Mark locally as seen (for the blinking indicator)
    const seenIds = [...new Set([...offerSeen, ...offers.map(o => o.id)])];
    setOfferSeen(seenIds);
    try { localStorage.setItem("ear_offer_seen", JSON.stringify(seenIds)); } catch(e) {}
  };

  // Also check for unread periodically (even when chat not open)
  useEffect(() => {
    if (!profId && !ph2) return;
    const checkUnread = async () => {
      try {
        const params = new URLSearchParams({
          action: 'patient_messages',
          prof_id: profId || '',
          phone: ph2 || '',
        });
        const res = await fetch(`${chatApiUrl}?${params}`);
        const data = await res.json();
        if (data.success) {
          const unread = (data.messages || []).filter(m => m.sender === 'audiologist' && parseInt(m.is_read) === 0).length;
          setChatUnread(unread);
        }
      } catch(e) {}
    };
    checkUnread();
    const iv = setInterval(checkUnread, 30000); // Check every 30 sec
    return () => clearInterval(iv);
  }, [profId, ph2]);

  // v1.3.34 — Upload file to chat-api.php?action=upload (15 MB max,
  // server validates MIME type). Returns { url, type, name, size } that
  // we attach to the next sendChatMessage. Shows preview state immediately
  // via local FileReader, then replaces previewUrl with server URL on success.
  const uploadChatAttachment = async (file) => {
    if (!file) return;
    const MAX = 15 * 1024 * 1024;
    if (file.size > MAX) {
      alert("Το αρχείο είναι πολύ μεγάλο (max 15 MB)");
      return;
    }
    // Local preview for instant feedback (image only)
    let localPreview = "";
    if (file.type.startsWith("image/")) {
      try { localPreview = URL.createObjectURL(file); } catch (e) {}
    }
    setChatAttachment({
      url: "", type: file.type, name: file.name, size: file.size,
      previewUrl: localPreview, uploading: true,
    });
    setChatUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prof_id", profId || "");
      fd.append("phone", ph2 || "");
      const res = await fetch(`${chatApiUrl}?action=upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success && data.url) {
        setChatAttachment({
          url: data.url, type: data.type, name: data.name, size: data.size,
          previewUrl: localPreview || data.url, uploading: false,
        });
      } else {
        setChatAttachment(null);
        alert("Αποτυχία ανεβάσματος. " + (data.error || ""));
      }
    } catch (err) {
      setChatAttachment(null);
      alert("Πρόβλημα σύνδεσης κατά το ανέβασμα.");
    } finally {
      setChatUploading(false);
    }
  };

  const sendChatMessage = async () => {
    const msg = chatInput.trim();
    const att = chatAttachment;
    // Must have at least text OR an uploaded attachment (uploaded = has url)
    if ((!msg && !att?.url) || chatSending || chatUploading) return;
    setChatSending(true);
    try {
      console.log('[Chat] Sending:', { profId, ph2: ph2, msg: msg.substring(0, 50), attachment: att?.name });
      const body = {
        prof_id: profId || '',
        phone: ph2 || '',
        message: msg,
      };
      if (att?.url) {
        body.file_url  = att.url;
        body.file_type = att.type;
        body.file_name = att.name;
        body.file_size = att.size;
      }
      const res = await fetch(`${chatApiUrl}?action=patient_send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      console.log('[Chat] Response:', data);
      if (data.success) {
        setChatInput('');
        setChatAttachment(null);
        setChatMessages(prev => [...prev, {
          id: data.msg_id,
          sender: 'patient',
          sender_name: nm || 'Εσείς',
          message: msg,
          file_url:  att?.url || '',
          file_type: att?.type || '',
          file_name: att?.name || '',
          file_size: att?.size || 0,
          is_read: 0,
          created_at: new Date().toISOString().replace('T', ' ').substring(0, 19),
        }]);
      } else {
        console.error('[Chat] Send failed:', data);
        alert('Δεν ήταν δυνατή η αποστολή. ' + (data.error || '') + (data.debug ? '\n(' + data.debug + ')' : ''));
      }
    } catch(err) {
      console.error('[Chat] Send error:', err);
      alert('Πρόβλημα σύνδεσης. Δοκιμάστε ξανά.');
    } finally {
      setChatSending(false);
    }
  };

  // ─── AI Agent: Send message to Claude ──────────────────────
  // ── v1.3.57 — Εικόνα προς αξιολόγηση ──────────────────────────────
  // Σμίκρυνση client-side (max 1600px, JPEG 0.85) ώστε το payload να μένει μικρό
  // και ταυτόχρονα μετατρέπει HEIC (iOS) → JPEG μέσω canvas. Σε αποτυχία decode,
  // στέλνει το raw data URL αν είναι έγκυρη εικόνα.
  const aiDownscaleImage = (file) => new Promise((resolve, reject) => {
    const MAX = 1600, Q = 0.85;
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => {
        if (typeof reader.result === "string" && /^data:image\//.test(reader.result)) resolve({ url: reader.result, name: file.name || "image" });
        else reject(new Error("decode"));
      };
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) { const r = Math.min(MAX / w, MAX / h); w = Math.round(w * r); h = Math.round(h * r); }
        try {
          const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
          cv.getContext("2d").drawImage(img, 0, 0, w, h);
          resolve({ url: cv.toDataURL("image/jpeg", Q), name: file.name || "image" });
        } catch (e) { resolve({ url: reader.result, name: file.name || "image" }); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

  const onAiImageSelected = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "")) {
      setAiMessages(prev => [...prev, { role: "assistant", content: "Μπορώ να δω εικόνες — φωτογραφία ή screenshot (π.χ. ακοόγραμμα ή μια βλάβη). Δοκιμάστε ξανά με μια εικόνα." }]);
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setAiMessages(prev => [...prev, { role: "assistant", content: "Η εικόνα είναι πολύ μεγάλη. Δοκιμάστε μια μικρότερη φωτογραφία." }]);
      return;
    }
    try { setAiImage(await aiDownscaleImage(file)); }
    catch (err) { setAiMessages(prev => [...prev, { role: "assistant", content: "Δεν μπόρεσα να διαβάσω την εικόνα. Δοκιμάστε ξανά." }]); }
  };

  // ── v1.3.57 — Φωνή → κείμενο (ηχογράφηση → Whisper) ───────────────
  const aiStartRecording = async () => {
    if (aiRecording || aiTranscribing || aiLoading) return;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === "undefined") {
      setAiMessages(prev => [...prev, { role: "assistant", content: "Η ηχογράφηση δεν υποστηρίζεται σε αυτή τη συσκευή. Γράψτε ελεύθερα το μήνυμά σας." }]);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      aiAudioChunks.current = [];
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) aiAudioChunks.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(aiAudioChunks.current, { type: mr.mimeType || "audio/webm" });
        if (!blob.size) { setAiRecording(false); return; }
        setAiTranscribing(true);
        try {
          const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });
          const resp = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/transcribe", {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-Auth-Token": authToken || "" },
            body: JSON.stringify({ audio: dataUrl }),
          });
          const d = await resp.json().catch(() => null);
          if (d && d.ok && d.text) setAiInput(prev => (prev ? prev + " " : "") + d.text);
          else setAiMessages(prev => [...prev, { role: "assistant", content: "Δεν κατάλαβα την ηχογράφηση. Δοκιμάστε ξανά ή γράψτε το μήνυμά σας." }]);
        } catch (err) {
          setAiMessages(prev => [...prev, { role: "assistant", content: "Σφάλμα στη μεταγραφή. Δοκιμάστε ξανά." }]);
        } finally { setAiTranscribing(false); }
      };
      aiMediaRec.current = mr;
      mr.start();
      setAiRecording(true);
    } catch (err) {
      setAiMessages(prev => [...prev, { role: "assistant", content: "Δεν έχω πρόσβαση στο μικρόφωνο. Επιτρέψτε την πρόσβαση από τις ρυθμίσεις και δοκιμάστε ξανά." }]);
    }
  };
  const aiStopRecording = () => {
    if (aiMediaRec.current && aiRecording) { try { aiMediaRec.current.stop(); } catch (e) {} setAiRecording(false); }
  };

  const sendAiMessage = async () => {
    const msg = aiInput.trim();
    if ((!msg && !aiImage) || aiLoading) return;

    const sentImage = aiImage;
    const userMsg = { role: "user", content: msg || (sentImage ? "Παρακαλώ αξιολόγησε αυτή την εικόνα και πες μου τι να κάνω." : "") };
    setAiMessages(prev => [...prev, { ...userMsg, image: sentImage ? sentImage.url : undefined }]);
    setAiInput("");
    setAiImage(null);
    setAiLoading(true);

    // Build system prompt with patient context
    const deviceInfo = dv ? `${dv.b || ''} ${dv.m || ''} (${dv.t || 'άγνωστος τύπος'})` : 'Δεν έχει οριστεί συσκευή';
    const powerInfo = dv?.p === 'battery' ? `Μπαταρίες${batteryProfile ? ` Νο${batteryProfile.batteryType}` : ''}` : dv?.p === 'rechargeable' ? 'Επαναφορτιζόμενο' : 'Δεν έχει οριστεί';
    const trialInfo = isSupport ? "Πελάτης υποστήριξης (χρήστης ακουστικών άλλης εταιρίας)." : isTrial ? `Ημέρα ${trialDay} από ${trialDays} δοκιμαστική περίοδο. Απομένουν ${trialDaysLeft} ημέρες.` : `Αγορά — ${ds + 1} ημέρες χρήσης.`;

    // Fetch knowledge base (cached in sessionStorage for 1 hour)
    let knowledgeText = '';
    try {
      const cacheKey = `earmedical_kb_${clinic.i || 'all'}`;
      const cached = sessionStorage.getItem(cacheKey);
      const cacheTime = sessionStorage.getItem(cacheKey + '_time');
      const now = Date.now();
      let knowledge = null;

      if (cached && cacheTime && (now - parseInt(cacheTime)) < 3600000) {
        knowledge = JSON.parse(cached);
      } else {
        const kbRes = await fetch(`https://admin.earmedical.gr/wp-json/earmedical/v1/knowledge?clinic=${clinic.i || ''}`);
        const kbData = await kbRes.json();
        if (kbData.success) {
          knowledge = kbData.knowledge;
          sessionStorage.setItem(cacheKey, JSON.stringify(knowledge));
          sessionStorage.setItem(cacheKey + '_time', String(now));
        }
      }

      if (knowledge && knowledge.length > 0) {
        knowledgeText = '\n\nΒΑΣΗ ΓΝΩΣΗΣ (πηγή αληθείας — χρησιμοποίησε αυτές τις πληροφορίες όταν είναι σχετικές):\n';
        knowledge.forEach(k => {
          const pri = k.priority === 'high' ? ' [ΥΨΗΛΗΣ ΠΡΟΤΕΡΑΙΟΤΗΤΑΣ]' : '';
          knowledgeText += `\n● ${k.title}${pri}\n${k.content}\n`;
          if (k.keywords) knowledgeText += `(keywords: ${k.keywords})\n`;
        });
      }
    } catch(e) { console.error('[AI] Knowledge fetch error:', e); }

    const systemPrompt = `Είσαι ο ψηφιακός βοηθός της Earmedical, μιας ελληνικής αλυσίδας κέντρων αποκατάστασης ακοής.
Μιλάς πάντα στα ελληνικά, με φιλικό, απλό και καθησυχαστικό τρόπο. Απαντάς σύντομα και κατανοητά, χωρίς τεχνικούς όρους.

Στοιχεία χρήστη:
- Όνομα: ${nm || 'Δεν γνωρίζω'}
- Ακουστικό: ${deviceInfo}
- Τροφοδοσία: ${powerInfo}
- Κατάσταση: ${trialInfo}
- Κέντρο: ${clinic.c || 'Δεν γνωρίζω'}
- Ακοοπροθετιστής: ${audiologist || 'Δεν έχει ανατεθεί'}

Ρόλος σου:
Βοηθάς τον χρήστη με:
- Προβλήματα ακοής (π.χ. "δεν ακούω καλά", "ακούγεται περίεργα")
- Καθημερινή χρήση (μπαταρία, φόρτιση, καθαρισμός)
- Απλές ρυθμίσεις και συμβουλές
- Ερωτήσεις σχετικά με τη δοκιμαστική περίοδο

${isSupport ? `SUPPORT MODE — ΕΙΔΙΚΕΣ ΟΔΗΓΙΕΣ:
Ο χρήστης είναι πελάτης υποστήριξης που ΔΕΝ έχει ακουστικά Earmedical — έχει ακουστικά άλλης εταιρίας και έρχεται για βοήθεια/service/μπαταρίες.
- ΜΗΝ αναφέρεις "δοκιμαστική περίοδο", "προσαρμογή", "ημέρες χρήσης"
- ΜΗΝ προτείνεις αγορά ή trial
- Εστίασε σε: γενικές συμβουλές φροντίδας, τεχνικά θέματα, ραντεβού service, πληροφορίες για κέντρα & μπαταρίες
- Για πιο εξειδικευμένες ρυθμίσεις/επιδιόρθωση: πρότεινε ραντεβού στο κέντρο Earmedical
- Τόνος: φιλικός, χωρίς πίεση πώλησης

` : ''}Κανόνες απάντησης:
1. Να είσαι σύντομος (2–4 προτάσεις)
2. Να δίνεις πρακτική λύση (βήμα-βήμα αν χρειάζεται)
3. Να χρησιμοποιείς απλή γλώσσα (όχι τεχνικούς όρους)
4. Να προσαρμόζεις την απάντηση ανάλογα με τη μέρα δοκιμής

ΠΟΤΕ ΝΑ ΠΡΟΤΕΙΝΕΙΣ ΑΝΘΡΩΠΙΝΗ ΒΟΗΘΕΙΑ (κρίσιμο):

1. ΑΜΕΣΩΣ — Ιατρικές επείγουσες καταστάσεις:
   - Πόνος στο αυτί, αιμορραγία, ζάλη, λοίμωξη, ξαφνική απώλεια ακοής, έντονο βούισμα/σφύριγμα
   - Απάντηση: "Αυτό χρειάζεται άμεση εξέταση. Καλέστε τώρα το κέντρο σας ή απευθυνθείτε σε γιατρό."

2. ΑΜΕΣΩΣ — Τεχνικά προβλήματα που δεν λύνονται από απόσταση:
   - Σπασμένο ακουστικό, έπεσε στο νερό/χτύπησε, δεν ανοίγει καθόλου, σοβαρή δυσλειτουργία
   - Απάντηση: "Αυτό χρειάζεται τεχνικό έλεγχο από τον ακοοπροθετιστή σας. Κλείστε ραντεβού ή στείλτε του μήνυμα."

3. ΑΜΕΣΩΣ — Συναισθηματική δυσφορία / απογοήτευση:
   - "Δεν με βοηθάει", "χειρότερα από πριν", "απογοητευμένος", "θέλω να τα επιστρέψω"
   - Απάντηση: Αναγνώρισε το συναίσθημα και πρότεινε επικοινωνία: "Σας καταλαβαίνω. Ο ακοοπροθετιστής σας μπορεί να κάνει ρύθμιση για εσάς — είναι συνηθισμένο αυτό και διορθώνεται."

4. ΟΤΑΝ ΔΕΝ ΛΥΝΕΤΑΙ:
   - Αν εκτιμάς ότι το πρόβλημα δεν μπορεί να λυθεί μέσω chat (π.χ. χρειάζεται χέρι, εξέταση, ρύθμιση μηχανήματος)
   - Απάντηση: "Αυτό θα το δούμε καλύτερα από κοντά. Θέλετε να κλείσω ραντεβού ή να στείλετε μήνυμα στον ${audiologist || 'ακοοπροθετιστή σας'};"

5. ΟΤΑΝ Ο ΧΡΗΣΤΗΣ ΡΩΤΑΕΙ ΓΙΑ ΤΟ ΚΕΝΤΡΟ:
   - Ραντεβού, ωράριο, τηλέφωνο, διεύθυνση → δώσε την πληροφορία και προτρέψε επικοινωνία

ΚΛΙΜΑΚΩΣΗ ΣΤΟΝ ΕΙΔΙΚΟ (πολύ σημαντικό): Μόλις καταλάβεις ότι το θέμα ΔΕΝ λύνεται από εδώ — τεχνική βλάβη, χρειάζεται έλεγχος/ρύθμιση από κοντά, ιατρικό ζήτημα, ή ο πελάτης παραμένει δυσαρεστημένος μετά από 1-2 προσπάθειες — μην επιμένεις άλλο. Πες του ζεστά και ΞΕΚΑΘΑΡΑ ότι θα τον συνδέσεις με τον ειδικό του${audiologist ? ` (${audiologist})` : ''} που τον παρακολουθεί, και χρησιμοποίησε ΡΗΤΑ τη φράση «ο ειδικός σας». Τότε ο χρήστης βλέπει αυτόματα κουμπί να μιλήσει με τον ειδικό του / να καλέσει το κέντρο. Στόχος: γρήγορη, ζεστή παραπομπή — όχι ατέρμονες προσπάθειες που δεν βοηθούν.

Στόχος: Σιγουριά, ποιοτική εξυπηρέτηση, σωστή καθοδήγηση.${knowledgeText}`;

    // Build conversation history
    const apiMessages = aiMessages.concat(userMsg).map(m => ({
      role: m.role,
      content: m.content,
    }));

    try {
      const response = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/ai-chat", {
        method: "POST",
        // SECURITY: στείλε το verified auth token ώστε ο server να ταυτοποιεί τον ασθενή
        // από αυτό (όχι από spoofable prof_id/phone). Δες memory: customer-ai-data-security.
        headers: { "Content-Type": "application/json", "X-Auth-Token": authToken || "" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: systemPrompt,
          messages: apiMessages,
          // v1.3.57 — Εικόνα προς αξιολόγηση (ξεχωριστά από τα messages → ΔΕΝ αποθηκεύεται base64/PII).
          image: sentImage ? sentImage.url : undefined,
          // v1.3.38 — Identify the patient so the backend can persist this AI
          // exchange in wp_em_ai_messages and the audiologist dashboard can
          // read the full transcript. Anonymous chats (no creds) are still
          // proxied to Claude but not persisted.
          prof_id: profId || "",
          phone:   ph2    || "",
        }),
      });

      const rawText = await response.text();
      console.log('[AI] Status:', response.status, 'Raw:', rawText.substring(0, 500));

      let data;
      try { data = JSON.parse(rawText); } catch(e) {
        setAiMessages(prev => [...prev, { role: "assistant", content: "Σφάλμα: Μη έγκυρη απάντηση (" + response.status + "): " + rawText.substring(0, 200) }]);
        return;
      }

      if (data.error) {
        setAiMessages(prev => [...prev, { role: "assistant", content: "Σφάλμα API: " + (data.error.message || data.error) + (data.debug ? " [" + data.debug + "]" : "") }]);
        return;
      }

      const aiText = data.content?.map(c => c.text || '').join('') || 'Δεν μπόρεσα να απαντήσω. Δοκιμάστε ξανά.';
      setAiMessages(prev => [...prev, { role: "assistant", content: aiText }]);
    } catch (err) {
      console.error('[AI] Error:', err);
      setAiMessages(prev => [...prev, {
        role: "assistant",
        content: "Σφάλμα σύνδεσης: " + err.message
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  // Direct send for quick suggestion buttons
  const sendAiMessageDirect = async (msg) => {
    if (!msg || aiLoading) return;
    const userMsg = { role: "user", content: msg };
    setAiMessages([userMsg]);
    setAiInput("");
    setAiLoading(true);

    const deviceInfo = dv ? `${dv.b || ''} ${dv.m || ''} (${dv.t || 'άγνωστος τύπος'})` : 'Δεν έχει οριστεί συσκευή';
    const powerInfo = dv?.p === 'battery' ? `Μπαταρίες${batteryProfile ? ` Νο${batteryProfile.batteryType}` : ''}` : dv?.p === 'rechargeable' ? 'Επαναφορτιζόμενο' : 'Δεν έχει οριστεί';
    const trialInfo = isSupport ? "Πελάτης υποστήριξης (χρήστης ακουστικών άλλης εταιρίας)." : isTrial ? `Ημέρα ${trialDay} από ${trialDays} δοκιμαστική περίοδο. Απομένουν ${trialDaysLeft} ημέρες.` : `Αγορά — ${ds + 1} ημέρες χρήσης.`;

    // Fetch knowledge base (cached)
    let knowledgeText2 = '';
    try {
      const cacheKey = `earmedical_kb_${clinic.i || 'all'}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const knowledge = JSON.parse(cached);
        if (knowledge && knowledge.length > 0) {
          knowledgeText2 = '\n\nΒΑΣΗ ΓΝΩΣΗΣ:\n';
          knowledge.forEach(k => {
            knowledgeText2 += `\n● ${k.title}\n${k.content}\n`;
          });
        }
      }
    } catch(e) {}

    const systemPrompt = `Είσαι ο ψηφιακός βοηθός της Earmedical, μιας ελληνικής αλυσίδας κέντρων αποκατάστασης ακοής.
Μιλάς πάντα στα ελληνικά, με φιλικό, απλό και καθησυχαστικό τρόπο. Απαντάς σύντομα και κατανοητά, χωρίς τεχνικούς όρους.

Στοιχεία χρήστη:
- Όνομα: ${nm || 'Δεν γνωρίζω'}
- Ακουστικό: ${deviceInfo}
- Τροφοδοσία: ${powerInfo}
- Κατάσταση: ${trialInfo}
- Κέντρο: ${clinic.c || 'Δεν γνωρίζω'}
- Ακοοπροθετιστής: ${audiologist || 'Δεν έχει ανατεθεί'}

Κανόνες:
1. Σύντομες απαντήσεις (2–4 προτάσεις)
2. Πρακτική λύση βήμα-βήμα
3. Απλή γλώσσα

ΠΟΤΕ ΝΑ ΠΡΟΤΕΙΝΕΙΣ ΑΝΘΡΩΠΙΝΗ ΒΟΗΘΕΙΑ (κρίσιμο):

1. Ιατρικές επείγουσες (πόνος, αιμορραγία, ζάλη, λοίμωξη, ξαφνική απώλεια) → "Καλέστε άμεσα το κέντρο"
2. Σπασμένο/έπεσε/δεν ανοίγει → "Αυτό χρειάζεται τεχνικό έλεγχο"
3. Συναισθηματική δυσφορία ("δεν με βοηθάει", "απογοητευμένος") → Αναγνώρισε & πρότεινε επικοινωνία
4. Όταν δεν λύνεται από απόσταση → "Θέλετε να κλείσω ραντεβού ή να στείλετε μήνυμα στον ${audiologist || 'ακοοπροθετιστή σας'};"
5. Ερωτήσεις για κέντρο/ωράριο → Δώσε info + προτρέψε επικοινωνία

Όταν αναφέρεις "επικοινωνία με κέντρο", "ραντεβού", "ρωτήστε τον ειδικό" — εμφανίζονται αυτόματα κουμπιά. Πρότεινέ το ελεύθερα.

Στόχος: Σιγουριά, ποιοτική εξυπηρέτηση.${knowledgeText2}`;

    try {
      const response = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/ai-chat", {
        method: "POST",
        // SECURITY: στείλε το verified auth token ώστε ο server να ταυτοποιεί τον ασθενή
        // από αυτό (όχι από spoofable prof_id/phone). Δες memory: customer-ai-data-security.
        headers: { "Content-Type": "application/json", "X-Auth-Token": authToken || "" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1000,
          system: systemPrompt,
          messages: [userMsg],
          // v1.3.38 — Persist this AI exchange against the logged-in patient.
          prof_id: profId || "",
          phone:   ph2    || "",
        }),
      });
      const rawText = await response.text();
      console.log('[AI-Direct] Status:', response.status, 'Raw:', rawText.substring(0, 500));
      let data;
      try { data = JSON.parse(rawText); } catch(e) {
        setAiMessages([userMsg, { role: "assistant", content: "Σφάλμα: " + rawText.substring(0, 200) }]);
        return;
      }
      if (data.error) {
        setAiMessages([userMsg, { role: "assistant", content: "Σφάλμα: " + (data.error.message || data.error) + (data.debug ? " [" + data.debug + "]" : "") }]);
        return;
      }
      const aiText = data.content?.map(c => c.text || '').join('') || 'Δεν μπόρεσα να απαντήσω.';
      setAiMessages([userMsg, { role: "assistant", content: aiText }]);
    } catch (err) {
      setAiMessages([userMsg, { role: "assistant", content: "Δεν ήταν δυνατή η σύνδεση. Δοκιμάστε ξανά." }]);
    } finally {
      setAiLoading(false);
    }
  };

  // v1.3.38 — Body class for native vs PWA so CSS can target headers
  // differently. Header top padding is reduced in PWA (Safari iOS / Chrome
  // PWA) where env(safe-area-inset-top) is already authoritative, while
  // Android Capacitor keeps the 32px minimum + 14px buffer it needs for
  // the system status bar. See `--em-header-pad-top` in index.css.
  //
  // v1.3.39 — Επίσης iOS standalone PWA detection για το γνωστό bug όπου
  // η env(safe-area-inset-bottom) επιστρέφει 0 (home indicator overlay).
  // Όταν είναι iOS + standalone, βάζουμε class `em-pwa-ios` που στο CSS
  // εγγυάται minimum 34px floor για bottom padding.
  useEffect(() => {
    const win = typeof window !== 'undefined' ? window : null;
    if (!win) return;
    const isNative = !!(win.Capacitor && win.Capacitor.isNativePlatform && win.Capacitor.isNativePlatform());
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '')
                  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPadOS 13+
    const isStandalone = (win.matchMedia && win.matchMedia('(display-mode: standalone)').matches)
                         || win.navigator.standalone === true;
    if (isNative) document.body.classList.add('em-native');
    else          document.body.classList.remove('em-native');
    // v1.4.0 — Native iOS Capacitor: το --em-safe-bottom (Android MainActivity)
    // ΔΕΝ υπάρχει στο iOS, και το env(safe-area-inset-bottom) στο WKWebView είναι
    // αναξιόπιστο (επιστρέφει 0 σε first paint, αλλάζει στο scroll) → το bottom nav
    // κόβεται κάτω από το home indicator. Class `em-native-ios` = hard floor 34px
    // στο CSS (ίδιο pattern με το em-pwa-ios), εγγυημένο ανεξάρτητα από το env().
    if (isNative && isIOS) document.body.classList.add('em-native-ios');
    else                   document.body.classList.remove('em-native-ios');
    if (isIOS && isStandalone && !isNative) document.body.classList.add('em-pwa-ios');
    else                                    document.body.classList.remove('em-pwa-ios');
  }, []);

  // ─── Service Worker Registration (required for PWA install) ───
  useEffect(() => {
    // Skip στο Capacitor native — service workers conflict με το WebView του native app
    const isNative = typeof window !== 'undefined' && window.Capacitor &&
      window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    if (isNative) return;
    if ('serviceWorker' in navigator) {
      // Register after page load to not block rendering
      const register = () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(reg => console.log('[PWA] Service Worker registered:', reg.scope))
          .catch(err => console.error('[PWA] Service Worker registration failed:', err));
      };
      if (document.readyState === 'complete') {
        register();
      } else {
        window.addEventListener('load', register);
      }
    }
  }, []);

  // ─── PWA: "Εγκατάσταση" handler ────────────────────────────
  useEffect(() => {
    // Capacitor native app — already installed, no need for install prompt
    const isCapacitor = typeof window !== 'undefined' && window.Capacitor && 
      window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    if (isCapacitor) {
      setIsAppInstalled(true);
      return;
    }
    // Ελέγχουμε αν είναι ήδη εγκατεστημένο ως PWA
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      setIsAppInstalled(true);
      return;
    }
    if (window.navigator.standalone === true) {
      // iOS Safari standalone
      setIsAppInstalled(true);
      return;
    }

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    const handleInstalled = () => {
      setIsAppInstalled(true);
      setDeferredInstallPrompt(null);
    };
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const isIos = () => {
    if (typeof window === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
  };

  const isIosSafari = () => {
    if (!isIos()) return false;
    const ua = window.navigator.userAgent;
    // Chrome on iOS has "CriOS", Firefox has "FxiOS", Edge has "EdgiOS"
    return !(/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua));
  };

  const handleInstallApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsAppInstalled(true);
      }
      setDeferredInstallPrompt(null);
      return;
    }

    if (isIos()) {
      if (isIosSafari()) {
        // Safari — δείξε οδηγίες
        setShowIosInstall(true);
      } else {
        // Chrome/Firefox on iOS — πρέπει να ανοίξει Safari
        alert("Η εγκατάσταση στο iPhone γίνεται μόνο μέσω Safari.\n\n1. Αντιγράψτε τη διεύθυνση: app.earmedical.gr\n2. Ανοίξτε τον Safari\n3. Επικολλήστε τη διεύθυνση\n4. Πατήστε Share → Add to Home Screen");
      }
      return;
    }

    // Android / Chrome Desktop — detect browser for specific instructions
    const ua = navigator.userAgent;
    const isAndroid = /Android/i.test(ua);
    const isSamsung = /SamsungBrowser/i.test(ua);
    const isFirefox = /Firefox/i.test(ua);

    if (isAndroid) {
      if (isSamsung) {
        alert("Για εγκατάσταση με Samsung Internet:\n\n1. Πατήστε το μενού (3 γραμμές κάτω δεξιά)\n2. Επιλέξτε \"Add page to\"\n3. Πατήστε \"Home screen\"");
      } else if (isFirefox) {
        alert("Για εγκατάσταση με Firefox:\n\n1. Πατήστε το μενού (3 κουκκίδες πάνω δεξιά)\n2. Επιλέξτε \"Install\"");
      } else {
        alert("Για εγκατάσταση:\n\n1. Πατήστε το μενού του Chrome (3 κουκκίδες πάνω δεξιά)\n2. Επιλέξτε \"Εγκατάσταση εφαρμογής\" ή \"Add to Home screen\"\n\nΑν δεν εμφανίζεται, κλείστε και ξανανοίξτε τη σελίδα.");
      }
    } else {
      alert("Η εφαρμογή μπορεί να εγκατασταθεί από το μενού του browser σας.");
    }
  };

  // ─── Φόρτωση πελάτη από WordPress ───
  const loadPatientFromWp = async (phone, email) => {
    try {
      const param = phone ? `phone=${encodeURIComponent(phone)}` : `email=${encodeURIComponent(email)}`;
      const res  = await fetch(`${API_URL}/patients?${param}`);
      const data = await res.json();

      if (data.found) {
        if (data.patient_name)  setNm(data.patient_name);
        if (data.patient_phone) setPh2(data.patient_phone);
        if (data.patient_email) setEm(data.patient_email);
        if (data.birthday)      setBirthday(data.birthday);
        if (data.prof_id)       setProfId(data.prof_id);
        if (data.fitting_date)  setSd(data.fitting_date);
        if (data.clinic)        setClinicId(data.clinic);
        if (data.power_source || data.device_brand || data.device_model || data.device_type) {
          setDv(prev => ({
            ...prev,
            p: data.power_source || prev.p,
            b: data.device_brand  || prev.b,
            m: data.device_model  || prev.m,
            t: data.device_type   || prev.t,
          }));
        }
        if (data.serial_left)    setSL(data.serial_left);
        if (data.serial_right)   setSR(data.serial_right);
        if (data.serial_charger) setSC(data.serial_charger);
        if (data.patient_status) setPatientStatus(data.patient_status);
        if (data.trial_days) setTrialDays(data.trial_days);
        if (data.trial_start) setTrialStart(data.trial_start);
        if (data.audiologist) setAudiologist(data.audiologist);

        // Φόρτωση loyalty stamps
        if (data.prof_id) {
          try {
            const lr = await fetch(`${API_URL}/loyalty/${data.prof_id}`);
            const lj = await lr.json();
            if (lj.found) setLoyaltyStamps(lj.stamps || 0);
          } catch(e) {}
        }

        setSp(false);
        return true;
      }
    } catch (err) {
      console.error('loadPatientFromWp error:', err);
    }
    return false;
  };

  const bookScheduledAppointments = async (patientNm, patientPh, patientEm, patientProfId, fittingDate, patientClinicId, patientPowerSource, patientBirthday, patientWarrantyYears) => {
    // Skip για support πελάτες (δεν χρειάζονται auto follow-ups)
    if (patientStatus === 'support') {
      console.log('[Support Mode] Skipping auto-schedule appointments');
      return;
    }

    const flagKey = 'earmedical_scheduled_v1';
    if (localStorage.getItem(flagKey)) return;

    // Σέταρε flag ΑΜΕΣΩΣ για να αποφύγουμε duplicate κλήσεις
    localStorage.setItem(flagKey, '1');

    try {
      // Έλεγχος αν υπάρχουν ΗΔΗ μελλοντικά purchased ραντεβού (2η/3η Ρύθμιση κλπ)
      // ΔΕΝ μπλοκάρουμε αν υπάρχουν μόνο trial_end ή παλιά ραντεβού
      const checkRes  = await fetch(`${API_URL}/appointments?phone=${encodeURIComponent(patientPh)}`);
      const checkData = await checkRes.json();
      const today = new Date().toISOString().substring(0, 10);
      const futurePurchasedAppts = Array.isArray(checkData)
        ? checkData.filter(a => {
            const isTrialEnd = (a.reason === 'trial_end');
            const isFuture = a.appointment_date && a.appointment_date >= today;
            return !isTrialEnd && isFuture;
          })
        : [];
      if (futurePurchasedAppts.length > 0) {
        console.log('Ραντεβού αγοράς υπάρχουν ήδη — skip bookScheduledAppointments');
        return;
      }

      const patientClinic = getClinicById(patientClinicId);
      const appointments  = mkA(fittingDate, patientNm, patientEm, patientPh, patientClinic, patientProfId, patientWarrantyYears);
      const upcoming      = appointments.filter(a => !a.past);

      // Helper: βρες την πρώτη ανοιχτή μέρα του κέντρου ΜΠΡΟΣΤΑ (≥ startDate)
      // Επιστρέφει { date: "YYYY-MM-DD", time: "HH:MM" } ή null
      const findNextOpenSlot = (startDate) => {
        for (let i = 0; i < 14; i++) {
          const d = new Date(startDate);
          d.setDate(d.getDate() + i);
          const dateStr = `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
          const firstOpen = getFirstOpeningTime(patientClinic, dateStr);
          if (firstOpen) return { date: dateStr, time: firstOpen };
        }
        return null;
      };

      for (const appt of upcoming) {
        const rawDate = appt.dt instanceof Date ? appt.dt : new Date(appt.dt);

        // Βρες την πρώτη ανοιχτή μέρα ΜΠΡΟΣΤΑ (ή την ίδια αν είναι ανοιχτή)
        const slot = findNextOpenSlot(rawDate);
        if (!slot) {
          console.warn('No open slot found for:', appt.l);
          continue;
        }

        const payload = {
          patient_name:     patientNm     || 'Πελάτης',
          patient_phone:    patientPh     || '',
          patient_email:    patientEm     || '',
          patient_prof_id:  patientProfId || '',
          prof_id:          patientProfId || '',
          number_prof:      patientProfId || '',
          birthday:         patientBirthday || '',
          appointment_date: slot.date,
          appointment_time: slot.time,
          clinic:           patientClinicId,
          reason:           appt.l,
          reason_label:     appt.l,
          power_source:     patientPowerSource || '',
          status:           'pending',
        };

        try {
          await fetch(API_URL + '/appointments', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify(payload),
          });
        } catch (err) {
          console.error('Scheduled appt failed:', appt.l, err);
        }
      }
    } catch (err) {
      console.error('bookScheduledAppointments error:', err);
    }
  };

  // ─── Δημιουργία ραντεβού λήξης δοκιμαστικής περιόδου ──────────
  const bookTrialEndAppointment = async (patientNm, patientPh, patientEm, patientProfId, patientClinicId, trialStartStr, trialDaysCount, patientBirthday) => {
    const flagKey = `earmedical_trial_end_${patientProfId || patientPh}`;
    if (localStorage.getItem(flagKey)) return;

    // Set flag IMMEDIATELY to prevent duplicate calls
    localStorage.setItem(flagKey, '1');

    // Double-check server: if trial_end appointment already exists for this patient, skip
    try {
      const checkRes = await fetch(`${API_URL}/appointments?phone=${encodeURIComponent(patientPh)}`);
      const checkData = await checkRes.json();
      const today = new Date().toISOString().substring(0, 10);
      const hasActiveTrialEnd = Array.isArray(checkData) && checkData.some(a =>
        a.reason === 'trial_end' && a.appointment_date >= today && a.status !== 'cancelled'
      );
      if (hasActiveTrialEnd) {
        console.log('Trial-end appointment already exists on server — skip');
        return;
      }
    } catch(e) { console.warn('Could not verify existing appts:', e); }

    const patientClinic = getClinicById(patientClinicId);
    const endAppt = computeTrialEndAppointment(patientClinic, trialStartStr, trialDaysCount);
    if (!endAppt) {
      console.warn('bookTrialEndAppointment: could not compute end date');
      return;
    }

    const reasonLabel = "Επιστροφή ακουστικών & αξιολόγηση";

    const payload = {
      patient_name:     patientNm     || 'Πελάτης',
      patient_phone:    patientPh     || '',
      patient_email:    patientEm     || '',
      patient_prof_id:  patientProfId || '',
      prof_id:          patientProfId || '',
      number_prof:      patientProfId || '',
      birthday:         patientBirthday || '',
      appointment_date: endAppt.date,
      appointment_time: endAppt.time,
      clinic:           patientClinicId,
      reason:           "trial_end",
      reason_label:     reasonLabel,
      status:           'pending',
    };

    try {
      const res = await fetch(API_URL + '/appointments', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();
      console.log('Trial-end appt created:', data);
    } catch (err) {
      console.error('bookTrialEndAppointment error:', err);
    }
  };

  const bookAppointment = async () => {
    if (!newAReason || !newADate) return;
    if (toDateOnlyTime(newADate) < toDateOnlyTime(todayDateStr)) {
      setBooking("error_past_date");
      return;
    }
    if (!newATime || !availableTimeSlots.includes(newATime)) return;
    setBooking("loading");
    try {
      const res = await fetch(API_URL + "/appointments", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_name:     nm || "Πελάτης",
          patient_phone:    ph2,
          patient_email:    em,
          patient_prof_id:  profId,
          prof_id:          profId,
          birthday:         birthday,
          appointment_date: newADate,
          appointment_time: newATime,
          clinic:           clinicId,
          power_source:     dv.p,
          battery_type:     batteryProfile?.batteryType || "",
          device_count:     batteryProfile?.deviceCount || ([sL, sR].filter(Boolean).length || 1),
          reason:           newAReason,
          reason_label:     newAReason,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setBooking("success");
      } else {
        setBooking("error");
      }
    } catch (e) {
      setBooking("error");
    }
  };
  const fetchAppointments = async () => {
    if (!ph2 && !em) return;

    const cacheKey = `earmedical_appts_${ph2 || em}`;

    // 1. Load from cache first (instant)
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed.data)) {
          setWpAppts(parsed.data.map((a) => normalizeWpAppointment(a)));
        }
      }
    } catch(e) {}

    setLoadingAppts(true);
    try {
      const params = ph2 ? `phone=${encodeURIComponent(ph2)}` : `email=${encodeURIComponent(em)}`;
      const res = await fetch(`${API_URL}/appointments?${params}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        // 2. Update state
        setWpAppts(
          data
            .map((a) => normalizeWpAppointment(a))
            .sort((a, b) => {
              if (!a.dt && !b.dt) return 0;
              if (!a.dt) return 1;
              if (!b.dt) return -1;
              return a.dt - b.dt;
            }),
        );
        // 3. Save to cache
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
        } catch(e) {}
      }
    } catch (e) {}
    setLoadingAppts(false);
  };
  useEffect(() => {
    if (!sp && (ph2 || em)) fetchAppointments();
  }, [sp, ph2, em]);
  useEffect(() => {
    if (tab === "cal" && !sp) fetchAppointments();
  }, [tab, sp]);
  useEffect(() => {
    if (tab === "shop" && profId) {
      fetch(`${API_URL}/loyalty/${profId}`)
        .then(r => r.json())
        .then(j => { if (j.found) setLoyaltyStamps(j.stamps || 0); })
        .catch(() => {});
    }
  }, [tab, profId]);
  useEffect(() => {
    if (booking === "success") fetchAppointments();
  }, [booking]);
  useEffect(() => {
    if (!wpAppts.length) return;
    const currentMap = Object.fromEntries(wpAppts.map((a) => [String(a.id || `${a.appointment_date}-${a.appointment_time}-${a.reason}`), a.status]));
    const prevMap = prevWpStatusesRef.current;
    const changed = wpAppts.find((a) => {
      const key = String(a.id || `${a.appointment_date}-${a.appointment_time}-${a.reason}`);
      return prevMap[key] && prevMap[key] !== a.status;
    });
    prevWpStatusesRef.current = currentMap;
    if (changed && tab !== "cal") {
      const meta = getStatusMeta(changed.status);
      window.setTimeout(() => {
        window.alert(`Ενημέρωση ραντεβού: ${meta.label}`);
      }, 300);
    }
  }, [wpAppts]);
  /* ═══════════════════════════════════════════════════════════════
   *  AUTH SCREENS — Phone / OTP / Set PIN / Enter PIN
   * ═══════════════════════════════════════════════════════════════ */
  // ─── Booting splash screen ───
  // Shows ONLY the logo while we check stored auth token (1-3 sec on cold start).
  // This prevents the "flash of login screen" before the user is recognized.
  if (authStep === "booting") {
    return (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        background: BG,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui",
        zIndex: 9999,
      }}>
        <div style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          animation: "earIntroIn 0.5s ease-out both",
        }}>
          {/* Sound-wave equalizer — the creative */}
          <EarPulse size={132} color={O} bars={7} />
          {/* Real brand wordmark */}
          <div style={{ marginTop: 34 }}><EarLogo h={50} /></div>
        </div>
        {/* Brand watermark */}
        <p style={{ position: "absolute", bottom: "max(env(safe-area-inset-bottom), 22px)", left: 0, right: 0, textAlign: "center", margin: 0, color: "rgba(0,0,0,0.22)", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3em" }}>Earmedical</p>
        <style>{`
          @keyframes earEq { 0%,100% { transform: scaleY(.32) } 50% { transform: scaleY(1) } }
          @keyframes earIntroIn { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
        `}</style>
      </div>
    );
  }

  if (authStep !== "logged-in" && authStep !== undefined) {
    const screenWrap = (content) => (
      <div style={{
        minHeight: "100dvh",
        background: BG,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 20px",
        fontFamily: "system-ui",
      }}>
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <EarLogo h={60} />
        </div>
        <div style={{
          width: "100%",
          maxWidth: 380,
          background: WH,
          borderRadius: 20,
          padding: 28,
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
        }}>
          {content}
        </div>
        <p style={{
          margin: "24px 0 0",
          fontSize: 11,
          color: MG,
          textAlign: "center",
          letterSpacing: 0.5,
          opacity: 0.7,
        }}>
          Earmedical App v{process.env.REACT_APP_VERSION || '?'}
        </p>
      </div>
    );

    const inputStyle = {
      width: "100%",
      padding: "14px 16px",
      borderRadius: 12,
      border: "1.5px solid #e0e0e0",
      fontSize: 16,
      outline: "none",
      fontFamily: "inherit",
      boxSizing: "border-box",
      marginBottom: 12,
    };

    const buttonStyle = (disabled) => ({
      width: "100%",
      padding: 16,
      borderRadius: 12,
      background: disabled ? "#e0e0e0" : O,
      color: disabled ? "#999" : WH,
      border: "none",
      fontSize: 15,
      fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: "inherit",
      boxShadow: disabled ? "none" : "0 2px 8px rgba(239,128,26,0.25)",
      transition: "all 0.15s ease",
    });

    const errorBox = authError ? (
      <div style={{
        background: "#FFEBEE",
        color: "#C62828",
        padding: "10px 14px",
        borderRadius: 10,
        fontSize: 13,
        marginBottom: 12,
        fontWeight: 600,
      }}>{authError}</div>
    ) : null;

    // ─── Οθόνη 1: Εισαγωγή κινητού ───
    if (authStep === "phone") {
      return screenWrap(
        <>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: DK, textAlign: "center", marginBottom: 6 }}>
            Καλωσήρθατε
          </h2>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: MG, textAlign: "center", lineHeight: 1.5 }}>
            Εισάγετε τον αριθμό του κινητού σας
          </p>
          {errorBox}
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Κινητό τηλέφωνο
          </label>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="69XXXXXXXX"
            value={authPhone}
            onChange={(e) => setAuthPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            style={inputStyle}
            onKeyDown={(e) => e.key === "Enter" && authPhone.length === 10 && (setAuthStep("enter-pin"), setAuthError(""), setAuthPin(""))}
            autoFocus
          />

          {/* v1.3.43 — Friendlier labels for first-time customers.
              PRIMARY = "Σύνδεση" (PIN). On no_pin response, the handler
              auto-routes to OTP so the user never sees an error.
              SECONDARY = explicit "Πρώτη φορά" hint for new patients. */}
          {/* PRIMARY: Σύνδεση με PIN */}
          <button
            onClick={() => {
              setAuthStep("enter-pin");
              setAuthError("");
              setAuthPin("");
            }}
            disabled={authPhone.length !== 10}
            style={buttonStyle(authPhone.length !== 10)}
          >
            Σύνδεση
          </button>

          {/* SECONDARY: SMS κωδικός για πρώτη σύνδεση ή ανάκτηση */}
          <button
            onClick={authRequestOtp}
            disabled={authLoading || authPhone.length !== 10}
            style={{
              width: "100%",
              marginTop: 12,
              padding: 14,
              borderRadius: 12,
              background: "transparent",
              color: authPhone.length === 10 ? O : MG,
              border: `1.5px solid ${authPhone.length === 10 ? O : "#e0e0e0"}`,
              fontSize: 14,
              fontWeight: 600,
              cursor: authPhone.length === 10 ? "pointer" : "not-allowed",
              fontFamily: "inherit",
            }}
          >
            {authLoading ? "Αποστολή SMS..." : "Πρώτη φορά; SMS κωδικός"}
          </button>

          {/* v1.3.33 — Self-signup CTA για ενδιαφερόμενους που δεν είναι ακόμα πελάτες */}
          <div style={{ margin: "24px 0 8px", textAlign: "center", fontSize: 12, color: MG }}>
            ──── ή ────
          </div>
          <button
            onClick={() => {
              setAuthError("");
              setSignupName("");
              setSignupEmail("");
              setSignupClinic("athens");
              setAuthStep("prospect-signup");
            }}
            style={{
              width: "100%",
              marginTop: 8,
              padding: 14,
              borderRadius: 12,
              background: WH,
              color: DK,
              border: `1.5px dashed ${MG}`,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Δεν είμαι πελάτης ακόμα — Εγγραφή ως Ενδιαφερόμενος
          </button>
        </>
      );
    }

    // ─── Οθόνη: Prospect signup (v1.3.33) ───────────────────────
    if (authStep === "prospect-signup") {
      const clinics = [
        { id: "athens",   label: "Αθήνα" },
        { id: "larisa",   label: "Λάρισα" },
        { id: "katerini", label: "Κατερίνη" },
        { id: "farsala",  label: "Φάρσαλα" },
      ];
      return screenWrap(
        <>
          <button
            onClick={() => { setAuthStep("phone"); setAuthError(""); }}
            style={{
              background: "none", border: "none", color: MG, fontSize: 13,
              padding: 0, marginBottom: 12, cursor: "pointer", fontFamily: "inherit",
            }}
          >← Πίσω</button>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: DK, textAlign: "center", marginBottom: 6 }}>
            Νέα Εγγραφή
          </h2>
          <p style={{ margin: "0 0 24px", fontSize: 13, color: MG, textAlign: "center", lineHeight: 1.5 }}>
            Συμπληρώστε τα στοιχεία σας για να εγγραφείτε ως <b style={{ color: O }}>Ενδιαφερόμενος</b>
            <br />και να κλείσετε δωρεάν ραντεβού.
          </p>
          {errorBox}

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Ονοματεπώνυμο
          </label>
          <input
            type="text"
            placeholder="π.χ. Γιώργος Παπαδόπουλος"
            value={signupName}
            onChange={(e) => setSignupName(e.target.value)}
            style={{ ...inputStyle, marginBottom: 14 }}
            autoFocus
            autoComplete="name"
          />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Email
          </label>
          <input
            type="email"
            inputMode="email"
            placeholder="π.χ. name@example.com"
            value={signupEmail}
            onChange={(e) => setSignupEmail(e.target.value)}
            style={{ ...inputStyle, marginBottom: 14 }}
            autoComplete="email"
          />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Κινητό τηλέφωνο
          </label>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="69XXXXXXXX"
            value={authPhone}
            onChange={(e) => setAuthPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            style={{ ...inputStyle, marginBottom: 14 }}
            autoComplete="tel"
          />

          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Κέντρο Earmedical
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 18 }}>
            {clinics.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSignupClinic(c.id)}
                style={{
                  padding: "12px 8px",
                  borderRadius: 10,
                  background: signupClinic === c.id ? OL : WH,
                  border: `1.5px solid ${signupClinic === c.id ? O : "#e0e0e0"}`,
                  color: signupClinic === c.id ? O : DK,
                  fontSize: 13,
                  fontWeight: signupClinic === c.id ? 700 : 500,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          <button
            onClick={authProspectSignup}
            disabled={authLoading}
            style={buttonStyle(authLoading)}
          >
            {authLoading ? "Εγγραφή..." : "Εγγραφή"}
          </button>

          <p style={{ margin: "16px 0 0", fontSize: 11, color: MG, textAlign: "center", lineHeight: 1.5 }}>
            Με την εγγραφή αποδέχεστε την επικοινωνία από την Earmedical
            <br />για ενημερώσεις σχετικά με τις υπηρεσίες μας.
          </p>
        </>
      );
    }

    // ─── Οθόνη 2: OTP ───
    if (authStep === "otp") {
      return screenWrap(
        <>
          <button
            onClick={() => { setAuthStep("phone"); setAuthError(""); setAuthOtp(""); }}
            style={{
              background: "none", border: "none", color: MG, fontSize: 13,
              padding: 0, marginBottom: 12, cursor: "pointer", fontFamily: "inherit",
            }}
          >← Αλλαγή αριθμού</button>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: DK, textAlign: "center", marginBottom: 6 }}>
            Επαλήθευση
          </h2>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: MG, textAlign: "center", lineHeight: 1.5 }}>
            Στείλαμε 4ψήφιο κωδικό στο<br />
            <b style={{ color: DK }}>{authPhoneMasked || authPhone}</b>
          </p>
          {errorBox}
          <input
            type="tel"
            inputMode="numeric"
            placeholder="• • • •"
            value={authOtp}
            onChange={(e) => setAuthOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
            style={{
              ...inputStyle,
              textAlign: "center",
              fontSize: 28,
              letterSpacing: 12,
              fontWeight: 700,
            }}
            onKeyDown={(e) => e.key === "Enter" && authVerifyOtp()}
            autoFocus
            maxLength={4}
          />
          <button
            onClick={authVerifyOtp}
            disabled={authLoading || authOtp.length !== 4}
            style={buttonStyle(authLoading || authOtp.length !== 4)}
          >
            {authLoading ? "Επαλήθευση..." : "Επαλήθευση"}
          </button>
          <p style={{ margin: "16px 0 0", fontSize: 13, color: MG, textAlign: "center" }}>
            Δεν λάβατε κωδικό;{" "}
            {authResendTimer > 0 ? (
              <span style={{ color: MG }}>Αποστολή ξανά σε {authResendTimer}"</span>
            ) : (
              <button
                onClick={authRequestOtp}
                style={{ background: "none", border: "none", color: O, fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0, fontFamily: "inherit" }}
              >Αποστολή ξανά</button>
            )}
          </p>
        </>
      );
    }

    // ─── Οθόνη 3: Set PIN ───
    if (authStep === "set-pin") {
      return screenWrap(
        <>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: DK, textAlign: "center", marginBottom: 6 }}>
            Δημιουργήστε PIN
          </h2>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: MG, textAlign: "center", lineHeight: 1.5 }}>
            Επιλέξτε 4ψήφιο PIN για γρήγορη σύνδεση την επόμενη φορά
          </p>
          {errorBox}
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Νέο PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            placeholder="• • • •"
            value={authPin}
            onChange={(e) => setAuthPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            style={{ ...inputStyle, textAlign: "center", fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
            maxLength={4}
            autoFocus
          />
          <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: MG, marginBottom: 6 }}>
            Επιβεβαίωση PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            placeholder="• • • •"
            value={authPinConfirm}
            onChange={(e) => setAuthPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
            style={{ ...inputStyle, textAlign: "center", fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
            maxLength={4}
            onKeyDown={(e) => e.key === "Enter" && authSetPin()}
          />
          <button
            onClick={authSetPin}
            disabled={authLoading || authPin.length !== 4 || authPinConfirm.length !== 4}
            style={buttonStyle(authLoading || authPin.length !== 4 || authPinConfirm.length !== 4)}
          >
            {authLoading ? "Αποθήκευση..." : "Αποθήκευση PIN"}
          </button>
          <button
            onClick={() => setAuthStep("logged-in")}
            style={{
              width: "100%", marginTop: 12, padding: 12,
              background: "transparent", border: "none", color: MG,
              fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            }}
          >Παράλειψη</button>
        </>
      );
    }

    // ─── Οθόνη 4: Enter PIN ───
    if (authStep === "enter-pin") {
      return screenWrap(
        <>
          <button
            onClick={() => { setAuthStep("phone"); setAuthError(""); setAuthPin(""); }}
            style={{
              background: "none", border: "none", color: MG, fontSize: 13,
              padding: 0, marginBottom: 12, cursor: "pointer", fontFamily: "inherit",
            }}
          >← Πίσω</button>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: DK, textAlign: "center", marginBottom: 6 }}>
            Εισάγετε κωδικό
          </h2>
          <p style={{ margin: "0 0 24px", fontSize: 14, color: MG, textAlign: "center", lineHeight: 1.5 }}>
            Τον κωδικό σας σας έδωσε ο ακοοπροθετιστής σας<br />
            <b style={{ color: DK }}>{authPhone}</b>
          </p>
          {errorBox}
          <input
            type="password"
            inputMode="numeric"
            placeholder="Εισάγετε τον κωδικό σας"
            value={authPin}
            onChange={(e) => setAuthPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
            // v1.3.30 — Conditional styling: placeholder text με κανονικό
            // spacing/size, εισαγμένα ψηφία με μεγάλο size + letter-spacing
            // ώστε να φαίνονται σαν OTP. Πριν: το letterSpacing 6 «έσκιζε»
            // και το placeholder text σε τεράστιες αποστάσεις γραμμάτων.
            style={{
              ...inputStyle,
              textAlign: "center",
              fontSize: authPin.length > 0 ? 22 : 15,
              letterSpacing: authPin.length > 0 ? 6 : 0,
              fontWeight: authPin.length > 0 ? 700 : 500,
            }}
            onKeyDown={(e) => e.key === "Enter" && authLoginWithPin()}
            autoFocus
            maxLength={10}
          />
          <button
            onClick={authLoginWithPin}
            disabled={authLoading || authPin.length < 4}
            style={buttonStyle(authLoading || authPin.length < 4)}
          >
            {authLoading ? "Σύνδεση..." : "Σύνδεση"}
          </button>
          <button
            onClick={() => { setAuthStep("phone"); setAuthError(""); setAuthPin(""); authRequestOtp(); }}
            style={{
              width: "100%", marginTop: 12, padding: 12,
              background: "transparent", border: "none", color: O,
              fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            }}
          >Ξέχασα τον κωδικό — Αποστολή SMS</button>
        </>
      );
    }
  }

  /* ONBOARDING */
  if (sp)
    return (
      <div
        style={{
          height: "100dvh",
          background: BG,
          display: "flex",
          flexDirection: "column",
          fontFamily: "system-ui",
        }}
      >
        {!isOnline && (
          <div style={{
            background: '#C62828',
            color: 'white',
            padding: '8px 16px',
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.3,
          }}>
            Δεν υπάρχει σύνδεση στο διαδίκτυο — εμφανίζονται αποθηκευμένα δεδομένα
          </div>
        )}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "24px 24px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ margin: "16px 0 24px" }}>
            <EarLogo h={90} />
          </div>

          {/* ─── Install PWA banner στην αρχική οθόνη ─── */}
          {!isAppInstalled && !installBannerDismissed && (
            <div
              onClick={handleInstallApp}
              style={{
                width: "100%",
                maxWidth: 380,
                margin: "0 0 16px",
                padding: "12px 14px",
                background: `linear-gradient(135deg, ${O}, #D06A0E)`,
                borderRadius: 14,
                display: "flex",
                alignItems: "center",
                gap: 12,
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(239,128,26,0.25)",
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: "rgba(255,255,255,0.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v13M5 9l7-7 7 7"/>
                  <path d="M3 15v4a2 2 0 002 2h14a2 2 0 002-2v-4"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: "0 0 1px", fontSize: 13, fontWeight: 800, color: WH }}>
                  Εγκατάσταση εφαρμογής
                </p>
                <p style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.9)", lineHeight: 1.3 }}>
                  Προσθέστε στην αρχική οθόνη για γρήγορη πρόσβαση
                </p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); dismissInstallBanner(); }}
                style={{
                  background: "rgba(255,255,255,0.2)", border: "none",
                  width: 28, height: 28, borderRadius: 8, padding: 0,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: WH, flexShrink: 0,
                }}
                aria-label="Close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
          )}

          <C s={{ maxWidth: 380, padding: 24, borderRadius: 20 }}>
            <p
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: O,
                letterSpacing: 1,
                margin: "0 0 4px",
              }}
            >
              EARMEDICAL
            </p>
            <p
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: DK,
                margin: "0 0 4px",
              }}
            >
              Καλωσήρθατε!
            </p>
            <p
              style={{
                fontSize: 13,
                color: MG,
                margin: "0 0 18px",
                lineHeight: 1.5,
              }}
            >
              Είμαστε δίπλα σας σε κάθε βήμα
            </p>
            {/* Κουμπί επαναφοράς στοιχείων */}
            <div style={{ background: OL, borderRadius: 12, padding: 12, marginBottom: 16, border: `1px solid ${O}20` }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: DK, fontWeight: 600 }}>
                Επιστρέφετε; Φορτώστε τα στοιχεία σας
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="tel"
                  id="restore_phone"
                  placeholder="Κινητό τηλέφωνο"
                  style={{
                    flex: 1, padding: "10px 12px", borderRadius: 10,
                    border: "1.5px solid #e0e0e0", fontSize: 13, outline: "none",
                  }}
                />
                <button
                  onClick={async () => {
                    const phone = document.getElementById('restore_phone').value;
                    if (!phone) return;
                    const found = await loadPatientFromWp(phone, '');
                    if (!found) alert('Δεν βρέθηκαν στοιχεία για αυτό το τηλέφωνο.');
                  }}
                  style={{
                    padding: "10px 16px", background: O, color: WH,
                    border: "none", borderRadius: 10, fontSize: 13,
                    fontWeight: 700, cursor: "pointer",
                  }}
                >
                  Φόρτωση
                </button>
              </div>
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Ονοματεπώνυμο
            </label>
            <input
              ref={nR}
              defaultValue={nm}
              onBlur={(e) => setNm(e.target.value)}
              placeholder="Όνομα Επώνυμο"
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Κινητό
            </label>
            <input
              ref={pR}
              type="tel"
              defaultValue={ph2}
              onBlur={(e) => setPh2(e.target.value)}
              placeholder="69xxxxxxxx"
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Email
            </label>
            <input
              ref={eR}
              type="email"
              defaultValue={em}
              onBlur={(e) => setEm(e.target.value)}
              placeholder="email@example.com"
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Κωδικός πελάτη Prof
            </label>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              step="1"
              defaultValue={profId}
              onBlur={(e) => setProfId((e.target.value || "").replace(/\D/g, ""))}
              placeholder="π.χ. 12345"
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Ημερομηνία γέννησης
            </label>
            <input
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                background: WH,
                color: birthday ? DK : MG,
                fontSize: 14,
                fontWeight: 600,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            {/* ─── Toggle Δοκιμή / Αγορά ─── */}
            <label style={{ fontSize: 13, fontWeight: 700, color: DK, display: "block", marginBottom: 8 }}>Κατάσταση</label>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              {[
                { id: "trial", label: "Δοκιμαστική περίοδος" },
                { id: "purchased", label: "Αγορά" },
              ].map((opt) => (
                <button key={opt.id} onClick={() => setPatientStatus(opt.id)} style={{
                  flex: 1, padding: "14px 0", borderRadius: 12,
                  border: "none",
                  background: patientStatus === opt.id ? O : LG,
                  color: patientStatus === opt.id ? WH : MG,
                  fontSize: 13, fontWeight: 800, cursor: "pointer",
                }}>{opt.label}</button>
              ))}
            </div>

            {patientStatus === "trial" && (
              <div style={{ background: OL, borderRadius: 12, padding: 14, marginBottom: 14, border: `1px solid ${O}20` }}>
                <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: O }}>ΔΙΑΡΚΕΙΑ ΔΟΚΙΜΗΣ</p>
                <div style={{ display: "flex", gap: 8 }}>
                  {[7, 14, 21, 28].map(d => (
                    <button key={d} onClick={() => setTrialDays(d)} style={{
                      flex: 1, padding: "10px 0", borderRadius: 10,
                      border: `2px solid ${trialDays === d ? O : "#e0e0e0"}`,
                      background: trialDays === d ? O : WH,
                      color: trialDays === d ? WH : MG,
                      fontSize: 14, fontWeight: 800, cursor: "pointer",
                    }}>{d}</button>
                  ))}
                </div>
                <p style={{ margin: "6px 0 0", fontSize: 11, color: MG, textAlign: "center" }}>ημέρες</p>
              </div>
            )}

            {patientStatus !== "trial" && (<>
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Ημ/νία εφαρμογής
            </label>
            <input
              type="date"
              value={sd}
              onChange={(e) => setSd(e.target.value)}
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                background: WH,
                color: DK,
                fontSize: 14,
                fontWeight: 600,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            </>)}
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Κέντρο Ακοής
            </label>
            <select
              value={clinicId}
              onChange={(e) => {
                const newClinic = e.target.value;
                setClinicId(newClinic);
                // Αν ο audiologist δεν υπάρχει στο νέο κέντρο, reset
                const validAudios = getAudiologistsForClinic(newClinic);
                if (audiologist && !validAudios.includes(audiologist)) {
                  setAudiologist("");
                }
              }}
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
                background: WH,
                color: DK,
                fontWeight: 500,
                WebkitAppearance: "none",
              }}
            >
              {CLINICS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.c}
                </option>
              ))}
            </select>
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Ακοοπροθετιστής
            </label>
            <select
              value={audiologist}
              onChange={(e) => setAudiologist(e.target.value)}
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: `1.5px solid ${audiologist ? "#e0e0e0" : O}`,
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
                background: WH,
                color: audiologist ? DK : MG,
                fontWeight: 500,
                WebkitAppearance: "none",
              }}
            >
              <option value="">— Επιλέξτε ακοοπροθετιστή —</option>
              {getAudiologistsForClinic(clinicId).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Εταιρία
            </label>
            <select
              value={dv.b}
              onChange={(e) => setDv((p) => ({ ...p, b: e.target.value }))}
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
                background: "white",
                WebkitAppearance: "none",
              }}
            >
              <option value="">Επιλέξτε...</option>
              {[
                "Coselgi",
                "Phonak",
                "Oticon",
                "Widex",
                "Starkey",
                "ReSound",
                "Unitron",
                "Philips",
                "Rexton",
                "Audio Service",
                "AM Hearing",
                "Bernafon",
                "Άλλη",
              ].map((b) => (
                <option key={b}>{b}</option>
              ))}
            </select>
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Μοντέλο
            </label>
            <input
              ref={mR}
              defaultValue={dv.m}
              onBlur={(e) => setDv((p) => ({ ...p, m: e.target.value }))}
              placeholder="π.χ. Mojo M7"
              style={{
                width: "100%",
                display: "block",
                boxSizing: "border-box",
                padding: 12,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 14,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Τύπος
            </label>
            <select
              value={dv.t}
              onChange={(e) => setDv((p) => ({ ...p, t: e.target.value }))}
              style={{
                width: "100%", padding: "11px 14px", borderRadius: 12,
                border: `1.5px solid ${O}`, background: WH, color: DK,
                fontSize: 14, fontWeight: 700, cursor: "pointer",
                margin: "4px 0 14px", outline: "none", appearance: "none",
              }}
            >
              {DEVICE_TYPES.map(d => (
                <option key={d.id} value={d.id}>{d.label}</option>
              ))}
            </select>
            <label style={{ fontSize: 12, fontWeight: 600, color: MG }}>
              Τροφοδοσία
            </label>
            <div
              style={{
                display: "flex",
                gap: 8,
                margin: "4px 0 14px",
                flexWrap: "wrap",
              }}
            >
              {[
                { id: "battery", label: "Μπαταρίες" },
                { id: "rechargeable", label: "Επαναφορτιζόμενα" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setDv((p) => ({ ...p, p: opt.id }))}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 20,
                    border: `1.5px solid ${dv.p === opt.id ? O : "#e0e0e0"}`,
                    background: dv.p === opt.id ? OL : WH,
                    color: dv.p === opt.id ? O : MG,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {batteryProfile && (
              <div style={{ background: OL, borderRadius: 12, padding: 12, marginBottom: 14, border: `1px solid ${O}20` }}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: O }}>ΤΥΠΟΣ ΜΠΑΤΑΡΙΑΣ</p>
                <p style={{ margin: "4px 0 0", fontSize: 13, fontWeight: 700, color: DK }}>
                  Νο {batteryProfile.batteryType} • περίπου {batteryProfile.daysPerBattery} ημέρες ανά μπαταρία
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 11, color: MG, lineHeight: 1.5 }}>
                  Υπολογισμός για {batteryProfile.deviceCount === 2 ? "2 ακουστικά" : "1 ακουστικό"}. Κάθε κουτάκι έχει 6 μπαταρίες και αρκεί περίπου για {batteryProfile.packDays} ημέρες.
                </p>
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: DK, display: "block", marginBottom: 8 }}>Εγγύηση</label>
              <div style={{ display: "flex", gap: 10 }}>
                {[2, 4].map(y => (
                  <button key={y} onClick={() => setWarrantyYears(y)} style={{
                    flex: 1, padding: "12px 0", borderRadius: 12, border: `2px solid ${warrantyYears === y ? O : "#e0e0e0"}`,
                    background: warrantyYears === y ? OL : WH, color: warrantyYears === y ? O : MG,
                    fontSize: 15, fontWeight: 800, cursor: "pointer"
                  }}>
                    {y} χρόνια
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: MG }}>
                  S/N Αριστερό
                </label>
                <input
                  ref={sLR}
                  defaultValue={sL}
                  onBlur={(e) => setSL(e.target.value)}
                  placeholder="Serial L"
                  style={{
                    width: "100%",
                    padding: 11,
                    borderRadius: 12,
                    border: "1.5px solid #e0e0e0",
                    fontSize: 13,
                    outline: "none",
                    marginTop: 4,
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: MG }}>
                  S/N Δεξί
                </label>
                <input
                  ref={sRR}
                  defaultValue={sR}
                  onBlur={(e) => setSR(e.target.value)}
                  placeholder="Serial R"
                  style={{
                    width: "100%",
                    padding: 11,
                    borderRadius: 12,
                    border: "1.5px solid #e0e0e0",
                    fontSize: 13,
                    outline: "none",
                    marginTop: 4,
                  }}
                />
              </div>
            </div>
            <label style={{ fontSize: 11, fontWeight: 600, color: MG }}>
              S/N Φορτιστή
            </label>
            <input
              ref={sCR}
              defaultValue={sC}
              onBlur={(e) => setSC(e.target.value)}
              placeholder="Serial φορτιστή"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: 11,
                borderRadius: 12,
                border: "1.5px solid #e0e0e0",
                fontSize: 13,
                outline: "none",
                marginTop: 4,
                marginBottom: 14,
              }}
            />
            <div
              style={{
                background: WH,
                borderRadius: 12,
                padding: 12,
                textAlign: "left",
                border: `1px solid ${O}20`,
                marginBottom: 14,
              }}
            >
              <p
                style={{
                  margin: "0 0 4px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: O,
                }}
              >
                ΚΕΝΤΡΟ ΕΞΥΠΗΡΕΤΗΣΗΣ
              </p>
              <p
                style={{ margin: 0, fontSize: 13, fontWeight: 700, color: DK }}
              >
                {clinic.c}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 11, color: MG }}>
                {clinic.a} • {clinic.f}
              </p>
              <p style={{ fontSize: 11, color: MG, margin: "6px 0 0", lineHeight: 1.5 }}>
                Ωράριο: {clinic.hoursText}
              </p>
            </div>
            {patientStatus !== "trial" ? (
            <div
              style={{
                background: OL,
                borderRadius: 12,
                padding: 12,
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0, fontSize: 12, color: O, fontWeight: 600 }}>
                Εγγύηση {warrantyYears} ετών έως: <b>{wF}</b>
              </p>
            </div>
            ) : (
            <div style={{ background: DK, borderRadius: 12, padding: 14, textAlign: "center" }}>
              <p style={{ margin: 0, fontSize: 13, color: WH, fontWeight: 600 }}>
                Δοκιμαστική περίοδος: <b>{trialDays} ημέρες</b>
              </p>
            </div>
            )}
          </C>
          {/* ─── Αποδοχή όρων ─── */}
          <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "12px 14px",
            background: WH,
            border: `1.5px solid ${termsAccepted ? O : "#e0e0e0"}`,
            borderRadius: 12,
            marginTop: 16,
            cursor: "pointer",
          }}
          onClick={() => setTermsAccepted(t => !t)}
          >
            <div style={{
              width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
              border: `2px solid ${termsAccepted ? O : "#bbb"}`,
              background: termsAccepted ? O : WH,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {termsAccepted && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: DK }}>
              Αποδέχομαι τους{" "}
              <a
                href="https://www.earmedical.gr/oroi-kai-proypotheseis/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: O, fontWeight: 700, textDecoration: "underline" }}
              >
                όρους χρήσης & την πολιτική προσωπικών δεδομένων
              </a>
            </div>
          </div>

          <button
            onClick={async () => {
              if (!termsAccepted) return;
              if (!audiologist) return;
              // Force sync refs → state (mobile fix)
              const finalNm  = nR.current  ? nR.current.value.trim()  : nm;
              const finalPh  = pR.current  ? pR.current.value.trim()  : ph2;
              const finalEm  = eR.current  ? eR.current.value.trim()  : em;
              const finalM   = mR.current  ? mR.current.value.trim()  : dv.m;
              const finalSL  = sLR.current ? sLR.current.value.trim() : sL;
              const finalSR  = sRR.current ? sRR.current.value.trim() : sR;
              const finalSC  = sCR.current ? sCR.current.value.trim() : sC;
              if (!clinicId) return;

              // Validate: at least phone or email required
              if (!finalPh && !finalEm) {
                alert("Παρακαλώ συμπληρώστε τηλέφωνο ή email.");
                return;
              }

              // Update state immediately
              setNm(finalNm);
              setPh2(finalPh);
              setEm(finalEm);
              setDv((p) => ({ ...p, m: finalM }));
              setSL(finalSL);
              setSR(finalSR);
              setSC(finalSC);

              // Αποθήκευση πελάτη στη βάση
              const trialStartValue = patientStatus === "trial" ? (trialStart || getTodayDateStr()) : "";
              if (patientStatus === "trial" && !trialStart) setTrialStart(trialStartValue);

              await savePatientToWp({
                patient_name:  finalNm,
                patient_phone: finalPh,
                patient_email: finalEm,
                birthday:      birthday,
                prof_id:       profId,
                fitting_date:  patientStatus === "trial" ? "" : sd,
                clinic:        clinicId,
                audiologist:   audiologist,
                power_source:  dv.p,
                device_brand:  dv.b,
                device_model:  finalM,
                device_type:   dv.t,
                serial_left:   finalSL,
                serial_right:  finalSR,
                serial_charger: finalSC,
                patient_status: patientStatus,
                trial_days:    patientStatus === "trial" ? trialDays : 0,
                trial_start:   trialStartValue,
                terms_accepted: 1,
                terms_accepted_at: new Date().toISOString(),
              });

              // Δημιουργία προτεινόμενων ραντεβού — μόνο αν αγόρασε (όχι trial)
              if (patientStatus !== "trial") {
                bookScheduledAppointments(
                  finalNm, finalPh, finalEm,
                  profId, sd, clinicId, dv.p, birthday, warrantyYears
                );
              } else {
                // Δοκιμαστική περίοδος: ραντεβού λήξης
                bookTrialEndAppointment(
                  finalNm, finalPh, finalEm,
                  profId, clinicId, trialStartValue, trialDays, birthday
                );
              }

              setSp(false);
            }}
            disabled={!termsAccepted || !audiologist}
            style={{
              marginTop: 12,
              marginBottom: 32,
              padding: "16px 0",
              background: (termsAccepted && audiologist) ? O : LG,
              color: (termsAccepted && audiologist) ? WH : MG,
              border: "none",
              borderRadius: 16,
              fontSize: 16,
              fontWeight: 700,
              cursor: (termsAccepted && audiologist) ? "pointer" : "not-allowed",
              width: "100%",
              maxWidth: 380,
            }}
          >
            Αποθήκευση & συνέχεια
          </button>
        </div>
      </div>
    );
  /* SOS */
  if (sub === "sos")
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        <SH2
          title="Άμεση Βοήθεια"
          onBack={() => {
            setSub(null);
            setSi(null);
          }}
        />
        <div
          ref={ref}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 20px 0", paddingBottom: "var(--em-sub-pad-bottom)",
            zoom: 1 + fs * 0.15,
          }}
        >
          {si === null ? (
            <>
              <div
                style={{
                  background: OL,
                  borderRadius: 14,
                  padding: 14,
                  marginBottom: 14,
                  border: `1px solid ${O}20`,
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: DK,
                    lineHeight: 1.6,
                  }}
                >
                  Μην ανησυχείτε — τα περισσότερα λύνονται εύκολα:
                </p>
              </div>
              {supportOptions.map((s, i) => (
                <C key={i} onClick={() => setSi(i)}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: LG, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon(DK, 20)}</div>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 14,
                        fontWeight: 700,
                        color: DK,
                      }}
                    >
                      {s.s}
                    </span>
                    {Ic.rt(MG, 16)}
                  </div>
                </C>
              ))}
              <C
                onClick={() => window.open(`tel:${clinic.t}`)}
                s={{
                  border: `1.5px solid ${DK}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    justifyContent: "center",
                  }}
                >
                  {Ic.ph(DK, 18)}
                  <span style={{ fontSize: 14, fontWeight: 700, color: DK }}>
                    Θέλω βοήθεια από ειδικό
                  </span>
                </div>
              </C>
            </>
          ) : (
            <>
              <button
                onClick={() => setSi(null)}
                style={{
                  background: "none",
                  border: "none",
                  color: O,
                  fontWeight: 600,
                  cursor: "pointer",
                  marginBottom: 10,
                  fontSize: 13,
                }}
              >
                ← Πίσω
              </button>
              <C s={{ border: `1.5px solid ${O}20` }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 14,
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: LG, display: "flex", alignItems: "center", justifyContent: "center" }}>{supportOptions[si].icon(O, 22)}</div>
                  <p
                    style={{
                      fontWeight: 700,
                      color: DK,
                      margin: 0,
                      fontSize: 16,
                    }}
                  >
                    {supportOptions[si].s}
                  </p>
                </div>
                <p style={{ fontSize: 12, color: MG, margin: "0 0 12px" }}>
                  Δοκιμάστε τα παρακάτω:
                </p>
                {supportOptions[si].t.map((t, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      marginBottom: 10,
                      padding: "10px 12px",
                      background: BG,
                      borderRadius: 12,
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: O,
                        color: WH,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      {i + 1}
                    </div>
                    <span style={{ color: DK, fontSize: 13, lineHeight: 1.5 }}>
                      {t}
                    </span>
                  </div>
                ))}
              </C>
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: DK,
                  margin: "14px 0 10px",
                }}
              >
                Δεν λύθηκε; Είμαστε εδώ:
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Btn onClick={() => setSub("chat")}>{Ic.chat("white", 16)} Μίλα με τον ειδικό</Btn>
                <Btn
                  onClick={() => {
                    setBooking(null);
                    setNewAReason("Νέο θέμα / Επείγον");
                    setNewADate(
                      new Date(Date.now() + 3 * 86400000)
                        .toISOString()
                        .split("T")[0],
                    );
                    setSub("newappt");
                  }}
                  s={{ background: DK }}
                >
                  {Ic.cal("white", 16)} Προγραμμάτισε ραντεβού
                </Btn>
                <Btn
                  onClick={() => window.open(`tel:${clinic.t}`)}
                  primary={false}
                >
                  {Ic.ph(O, 16)} {clinic.f}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    );
  /* PROGRAM */
  if (sub === "prog")
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        <SH2 title="Πρόγραμμα Αποκατάστασης" onBack={() => setSub(null)} />
        <div
          ref={ref}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "0 20px 0", paddingBottom: "var(--em-sub-pad-bottom)",
            zoom: 1 + fs * 0.15,
          }}
        >
          <C
            s={{
              border: `1.5px solid ${O}30`,
              marginBottom: 14,
            }}
          >
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 11,
                fontWeight: 600,
                color: MG,
              }}
            >
              ΕΒΔΟΜΑΔΑ {wk} • ΗΜΕΡΑ {ds + 1}
            </p>
            <p
              style={{
                margin: "0 0 10px",
                fontSize: 16,
                fontWeight: 800,
                color: DK,
              }}
            >
              {PS[ps].t}
            </p>
            <div
              style={{
                background: LG,
                borderRadius: 10,
                height: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min((ps / 3) * 100 + 25, 100)}%`,
                  height: "100%",
                  background: O,
                  borderRadius: 10,
                }}
              />
            </div>
          </C>
          <C s={{ border: `1.5px solid #e0e0e0` }}>
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: DK,
                lineHeight: 1.6,
                fontStyle: "italic",
              }}
            >
              "{PS[ps].m}"
            </p>
          </C>
          {PS.map((st, i) => (
            <C
              key={i}
              s={{
                opacity: i <= ps ? 1 : 0.5,
                borderLeft: `3px solid ${i <= ps ? O : LG}`,
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: i === ps ? O : MG,
                  margin: "0 0 2px",
                }}
              >
                {st.w}
                {i === ps ? " ← εδώ" : ""}
              </p>
              <p
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: DK,
                  margin: "0 0 4px",
                }}
              >
                {st.t}
              </p>
              <p
                style={{ fontSize: 12, color: MG, margin: 0, lineHeight: 1.5 }}
              >
                {st.d}
              </p>
            </C>
          ))}
          <div
            style={{
              background: OL,
              borderRadius: 14,
              padding: 14,
              marginTop: 6,
              border: `1px solid ${O}20`,
              textAlign: "center",
            }}
          >
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 12,
                color: DK,
                fontWeight: 600,
              }}
            >
              Χρήσιμες οδηγίες
            </p>
            <p style={{ margin: 0, fontSize: 11, color: MG, lineHeight: 1.6 }}>
              Στόχος είναι να τα φοράτε το μεγαλύτερο μέρος της
              ημέρας με άνεση και συνέπεια. Αν στην αρχή νιώσετε κόπωση,
              κάντε μικρά διαλείμματα και ξαναφορέστε τα. Δεν τα φοράμε στον
              ύπνο ή στο μπάνιο. Αν ο ήχος ενοχλεί, αν η φωνή σας ακούγεται
              αφύσικη ή αν η εφαρμογή δεν είναι άνετη, επικοινωνήστε μαζί μας
              για ρύθμιση. Service κάθε 2 μήνες. Αλλαγή φίλτρων κάθε μήνα.
              ΚΟΚΚΙΝΟ = δεξί, ΜΠΛΕ = αριστερό αυτί.
            </p>
          </div>
        </div>
      </div>
    );
  /* PROFILE */
  if (sub === "profile")
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: BG, fontFamily: "system-ui" }}>
        <SH2 title="Προφίλ μου" onBack={() => setSub(null)} />
        <div ref={ref} style={{ flex: 1, overflowY: "auto", padding: "0 20px 0", paddingBottom: "var(--em-sub-pad-bottom)", zoom: 1 + fs * 0.15 }}>
          <C>
            <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>ΠΡΟΣΩΠΙΚΑ ΣΤΟΙΧΕΙΑ</p>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: DK }}>{nm || "—"}</p>
            <p style={{ margin: "6px 0 0", fontSize: 12, color: MG }}>{ph2 || "—"}{em ? ` • ${em}` : ""}</p>
            {profId && <p style={{ margin: "6px 0 0", fontSize: 12, color: MG }}>Κωδικός πελάτη Prof: <b style={{ color: DK }}>{profId}</b></p>}
          </C>
          <C>
            <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>ΣΥΣΚΕΥΗ</p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK }}>{dv.b || "—"} {dv.m || ""}</p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: MG }}>Τύπος: {dv.t || "—"}</p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: MG }}>Τροφοδοσία: {getPowerSourceLabel(dv.p)}</p>
            {(sL || sR || sC) && <p style={{ margin: "8px 0 0", fontSize: 11, color: MG, lineHeight: 1.6 }}>{sL ? `S/N Αριστερό: ${sL} ` : ""}{sR ? `• S/N Δεξί: ${sR} ` : ""}{sC ? `• S/N Φορτιστή: ${sC}` : ""}</p>}
          </C>
          {batteryProfile && (
            <C s={{ background: OL, border: `1px solid ${O}20` }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>ΜΠΑΤΑΡΙΕΣ</p>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK }}>Τύπος μπαταρίας: Νο {batteryProfile.batteryType}</p>
              <p style={{ margin: "6px 0 0", fontSize: 12, color: MG, lineHeight: 1.6 }}>
                Κάθε μπαταρία κρατά περίπου {batteryProfile.daysPerBattery} ημέρες. Με {batteryProfile.deviceCount === 2 ? "δύο" : "ένα"} ακουστικά, ένα κουτάκι με 6 μπαταρίες αρκεί περίπου για {batteryProfile.packDays} ημέρες.
              </p>
              <p style={{ margin: "6px 0 0", fontSize: 11, color: O, fontWeight: 600 }}>
                Θα χρησιμοποιείται αυτό το διάστημα για τις υπενθυμίσεις επαναγοράς μπαταριών.
              </p>
            </C>
          )}
          <C>
            <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>ΚΕΝΤΡΟ ΕΞΥΠΗΡΕΤΗΣΗΣ</p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK }}>{clinic.c}</p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: MG }}>{clinic.a}</p>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: MG }}>Τηλέφωνο: {clinic.f}</p>
            <p style={{ margin: "6px 0 0", fontSize: 11, color: MG, lineHeight: 1.6 }}>Ωράριο: {clinic.hoursText}</p>
          </C>
          <C>
            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>ΕΓΓΥΗΣΗ</p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK }}>Έως {wF}</p>
          </C>
        </div>
      </div>
    );
  /* NEWAPPT */
  if (sub === "newappt")
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        <SH2 title="Προγραμμάτισε Ραντεβού" onBack={() => { setSub(null); setBooking(null); setNewAReason(""); setNewATime(""); setShowBookingCalendar(false); if (toDateOnlyTime(newADate) < toDateOnlyTime(todayDateStr)) setNewADate(todayDateStr); }} />
        <div
          ref={ref}
          style={{ flex: 1, overflowY: "auto", padding: "0 20px 0", paddingBottom: "var(--em-sub-pad-bottom)" }}
        >
          <p style={{ fontSize: 13, color: MG, margin: "0 0 8px" }}>
            Επιλέξτε λόγο, ημερομηνία και ώρα. Το ραντεβού θα σταλεί στο {clinic.c}.
          </p>
          <p style={{ fontSize: 11, color: MG, margin: "0 0 14px", lineHeight: 1.5 }}>
            Ωράριο κέντρου: {clinic.hoursText}
          </p>
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: MG,
              marginBottom: 6,
              display: "block",
            }}
          >
            Λόγος
          </label>
          {[
            ...(isProspect ? PROSPECT_SERVICES.map(s => s.title) :
               isTrial ? [
              "Δεν ακούω καλά",
              "Θέλω βελτίωση / ρύθμιση",
              "Θέλω να προχωρήσω σε αγορά",
              "Θέλω να επιστρέψω τα δοκιμαστικά",
            ] : [
              "Δεν ακούω καλά",
              "Θέλω βελτίωση στον ήχο",
              "Συντήρηση / Καθαρισμός",
              "Έλεγχος ακοής",
              "Κάτι άλλο",
            ]),
          ].map((r) => (
            <button
              key={r}
              onClick={() => setNewAReason(r)}
              style={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                padding: "14px 16px",
                borderRadius: 14,
                border: `1.5px solid ${newAReason === r ? O : "#e0e0e0"}`,
                background: newAReason === r ? OL : WH,
                color: newAReason === r ? O : DK,
                fontSize: 14,
                fontWeight: newAReason === r ? 700 : 500,
                cursor: "pointer",
                textAlign: "left",
                marginBottom: 6,
              }}
            >
              {r}
            </button>
          ))}
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: MG,
              display: "block",
              margin: "14px 0 6px",
            }}
          >
            Ημερομηνία
          </label>
          <button
            type="button"
            onClick={() => setShowBookingCalendar((prev) => !prev)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxSizing: "border-box",
              padding: 12,
              borderRadius: 12,
              border: `1.5px solid ${O}50`,
              background: OL,
              fontSize: 14,
              fontWeight: 600,
              color: DK,
              outline: "none",
              marginBottom: 12,
              cursor: "pointer",
            }}
          >
            <span>{toDateOnlyTime(newADate).toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
            <span style={{ color: MG, fontSize: 18, lineHeight: 1 }}>▾</span>
          </button>
          {showBookingCalendar && (
            <div
              style={{
                background: WH,
                border: "1px solid #e6e0d8",
                borderRadius: 14,
                padding: 14,
                marginBottom: 12,
                boxShadow: SH,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={() => setBookingCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                  style={{ background: LG, border: "none", width: 34, height: 34, borderRadius: 10, cursor: "pointer", color: DK, fontSize: 18 }}
                >
                  ‹
                </button>
                <div style={{ fontSize: 15, fontWeight: 700, color: DK }}>
                  {MONTH_NAMES[bookingCalendarMonth.getMonth()]} {bookingCalendarMonth.getFullYear()}
                </div>
                <button
                  type="button"
                  onClick={() => setBookingCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                  style={{ background: LG, border: "none", width: 34, height: 34, borderRadius: 10, cursor: "pointer", color: DK, fontSize: 18 }}
                >
                  ›
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6, marginBottom: 8 }}>
                {DAY_NAMES_SHORT.map((day) => (
                  <div key={day} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: MG, padding: "4px 0" }}>
                    {day}
                  </div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                {bookingCalendarDays.map((day) => {
                  const disabled = !day.isSelectable;
                  return (
                    <button
                      key={day.dateStr}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setNewADate(day.dateStr);
                        setShowBookingCalendar(false);
                      }}
                      style={{
                        height: 40,
                        borderRadius: 10,
                        border: day.isSelected ? `1.5px solid ${O}` : "1px solid #ece7e0",
                        background: day.isSelected ? OL : WH,
                        color: disabled ? "#c9c2ba" : day.inMonth ? DK : "#b7aea3",
                        fontWeight: day.isSelected || day.isToday ? 700 : 500,
                        opacity: disabled ? 0.45 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                      }}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: MG, margin: "12px 0 0", lineHeight: 1.5 }}>
                Οι αχνές ημερομηνίες δεν είναι διαθέσιμες για το επιλεγμένο κέντρο ή έχουν ήδη περάσει.
              </p>
            </div>
          )}
          <label
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: MG,
              display: "block",
              margin: "0 0 6px",
            }}
          >
            Ώρα προτίμησης
          </label>
          <select
            value={newATime}
            onChange={(e) => setNewATime(e.target.value)}
            style={{
              width: "100%",
              display: "block",
              boxSizing: "border-box",
              padding: 12,
              borderRadius: 12,
              border: `1.5px solid ${O}50`,
              background: OL,
              fontSize: 14,
              fontWeight: 600,
              color: DK,
              outline: "none",
              marginBottom: 16,
              WebkitAppearance: "none",
            }}
          >
            {loadingSlots ? (
              <option value="">Φόρτωση διαθέσιμων ωρών...</option>
            ) : availableTimeSlots.length ? (
              availableTimeSlots.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))
            ) : (
              <option value="">Δεν υπάρχουν διαθέσιμες ώρες</option>
            )}
          </select>
          <p style={{ fontSize: 11, color: availableTimeSlots.length ? MG : "#C62828", margin: "0 0 16px", lineHeight: 1.5 }}>
            {clinicHoursSummary}
          </p>
          {newAReason ? (
            <>
              {booking === "success" ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#E8F5E9", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </div>
                  <p style={{ fontSize: 18, fontWeight: 800, color: DK, margin: "0 0 6px" }}>
                    Το ραντεβού καταχωρήθηκε!
                  </p>
                  <p style={{ fontSize: 13, color: MG, margin: "0 0 6px", lineHeight: 1.5 }}>
                    Το {clinic.c} ενημερώθηκε και θα επικοινωνήσει μαζί σας για επιβεβαίωση.
                  </p>
                  <p style={{ fontSize: 12, color: MG, margin: "0 0 20px" }}>
                    {newAReason} • {new Date(newADate).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" })} • {newATime}
                  </p>
                  <Btn onClick={() => { setBooking(null); setNewAReason(""); setSub(null); }}>
                    Επιστροφή στην αρχική
                  </Btn>
                </div>
              ) : booking === "error_past_date" ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: OL, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 700, color: DK, margin: "0 0 8px" }}>
                    Επιλέξτε μελλοντική ημερομηνία
                  </p>
                  <p style={{ fontSize: 13, color: MG, margin: "0 0 16px", lineHeight: 1.5 }}>
                    Δεν μπορείτε να προγραμματίσετε ραντεβού σε παρελθοντική ημερομηνία.
                  </p>
                  <Btn onClick={() => { setBooking(null); setNewADate(todayDateStr); }}>
                    Εντάξει
                  </Btn>
                </div>
              ) : booking === "error" ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#FFEBEE", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#C62828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 700, color: DK, margin: "0 0 8px" }}>
                    Κάτι πήγε στραβά
                  </p>
                  <p style={{ fontSize: 13, color: MG, margin: "0 0 16px", lineHeight: 1.5 }}>
                    Δοκιμάστε ξανά ή επικοινωνήστε μαζί μας
                  </p>
                  <Btn onClick={() => { setBooking(null); bookAppointment(); }}>
                    Δοκιμάστε ξανά
                  </Btn>
                  <div style={{ height: 8 }} />
                  <Btn onClick={() => window.open(`tel:${clinic.t}`)} primary={false}>
                    {Ic.ph(O, 16)} {clinic.f}
                  </Btn>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      background: OL,
                      borderRadius: 12,
                      padding: 14,
                      marginBottom: 14,
                      border: `1px solid ${O}20`,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 12, color: DK, lineHeight: 1.6 }}>
                      Το αίτημα για ραντεβού θα σταλεί στο <b>{clinic.c}</b> με την ώρα προτίμησής σας και θα σας καλέσουμε για επιβεβαίωση.
                    </p>
                  </div>
                  {!availableTimeSlots.length && (
                    <div style={{ background: "#FFEBEE", color: "#C62828", borderRadius: 12, padding: 12, marginBottom: 12, fontSize: 12, lineHeight: 1.5 }}>
                      Το {clinic.c} είναι κλειστό την επιλεγμένη ημέρα. Επιλέξτε άλλη ημερομηνία.
                    </div>
                  )}
                  <Btn onClick={bookAppointment} s={{ opacity: booking === "loading" || !availableTimeSlots.length ? 0.6 : 1, pointerEvents: booking === "loading" || !availableTimeSlots.length ? "none" : "auto" }}>
                    {booking === "loading" ? "Αποστολή..." : <>{Ic.cal("white", 16)} Επιβεβαίωση ραντεβού</>}
                  </Btn>
                  <div style={{ height: 8 }} />
                  <Btn onClick={() => setSub("chat")} primary={false}>
                    {Ic.chat("#F97316", 16)} Μίλα με τον ειδικό{chatUnread > 0 && <div style={{position:"absolute",top:8,right:12,width:10,height:10,borderRadius:"50%",background:"#C62828",border:"2px solid white"}} />}
                  </Btn>
                  <div style={{ height: 8 }} />
                  <Btn
                    onClick={() => window.open(`tel:${clinic.t}`)}
                    primary={false}
                  >
                    {Ic.ph(O, 16)} Καλέστε το κέντρο
                  </Btn>
                </>
              )}
            </>
          ) : (
            <div
              style={{
                padding: 16,
                background: LG,
                borderRadius: 14,
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0, fontSize: 13, color: MG }}>
                Επιλέξτε λόγο
              </p>
            </div>
          )}
        </div>
      </div>
    );
  /* REVIEW */
  if (sub === "review")
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        <SH2 title="Αξιολογήστε μας" onBack={() => setSub(null)} />
        <div
          ref={ref}
          style={{ flex: 1, overflowY: "auto", padding: "0 20px 0", paddingBottom: "var(--em-sub-pad-bottom)" }}
        >
          <C
            s={{
              background: OL,
              border: `1px solid ${O}20`,
              textAlign: "center",
            }}
          >
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 15,
                fontWeight: 700,
                color: DK,
              }}
            >
              Η γνώμη σας μετράει!
            </p>
            <p style={{ margin: 0, fontSize: 12, color: MG }}>
              Επιλέξτε κέντρο για αξιολόγηση.
            </p>
          </C>
          {LOCS.map((l, i) => (
            <C key={i} onClick={() => window.open(l.rv, "_blank")}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {Ic.pin(O, 16)}
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: DK }}>
                      {l.c}
                    </span>
                    <br />
                    <span style={{ fontSize: 11, color: MG }}>{l.a}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 1 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <span key={n}>{Ic.star("#FFB800", 12)}</span>
                  ))}
                </div>
              </div>
            </C>
          ))}
        </div>
      </div>
    );
  /* QUIZ */
  if (sub === "guide") {
    const guideSections = [
      {
        icon: "👋",
        title: "Τι κάνει η εφαρμογή",
        content: (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Η εφαρμογή <strong>Earmedical</strong> σας συνδέει με το κέντρο ακουστικών βαρηκοΐας σας.
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Μέσα από την εφαρμογή μπορείτε:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Να κλείνετε ραντεβού εύκολα</li>
              <li>Να μιλάτε με τον ακοοπροθετιστή σας</li>
              <li>Να βλέπετε υπενθυμίσεις για τα ραντεβού σας</li>
              <li>Να ελέγχετε πώς ακούτε καθημερινά</li>
              <li>Να ζητάτε μπαταρίες ή σέρβις</li>
            </ul>
            <div style={{
              background: `${O}10`, padding: 14, borderRadius: 10,
              marginTop: 14, border: `1px solid ${O}25`,
            }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                <strong>Όλα γίνονται με ένα πάτημα.</strong> Δεν χρειάζεται να ξέρετε από τεχνολογία.
              </p>
            </div>
          </div>
        ),
      },
      {
        icon: "🏠",
        title: "Η αρχική σελίδα",
        content: (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Όταν ανοίγετε την εφαρμογή, βλέπετε την αρχική σελίδα.
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK, fontWeight: 700 }}>
              Εκεί μπορείτε να δείτε:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Το επόμενο ραντεβού σας</li>
              <li>Νέες ειδοποιήσεις</li>
              <li>Πώς νιώθατε τις τελευταίες ημέρες</li>
              <li>Νέα μηνύματα από τον ακοοπροθετιστή</li>
            </ul>
            <p style={{ margin: "14px 0 0", fontSize: 14, lineHeight: 1.6, color: MG, fontStyle: "italic" }}>
              Στο κάτω μέρος της οθόνης βρίσκονται 4 κουμπιά που σας πάνε σε διαφορετικές σελίδες της εφαρμογής.
            </p>
          </div>
        ),
      },
      {
        icon: "📅",
        title: "Πώς κλείνω ραντεβού",
        content: (
          <div>
            <p style={{ margin: "0 0 14px", fontSize: 15, lineHeight: 1.7, color: DK, fontWeight: 700 }}>
              Για να κλείσετε νέο ραντεβού:
            </p>
            <ol style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Πατήστε το <strong>"+"</strong> στην αρχική σελίδα</li>
              <li>Επιλέξτε <strong>"Νέο ραντεβού"</strong></li>
              <li>Διαλέξτε ημερομηνία και ώρα</li>
              <li>Γράψτε για ποιον λόγο θέλετε το ραντεβού</li>
              <li>Πατήστε <strong>"Επιβεβαίωση"</strong></li>
            </ol>
            <div style={{
              background: "#E8F5E9", padding: 14, borderRadius: 10,
              marginTop: 14, border: "1px solid #C8E6C9",
            }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "#2E7D32" }}>
                ✓ Θα λάβετε <strong>SMS και ειδοποίηση</strong> όταν ο ακοοπροθετιστής σας επιβεβαιώσει το ραντεβού.
              </p>
            </div>
            <p style={{ margin: "14px 0 6px", fontSize: 14, lineHeight: 1.6, color: DK, fontWeight: 700 }}>
              Για να ακυρώσετε ραντεβού:
            </p>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: DK }}>
              Πατήστε πάνω στο ραντεβού και μετά <strong>"Ακύρωση"</strong>. Ή τηλεφωνήστε απευθείας στο κέντρο.
            </p>
          </div>
        ),
      },
      {
        icon: "💬",
        title: "Επικοινωνία με τον ακοοπροθετιστή",
        content: (
          <div>
            <p style={{ margin: "0 0 14px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Έχετε <strong>δύο τρόπους</strong> να επικοινωνήσετε:
            </p>

            <div style={{
              background: WH, border: "1px solid #eee",
              padding: 14, borderRadius: 12, marginBottom: 12,
            }}>
              <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: DK }}>
                Συνομιλία (Chat)
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Στείλτε μήνυμα στον ακοοπροθετιστή σας οποιαδήποτε ώρα. Θα σας απαντήσει το συντομότερο.
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: MG }}>
                Το βρίσκετε στο κουμπί <strong>"Συνομιλία"</strong> κάτω.
              </p>
            </div>

            <div style={{
              background: "#FFF5F5", border: "1px solid #FFCDD2",
              padding: 14, borderRadius: 12,
            }}>
              <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: "#C62828" }}>
                🆘 Κουμπί SOS (Έκτακτη ανάγκη)
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Αν έχετε <strong>επείγον πρόβλημα</strong> με τα ακουστικά σας (π.χ. δεν λειτουργούν), πατήστε το κόκκινο κουμπί SOS.
              </p>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: MG }}>
                Ο ακοοπροθετιστής θα ειδοποιηθεί άμεσα.
              </p>
            </div>
          </div>
        ),
      },
      {
        icon: "🔔",
        title: "Ειδοποιήσεις",
        content: (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Η εφαρμογή σας στέλνει ειδοποιήσεις για:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Υπενθύμιση ραντεβού (1 ημέρα και 2 ώρες πριν)</li>
              <li>Νέα μηνύματα από τον ακοοπροθετιστή</li>
              <li>Προσφορές από το κέντρο σας</li>
              <li>Υπενθυμίσεις για ερωτηματολόγια</li>
            </ul>
            <p style={{ margin: "14px 0 12px", fontSize: 15, lineHeight: 1.7, color: DK, fontWeight: 700 }}>
              Πώς τις βλέπω;
            </p>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: DK }}>
              Πατήστε το <strong>καμπανάκι</strong> πάνω-δεξιά στην αρχική σελίδα. Εκεί υπάρχουν όλες οι ειδοποιήσεις σας.
            </p>
            <div style={{
              background: `${O}10`, padding: 14, borderRadius: 10,
              marginTop: 14, border: `1px solid ${O}25`,
            }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Αν δεν θέλετε ειδοποιήσεις, μπορείτε να τις απενεργοποιήσετε από τις ρυθμίσεις του κινητού σας.
              </p>
            </div>
          </div>
        ),
      },
      {
        icon: "📊",
        title: "Καθημερινός έλεγχος",
        content: (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Κάθε ημέρα μπορείτε να μας πείτε <strong>πώς νιώθετε</strong> με τα ακουστικά σας.
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Είναι πολύ απλό:
            </p>
            <ol style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Στην αρχική σελίδα πατήστε <strong>"Πώς νιώθεις σήμερα;"</strong></li>
              <li>Επιλέξτε ένα από τα προσωπάκια (χαρούμενο, ουδέτερο, στενοχωρημένο)</li>
              <li>Έτοιμο!</li>
            </ol>
            <div style={{
              background: "#E3F2FD", padding: 14, borderRadius: 10,
              marginTop: 14, border: "1px solid #BBDEFB",
            }}>
              <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "#1565C0" }}>
                Γιατί να το κάνω;
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Ο ακοοπροθετιστής σας βλέπει αν έχετε δυσκολίες και μπορεί να σας καλέσει για βοήθεια.
              </p>
            </div>
          </div>
        ),
      },
      {
        icon: "📝",
        title: "Έλεγχος ακοής (Ερωτηματολόγιο)",
        content: (
          <div>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Κατά καιρούς ο ακοοπροθετιστής σας στέλνει ένα <strong>ερωτηματολόγιο</strong>.
            </p>
            <p style={{ margin: "0 0 12px", fontSize: 15, lineHeight: 1.7, color: DK }}>
              Είναι 10-15 ερωτήσεις για να δούμε <strong>πόσο καλά ακούτε</strong> σε διάφορες καταστάσεις:
            </p>
            <ul style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Στο σπίτι</li>
              <li>Σε παρέα</li>
              <li>Στο τηλέφωνο</li>
              <li>Στην τηλεόραση</li>
            </ul>
            <p style={{ margin: "14px 0 12px", fontSize: 15, lineHeight: 1.7, color: DK, fontWeight: 700 }}>
              Πώς το συμπληρώνω;
            </p>
            <ol style={{ margin: 0, paddingLeft: 22, fontSize: 15, lineHeight: 1.9, color: DK }}>
              <li>Όταν είναι έτοιμο, θα δείτε ειδοποίηση</li>
              <li>Πατήστε πάνω της και ανοίγει το ερωτηματολόγιο</li>
              <li>Διαβάστε κάθε ερώτηση και διαλέξτε την απάντηση</li>
              <li>Πατήστε <strong>"Υποβολή"</strong> στο τέλος</li>
            </ol>
            <div style={{
              background: "#FFF8E1", padding: 14, borderRadius: 10,
              marginTop: 14, border: "1px solid #FFE082",
            }}>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Παίρνει <strong>μόνο 3-5 λεπτά</strong>. Δεν υπάρχουν σωστές ή λάθος απαντήσεις.
              </p>
            </div>
          </div>
        ),
      },
      {
        icon: "❓",
        title: "Συχνές ερωτήσεις",
        content: (
          <div>
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: DK }}>
                Δεν θυμάμαι τον κωδικό μου. Τι κάνω;
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Πατήστε <strong>"Ξέχασα τον κωδικό"</strong> στην οθόνη σύνδεσης. Θα λάβετε <strong>SMS με νέο κωδικό</strong>.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: DK }}>
                Άλλαξα κινητό. Τι θα γίνει;
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Κατεβάστε ξανά την εφαρμογή στο νέο κινητό και συνδεθείτε με τον <strong>ίδιο αριθμό τηλεφώνου</strong>. Όλα τα στοιχεία σας θα είναι εκεί.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: DK }}>
                Πώς αλλάζω τα στοιχεία μου;
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Πηγαίνετε <strong>"Πληροφορίες"</strong> και πατήστε <strong>"Τα στοιχεία μου"</strong>. Από εκεί αλλάζετε ό,τι θέλετε.
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: DK }}>
                Δεν λαμβάνω ειδοποιήσεις.
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Ελέγξτε τις <strong>ρυθμίσεις του κινητού σας</strong>. Στις ρυθμίσεις, βρείτε την εφαρμογή Earmedical και ενεργοποιήστε τις ειδοποιήσεις.
              </p>
            </div>

            <div>
              <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 800, color: DK }}>
                Μπορώ να διαγράψω τον λογαριασμό μου;
              </p>
              <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
                Ναι. Στο κάτω μέρος του μενού <strong>"Πληροφορίες"</strong> θα βρείτε το <strong>"Διαγραφή Λογαριασμού"</strong>.
              </p>
            </div>
          </div>
        ),
      },
    ];

    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: BG, fontFamily: "system-ui" }}>
        {/* Header — v1.3.30 safe-area top fix */}
        <div style={{
          background: WH,
          padding: "var(--em-header-pad-top) 16px 14px",
          borderBottom: "1px solid #e8e8e8",
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={() => setSub(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: DK }}>Οδηγίες Χρήσης</p>
            <p style={{ margin: 0, fontSize: 12, color: MG }}>Όλα όσα χρειάζεστε να ξέρετε</p>
          </div>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: `${O}15`, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
            </svg>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px 40px" }}>
          {/* Welcome message */}
          <div style={{
            background: `linear-gradient(135deg, ${O}10 0%, ${O}05 100%)`,
            border: `1px solid ${O}25`,
            borderRadius: 16,
            padding: "18px 18px",
            marginBottom: 20,
          }}>
            <p style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: DK }}>
              Καλώς ήρθατε!
            </p>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: DK }}>
              Σε αυτές τις οδηγίες θα βρείτε <strong>όλα όσα χρειάζεστε</strong> για να χρησιμοποιείτε εύκολα την εφαρμογή. Πατήστε σε κάθε ενότητα για να την ανοίξετε.
            </p>
          </div>

          {/* Accordion sections */}
          {guideSections.map((section, idx) => {
            const isOpen = expandedGuideSection === idx;
            return (
              <div
                key={idx}
                style={{
                  background: WH,
                  border: isOpen ? `1.5px solid ${O}40` : "1px solid #eee",
                  borderRadius: 14,
                  marginBottom: 12,
                  overflow: "hidden",
                  transition: "border 0.2s",
                  boxShadow: isOpen ? `0 4px 16px ${O}10` : "none",
                }}
              >
                {/* Section header (clickable) */}
                <button
                  onClick={() => setExpandedGuideSection(isOpen ? -1 : idx)}
                  style={{
                    width: "100%", padding: "16px 16px",
                    background: WH, border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 12,
                    fontFamily: "inherit",
                  }}
                >
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: isOpen ? `${O}15` : "#F5F5F5",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 22, flexShrink: 0,
                    transition: "background 0.2s",
                  }}>
                    {idx + 1}
                  </div>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <p style={{
                      margin: 0, fontSize: 15, fontWeight: 700,
                      color: DK, lineHeight: 1.3,
                    }}>
                      {section.title}
                    </p>
                  </div>
                  <svg
                    width="18" height="18" viewBox="0 0 24 24"
                    fill="none" stroke={isOpen ? O : MG}
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.25s",
                      flexShrink: 0,
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>

                {/* Section body (expandable) */}
                {isOpen && (
                  <div style={{
                    padding: "0 18px 20px",
                    borderTop: "1px solid #f5f5f5",
                    paddingTop: 16,
                  }}>
                    {section.content}
                  </div>
                )}
              </div>
            );
          })}

          {/* Footer help card */}
          <div style={{
            marginTop: 20, padding: 18,
            background: WH, borderRadius: 16,
            border: "1px solid #eee", textAlign: "center",
          }}>
            <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: DK }}>
              Χρειάζεστε επιπλέον βοήθεια;
            </p>
            <p style={{ margin: "0 0 14px", fontSize: 14, lineHeight: 1.6, color: MG }}>
              Επικοινωνήστε με τον ακοοπροθετιστή σας. Είναι εδώ για εσάς.
            </p>
            <button
              onClick={() => {
                // FIX (v1.3.15): "chat" is NOT a top-level tab — the valid
                // tab values are "home"|"cal"|"progress"|"shop"|"more". The
                // previous setTab("chat") put the app into a state with no
                // matching render block, hence the blank white screen the
                // operator reported. The chat conversation lives as a
                // SUB-VIEW opened via setSub("chat") (same pattern as the
                // "Μίλα με τον ειδικό" button in the help section).
                setSub("chat");
              }}
              style={{
                width: "100%", padding: "14px",
                background: O, color: WH,
                border: "none", borderRadius: 12,
                fontSize: 15, fontWeight: 700,
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                fontFamily: "inherit",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              Άνοιγμα συνομιλίας
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* SETTINGS — ΡΥΘΜΙΣΕΙΣ ΕΙΔΟΠΟΙΗΣΕΩΝ */
  if (sub === "settings") {
    const optinDefs = [
      {
        key: "reminders",
        icon: "🏥",
        title: "Υπενθυμίσεις & Ραντεβού",
        desc: "Service, ραντεβού, σημαντικές ιατρικές υπενθυμίσεις",
        protected: true,
      },
      {
        key: "offers",
        icon: "🎁",
        title: "Προσφορές",
        desc: "Μπαταρίες, νέα μοντέλα, εκπτώσεις",
      },
      {
        key: "news",
        icon: "📰",
        title: "Νέα Κέντρου",
        desc: "Ανακοινώσεις, νέες υπηρεσίες, αλλαγές ωραρίου",
      },
      {
        key: "nps",
        icon: "⭐",
        title: "Ερωτηματολόγια Ικανοποίησης",
        desc: "Πόσο σας αρέσει η εμπειρία μαζί μας;",
      },
    ];

    const toggleOptin = (key) => {
      setOptins((prev) => ({ ...prev, [key]: !prev[key] }));
      setOptinsSaved(false);
    };

    const saveOptins = async () => {
      if (!ph2 || !profId) return;
      setOptinsSaving(true);
      try {
        const tk = sessionStorage.getItem("authToken") || localStorage.getItem("authToken") || "";
        const res = await fetch("https://admin.earmedical.gr/wp-json/earmedical/v1/auth/optins", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: tk ? `Bearer ${tk}` : "",
          },
          body: JSON.stringify(optins),
        });
        const data = await res.json();
        if (data.success) {
          setOptins(data.optins);
          setOptinsSaved(true);
          setTimeout(() => setOptinsSaved(false), 3000);
        }
      } catch (e) {
        console.warn("[Optins] Save failed:", e.message);
        alert("Σφάλμα αποθήκευσης. Δοκιμάστε ξανά.");
      } finally {
        setOptinsSaving(false);
      }
    };

    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        {/* Header — v1.3.30 safe-area top fix */}
        <div
          style={{
            background: BG,
            padding: "var(--em-header-pad-top) 16px 12px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setSub(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              display: "flex",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div style={{ flex: 1, fontSize: 18, fontWeight: 700, color: DK }}>
            Ρυθμίσεις Ειδοποιήσεων
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 24px", zoom: 1 + fs * 0.15 }}>
          {/* Intro card */}
          <div
            style={{
              background: WH,
              borderRadius: 14,
              padding: "16px 18px",
              marginBottom: 16,
              borderLeft: `3px solid ${O}`,
            }}
          >
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK, marginBottom: 6 }}>
              Διαχείριση Ειδοποιήσεων
            </p>
            <p style={{ margin: 0, fontSize: 13, color: "#555", lineHeight: 1.5 }}>
              Επιλέξτε τι θέλετε να λαμβάνετε. Μπορείτε να αλλάξετε αυτές τις προτιμήσεις
              οποτεδήποτε.
            </p>
          </div>

          {/* Optin toggles */}
          {optinDefs.map((def) => {
            const isOn = optins[def.key];
            return (
              <div
                key={def.key}
                onClick={() => toggleOptin(def.key)}
                style={{
                  background: WH,
                  borderRadius: 14,
                  padding: "14px 16px",
                  marginBottom: 10,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  transition: "all 0.15s",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: isOn ? `${O}20` : "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 20,
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isOn ? O : MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK, marginBottom: 2 }}>
                    {def.title}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: MG, lineHeight: 1.4 }}>{def.desc}</p>
                </div>
                {/* Toggle switch */}
                <div
                  style={{
                    width: 44,
                    height: 26,
                    borderRadius: 99,
                    background: isOn ? O : "#ccc",
                    position: "relative",
                    flexShrink: 0,
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 3,
                      left: isOn ? 21 : 3,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: WH,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
              </div>
            );
          })}

          {/* Info note */}
          <div
            style={{
              background: "#F0EFED",
              borderRadius: 12,
              padding: "12px 14px",
              marginTop: 16,
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
            }}
          >
            <div style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</div>
            <p style={{ margin: 0, fontSize: 12, color: "#555", lineHeight: 1.5 }}>
              Κρίσιμες ιατρικές υπενθυμίσεις (π.χ. ραντεβού σε λίγες ώρες) ενδέχεται να
              αποστέλλονται ανεξάρτητα από τις προτιμήσεις σας για λόγους ασφάλειας.
            </p>
          </div>

          {/* Save button */}
          <button
            onClick={saveOptins}
            disabled={optinsSaving}
            style={{
              width: "100%",
              marginTop: 20,
              padding: "14px 20px",
              borderRadius: 14,
              border: "none",
              background: optinsSaved ? "#2E7D32" : O,
              color: WH,
              fontSize: 15,
              fontWeight: 700,
              cursor: optinsSaving ? "wait" : "pointer",
              opacity: optinsSaving ? 0.7 : 1,
              transition: "all 0.2s",
            }}
          >
            {optinsSaving
              ? "Αποθήκευση…"
              : optinsSaved
              ? "✓ Αποθηκεύτηκαν!"
              : "Αποθήκευση Αλλαγών"}
          </button>
        </div>
      </div>
    );
  }


  if (sub === "quiz")
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        <SH2
          title="Πώς ακούτε σήμερα;"
          onBack={() => {
            setSub(null);
            setQD(false);
            setAns({});
            setQScore(null);
          }}
        />
        <div
          ref={ref}
          style={{ flex: 1, overflowY: "auto", padding: "0 20px 0", paddingBottom: "var(--em-sub-pad-bottom)" }}
        >
          {qD ? (
            <div style={{ padding: "10px 0" }}>
              {/* Ευχαριστήριο + αποτέλεσμα */}
              <C s={{ border: `1.5px solid ${O}30`, marginBottom: 14, textAlign: "center" }}>
                {Ic.chk(O, 36)}
                <p style={{ margin: "8px 0 4px", fontSize: 18, fontWeight: 800, color: DK }}>Ευχαριστούμε για την ενημέρωση</p>
                <p style={{ margin: 0, fontSize: 13, color: MG, lineHeight: 1.5 }}>
                  Οι απαντήσεις σας μας βοηθούν να σας υποστηρίζουμε καλύτερα και να κάνουμε πιο σωστές ρυθμίσεις όταν χρειάζεται.
                </p>
              </C>

              {/* TRIAL RESULT — 3 variants βάσει score + purchase intent */}
              {isTrial && (() => {
                const result = getTrialResult(ans, qScore);
                const resultColor = result.level === "positive" ? "#2E7D32" : result.level === "medium" ? O : "#C62828";
                const resultBg = result.level === "positive" ? "#E8F5E9" : result.level === "medium" ? OL : "#FFEBEE";
                return (
                  <C s={{ background: resultBg, border: `1px solid ${resultColor}30`, marginBottom: 14 }}>
                    <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 800, color: resultColor, letterSpacing: 0.5 }}>
                      ΑΠΟΤΕΛΕΣΜΑ ΑΞΙΟΛΟΓΗΣΗΣ
                    </p>
                    <p style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 800, color: DK, lineHeight: 1.3 }}>{result.title}</p>
                    <p style={{ margin: "0 0 14px", fontSize: 14, color: DK2, lineHeight: 1.6 }}>{result.text}</p>
                    <Btn onClick={() => {
                      if (result.cta.action === "call") window.open(`tel:${clinic.t}`);
                      else { setSub("newappt"); setNewAReason("Ρύθμιση / Follow-up"); }
                    }}>
                      {result.cta.action === "call" ? Ic.ph(WH, 16) : Ic.cal(WH, 16)} {result.cta.label}
                    </Btn>
                  </C>
                );
              })()}

              {/* Αναλυτικά αποτελέσματα */}
              <C>
                <p style={{ margin: "0 0 12px", fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>ΑΠΑΝΤΗΣΕΙΣ</p>
                {activeQZ.map((q, i) => {
                  const a = ans[i];
                  const good = q.sc[a] >= 2;
                  return (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < activeQZ.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                      <span style={{ fontSize: 12, color: MG, flex: 1 }}>{q.q}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: good ? "#2E7D32" : "#C62828", marginLeft: 10, flexShrink: 0 }}>{q.o[a] || "—"}</span>
                    </div>
                  );
                })}
              </C>

              {/* PURCHASED — Σύσταση αν πολύ θετικό (score >= 80) */}
              {!isTrial && qScore >= 80 && (
                <C s={{ background: OL, border: `1px solid ${O}20` }}>
                  <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: DK }}>Χαιρόμαστε πολύ!</p>
                  <p style={{ margin: "0 0 12px", fontSize: 12, color: MG, lineHeight: 1.5 }}>
                    Αν γνωρίζετε κάποιον που δυσκολεύεται με την ακοή του, μπορείτε να τον βοηθήσετε — και κερδίζετε 100€ έκπτωση!
                  </p>
                  <Btn onClick={() => {
                    const t = "Δωρεάν έλεγχος ακοής στην Earmedical!\nΑνέφερε το όνομά μου: 100€ έκπτωση.\nΤηλ: 210 360 8005\nearmedical.gr";
                    shareContent(t, "Earmedical — 100€ έκπτωση");
                  }}>
                    Σύστησέ μας — 100€ έκπτωση
                  </Btn>
                </C>
              )}

              {/* PURCHASED — Ραντεβού ρύθμισης αν χαμηλό score */}
              {!isTrial && qScore < 60 && (
                <C s={{ background: "#FFF3E0", border: `1px solid ${O}20` }}>
                  <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: DK }}>Μπορούμε να βελτιώσουμε την εμπειρία σας</p>
                  <p style={{ margin: "0 0 12px", fontSize: 12, color: MG, lineHeight: 1.5 }}>
                    Τα αποτελέσματα στάλθηκαν στο κέντρο σας. Κλείστε ραντεβού ρύθμισης για βελτιστοποίηση.
                  </p>
                  <Btn onClick={() => { setSub("newappt"); setNewAReason("Ρύθμιση / Follow-up"); }} s={{ background: DK }}>
                    {Ic.cal("white", 16)} Κλείσε ραντεβού ρύθμισης
                  </Btn>
                </C>
              )}

              {/* PURCHASED — Google review αν πολύ θετικό */}
              {!isTrial && qScore >= 80 && (
                <C onClick={() => window.open(clinic.rv, "_blank")} s={{ cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {Ic.star(O, 20)}
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: DK }}>Αξιολογήστε μας στο Google</p>
                      <p style={{ margin: "2px 0 0", fontSize: 11, color: MG }}>Η γνώμη σας μάς βοηθά πολύ!</p>
                    </div>
                    {Ic.rt(MG, 16)}
                  </div>
                </C>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <Btn onClick={() => { setQD(false); setAns({}); setQScore(null); }} primary={false} s={{ flex: 1 }}>
                  Αποστολή & κλείσιμο
                </Btn>
                <Btn onClick={() => { setSub(null); setQD(false); setAns({}); setQScore(null); }} s={{ flex: 1 }}>
                  Επιστροφή στην αρχική
                </Btn>
              </div>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: MG, margin: "8px 0 14px", lineHeight: 1.5 }}>
                Απαντήστε σε λίγες σύντομες ερωτήσεις για να σας βοηθήσουμε καλύτερα.
              </p>
              {activeQZ.map((q, qi) => (
                <C key={qi}>
                  <p
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: DK,
                      margin: "0 0 10px",
                    }}
                  >
                    <span style={{ color: O, marginRight: 4 }}>{qi + 1}.</span>
                    {q.q}
                  </p>
                  {q.o.map((o, oi) => (
                    <button
                      key={oi}
                      onClick={() => setAns((p) => ({ ...p, [qi]: oi }))}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        borderRadius: 12,
                        border: `1.5px solid ${ans[qi] === oi ? O : "#e8e8e8"}`,
                        background: ans[qi] === oi ? OL : WH,
                        cursor: "pointer",
                        width: "100%",
                        textAlign: "left",
                        marginBottom: 4,
                      }}
                    >
                      <div
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: "50%",
                          border: `2px solid ${ans[qi] === oi ? O : "#ddd"}`,
                          background: ans[qi] === oi ? O : "transparent",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 13, color: DK }}>{o}</span>
                    </button>
                  ))}
                </C>
              ))}
              <Btn
                onClick={submitQuiz}
                s={{ opacity: Object.keys(ans).length === activeQZ.length && !qSending ? 1 : 0.4, pointerEvents: qSending ? "none" : "auto" }}
              >
                {qSending ? "Αποστολή..." : "Στείλε στο κέντρο"}
              </Btn>
            </>
          )}
        </div>
      </div>
    );
  /* NOTIFICATIONS INBOX */
  // v1.3.44 — Announcement detail screen (full content + back arrow)
  if (sub === "announcement-detail" && selectedAnnouncement) {
    const a = selectedAnnouncement;
    const pri = ANN_PRIORITY_META[a.priority] || ANN_PRIORITY_META.medium;
    const createdDate = a.created_at ? new Date(a.created_at.replace(' ', 'T')).toLocaleString('el-GR', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }) : '';
    return (
      <div
        style={{
          minHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        {/* Hero section with priority gradient + title + date */}
        <div
          style={{
            background: `linear-gradient(135deg, ${pri.color}, ${pri.color}DD)`,
            padding: "var(--em-header-pad-top) 20px 28px",
            color: "white",
            position: "relative",
          }}
        >
          {/* Back arrow */}
          <button
            onClick={() => { setSub(null); setSelectedAnnouncement(null); }}
            aria-label="Πίσω"
            style={{
              background: "rgba(0,0,0,0.25)",
              border: "none",
              cursor: "pointer",
              padding: 0,
              width: 38,
              height: 38,
              borderRadius: 99,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 22,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>

          {/* Category-driven label pill — v1.3.49 (color from priority) */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.25)", padding: "6px 14px", borderRadius: 99, marginBottom: 16 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 11 18-5v12L3 14v-3z" />
              <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 700 }}>{getAnnouncementLabel(a)}</span>
          </div>

          {/* Title */}
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, lineHeight: 1.25 }}>{a.title}</h1>

          {/* Date */}
          {createdDate && (
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 13, opacity: 0.9 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>{createdDate}</span>
            </div>
          )}
        </div>

        {/* Body card */}
        <div style={{ flex: 1, padding: "20px 16px 24px", zoom: 1 + fs * 0.15 }}>
          <div style={{ background: WH, borderRadius: 14, padding: 18, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: OL, display: "flex", alignItems: "center", justifyContent: "center", color: O, flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
              </div>
              <span style={{ fontWeight: 700, fontSize: 14, color: DK }}>Μήνυμα</span>
            </div>
            <div style={{ fontSize: 15, color: DK, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
              {a.body}
            </div>
            {a.created_by_name && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #EEE", fontSize: 12, color: "#888" }}>
                Από: <strong style={{ color: DK }}>{a.created_by_name}</strong>
              </div>
            )}
          </div>

          {/* v1.3.47 — Action CTAs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            <button
              onClick={() => {
                logAnnouncementEvent(a.id, 'cta_appointment');
                dismissAnnouncement(a.id);
                setSub("newappt");
                setSelectedAnnouncement(null);
                if (typeof setNewAReason === "function") setNewAReason("");
              }}
              style={{
                background: O, color: WH, border: "none", borderRadius: 12,
                padding: "14px 18px", fontWeight: 700, fontSize: 15,
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8, boxShadow: "0 2px 6px rgba(239,128,26,0.25)",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              Κλείσε ραντεβού
            </button>
            <button
              onClick={() => {
                logAnnouncementEvent(a.id, 'cta_chat');
                dismissAnnouncement(a.id);
                setSub("chat");
                setSelectedAnnouncement(null);
              }}
              style={{
                background: WH, color: DK, border: "1.5px solid #e0e0e0",
                borderRadius: 12, padding: "12px 18px", fontWeight: 700, fontSize: 14,
                cursor: "pointer", display: "flex", alignItems: "center",
                justifyContent: "center", gap: 8,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Μίλα με τον ειδικό
            </button>
          </div>

          {/* "Το είδα" — dismiss from home banner, stays in bell inbox */}
          <button
            onClick={() => {
              dismissAnnouncement(a.id);
              setSub(null);
              setSelectedAnnouncement(null);
            }}
            style={{
              width: "100%", background: "transparent", color: MG,
              border: "none", padding: "10px", fontSize: 13, fontWeight: 600,
              cursor: "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", gap: 6,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Το είδα — απόκρυψη από την αρχική
          </button>
        </div>
      </div>
    );
  }

  if (sub === "notifs") {
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        {/* Header — v1.3.30 safe-area top fix */}
        <div
          style={{
            background: BG,
            padding: "var(--em-header-pad-top) 16px 12px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setSub(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              display: "flex",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div style={{ flex: 1, fontSize: 18, fontWeight: 700, color: DK }}>Ειδοποιήσεις</div>
          {notifsUnread > 0 && (
            <button
              onClick={markAllNotifsRead}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: 12,
                color: O,
                fontWeight: 600,
                padding: "4px 8px",
              }}
            >
              Σήμανση όλων
            </button>
          )}
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 24px", zoom: 1 + fs * 0.15 }}>
          {notifsLoading && notifs.length === 0 && (
            <EarLoading text="Φόρτωση…" size={78} pad={36} />
          )}

          {!notifsLoading && notifs.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 20px", color: MG }}>
              <div style={{ marginBottom: 12, opacity: 0.4, display: "flex", justifyContent: "center" }}>
                <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
              </div>
              <p style={{ fontSize: 15, fontWeight: 600, color: DK, margin: "0 0 6px" }}>
                Δεν έχετε ειδοποιήσεις
              </p>
              <p style={{ fontSize: 13, margin: 0 }}>
                Όταν έρχονται νέα μηνύματα ή υπενθυμίσεις, θα εμφανίζονται εδώ.
              </p>
            </div>
          )}

          {notifs.map((n) => {
            const cat = NOTIF_CATEGORIES[n.type] || NOTIF_CATEGORIES.system;
            const isRead = !!n.is_read;
            return (
              <div
                key={n.id}
                onClick={() => openNotif(n)}
                style={{
                  background: isRead ? "#F0EEE8" : WH,
                  borderRadius: 14,
                  padding: "14px 16px",
                  marginBottom: 12,
                  borderLeft: `3px solid ${cat.color}`,
                  cursor: "pointer",
                  opacity: isRead ? 0.78 : 1,
                  transition: "all 0.15s",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: isRead ? 500 : 700, color: DK, flex: 1 }}>
                    {n.title}
                  </div>
                  <div style={{ fontSize: 11, color: MG, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {formatRelativeTime(n.created_at)}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5, marginBottom: 10, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {n.body}
                </div>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    background: cat.bg,
                    color: cat.textColor,
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "4px 10px",
                    borderRadius: 99,
                  }}
                >
                  {cat.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* CHAT */
  if (sub === "chat")
    return (
      <div
        style={{
          height: "100dvh",
          display: "flex",
          flexDirection: "column",
          background: BG,
          fontFamily: "system-ui",
        }}
      >
        {/* Chat Header — v1.3.29: max() για να εγγυηθεί ≥32px + 14px = 46px
            top padding ανεξάρτητα από τι κάνει το --em-safe-top var.
            Πιάνει την περίπτωση που το MainActivity injection έβαλε 0px. */}
        <div
          style={{
            background: WH,
            padding: "var(--em-header-pad-top) 16px 14px",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setSub(null)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: 4, display: "flex",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div style={{ flex: 1 }}>
            {/* v1.3.17 — Δυναμικός τίτλος. Αντί για το statically
                assigned audiologist, δείχνουμε το όνομα του ΤΕΛΕΥΤΑΙΟΥ
                ακοοπροθετιστή που απάντησε στο thread. Έτσι αν στο ίδιο
                κέντρο (πχ Αθήνα) απαντούν και ο Γιώργος και ο Αλέξης, ο
                πελάτης βλέπει κάθε φορά με ποιον "μιλάει τώρα". Όταν
                κανείς δεν έχει απαντήσει ακόμα, fallback σε
                "Ομάδα Earmedical {κέντρο}" — group-chat affordance. */}
            {(() => {
              const lastAudiologistMsg = [...chatMessages]
                .reverse()
                .find(m => m.sender === 'audiologist' && m.sender_name);
              const currentSpeaker = lastAudiologistMsg
                ? lastAudiologistMsg.sender_name
                : (audiologist || "Ομάδα " + (clinic.c || "Earmedical"));
              return (
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: DK }}>
                  {currentSpeaker}
                </p>
              );
            })()}
            <p style={{ margin: 0, fontSize: 11, color: MG }}>
              {clinic.c} — Earmedical
            </p>
          </div>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: OL, display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            {Ic.ear(O, 20)}
          </div>
        </div>

        {/* Messages list */}
        <div
          id="chat-messages-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
          ref={el => {
            if (el) setTimeout(() => el.scrollTop = el.scrollHeight, 50);
          }}
        >
          {chatMessages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: OL, display: "inline-flex",
                alignItems: "center", justifyContent: "center",
                marginBottom: 16,
              }}>
                {Ic.chat(O, 32)}
              </div>
              <p style={{ fontSize: 16, fontWeight: 800, color: DK, margin: "0 0 8px" }}>
                Γράψτε το πρώτο μήνυμα
              </p>
              <p style={{ fontSize: 13, color: MG, lineHeight: 1.5, margin: 0 }}>
                Ο ακοοπροθετιστής σας θα λάβει ειδοποίηση
                και θα απαντήσει το συντομότερο δυνατό.
              </p>
            </div>
          ) : (
            <>
              {chatMessages.map((m, i) => {
                const isMe = m.sender === 'patient';
                const showDate = i === 0 || chatMessages[i-1].created_at?.substring(0,10) !== m.created_at?.substring(0,10);
                const time = m.created_at ? new Date(m.created_at.replace(' ', 'T')).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' }) : '';
                const dateStr = m.created_at ? new Date(m.created_at.replace(' ', 'T')).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' }) : '';

                return (
                  <React.Fragment key={m.id || i}>
                    {showDate && (
                      <div style={{
                        textAlign: "center", margin: "12px 0 8px",
                        fontSize: 11, color: MG, fontWeight: 600,
                      }}>
                        {dateStr}
                      </div>
                    )}
                    <div style={{
                      display: "flex",
                      justifyContent: isMe ? "flex-end" : "flex-start",
                    }}>
                      <div style={{
                        maxWidth: "80%",
                        padding: "10px 14px",
                        borderRadius: isMe ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                        background: isMe ? O : WH,
                        color: isMe ? WH : DK,
                        fontSize: 14,
                        lineHeight: 1.5,
                        boxShadow: isMe ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                        wordBreak: "break-word",
                      }}>
                        {!isMe && (
                          <p style={{
                            margin: "0 0 2px", fontSize: 11,
                            fontWeight: 700, color: O, opacity: 0.9,
                          }}>
                            {m.sender_name || 'Ακοοπροθετιστής'}
                          </p>
                        )}
                        {/* v1.3.34 — Attachment display: inline image OR file chip */}
                        {m.file_url && (m.file_type || '').startsWith('image/') && (
                          <a href={m.file_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: m.message ? 6 : 0 }}>
                            <img src={m.file_url} alt={m.file_name || 'image'} style={{
                              maxWidth: '100%', maxHeight: 220, borderRadius: 12, display: 'block',
                              objectFit: 'cover', cursor: 'zoom-in',
                            }}/>
                          </a>
                        )}
                        {m.file_url && !(m.file_type || '').startsWith('image/') && (
                          <a href={m.file_url} target="_blank" rel="noopener noreferrer" style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 10px', borderRadius: 10,
                            background: isMe ? 'rgba(255,255,255,0.15)' : '#f0f0f0',
                            textDecoration: 'none', color: isMe ? WH : DK,
                            fontSize: 13, marginBottom: m.message ? 6 : 0,
                          }}>
                            <span style={{ display: 'flex', flexShrink: 0 }}>{(m.file_type || '').startsWith('video/') ? (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>) : (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>)}</span>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {m.file_name || 'Αρχείο'}
                            </span>
                            {m.file_size > 0 && (
                              <span style={{ fontSize: 11, opacity: 0.7 }}>
                                {(m.file_size / 1024 / 1024).toFixed(1)} MB
                              </span>
                            )}
                          </a>
                        )}
                        {m.message && <p style={{ margin: 0 }}>{m.message}</p>}
                        <p style={{
                          margin: "4px 0 0", fontSize: 10,
                          textAlign: "right",
                          color: isMe ? "rgba(255,255,255,0.7)" : MG,
                        }}>
                          {time}
                        </p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

              {/* Hint if no audiologist reply yet */}
              {chatMessages.length > 0 && chatMessages[chatMessages.length - 1].sender === 'patient' && (
                <p style={{
                  textAlign: "center", fontSize: 11, color: MG,
                  margin: "12px 0 0", fontStyle: "italic",
                }}>
                  {isClinicOpen(clinic).open
                    ? "Ο ειδικός σας θα απαντήσει σύντομα"
                    : `Το κέντρο είναι κλειστό — θα λάβετε απάντηση ${isClinicOpen(clinic).next ? isClinicOpen(clinic).next : "την επόμενη εργάσιμη"}`}
                </p>
              )}
            </>
          )}
        </div>

        {/* v1.3.34 — Attachment preview chip (shown above input when file selected) */}
        {chatAttachment && (
          <div style={{
            background: WH, padding: "8px 16px", borderTop: "1px solid #e8e8e8",
            display: "flex", alignItems: "center", gap: 10,
          }}>
            {chatAttachment.previewUrl && (chatAttachment.type || '').startsWith('image/') ? (
              <img src={chatAttachment.previewUrl} alt="preview" style={{
                width: 48, height: 48, borderRadius: 8, objectFit: "cover",
                opacity: chatAttachment.uploading ? 0.5 : 1,
              }}/>
            ) : (
              <div style={{
                width: 48, height: 48, borderRadius: 8, background: OL,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
              }}>{(chatAttachment.type || '').startsWith('video/') ? (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>) : (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>)}</div>
            )}
            <div style={{ flex: 1, fontSize: 12, overflow: "hidden" }}>
              <p style={{ margin: 0, fontWeight: 700, color: DK, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {chatAttachment.name}
              </p>
              <p style={{ margin: "2px 0 0", color: MG }}>
                {chatAttachment.uploading
                  ? "Ανέβασμα…"
                  : `${(chatAttachment.size / 1024 / 1024).toFixed(2)} MB · έτοιμο`}
              </p>
            </div>
            <button
              onClick={() => { if (chatAttachment.previewUrl) try { URL.revokeObjectURL(chatAttachment.previewUrl); } catch(e){}; setChatAttachment(null); }}
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                fontSize: 20, color: MG, padding: 4,
              }}
              aria-label="Αφαίρεση αρχείου"
            >✕</button>
          </div>
        )}

        {/* Input area */}
        <div style={{
          background: WH,
          padding: "12px 16px var(--em-nav-pad-bottom)",
          borderTop: chatAttachment ? "none" : "1px solid #e8e8e8",
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
          flexShrink: 0,
        }}>
          {/* v1.3.34 — Hidden file input + paperclip button (camera + gallery + files) */}
          <input
            ref={chatFileInputRef}
            type="file"
            accept="image/*,application/pdf,video/mp4,video/quicktime"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) uploadChatAttachment(f);
              e.target.value = ""; // allow re-selecting same file
            }}
          />
          <button
            onClick={() => chatFileInputRef.current && chatFileInputRef.current.click()}
            disabled={chatSending || chatUploading || !!chatAttachment}
            style={{
              width: 42, height: 42, borderRadius: "50%",
              background: "transparent",
              border: "1.5px solid " + (chatAttachment ? "#e0e0e0" : "#d0d0d0"),
              cursor: chatAttachment ? "default" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
            aria-label="Επισύναψη"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={chatAttachment ? MG : DK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>

          <textarea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendChatMessage();
              }
            }}
            placeholder={chatAttachment ? "Προαιρετικό σχόλιο…" : "Γράψτε μήνυμα..."}
            rows={1}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: 20,
              border: "1.5px solid #e0e0e0",
              fontSize: 16,  // 16px prevents iOS auto-zoom on focus
              outline: "none",
              resize: "none",
              fontFamily: "system-ui",
              lineHeight: 1.4,
              maxHeight: 100,
              minHeight: 42,
            }}
          />
          <button
            onClick={sendChatMessage}
            disabled={(!chatInput.trim() && !chatAttachment?.url) || chatSending || chatUploading}
            style={{
              width: 42, height: 42, borderRadius: "50%",
              background: (chatInput.trim() || chatAttachment?.url) ? O : LG,
              border: "none",
              cursor: (chatInput.trim() || chatAttachment?.url) ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.2s",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={(chatInput.trim() || chatAttachment?.url) ? WH : MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    );

  /* AI AGENT */
  if (sub === "ai")
    return (
      <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: BG, fontFamily: "system-ui" }}>
        {/* Header — v1.3.30 safe-area top fix */}
        <div style={{ background: WH, padding: "var(--em-header-pad-top) 16px 14px", borderBottom: "1px solid #e8e8e8", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <button onClick={() => setSub(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: DK }}>Ορφέας</p>
            <p style={{ margin: 0, fontSize: 11, color: MG }}>Ρωτήστε με οτιδήποτε — είμαι εδώ για εσάς</p>
          </div>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: OL, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
            </svg>
          </div>
        </div>

        {/* Phase A — handoff to specialist + booking, always available inside the Orfeas chat */}
        <div style={{ display: "flex", gap: 8, padding: "8px 12px", borderBottom: "1px solid #eee", background: "#FAFAFA", flexShrink: 0 }}>
          <button onClick={() => setSub("chat")} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px", border: "1.5px solid #e0e0e0", borderRadius: 10, background: WH, color: DK, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>
            {Ic.chat(DK, 15)} Μίλα με τον ειδικό
          </button>
          <button onClick={() => { setNewAReason("Ρύθμιση / Follow-up"); setSub("newappt"); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px", border: "none", borderRadius: 10, background: O, color: WH, fontWeight: 700, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit" }}>
            {Ic.cal(WH, 15)} Κλείσε ραντεβού
          </button>
        </div>

        {/* Messages */}
        <div
          style={{ flex: 1, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: 10 }}
          ref={el => { if (el) setTimeout(() => el.scrollTop = el.scrollHeight, 50); }}
        >
          {aiMessages.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 20px" }}>
              <div style={{ width: 72, height: 72, borderRadius: "50%", background: OL, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
              </div>
              <p style={{ fontSize: 17, fontWeight: 800, color: DK, margin: "0 0 8px" }}>Γεια σας{nm ? `, ${nm.split(" ")[0]}` : ''}!</p>
              <p style={{ fontSize: 14, color: DK2, lineHeight: 1.6, margin: "0 0 8px" }}>
                Ρωτήστε με <strong>οτιδήποτε</strong> για τα ακουστικά σας, την ακοή σας, τη χρήση, τη συντήρηση — ή οποιαδήποτε άλλη απορία.
              </p>
              <p style={{ fontSize: 12, color: MG, lineHeight: 1.5, margin: "0 0 20px", fontStyle: "italic" }}>
                Γράψτε ελεύθερα στο πλαίσιο κάτω ή δοκιμάστε μία από τις παρακάτω ιδέες:
              </p>
              {/* Quick suggestions */}
              <p style={{ fontSize: 11, color: MG, fontWeight: 700, margin: "0 0 10px", textTransform: "uppercase", letterSpacing: 0.5, textAlign: "left" }}>
                Ιδέες για να ξεκινήσετε
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, textAlign: "left" }}>
                {[
                  "Δεν ακούω καλά, τι να κάνω;",
                  "Πώς αλλάζω μπαταρία;",
                  "Πώς καθαρίζω τα ακουστικά μου;",
                  "Σφυρίζουν τα ακουστικά μου",
                  "Δεν συνδέεται το Bluetooth",
                ].map((q, i) => (
                  <button key={i} onClick={() => { setAiInput(q); setTimeout(() => { setAiInput(q); sendAiMessageDirect(q); }, 100); }} style={{
                    padding: "12px 16px", borderRadius: 14, border: "none",
                    background: WH, boxShadow: SH, cursor: "pointer",
                    fontSize: 13, color: DK, textAlign: "left", fontFamily: "inherit",
                    lineHeight: 1.4,
                  }}>
                    {q}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 12, color: MG, lineHeight: 1.5, margin: "18px 0 0", textAlign: "center" }}>
                Ή γράψτε τη δική σας ερώτηση παρακάτω
              </p>
            </div>
          ) : (
            <>
              {aiMessages.map((m, i) => (
                <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                  <div style={{
                    maxWidth: "85%",
                    padding: "12px 16px",
                    borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: m.role === "user" ? O : WH,
                    color: m.role === "user" ? WH : DK,
                    fontSize: 14,
                    lineHeight: 1.7,
                    boxShadow: m.role === "user" ? "none" : SH,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}>
                    {m.image && (
                      <img src={m.image} alt="" style={{ width: "100%", maxWidth: 220, borderRadius: 10, marginBottom: m.content ? 8 : 0, display: "block" }} />
                    )}
                    {m.content}
                  </div>
                </div>
              ))}
              {aiLoading && (
                <div style={{ display: "flex", justifyContent: "flex-start" }}>
                  <div style={{
                    padding: "12px 20px", borderRadius: "16px 16px 16px 4px",
                    background: WH, boxShadow: SH, fontSize: 14, color: MG,
                  }}>
                    <span style={{ animation: "pulse 1.5s infinite" }}>Σκέφτομαι...</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* v1.3.39 — Unified 2-step escalation panel.
            Replaces the old two-panel design (resolution-check + immediate
            escalation) which surfaced contact buttons before the patient
            confirmed their issue wasn't solved. New flow:

              Step 1 (default): "Σας βοήθησε ο ψηφιακός βοηθός;"
                ├─ "Είμαι εντάξει τώρα" → mood='good', happy thank-you message
                └─ "Συνεχίζω να έχω θέμα" → resolutionFeedback='still_issue'
                                              (NO mood event yet)
              Step 2 (after still_issue OR critical keywords): "Πώς να συνεχίσουμε;"
                ├─ "Μίλα με τον ειδικό" → mood='struggle' + open chat
                └─ "Κάλεσε το κέντρο"   → mood='struggle' + tel:

            Trigger conditions: purchased patient AND (3+ messages OR critical
            keyword OR AI mentioned human help). Hidden after resolution is
            either 'resolved' or 'needs_human'. */}
        {(() => {
          const userMsgs = aiMessages.filter(m => m.role === 'user');
          const allText = aiMessages.map(m => (m.content || '').toLowerCase()).join(' ');
          const criticalKeywords = [
            'πόνος', 'πονάει', 'αιμορραγ', 'ζαλάδ', 'ζάλη', 'ζαλίζομαι',
            'λοίμωξη', 'μολυσμ', 'ιώδες', 'ψώρα', 'ξαφνική απώλεια',
            'έπεσε', 'έσπασε', 'σπασμένο', 'χτύπησε', 'βραχυκύκλω',
            'δεν ανοίγει καθόλου', 'απογοητευ', 'δεν βοηθάει',
            'χειρότερα', 'να τα επιστρέψω', 'θα τα επιστρέψω',
            'απαίσι', 'χάλια', 'τρομερό', 'ανυπόφορ',
          ];
          const hasCritical = criticalKeywords.some(k => allText.includes(k));
          // v1.3.59 — ΜΟΝΟ ισχυρές φράσεις παραπομπής (ο Ορφέας τις λέει όταν ΔΕΝ μπορεί να
          // λύσει το θέμα) — όχι γενικές λέξεις (ραντεβού/ρύθμιση) που εμφανίζονται casually.
          const aiHelpKeywords = [
            'ειδικό σας', 'ειδικός σας', 'ακοοπροθετιστή σας', 'ακοοπροθετιστής σας',
            'τεχνικό έλεγχο', 'άμεση εξέταση', 'από κοντά', 'να σας δει', 'να σας εξετάσει',
            'επισκεφθείτε το κέντρο', 'στο κέντρο μας', 'να το δει ο ειδικός', 'χρειάζεται έλεγχος',
          ];
          const aiMsgs = aiMessages.filter(m => m.role === 'assistant').map(m => (m.content || '').toLowerCase());
          const aiMentionsHelp = aiMsgs.some(t => aiHelpKeywords.some(k => t.includes(k)));

          // Hide after the patient has resolved the chat in either direction
          if (resolutionFeedback === 'resolved' || resolutionFeedback === 'needs_human') return null;

          // v1.3.59 — AI-driven: εμφανίζεται ΜΟΝΟ όταν ο Ορφέας παραπέμπει στον ειδικό
          // (aiMentionsHelp) ή σε κρίσιμα keywords — ΟΧΙ απλώς επειδή έγιναν 3 μηνύματα.
          const shouldShow = (patientStatus === 'purchased' || patientStatus === 'support') &&
            (hasCritical || aiMentionsHelp);
          if (!shouldShow) return null;

          // v1.3.59 — Πάμε ΚΑΤΕΥΘΕΙΑΝ σε παραπομπή στον ειδικό της πόλης (χωρίς το ενδιάμεσο
          // «Σας βοήθησε ο Ορφέας;») — η κλιμάκωση είναι πλέον AI-driven, σαν τον Πάρη στο Soundz.
          const showContactOptions = true;

          // Shared helper: fire a struggle mood + a custom comment/source then run an action.
          const fireStruggle = async (comment, source, after) => {
            let dayNum = 0;
            if (sd) {
              try {
                const fd = new Date(sd);
                if (!isNaN(fd)) dayNum = Math.max(1, Math.floor((Date.now() - fd.getTime()) / 86400000) + 1);
              } catch(e) {}
            }
            try {
              await fetch(API_URL + '/daily-checkin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  prof_id: profId,
                  patient_phone: ph2,
                  mood: 'struggle',
                  comment,
                  day: dayNum,
                  source,
                }),
              });
            } catch(e) { console.error('Struggle mood error:', e); }
            setResolutionFeedback('needs_human');
            if (typeof after === 'function') after();
          };

          // v1.3.40 — Helper: log "still has issue" mood when the patient
          // confirms the AI didn't solve it. Returns a Promise so the caller
          // can await before transitioning to Step 2.
          const fireStillIssue = async () => {
            let dayNum = 0;
            if (sd) {
              try {
                const fd = new Date(sd);
                if (!isNaN(fd)) dayNum = Math.max(1, Math.floor((Date.now() - fd.getTime()) / 86400000) + 1);
              } catch(e) {}
            }
            try {
              await fetch(API_URL + '/daily-checkin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  prof_id: profId,
                  patient_phone: ph2,
                  mood: 'struggle',
                  comment: 'Ο πελάτης συνεχίζει να έχει θέμα μετά τη συζήτηση με τον ψηφιακό βοηθό',
                  day: dayNum,
                  source: 'ai_still_issue',
                }),
              });
            } catch(e) { console.error('Still-issue mood error:', e); }
          };

          if (showContactOptions) {
            // Did "Συνεχίζω" already fire the mood event? If yes (non-critical
            // path), don't double-fire when patient picks Μίλα or Κάλεσε. If
            // the user landed here via critical-keyword detection, the
            // current resolutionFeedback is still '' so we fire on action.
            const moodAlreadyFired = resolutionFeedback === 'still_issue';
            // v1.3.59 — Ο ονομαστικός ειδικός που παρακολουθεί τον πελάτη + η πόλη του.
            const specName = (audiologist && audiologist.trim()) ? audiologist.trim().split(' ')[0] : '';
            const escClinic = getClinicById(clinicId);
            const cityName = (escClinic && escClinic.c) || '';
            const onChat = async () => {
              if (!moodAlreadyFired) await fireStruggle(
                'Ζήτησε chat με τον ειδικό (AI escalation)',
                'ai_escalation_chat',
              );
              setResolutionFeedback('needs_human');
              setSub('chat');
            };
            const onCall = async () => {
              if (!moodAlreadyFired) await fireStruggle(
                'Κάλεσε το κέντρο (AI escalation)',
                'ai_escalation_call',
              );
              setResolutionFeedback('needs_human');
              const phone = (escClinic && escClinic.t) || '';
              if (phone) window.open(`tel:${phone}`);
            };

            return (
              <div style={{
                background: hasCritical ? "#FFEBEE" : "#FFF8F0",
                borderTop: `1px solid ${hasCritical ? "#FFCDD2" : "#F5E6D0"}`,
                padding: "20px 16px",
                flexShrink: 0,
              }}>
                <div style={{ maxWidth: 380, margin: "0 auto" }}>
                  <p style={{
                    margin: "0 0 14px",
                    fontSize: 14,
                    color: hasCritical ? "#C62828" : DK,
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: "-0.2px",
                    lineHeight: 1.35,
                  }}>
                    {hasCritical
                      ? `Καλό είναι να επικοινωνήσετε άμεσα με τον ειδικό σας${specName ? ', ' + specName : ''}.`
                      : `${specName ? specName + ', ' : ''}ο ειδικός σας${cityName ? ' (' + cityName + ')' : ''} μπορεί να σας βοηθήσει από εδώ.`}
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <button
                      onClick={onChat}
                      style={{
                        width: "100%", padding: "14px 18px", borderRadius: 14,
                        border: `1.5px solid ${O}`, background: WH, color: O,
                        fontSize: 15, fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                        boxShadow: "0 1px 4px rgba(239,128,26,0.10)",
                        transition: "transform 0.1s, box-shadow 0.2s",
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                      </svg>
                      Μίλα με {specName || 'τον ειδικό'}
                    </button>
                    <button
                      onClick={onCall}
                      style={{
                        width: "100%", padding: "14px 18px", borderRadius: 14,
                        border: "none", background: O, color: WH,
                        fontSize: 15, fontWeight: 700, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                        boxShadow: "0 2px 8px rgba(239,128,26,0.30)",
                        transition: "transform 0.1s, box-shadow 0.2s",
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
                      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                      </svg>
                      Κάλεσε το κέντρο
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          // Step 1: resolution question
          return (
            <div style={{
              background: "#F0F9F0",
              borderTop: "1px solid #C8E6C9",
              padding: "20px 16px",
              flexShrink: 0,
            }}>
              <div style={{ maxWidth: 380, margin: "0 auto" }}>
                <p style={{
                  margin: "0 0 14px",
                  fontSize: 15,
                  color: DK,
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: "-0.2px",
                  lineHeight: 1.35,
                }}>
                  Σας βοήθησε ο Ορφέας;
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <button
                    onClick={async () => {
                      setResolutionFeedback('resolved');
                      let dayNum = 0;
                      if (sd) {
                        try {
                          const fd = new Date(sd);
                          if (!isNaN(fd)) dayNum = Math.max(1, Math.floor((Date.now() - fd.getTime()) / 86400000) + 1);
                        } catch(e) {}
                      }
                      try {
                        await fetch(API_URL + '/daily-checkin', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            prof_id: profId,
                            patient_phone: ph2,
                            mood: 'good',
                            comment: 'Επίλυση μέσω ψηφιακού βοηθού',
                            day: dayNum,
                            source: 'ai_resolution',
                          }),
                        });
                      } catch(e) { console.error('Resolution mood error:', e); }
                      setAiMessages(prev => [...prev, {
                        role: 'assistant',
                        content: 'Χαίρομαι που σας βοήθησα! Η εμπειρία σας έχει ενημερωθεί στο σύστημα. Μη διστάσετε να επιστρέψετε ανά πάσα στιγμή για βοήθεια.',
                      }]);
                    }}
                    style={{
                      width: "100%", padding: "14px 18px", borderRadius: 14,
                      border: "none", background: "#2E7D32", color: WH,
                      fontSize: 15, fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                      boxShadow: "0 2px 8px rgba(46,125,50,0.25)",
                      transition: "transform 0.1s, box-shadow 0.2s",
                    }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Είμαι εντάξει τώρα
                  </button>
                  <button
                    onClick={async () => {
                      // v1.3.40 — Fire 'struggle' mood the moment the patient
                      // confirms their issue isn't solved. Audiologist sees
                      // this immediately even before patient picks chat or
                      // phone in Step 2. Then reveal contact options.
                      await fireStillIssue();
                      setResolutionFeedback('still_issue');
                    }}
                    style={{
                      width: "100%", padding: "14px 18px", borderRadius: 14,
                      border: `1.5px solid ${O}`, background: WH, color: O,
                      fontSize: 15, fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                      boxShadow: "0 1px 4px rgba(239,128,26,0.10)",
                      transition: "transform 0.1s, box-shadow 0.2s",
                    }}
                    onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.98)"; }}
                    onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    Συνεχίζω να έχω θέμα
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Input — v1.3.57: κείμενο + εικόνα προς αξιολόγηση + φωνή→κείμενο */}
        <div style={{ background: WH, padding: "10px 12px var(--em-nav-pad-bottom)", borderTop: "1px solid #e8e8e8", flexShrink: 0 }}>
          {/* Preview εικόνας προς αποστολή */}
          {aiImage && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", marginBottom: 8, background: OL, border: "1px solid #F5E6D0", borderRadius: 12 }}>
              <img src={aiImage.url} alt="" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 12.5, color: DK2, lineHeight: 1.35 }}>Εικόνα έτοιμη — γράψτε ή πατήστε αποστολή για αξιολόγηση</span>
              <button onClick={() => setAiImage(null)} aria-label="Αφαίρεση εικόνας" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          )}
          {/* Ένδειξη ηχογράφησης / μεταγραφής */}
          {(aiRecording || aiTranscribing) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 6px 8px", color: aiRecording ? "#C62828" : O, fontSize: 12.5, fontWeight: 700 }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: aiRecording ? "#C62828" : O, animation: "pulse 1.2s infinite", flexShrink: 0 }} />
              {aiRecording ? "Ηχογράφηση… πατήστε ξανά το μικρόφωνο για τέλος" : "Μεταγραφή σε κείμενο…"}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <input ref={aiFileInputRef} type="file" accept="image/*" onChange={onAiImageSelected} style={{ display: "none" }} />
            {/* Ανέβασμα εικόνας */}
            <button
              onClick={() => aiFileInputRef.current && aiFileInputRef.current.click()}
              disabled={aiLoading}
              aria-label="Ανέβασμα εικόνας για αξιολόγηση"
              style={{ width: 42, height: 42, borderRadius: "50%", background: LG, border: "none", cursor: aiLoading ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            >
              <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke={DK2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
              </svg>
            </button>
            <textarea
              ref={aiTextRef}
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAiMessage(); } }}
              placeholder="Γράψτε, ανεβάστε εικόνα ή μιλήστε…"
              rows={1}
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 18,
                border: "1.5px solid #e5e5e5", fontSize: 16, outline: "none",  // 16px prevents iOS auto-zoom
                resize: "none", fontFamily: "system-ui", lineHeight: 1.4,
                maxHeight: 120, minHeight: 42, overflowY: "auto", boxSizing: "border-box",
              }}
            />
            {/* Ηχογράφηση (φωνή → κείμενο) */}
            <button
              onClick={aiRecording ? aiStopRecording : aiStartRecording}
              disabled={aiLoading || aiTranscribing}
              aria-label={aiRecording ? "Τέλος ηχογράφησης" : "Ηχογράφηση φωνής"}
              style={{ width: 42, height: 42, borderRadius: "50%", background: aiRecording ? "#C62828" : LG, border: "none", cursor: (aiLoading || aiTranscribing) ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.2s" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={aiRecording ? WH : DK2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 11v1a7 7 0 0 1-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
              </svg>
            </button>
            {/* Αποστολή */}
            <button
              onClick={sendAiMessage}
              disabled={(!aiInput.trim() && !aiImage) || aiLoading}
              style={{
                width: 42, height: 42, borderRadius: "50%",
                background: (aiInput.trim() || aiImage) && !aiLoading ? O : LG,
                border: "none", cursor: (aiInput.trim() || aiImage) && !aiLoading ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "background 0.2s",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={(aiInput.trim() || aiImage) && !aiLoading ? WH : MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      </div>
    );

  /* TABS
     v1.3.17 — Πρόοδος αφαιρέθηκε από το footer (έμενε λίγο χρησιμοποιημένο
     στις μετρήσεις) και αντικαταστάθηκε από το chat ("Μίλα με τον ειδικό").
     Το chat δεν είναι top-level tab — είναι sub-view του home — οπότε στο
     onClick handler κάτω, αν patεθεί η chat καρτέλα κάνουμε
     setTab("home") + setSub("chat") αντί για το default setTab(id). */
  // v1.3.24 — Inactive icon color από MG (#8a8a8a) → DK2 (#4a4a4a) για
  //           ταιριαστό contrast με τα label colors. Στις screenshots
  //           Android, το MG ήταν σχεδόν αόρατο.
  // v1.3.31 — Prospect tabs: Home · Chat · Ραντεβού · Υπηρεσίες · Μενού.
  // Δεν βλέπει Κατάστημα (δεν έχει αγοράσει). Έχει "Υπηρεσίες" tab με
  // ενημερωτικές κάρτες για το τι κάνει η Earmedical.
  // v1.3.56 — Ο Ορφέας γίνεται ΚΥΡΙΑΡΧΟ κεντρικό tab (raised orange mic). Ο ειδικός (human chat)
  // φεύγει από τη μπάρα (παραμένει στην αρχική «Μίλα με τον ειδικό» + το unread badge πέφτει στο «Μενού»).
  const tabs = isProspect ? [
    { id: "home",     ic: (a) => Ic.hm(a ? O : DK2, 24),   l: "Αρχική" },
    { id: "cal",      ic: (a) => Ic.cal(a ? O : DK2, 24),  l: "Ραντεβού" },
    { id: "ai",       orfeas: true,                          l: "Ορφέας" },
    { id: "services", ic: (a) => Ic.bag(a ? O : DK2, 24),  l: "Υπηρεσίες" },
    { id: "more",     ic: (a) => Ic.dot(a ? O : DK2, 24),  l: "Μενού" },
  ] : [
    { id: "home", ic: (a) => Ic.hm(a ? O : DK2, 24), l: "Αρχική" },
    { id: "cal", ic: (a) => Ic.cal(a ? O : DK2, 24), l: "Ραντεβού" },
    { id: "ai",  orfeas: true,                        l: "Ορφέας" },
    ...((!isTrial || isSupport) ? [{ id: "shop", ic: (a) => Ic.bag(a ? O : DK2, 24), l: "Κατάστημα" }] : []),
    { id: "more", ic: (a) => Ic.dot(a ? O : DK2, 24), l: "Μενού" },
  ];
  return (
    <div
      style={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: BG,
        fontFamily: "system-ui",
        position: "relative",
      }}
    >
      <div
        ref={ref}
        style={{
          flex: 1,
          overflowY: "auto",
          zoom: 1 + fs * 0.15,
          // v1.3.29 — Bulletproof: max() guarantees ≥32px regardless of what
          // MainActivity injects. Πιάνει την περίπτωση που --em-safe-top
          // περάστηκε ως "0px" (broken edge-to-edge detection) — ο var()
          // fallback chain ΔΕΝ θα έπιανε το env() ή το hardcoded fallback
          // αν το var ορίστηκε με τιμή "0px".
          paddingTop: "var(--em-header-pad-top-raw)",
        }}
      >
        {/* v1.3.63 — Store-download banner: εμφανίζεται ΜΟΝΟ στο PWA/web (όχι στο native app),
            ώστε όπου χρησιμοποιείται το PWA να προτείνονται τα App Store / Google Play. Dismissible. */}
        {tab === "home" && !isNativeApp && !storeBannerDismissed && (
          <div style={{ margin: "12px 20px 0", padding: "12px 14px", background: OL, border: "1px solid #F1E2CE", borderRadius: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: "0 0 9px", fontSize: 13, fontWeight: 700, color: DK, lineHeight: 1.35 }}>Αποκτήστε την πλήρη εφαρμογή Earmedical</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <a href="https://apps.apple.com/gr/app/earmedical/id6771120582" target="_blank" rel="noopener" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#1A1A1B", color: WH, textDecoration: "none", padding: "8px 13px", borderRadius: 10, fontSize: 12.5, fontWeight: 700 }}>
                  <svg width="15" height="15" viewBox="0 0 384 512" fill={WH} aria-hidden="true"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                  App Store
                </a>
                <a href="https://play.google.com/store/apps/details?id=gr.earmedical.app" target="_blank" rel="noopener" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#1A1A1B", color: WH, textDecoration: "none", padding: "8px 13px", borderRadius: 10, fontSize: 12.5, fontWeight: 700 }}>
                  <svg width="14" height="15" viewBox="0 0 24 24" fill={WH} aria-hidden="true"><path d="M4 2.4c-.5.27-.8.79-.8 1.45v16.3c0 .66.3 1.18.8 1.45l9.3-9.6zm11.1 6.9L6.2 4.1l9.9 5.7zm0 5.4-2.97-2.7 2.97-2.7 3.6 2.08c.86.5.86 1.74 0 2.24zM6.2 19.9l8.9-5.2-1.8-1.75z"/></svg>
                  Google Play
                </a>
              </div>
            </div>
            <button onClick={() => { setStoreBannerDismissed(true); try { localStorage.setItem('em_store_banner_dismissed', '1'); } catch(e) {} }} aria-label="Κλείσιμο" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", flexShrink: 0, alignSelf: "flex-start" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        )}
        {/* Soft-ask ειδοποιήσεων (web/PWA) — branded priming πριν το browser prompt */}
        <PushSoftAsk active={tab === "home"} />
        {/* HOME */}
        {/* HOME — v1.3.31 — Prospect παίρνει ξεχωριστό απλοποιημένο view:
            καλωσόρισμα, 5 service shortcuts, μέγεθος γραμμάτων κουμπί,
            ραντεβού shortcut. ΔΕΝ βλέπει trial countdown, mood, fittings. */}
        {tab === "home" && isProspect && (
          <div>
            <div style={{ padding: "16px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <EarLogo h={75} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setSub("notifs")}
                  aria-label="Ειδοποιήσεις"
                  style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: 30 }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {notifsUnread > 0 && (<span style={{ position: "absolute", top: -4, right: -4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{notifsUnread > 9 ? "9+" : notifsUnread}</span>)}
                </button>
                <button onClick={() => setFs(f => f >= 2 ? 0 : f + 1)} style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", color: DK, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{fs > 0 ? "A−" : "A+"}</button>
              </div>
            </div>

            {updateBannerEl}

            <div style={{ padding: "12px 20px 0" }}>
              <p style={{ fontSize: 12, color: MG, margin: 0, textTransform: "uppercase", letterSpacing: 0.4 }}>{(new Date()).toLocaleDateString("el-GR", { weekday: "long", day: "numeric", month: "long" })}</p>
              <p style={{ fontSize: 22, fontWeight: 800, color: DK, margin: "2px 0 16px" }}>
                Καλωσήρθατε{nm ? `, ${nm.split(" ")[0]}` : ""}
              </p>

              {/* Hero card */}
              <div style={{ background: O, borderRadius: 18, padding: 20, color: WH, marginBottom: 18, boxShadow: "0 8px 24px rgba(255,130,1,0.18)" }}>
                <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 600, opacity: 0.9 }}>EARMEDICAL · ΓΙΑ ΕΣΑΣ</p>
                <p style={{ margin: 0, fontSize: 19, fontWeight: 800, lineHeight: 1.35 }}>
                  Φροντίζουμε την ακοή σας, με επαγγελματισμό και διακριτικότητα.
                </p>
                <p style={{ margin: "10px 0 0", fontSize: 13, opacity: 0.95, lineHeight: 1.5 }}>
                  Κλείστε δωρεάν ραντεβού για αξιολόγηση, ενημέρωση ή δοκιμή.
                </p>
              </div>

              <p style={{ fontSize: 16, fontWeight: 800, color: DK, margin: "4px 0 10px" }}>Οι υπηρεσίες μας</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
                {PROSPECT_SERVICES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setNewAReason(s.title); setSub("newappt"); }}
                    style={{
                      background: WH, border: "1px solid #ececec", borderRadius: 14,
                      padding: 14, textAlign: "left", cursor: "pointer",
                      display: "flex", flexDirection: "column", gap: 6,
                      fontFamily: "inherit",
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: OL, display: "flex",
                      alignItems: "center", justifyContent: "center",
                    }}>{s.Icon(22, O)}</div>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: DK, lineHeight: 1.25 }}>{s.title}</p>
                    <p style={{ margin: 0, fontSize: 11, color: MG, lineHeight: 1.35 }}>{s.short}</p>
                  </button>
                ))}
              </div>

              <Btn onClick={() => setTab("services")} primary={false} s={{ marginBottom: 10 }}>
                Δες όλες τις υπηρεσίες σε αναλυτική μορφή
              </Btn>
              <Btn onClick={() => setTab("cal")}>
                {Ic.cal("white", 16)} Δες τα ραντεβού μου
              </Btn>
            </div>
            <div style={{ height: 24 }} />
          </div>
        )}
        {tab === "home" && !isProspect && (
          <div>
            <div
              style={{
                padding: "16px 20px 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <EarLogo h={75} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setSub("notifs")}
                  aria-label="Ειδοποιήσεις"
                  style={{
                    background: LG,
                    border: "none",
                    borderRadius: 10,
                    padding: "6px 10px",
                    cursor: "pointer",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 30,
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  {notifsUnread > 0 && (
                    <span style={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      background: "#E53935",
                      color: "white",
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: 99,
                      minWidth: 16,
                      height: 16,
                      padding: "0 4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                    }}>
                      {notifsUnread > 9 ? "9+" : notifsUnread}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setFs((f) => (f >= 2 ? 0 : f + 1))}
                  style={{
                    background: LG,
                    border: "none",
                    borderRadius: 10,
                    padding: "6px 10px",
                    color: DK,
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  {fs > 0 ? "A−" : "A+"}
                </button>
              </div>
            </div>
            <p
              style={{
                padding: "4px 20px 0",
                fontSize: 12,
                color: MG,
                margin: 0,
                textTransform: "capitalize",
              }}
            >
              {new Date().toLocaleDateString("el-GR", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </p>

            {updateBannerEl}

            {/* ─── v1.3.58 — Orfeas Moment: proactive διαδραστική κάρτα (tenure/συντήρηση/επανέλεγχος/γιορτές) ─── */}
            {orfeasMoment && (
              <div style={{ margin: "12px 20px 0", padding: "18px 20px", background: WH, borderRadius: 18, boxShadow: SH, animation: "emMomentIn 0.45s ease both" }}>
                {momentBody(orfeasMoment)}
                <style>{`@keyframes emMomentIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>
              </div>
            )}

            {/* ─── Offers Banner (sent offers that patient hasn't decided on) ─── */}
            {activeSentOffers.length > 0 && (() => {
              const offer = activeSentOffers[0]; // Show most recent
              const items = offer.items || [];
              const recommended = items.find(it => it.is_recommended) || items[0];
              const otherCount = items.length - 1;
              const hasUnseen = !offerSeen.includes(offer.id);
              // Personalized title
              const bannerTitle = isSupport
                ? "Προτάσεις για εσάς"
                : (isTrial ? "Οι προτάσεις σας" : "Νέες επιλογές");

              return (
                <div
                  onClick={openOffersScreen}
                  style={{
                    margin: "12px 20px 0",
                    padding: "14px 14px 12px",
                    background: `linear-gradient(135deg, ${O}, #D85F00)`,
                    borderRadius: 14,
                    cursor: "pointer",
                    color: WH,
                    boxShadow: hasUnseen ? `0 4px 20px ${O}50` : `0 2px 8px ${O}30`,
                    animation: hasUnseen ? "earOfferPulse 2s ease-in-out infinite" : "none",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {hasUnseen && (
                    <span style={{
                      position: "absolute", top: 10, right: 12,
                      background: WH, color: O, padding: "2px 8px",
                      borderRadius: 10, fontSize: 10, fontWeight: 800,
                      letterSpacing: 0.5,
                    }}>ΝΕΟ</span>
                  )}
                  <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.9, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>
                    {bannerTitle}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.3, marginBottom: 8 }}>
                    Ο ακοοπροθετιστής σας ετοίμασε {items.length === 1 ? "πρόταση" : `${items.length} επιλογές`} για εσάς
                  </div>
                  {recommended && (
                    <div style={{
                      background: "rgba(255,255,255,0.18)",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12 }}>
                          Προτεινόμενο: {recommended.brand} {recommended.model}
                        </div>
                        {otherCount > 0 && (
                          <div style={{ fontSize: 11, opacity: 0.85 }}>
                            + {otherCount === 1 ? "1 ακόμα επιλογή" : `${otherCount} ακόμα επιλογές`}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontSize: 12, fontWeight: 700,
                  }}>
                    <span>Δείτε τις προτάσεις →</span>
                  </div>
                </div>
              );
            })()}

            {/* ─── Install PWA banner (discreet, dismissable) ─── */}
            {!isAppInstalled && !installBannerDismissed && (
              <div
                onClick={handleInstallApp}
                style={{
                  margin: "12px 20px 0",
                  padding: "10px 12px",
                  background: `linear-gradient(135deg, ${O}15, ${O}08)`,
                  border: `1px solid ${O}30`,
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: O, display: "flex",
                  alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v13M5 9l7-7 7 7"/>
                    <path d="M3 15v4a2 2 0 002 2h14a2 2 0 002-2v-4"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: "0 0 1px", fontSize: 12, fontWeight: 800, color: DK }}>
                    Εγκατάσταση εφαρμογής
                  </p>
                  <p style={{ margin: 0, fontSize: 10, color: DK2, lineHeight: 1.3 }}>
                    Πατήστε για πρόσβαση από την αρχική οθόνη
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); dismissInstallBanner(); }}
                  style={{
                    background: "transparent", border: "none",
                    padding: 4, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: MG, flexShrink: 0,
                  }}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
            )}

            {(nm || true) && (
              <p
                style={{
                  padding: "2px 20px 0",
                  fontSize: 16,
                  fontWeight: 700,
                  color: DK,
                  margin: 0,
                }}
              >
                {nm ? `Καλημέρα, ${nm.split(" ")[0]}` : "Καλημέρα σας"}
              </p>
            )}
            <div style={{ padding: "16px 20px" }}>
              {/* v1.3.44 — Admin Announcement banner (top-1 by priority + recency).
                  Tap → setSub("announcement-detail") shows full content with back arrow.
                  Auto-dismissed locally when user dismisses; admin archive also hides it. */}
              {topAnnouncement && (() => {
                const a = topAnnouncement;
                const pri = ANN_PRIORITY_META[a.priority] || ANN_PRIORITY_META.medium;
                return (
                  <div
                    onClick={() => { logAnnouncementEvent(a.id, 'opened', { source: 'banner' }); setSelectedAnnouncement(a); setSub("announcement-detail"); }}
                    style={{
                      position: "relative",
                      background: WH,
                      border: `1px solid ${pri.color}20`,
                      borderRadius: 14,
                      padding: "16px 18px 14px",
                      marginBottom: 16,
                      cursor: "pointer",
                      boxShadow: `0 2px 12px ${pri.color}15, 0 1px 3px rgba(0,0,0,0.04)`,
                    }}
                  >
                    {/* v1.3.47 — X dismiss button (top-right, doesn't trigger card click) */}
                    <button
                      onClick={(e) => { e.stopPropagation(); dismissAnnouncement(a.id); }}
                      aria-label="Απόκρυψη"
                      style={{
                        position: "absolute", top: 8, right: 8,
                        width: 28, height: 28, borderRadius: 99,
                        background: "transparent", border: "none",
                        cursor: "pointer", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        color: "#999", padding: 0,
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>

                    {/* Top row: category-driven label pill (color from priority) */}
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: pri.bg, color: pri.color, padding: "3px 10px", borderRadius: 99, fontSize: 10, fontWeight: 800, letterSpacing: 0.3, marginBottom: 10, textTransform: "uppercase" }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m3 11 18-5v12L3 14v-3z" />
                        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
                      </svg>
                      <span>{getAnnouncementLabel(a)}</span>
                    </div>

                    {/* Title */}
                    <div style={{ fontSize: 16, fontWeight: 800, color: DK, lineHeight: 1.3, marginBottom: 6, paddingRight: 24 }}>
                      {a.title}
                    </div>

                    {/* Body preview */}
                    <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5, marginBottom: 12 }}>
                      {a.body.length > 110 ? a.body.substring(0, 110) + "…" : a.body}
                    </div>

                    {/* CTA bar */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 10, borderTop: "1px solid #F0F0F0" }}>
                      <span style={{ fontSize: 12, color: O, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        Δείτε περισσότερα
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                );
              })()}
              {/* ─── TRIAL HOME ─── */}
              {isTrial && !trialExpired && (
                <>
                  {/* ━━━ SECTION 1: HERO — Countdown ━━━ */}
                  <C s={{ background: `linear-gradient(135deg,${O},#D06A0E)`, border: "none", marginBottom: 16, textAlign: "center", padding: "22px 20px" }}>
                    <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.65)", letterSpacing: 1.5 }}>ΔΩΡΕΑΝ ΔΟΚΙΜΗ</p>
                    <p style={{ margin: "0 0 4px", fontSize: 48, fontWeight: 800, color: WH, letterSpacing: -1 }}>{trialDaysLeft}</p>
                    <p style={{ margin: "0 0 14px", fontSize: 14, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>
                      {trialDaysLeft === 1 ? "ημέρα ακόμη χωρίς καμία υποχρέωση" : "ημέρες ακόμη χωρίς καμία υποχρέωση"}
                    </p>
                    <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: 10, height: 4, overflow: "hidden" }}>
                      <div style={{ width: `${trialProgress}%`, height: "100%", background: WH, borderRadius: 10, transition: "width 0.5s" }} />
                    </div>
                  </C>

                  {/* v1.3.41 — Smart Quiz Prompt for trial users. Replaces the
                      previously-gated card that only showed on checkpoint days
                      (3/7/14/21 etc). Trial patients give us critical signal so
                      we surface the questionnaire always, with intensity
                      adapting to how long ago they last filled. */}
                  {renderQuizPromptCard()}

                  {/* ━━━ SECTION 2: DAILY GUIDANCE ━━━ */}
                  <C s={{ background: WH, marginBottom: 16, padding: "20px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: OL, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {Ic.ear(O, 15)}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.5 }}>
                        {trialDay <= Math.ceil(trialDays * 0.35) ? "ΠΡΟΣΑΡΜΟΓΗ" : trialDay <= Math.ceil(trialDays * 0.7) ? "ΕΞΟΙΚΕΙΩΣΗ" : "ΑΞΙΟΛΟΓΗΣΗ"}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: 15, color: DK, lineHeight: 1.8 }}>
                      {trialDay <= 3
                        ? "Οι πρώτες ημέρες είναι καθοριστικές. Φοράτε τα ακουστικά σας όσο περισσότερο μπορείτε μέσα στην ημέρα, ώστε ο εγκέφαλος να προσαρμοστεί πιο φυσικά."
                        : trialDay <= 7
                        ? "Δοκιμάστε τα ακουστικά σας σε διαφορετικά περιβάλλοντα — συζήτηση, τηλεόραση, βόλτα. Κάθε νέα εμπειρία βοηθά τον εγκέφαλο να ξαναμάθει ήχους."
                        : trialDay <= 14
                        ? "Μετά από μία εβδομάδα χρήσης, αρχίζετε να αντιλαμβάνεστε τη διαφορά στην καθημερινότητά σας. Συνεχίστε με σταθερότητα."
                        : "Έχετε χτίσει μια σταθερή συνήθεια. Αν κάτι δεν σας ικανοποιεί, μια μικρή ρύθμιση μπορεί να κάνει μεγάλη διαφορά."}
                    </p>
                  </C>

                  {/* Πώς πάτε σήμερα; — 1-tap daily checkin */}
                  {!dailySent && !dailyMood && (
                    <C s={{ background: WH, marginBottom: 16 }}>
                      <p style={{ margin: "0 0 14px", fontSize: 16, fontWeight: 700, color: DK }}>Πώς πάτε σήμερα;</p>
                      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                        {[
                          { id: "great",    icon: (c) => Ic.smile(c, 28),     label: "Πολύ καλά" },
                          { id: "good",     icon: (c) => Ic.smileSoft(c, 28), label: "Καλά" },
                          { id: "ok",       icon: (c) => Ic.meh(c, 28),       label: "Μέτρια" },
                          { id: "struggle", icon: (c) => Ic.frown(c, 28),     label: "Δυσκολεύομαι" },
                        ].map(m => (
                          <button key={m.id} onClick={() => { setDailyMood(m.id); submitDailyCheckin(m.id); }} style={{
                            display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
                            padding: "16px 8px", borderRadius: 16, border: "1.5px solid #E8E8E8",
                            background: WH, cursor: "pointer", flex: 1,
                            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                            transition: "transform 0.1s, box-shadow 0.15s",
                          }}>
                            {m.icon(DK)}
                            <span style={{ fontSize: 11, color: DK2, fontWeight: 600 }}>{m.label}</span>
                          </button>
                        ))}
                      </div>
                    </C>
                  )}

                  {/* Reactive feedback — Πολύ καλά */}
                  {dailyMood === "great" && (
                    <C s={{ background: "#F0F7F0", marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        {Ic.chk("#2E7D32", 20)}
                        <p style={{ margin: 0, fontSize: 13, color: DK, lineHeight: 1.6 }}>
                          Χαιρόμαστε πολύ! Συνεχίστε τη σταθερή χρήση — η πρόοδός σας είναι εξαιρετική.
                        </p>
                      </div>
                    </C>
                  )}

                  {/* Reactive feedback — Καλά */}
                  {dailyMood === "good" && (
                    <C s={{ background: OL, marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        {Ic.smileSoft(O, 20)}
                        <p style={{ margin: 0, fontSize: 13, color: DK, lineHeight: 1.6 }}>
                          Πολύ ωραία! Αν κάτι σας δυσκολεύει, σημειώστε το για να το συζητήσουμε στην επόμενη ρύθμιση.
                        </p>
                      </div>
                    </C>
                  )}

                  {/* Reactive feedback — Μέτρια */}
                  {dailyMood === "ok" && (
                    <C s={{ background: OL, marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                        {Ic.al(O, 20)}
                        <p style={{ margin: 0, fontSize: 13, color: DK, lineHeight: 1.6 }}>
                          Αυτό είναι φυσιολογικό στην αρχή. Μια μικρή ρύθμιση μπορεί να κάνει μεγάλη διαφορά.
                        </p>
                      </div>
                    </C>
                  )}

                  {/* Reactive feedback — Δυσκολεύομαι */}
                  {dailyMood === "struggle" && (
                    <C s={{ background: "#FFF5F5", marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
                        {Ic.al("#C62828", 20)}
                        <p style={{ margin: 0, fontSize: 13, color: DK, lineHeight: 1.6 }}>
                          Μην ανησυχείτε — πείτε μου τι συμβαίνει και θα προσπαθήσω να σας βοηθήσω αμέσως. Αν χρειαστεί, σας συνδέω με τον ειδικό σας ή κλείνουμε ραντεβού.
                        </p>
                      </div>
                      <button onClick={() => setSub("ai")} style={{
                        width: "100%", padding: "13px 18px", borderRadius: 14, border: "none",
                        background: "linear-gradient(135deg, #F97316, #EA580C)", color: WH,
                        fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                        boxShadow: "0 4px 14px rgba(249,115,22,0.3)",
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                        Μιλήστε με τον Ορφέα
                      </button>
                    </C>
                  )}

                  {/* Questionnaire CTA — checkpoint gating */}
                  {isCheckpoint && (
                    <C onClick={() => setSub("quiz")} s={{ borderLeft: `3px solid ${O}`, cursor: "pointer", marginBottom: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 700, color: DK }}>Πες μας πώς πας</p>
                          <p style={{ margin: 0, fontSize: 12, color: MG }}>2 λεπτά για να σας βοηθήσουμε καλύτερα</p>
                        </div>
                        {Ic.rt(O, 16)}
                      </div>
                    </C>
                  )}

                  {/* ━━━ SECTION 3: SUPPORT — 3 levels ━━━ */}
                  <div style={{ marginBottom: 20 }}>
                    {/* 1. ΟΡΦΕΑΣ — ΚΥΡΙΑΡΧΗ hero κάρτα, προσωποποιημένη (ο AI βοηθός πρώτος) */}
                    <button onClick={() => setSub("ai")} style={{
                      width: "100%", padding: "18px 18px", borderRadius: 18, textAlign: "left",
                      border: "none", background: "linear-gradient(135deg, #F97316, #EA580C)", color: WH,
                      cursor: "pointer", marginBottom: 12, fontFamily: "inherit",
                      boxShadow: "0 8px 22px rgba(249,115,22,0.34)",
                      display: "flex", alignItems: "center", gap: 13,
                    }}>
                      <div style={{ width: 44, height: 44, borderRadius: 13, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>{nm ? `Γεια σου ${String(nm).trim().split(" ")[0]}, είμαι ο Ορφέας` : "Μίλησε με τον Ορφέα"}</div>
                        {(() => {
                          const na = (upcomingWpAppointments && upcomingWpAppointments[0]) || null;
                          if (na && na.dtF) {
                            const t = na.timeF || na.appointment_time || "";
                            return (
                              <div style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.96, marginTop: 4, lineHeight: 1.3, display: "flex", alignItems: "center", gap: 5 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Επόμενο ραντεβού: {na.dtF}{t ? ` • ${t}` : ""}</span>
                              </div>
                            );
                          }
                          // Maintenance nudge: purchased owner, χωρίς επερχόμενο ραντεβού, ≥4 μήνες από τελευταία φροντίδα
                          if (patientStatus === "purchased") {
                            const pastMs = wpAppts.filter(a => a && a.past && a.dt).map(a => +new Date(a.dt)).filter(n => n > 0);
                            const fitMs = (sd && !isNaN(new Date(sd))) ? +new Date(sd) : 0;
                            const lastMs = Math.max(fitMs, ...(pastMs.length ? pastMs : [0]));
                            if (lastMs > 0) {
                              const months = Math.floor((Date.now() - lastMs) / (1000 * 60 * 60 * 24 * 30.44));
                              if (months >= 4) {
                                const txt = months >= 12 ? "Έχει περάσει καιρός — καιρός για φίλτρα ή καθαρισμό;" : `Πέρασαν ~${months} μήνες — καιρός για φίλτρα ή καθαρισμό;`;
                                return (
                                  <div style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.96, marginTop: 4, lineHeight: 1.3, display: "flex", alignItems: "center", gap: 5 }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="9"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{txt}</span>
                                  </div>
                                );
                              }
                            }
                          }
                          return <div style={{ fontSize: 12.5, fontWeight: 600, opacity: 0.93, marginTop: 3, lineHeight: 1.35 }}>Ρώτησέ με ή ζήτα μου να κλείσω ραντεβού, να λύσω ένα πρόβλημα ή να σε καθοδηγήσω.</div>;
                        })()}
                      </div>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.92 }}><polyline points="9 6 15 12 9 18"/></svg>
                    </button>
                    {/* 2. Κλείσε ραντεβού — secondary (bordered, ο Ορφέας είναι πλέον ο ήρωας) */}
                    <button onClick={() => { setSub("newappt"); setNewAReason(""); }} style={{
                      width: "100%", padding: "15px 20px", borderRadius: 16,
                      border: "2px solid #F97316", background: WH, color: "#EA580C",
                      fontSize: 15, fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      marginBottom: 10, fontFamily: "inherit",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    }}>
                      {Ic.cal("#EA580C", 16)} Κλείσε ραντεβού
                    </button>
                    {/* 3. Μίλα με τον ειδικό — tertiary (subtle) */}
                    <button onClick={() => setSub("chat")} style={{
                      width: "100%", padding: "15px 20px", borderRadius: 16,
                      border: "1.5px solid #DFE3E8", background: "#F6F8FA", color: DK, position: "relative",
                      fontSize: 14.5, fontWeight: 700, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      fontFamily: "inherit",
                    }}>
                      {Ic.chat(DK, 16)} Μίλα με τον ειδικό{chatUnread > 0 && <div style={{position:"absolute",top:8,right:12,width:10,height:10,borderRadius:"50%",background:"#C62828",border:"2px solid white"}} />}
                    </button>
                  </div>

                  {/* Note: "Συνέχεια με παραγγελία" CTA removed in v1.3.2 — replaced by proper Offers system */}
                </>
              )}

              {/* ─── Purchase Intent Confirmation Modal ─── */}
              {showPurchaseModal && (
                <div style={{
                  position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                  zIndex: 200, display: "flex", alignItems: "flex-end",
                }}>
                  <div style={{
                    background: WH, borderRadius: "20px 20px 0 0",
                    padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto",
                    animation: "slideUp 0.25s ease-out",
                  }}>
                    {!purchaseSent ? (
                      <>
                        <div style={{ textAlign: "center", marginBottom: 24 }}>
                          <div style={{
                            width: 64, height: 64, borderRadius: "50%",
                            background: "#F0F7F0", display: "inline-flex",
                            alignItems: "center", justifyContent: "center",
                            marginBottom: 14,
                          }}>
                            {Ic.chk("#2E7D32", 32)}
                          </div>
                          <p style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800, color: DK }}>
                            Θέλετε να προχωρήσετε;
                          </p>
                          <p style={{ margin: 0, fontSize: 14, color: DK2, lineHeight: 1.6 }}>
                            Ο ακοοπροθετιστής σας θα ειδοποιηθεί και θα επικοινωνήσει μαζί σας για να κλείσετε ραντεβού.
                          </p>
                        </div>

                        <div style={{ background: "#F5F0EB", borderRadius: 14, padding: 16, marginBottom: 24 }}>
                          <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: MG, letterSpacing: 0.5 }}>ΤΙ ΑΚΟΛΟΥΘΕΙ</p>
                          <div style={{ fontSize: 13, color: DK, lineHeight: 1.8 }}>
                            <div>Τηλεφωνική επιβεβαίωση</div>
                            <div>Προγραμματισμός ραντεβού</div>
                            <div>Οριστικοποίηση παραγγελίας</div>
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: 10 }}>
                          <button
                            onClick={() => setShowPurchaseModal(false)}
                            disabled={purchaseSubmitting}
                            style={{
                              flex: 1, padding: "14px 0", borderRadius: 14,
                              border: "none", background: LG,
                              color: DK2, fontSize: 14, fontWeight: 700,
                              cursor: purchaseSubmitting ? "not-allowed" : "pointer",
                            }}
                          >
                            Όχι ακόμα
                          </button>
                          <button
                            onClick={submitPurchaseIntent}
                            disabled={purchaseSubmitting}
                            style={{
                              flex: 2, padding: "14px 0", borderRadius: 14,
                              border: "none", background: "#2E7D32",
                              color: WH, fontSize: 14, fontWeight: 700,
                              cursor: purchaseSubmitting ? "not-allowed" : "pointer",
                              opacity: purchaseSubmitting ? 0.7 : 1,
                            }}
                          >
                            {purchaseSubmitting ? "Αποστολή..." : "Ναι, προχωρήστε"}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div style={{ textAlign: "center", padding: "20px 0" }}>
                        <div style={{
                          width: 80, height: 80, borderRadius: "50%",
                          background: "#E8F5E9", display: "inline-flex",
                          alignItems: "center", justifyContent: "center",
                          marginBottom: 16,
                        }}>
                          {Ic.chk("#2E7D32", 44)}
                        </div>
                        <p style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800, color: DK }}>
                          Το αίτημα στάλθηκε!
                        </p>
                        <p style={{ margin: "0 0 20px", fontSize: 14, color: DK2, lineHeight: 1.6 }}>
                          Ο ακοοπροθετιστής σας θα επικοινωνήσει άμεσα για να κλείσετε ραντεβού παραλαβής.
                        </p>
                        <button
                          onClick={() => setShowPurchaseModal(false)}
                          style={{
                            width: "100%", padding: "14px 0", borderRadius: 12,
                            border: "none", background: O, color: WH,
                            fontSize: 15, fontWeight: 800, cursor: "pointer",
                          }}
                        >
                          Εντάξει
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* END TRIAL HOME */}

              {/* ─── iOS Install Instructions Modal ─── */}
              {showIosInstall && (
                <div style={{
                  position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                  zIndex: 200, display: "flex", alignItems: "flex-end",
                }}>
                  <div style={{
                    background: WH, borderRadius: "20px 20px 0 0",
                    padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto",
                    animation: "slideUp 0.25s ease-out",
                  }}>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{
                        width: 64, height: 64, borderRadius: 16,
                        background: OL, display: "inline-flex",
                        alignItems: "center", justifyContent: "center",
                        marginBottom: 12,
                      }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 2v13M5 9l7-7 7 7"/>
                          <path d="M20 21H4a2 2 0 01-2-2V9a2 2 0 012-2h3"/>
                          <path d="M17 7h3a2 2 0 012 2v10a2 2 0 01-2 2"/>
                        </svg>
                      </div>
                      <p style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800, color: DK }}>
                        Εγκατάσταση στο iPhone
                      </p>
                      <p style={{ margin: 0, fontSize: 13, color: DK2, lineHeight: 1.5 }}>
                        Ακολουθήστε τα παρακάτω βήματα για να προσθέσετε την εφαρμογή στην αρχική οθόνη του κινητού σας.
                      </p>
                    </div>

                    <div style={{ background: OL, borderRadius: 12, padding: 16, marginBottom: 20 }}>
                      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: O, color: WH, fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>1</div>
                        <div style={{ fontSize: 13, color: DK, lineHeight: 1.5, paddingTop: 3 }}>
                          Πατήστε το κουμπί <strong>Κοινοποίηση</strong> (τετράγωνο με βέλος προς τα πάνω) στο κάτω μέρος του Safari.
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: O, color: WH, fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>2</div>
                        <div style={{ fontSize: 13, color: DK, lineHeight: 1.5, paddingTop: 3 }}>
                          Στη λίστα που θα εμφανιστεί, κατεβείτε και επιλέξτε <strong>"Προσθήκη στην Αρχική Οθόνη"</strong>.
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: O, color: WH, fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>3</div>
                        <div style={{ fontSize: 13, color: DK, lineHeight: 1.5, paddingTop: 3 }}>
                          Πατήστε <strong>"Προσθήκη"</strong> πάνω δεξιά. Η εφαρμογή θα εμφανιστεί στην αρχική οθόνη σαν κανονική εφαρμογή.
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setShowIosInstall(false)}
                      style={{
                        width: "100%", padding: "14px 0", borderRadius: 12,
                        border: "none", background: O, color: WH,
                        fontSize: 15, fontWeight: 800, cursor: "pointer",
                      }}
                    >
                      Εντάξει
                    </button>
                  </div>
                </div>
              )}

              {/* ─── TRIAL EXPIRED ─── */}
              {isTrial && trialExpired && (
                <>
                  <C s={{ border: "1.5px solid #37474F30", marginBottom: 12, textAlign: "center" }}>
                    <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: DK }}>Η δοκιμαστική περίοδος ολοκληρώθηκε</p>
                    <p style={{ margin: 0, fontSize: 13, color: MG, lineHeight: 1.5 }}>
                      Ελπίζουμε να είχατε μια θετική εμπειρία! Επικοινωνήστε μαζί μας για αξιολόγηση ή δοκιμή νέων ακουστικών.
                    </p>
                  </C>
                  <Btn onClick={() => { setSub("newappt"); setNewAReason("Ρύθμιση / Follow-up"); }}>
                    {Ic.cal("white", 16)} Κλείστε ραντεβού αξιολόγησης
                  </Btn>
                  <div style={{ height: 8 }} />
                  <Btn onClick={() => window.open(`tel:${clinic.t}`)} primary={false}>
                    {Ic.ph(O, 16)} {clinic.f}
                  </Btn>
                  <div style={{ height: 8 }} />
                  <C>
                    <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: MG, letterSpacing: 0.5 }}>ΚΕΝΤΡΑ ΑΚΟΗΣ</p>
                    {LOCS.map((l, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < LOCS.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                        <div>
                          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: DK }}>{l.c}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 11, color: MG }}>{l.a}</p>
                        </div>
                        <a href={`tel:${l.t}`} style={{ padding: "5px 10px", background: OL, borderRadius: 8, color: O, fontWeight: 700, fontSize: 11, textDecoration: "none" }}>{l.f}</a>
                      </div>
                    ))}
                  </C>
                </>
              )}

              {/* ─── SUPPORT HOME ─── */}
              {isSupport && (
                <>
                  {/* HERO — Simple header */}
                  <C s={{ background: `linear-gradient(135deg,${O},#D06A0E)`, border: "none", marginBottom: 16, textAlign: "center", padding: "28px 20px" }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                      {Ic.ear(WH, 30)}
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800, color: WH, letterSpacing: -0.3 }}>
                      Χρειάζεστε βοήθεια;
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 500, lineHeight: 1.5 }}>
                      Είμαστε εδώ για οποιαδήποτε υποστήριξη<br/>χρειάζεστε με τα ακουστικά σας
                    </p>
                  </C>

                  {/* 3 MAIN ACTIONS */}
                  <C s={{ background: WH, marginBottom: 16, padding: "18px 18px" }}>
                    <p style={{ margin: "0 0 14px", fontSize: 11, fontWeight: 800, color: MG, letterSpacing: 0.8, textTransform: "uppercase" }}>Ενέργειες</p>

                    <button
                      onClick={() => { setSub("newappt"); setNewAReason(""); }}
                      style={{ width: "100%", background: O, color: WH, border: "none", borderRadius: 12, padding: "14px 16px", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, fontFamily: "inherit" }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      Κλείσε ραντεβού
                    </button>

                    <button
                      onClick={() => setSub('chat')}
                      style={{ width: "100%", background: WH, color: DK, border: `1.5px solid ${LG}`, borderRadius: 12, padding: "14px 16px", fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 10, display: "flex", alignItems: "center", gap: 12, fontFamily: "inherit" }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      Μίλα με τον ειδικό
                    </button>

                    <button
                      onClick={() => setSub('ai')}
                      style={{ width: "100%", background: BG, color: DK, border: `1.5px solid ${LG}`, borderRadius: 12, padding: "14px 16px", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 12, fontFamily: "inherit" }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                      Ρώτησε τον βοηθό (AI)
                    </button>
                  </C>

                  {/* OUR CLINICS */}
                  <C s={{ background: WH, marginBottom: 16, padding: "18px 18px" }}>
                    <p style={{ margin: "0 0 14px", fontSize: 11, fontWeight: 800, color: MG, letterSpacing: 0.8, textTransform: "uppercase" }}>Τα κέντρα μας</p>
                    {CLINICS.map((cl, i) => (
                      <div key={cl.id} style={{
                        padding: "12px 0",
                        borderTop: i === 0 ? "none" : `1px solid ${LG}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 700, color: DK }}>{cl.c}</p>
                          <p style={{ margin: 0, fontSize: 12, color: MG, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cl.a}</p>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          <a
                            href={`tel:${cl.t}`}
                            style={{ width: 36, height: 36, borderRadius: "50%", background: OL, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                            aria-label="Κλήση"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          </a>
                          <a
                            href={cl.map}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ width: 36, height: 36, borderRadius: "50%", background: BG, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none", border: `1px solid ${LG}` }}
                            aria-label="Χάρτης"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          </a>
                        </div>
                      </div>
                    ))}
                  </C>

                  {/* BATTERIES / LOYALTY TEASER */}
                  <C s={{ background: WH, marginBottom: 16, padding: "18px 18px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 800, color: DK }}>Κάρτα μπαταριών</p>
                        <p style={{ margin: 0, fontSize: 12, color: MG, lineHeight: 1.4 }}>5 αγορές + 1 δώρο. Συλλέγεται αυτόματα σε κάθε αγορά.</p>
                      </div>
                      <button
                        onClick={() => setTab('shop')}
                        style={{ background: O, color: WH, border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}
                      >
                        Κάρτα μου
                      </button>
                    </div>
                  </C>
                </>
              )}

              {/* ─── PURCHASED HOME ─── */}
              {isPurchased && (
                <>
              {/* ━━━ HERO — Days/months/years with hearing aids (v1.3.44) ━━━ */}
              <C s={{ background: `linear-gradient(135deg,${O},#D06A0E)`, border: "none", marginBottom: 16, textAlign: "center", padding: "22px 20px" }}>
                <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.65)", letterSpacing: 1.5 }}>
                  {ds < 7 ? "ΠΕΡΙΟΔΟΣ ΠΡΟΣΑΡΜΟΓΗΣ" : ds < 21 ? "ΕΞΟΙΚΕΙΩΣΗ" : "ΣΤΑΘΕΡΗ ΧΡΗΣΗ"}
                </p>
                <p style={{ margin: "0 0 4px", fontSize: 48, fontWeight: 800, color: WH, letterSpacing: -1 }}>{dur.big}</p>
                <p style={{ margin: "0 0 6px", fontSize: 14, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>
                  {dur.unit}
                </p>
                {dur.sub && (
                  <p style={{ margin: "0", fontSize: 12, color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
                    {dur.sub}
                  </p>
                )}
              </C>

              {/* v1.3.41 — Smart Quiz Prompt — placed between hero and daily
                  check-in so it's visible without scrolling but doesn't compete
                  with the primary check-in CTA. Adapts to last-fill date. */}
              {renderQuizPromptCard()}

              {/* ━━━ Daily check-in ━━━ */}
              {checkin === null && (
                <C s={{ background: WH, marginBottom: 16 }}>
                  <p style={{ margin: "0 0 14px", fontSize: 16, fontWeight: 700, color: DK }}>Πώς είστε σήμερα;</p>
                  <div style={{ display: "flex", gap: 10 }}>
                    {/* v1.3.38 — Removed immediate submitPurchasedCheckin("issue") on click.
                        Previously every "Έχω κάποιο θέμα" tap fired a 'struggle' mood
                        in the admin feed even if the patient just wanted to chat with
                        the AI helper and ultimately resolved their question. Now the
                        struggle mood is only logged when the patient explicitly chooses
                        "Θέλω άνθρωπο" in the AI escalation panel (after 3+ messages or
                        critical keywords). Resolution path "Είμαι εντάξει τώρα" still
                        logs a 'good' mood. */}
                    <button onClick={() => { setCheckin("issue"); setSub("ai"); }} style={{
                      flex: 1, padding: "14px 0", borderRadius: 14,
                      border: "1.5px solid #E8E8E8", background: WH, color: DK,
                      fontSize: 13, fontWeight: 700, cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}>
                      Έχω κάποιο θέμα
                    </button>
                    <button onClick={() => { setCheckin("ok"); submitPurchasedCheckin("ok"); }} style={{
                      flex: 1, padding: "14px 0", borderRadius: 14,
                      border: "1.5px solid #E8E8E8", background: WH, color: DK,
                      fontSize: 13, fontWeight: 700, cursor: "pointer",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                    }}>
                      Όλα καλά
                    </button>
                  </div>
                </C>
              )}
              {checkin === "ok" && (
                <C s={{ background: "#F0F7F0", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {Ic.chk("#2E7D32", 22)}
                    <div>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DK }}>Τέλεια! Συνεχίστε έτσι!</p>
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: MG }}>Η προσαρμογή εξελίσσεται καθημερινά.</p>
                    </div>
                  </div>
                </C>
              )}

              {/* ━━━ Πρόγραμμα αποκατάστασης — soft card ━━━ */}
              <C onClick={() => setTab("progress")} s={{ background: "#F7F7F7", border: "none", marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, color: MG, letterSpacing: 0.8 }}>ΠΡΟΓΡΑΜΜΑ ΑΠΟΚΑΤΑΣΤΑΣΗΣ</p>
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: DK }}>Ημέρα {ds + 1} — {PS[ps].t}</p>
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: DK2, lineHeight: 1.5 }}>
                      {ds < 7 ? "Η σταθερή χρήση βοηθά τον εγκέφαλο να προσαρμοστεί" : ds < 14 ? "Η ακοή σας βελτιώνεται σταδιακά" : ds < 28 ? "Εξαιρετική πρόοδος!" : "Η σταθερή χρήση κάνει τη διαφορά"}
                    </p>
                  </div>
                  <div style={{ width: 44, height: 44, borderRadius: "50%", border: `2px solid ${O}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: O }}>{Math.min(Math.round(((ps + 1) / 4) * 100), 100)}%</span>
                  </div>
                </div>
              </C>

              {/* ━━━ Επόμενο ραντεβού ━━━ */}
              {homeNextAppointment ? (
                <C onClick={() => setTab("cal")} s={{ borderLeft: `3px solid ${getStatusMeta(homeNextAppointment).color}`, marginBottom: 16, padding: "18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ background: OL, borderRadius: 999, padding: "4px 10px" }}>
                      <p style={{ fontSize: 10, fontWeight: 800, color: O, margin: 0, letterSpacing: 0.5 }}>ΕΠΟΜΕΝΟ ΡΑΝΤΕΒΟΥ</p>
                    </div>
                    <div style={{ background: getStatusMeta(homeNextAppointment).bg, borderRadius: 999, padding: "4px 10px" }}>
                      <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: getStatusMeta(homeNextAppointment).color }}>{getStatusMeta(homeNextAppointment).label}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 15, fontWeight: 800, color: DK, margin: "0 0 4px" }}>
                    {homeNextAppointment.l || getReasonLabel(homeNextAppointment.reason, homeNextAppointment.title)}
                  </p>
                  <p style={{ fontSize: 13, color: DK, margin: "8px 0 0", fontWeight: 700 }}>
                    {homeNextAppointment.dtF} • {homeNextAppointment.timeF || homeNextAppointment.appointment_time || "10:00"}
                  </p>
                </C>
              ) : (
                <C onClick={() => { setSub("newappt"); setNewAReason(""); }} s={{ borderLeft: `3px solid ${O}`, marginBottom: 16, cursor: "pointer" }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: DK, margin: "0 0 4px" }}>Δεν έχετε προγραμματισμένο ραντεβού</p>
                  <p style={{ fontSize: 12, color: O, fontWeight: 700, margin: 0 }}>Προγραμμάτισε το επόμενο ›</p>
                </C>
              )}

              {/* ━━━ SUPPORT — 3 levels (same as trial) ━━━ */}
              <div style={{ marginBottom: 20 }}>
                <button onClick={() => { setSub("newappt"); setNewAReason(""); }} style={{
                  width: "100%", padding: "16px 20px", borderRadius: 16,
                  border: "none", background: "linear-gradient(135deg, #F97316, #EA580C)", color: WH,
                  fontSize: 15, fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(249,115,22,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  marginBottom: 10, fontFamily: "inherit",
                }}>
                  {Ic.cal(WH, 16)} Κλείσε ραντεβού
                </button>
                <button onClick={() => setSub("chat")} style={{
                  width: "100%", padding: "16px 20px", borderRadius: 16,
                  border: "2px solid #F97316", background: WH, color: "#F97316", position: "relative",
                  fontSize: 15, fontWeight: 700, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  marginBottom: 10, fontFamily: "inherit",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}>
                  {Ic.chat("#F97316", 16)} Μίλα με τον ειδικό{chatUnread > 0 && <div style={{position:"absolute",top:8,right:12,width:10,height:10,borderRadius:"50%",background:"#C62828",border:"2px solid white"}} />}
                </button>
                <button onClick={() => setSub("ai")} style={{
                  width: "100%", padding: "13px 16px", borderRadius: 14,
                  border: "none", background: "#F5F5F5", color: DK2,
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  fontFamily: "inherit",
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
                  </svg>
                  Ρωτήστε τον Ορφέα
                </button>
              </div>

              <p style={{ textAlign: "center", fontSize: 12, color: MG, fontStyle: "italic", margin: "0 0 10px" }}>
                Η προσαρμογή γίνεται βήμα-βήμα — είμαστε δίπλα σας
              </p>
              </>)}
            </div>
          </div>
        )}
        {/* SERVICES — v1.3.31 — Prospect-only ενημερωτική οθόνη με κάρτες
            για τις 5 υπηρεσίες της Earmedical. Κάθε κάρτα έχει CTA που
            προ-συμπληρώνει τον λόγο ραντεβού και ανοίγει το booking flow. */}
        {tab === "services" && (
          <div style={{ padding: "20px 20px 0" }}>
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: DK, margin: "0 0 4px" }}>Οι υπηρεσίες μας</p>
              <p style={{ fontSize: 13, color: MG, margin: 0 }}>
                Κέντρο: {clinic.c}
              </p>
            </div>
            {PROSPECT_SERVICES.map((s) => (
              <div
                key={s.id}
                style={{
                  background: WH,
                  borderRadius: 16,
                  padding: 18,
                  marginBottom: 12,
                  border: "1px solid #ececec",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 10 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: OL, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>{s.Icon(28, O)}</div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: DK }}>{s.title}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: O, fontWeight: 600 }}>{s.short}</p>
                  </div>
                </div>
                <p style={{ margin: "0 0 14px", fontSize: 13, color: DK2, lineHeight: 1.5 }}>{s.desc}</p>
                <button
                  onClick={() => {
                    setNewAReason(s.title);
                    setSub("newappt");
                  }}
                  style={{
                    width: "100%", padding: "12px 16px", borderRadius: 12,
                    background: O, color: WH, border: "none",
                    fontSize: 14, fontWeight: 700, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    fontFamily: "inherit",
                  }}
                >
                  {Ic.plus("white", 16)} Κλείσε ραντεβού
                </button>
              </div>
            ))}
            <div style={{ height: 24 }} />
          </div>
        )}
        {/* CAL */}
        {tab === "cal" && (
          <div style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: DK, margin: 0 }}>Τα ραντεβού σας</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => setSub("notifs")} aria-label="Ειδοποιήσεις" style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: 30 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {notifsUnread > 0 && (<span style={{ position: "absolute", top: -4, right: -4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{notifsUnread > 9 ? "9+" : notifsUnread}</span>)}
                </button>
                <button onClick={() => setFs(f => f >= 2 ? 0 : f + 1)} style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", color: DK, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{fs > 0 ? "A−" : "A+"}</button>
              </div>
            </div>
            <p style={{ fontSize: 12, color: MG, margin: "0 0 14px" }}>
              Κέντρο: {clinic.c}
            </p>
            <Btn onClick={() => setSub("newappt")} s={{ marginBottom: 14 }}>
              {Ic.plus("white", 16)} Προγραμμάτισε ραντεβού
            </Btn>

            {/* REAL APPOINTMENTS FROM WORDPRESS */}
            {wpAppts.length > 0 && (
              <>
                {/* Filters */}
                <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                  {[
                    { id: "upcoming",  label: "Επερχόμενα" },
                    { id: "completed", label: "Ολοκληρωμένα" },
                    { id: "all",       label: "Όλα" },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setCalFilter(item.id)}
                      style={{
                        flex: 1,
                        padding: "10px 8px",
                        background: calFilter === item.id ? O : WH,
                        borderRadius: 12,
                        color: calFilter === item.id ? WH : DK,
                        fontWeight: 700,
                        border: calFilter === item.id ? "none" : `1px solid ${O}20`,
                        cursor: "pointer",
                        fontSize: 11,
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                {wpAppts
                  .filter(a => {
                    if (calFilter === "all") return true;
                    if (calFilter === "completed") return a.past && a.status !== "cancelled";
                    return !a.past && a.status !== "cancelled";
                  })
                  .map((a, i) => {
                  const st = getStatusMeta(a);
                  const isNext = i === 0 && calFilter !== "completed";
                  return (
                    <C key={"wp" + i} s={{
                      borderLeft: `3px solid ${st.color}`,
                      opacity: isCompletedAppointment(a) ? 0.5 : 1,
                      marginBottom: 8,
                      boxShadow: isNext ? "0 2px 16px rgba(239,128,26,0.12)" : undefined,
                    }}>
                      {isNext && (
                        <p style={{ fontSize: 10, fontWeight: 800, color: O, margin: "0 0 6px", letterSpacing: 0.5 }}>ΕΠΟΜΕΝΟ ΡΑΝΤΕΒΟΥ</p>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <p style={{ fontSize: isNext ? 15 : 13, fontWeight: isNext ? 800 : 700, color: DK, margin: 0 }}>
                          {getReasonLabel(a.reason, a.reason_label || a.title)}
                        </p>
                        <div style={{ background: st.bg, borderRadius: 999, padding: "4px 10px" }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: st.color }}>{st.label}</span>
                        </div>
                      </div>
                      <p style={{ fontSize: isNext ? 13 : 12, color: isNext ? DK : MG, margin: "0 0 4px", fontWeight: isNext ? 700 : 400 }}>
                        {a.dtF} • {a.timeF || a.appointment_time || "09:00"}
                        {!a.past && a.days > 0 ? <span style={{ color: O, fontWeight: 600 }}> • {formatDaysLabel(a.days)}</span> : ""}
                      </p>
                      {!a.past && a.status !== "cancelled" && (
                        <button
                          onClick={() => {
                            setRescheduleAppt(a);
                            setRescheduleDate(a.appointment_date || getTodayDateStr());
                            setRescheduleTime(a.appointment_time || "09:00");
                            setRescheduleSuccess(false);
                          }}
                          style={{ fontSize: 12, color: O, fontWeight: 700, background: "none", border: "none", padding: "4px 0 0", cursor: "pointer", textAlign: "left", minHeight: 32, display: "block" }}
                        >
                          Διαχείριση ραντεβού ›
                        </button>
                      )}
                    </C>
                  );
                })}
              </>
            )}
            {loadingAppts && (
              <EarLoading text="Φόρτωση ραντεβού…" size={72} pad={20} />
            )}
            {!loadingAppts && wpAppts.length === 0 && (
              <div style={{ background: OL, borderRadius: 14, padding: 16, marginBottom: 14, textAlign: "center", border: `1px solid ${O}20` }}>
                <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600, color: DK }}>Δεν υπάρχουν κρατήσεις</p>
                <p style={{ margin: 0, fontSize: 11, color: MG }}>Προγραμμάτισε το πρώτο σας ραντεβού</p>
              </div>
            )}
          </div>
        )}
        {/* RESCHEDULE MODAL */}
        {rescheduleAppt && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "flex-end" }}>
            <div style={{ background: WH, borderRadius: "20px 20px 0 0", padding: 24, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <p style={{ fontSize: 16, fontWeight: 800, color: DK, margin: 0 }}>Διαχείριση ραντεβού</p>
                <button onClick={() => { setRescheduleAppt(null); setCancelConfirm(false); }} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: MG }}>✕</button>
              </div>

              <p style={{ fontSize: 13, fontWeight: 700, color: DK, margin: "0 0 4px" }}>{getReasonLabel(rescheduleAppt.reason, rescheduleAppt.title)}</p>
              <p style={{ fontSize: 12, color: MG, margin: "0 0 20px" }}>{rescheduleAppt.dtF} • {rescheduleAppt.appointment_time}</p>

              {rescheduleSuccess ? (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#E8F5E9", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 800, color: DK, margin: "0 0 6px" }}>Το αίτημα στάλθηκε!</p>
                  <p style={{ fontSize: 12, color: MG, margin: 0 }}>Το κέντρο θα επιβεβαιώσει σύντομα.</p>
                </div>
              ) : (
                <>
                  {/* Ημερομηνία */}
                  <p style={{ fontSize: 12, fontWeight: 700, color: DK, margin: "0 0 8px" }}>Νέα ημερομηνία</p>
                  <input
                    type="date"
                    value={rescheduleDate}
                    min={getTodayDateStr()}
                    onChange={e => setRescheduleDate(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: `1px solid ${LG}`, fontSize: 14, marginBottom: 16, background: BG }}
                  />

                  {/* Ώρα */}
                  <p style={{ fontSize: 12, fontWeight: 700, color: DK, margin: "0 0 8px" }}>Διαθέσιμες ώρες</p>
                  {rescheduleLoadingSlots ? (
                    <EarLoading text="Φόρτωση ωρών…" size={68} pad={16} />
                  ) : rescheduleLiveSlots && rescheduleLiveSlots.length === 0 ? (
                    <p style={{ fontSize: 12, color: "#C62828", marginBottom: 16 }}>Δεν υπάρχουν διαθέσιμες ώρες αυτή την ημέρα.</p>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                      {(rescheduleLiveSlots || getClinicTimeSlots(clinic, rescheduleDate)).map(slot => (
                        <button
                          key={slot}
                          onClick={() => setRescheduleTime(slot)}
                          style={{
                            padding: "8px 14px",
                            borderRadius: 10,
                            border: `2px solid ${rescheduleTime === slot ? O : LG}`,
                            background: rescheduleTime === slot ? OL : WH,
                            color: rescheduleTime === slot ? O : DK,
                            fontWeight: rescheduleTime === slot ? 700 : 500,
                            fontSize: 13,
                            cursor: "pointer",
                          }}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Επικοινωνία */}
                  <a href={`tel:${clinic.t}`} style={{ display: "block", textAlign: "center", fontSize: 12, color: MG, marginBottom: 16, textDecoration: "none" }}>
                    Επικοινωνία με το κέντρο: {clinic.f}
                  </a>

                  <button
                    onClick={doReschedule}
                    disabled={rescheduling || !rescheduleDate || !rescheduleTime}
                    style={{
                      width: "100%", padding: "14px", borderRadius: 12, border: "none",
                      background: (!rescheduleDate || !rescheduleTime) ? LG : O,
                      color: (!rescheduleDate || !rescheduleTime) ? MG : WH,
                      fontWeight: 800, fontSize: 15, cursor: "pointer",
                    }}
                  >
                    {rescheduling ? "Αποστολή..." : "Αποστολή αιτήματος αλλαγής"}
                  </button>

                  {/* ─── Ακύρωση ραντεβού ─── */}
                  <div style={{ borderTop: "1px solid #eee", margin: "20px 0 16px" }} />

                  {!cancelConfirm ? (
                    <button
                      onClick={() => setCancelConfirm(true)}
                      style={{
                        width: "100%", padding: "14px", borderRadius: 12,
                        border: "2px solid #C62828", background: WH,
                        color: "#C62828", fontWeight: 700, fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      Ακύρωση ραντεβού
                    </button>
                  ) : cancelConfirm === "done" ? (
                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                      <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#E8F5E9", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                      </div>
                      <p style={{ fontSize: 15, fontWeight: 700, color: "#2E7D32", margin: 0 }}>Το ραντεβού ακυρώθηκε</p>
                      <p style={{ fontSize: 12, color: MG, margin: "4px 0 0" }}>Το κέντρο ενημερώθηκε.</p>
                    </div>
                  ) : cancelling ? (
                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                      <p style={{ fontSize: 14, color: MG, margin: 0 }}>Ακύρωση σε εξέλιξη...</p>
                    </div>
                  ) : (
                    <div style={{ background: "#FFEBEE", borderRadius: 14, padding: 16 }}>
                      <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#C62828" }}>
                        Είστε σίγουροι;
                      </p>
                      <p style={{ margin: "0 0 14px", fontSize: 12, color: "#C62828", lineHeight: 1.5 }}>
                        Το ραντεβού θα ακυρωθεί και το κέντρο θα ενημερωθεί.
                      </p>
                      <div style={{ display: "flex", gap: 10 }}>
                        <button
                          onClick={() => setCancelConfirm(false)}
                          style={{
                            flex: 1, padding: "12px", borderRadius: 10,
                            border: "1px solid #ccc", background: WH,
                            color: DK, fontWeight: 600, fontSize: 13, cursor: "pointer",
                          }}
                        >
                          Πίσω
                        </button>
                        <button
                          onClick={cancelAppointment}
                          style={{
                            flex: 1, padding: "12px", borderRadius: 10,
                            border: "none", background: "#C62828",
                            color: WH, fontWeight: 800, fontSize: 13, cursor: "pointer",
                          }}
                        >
                          Ναι, ακύρωσε
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* PROGRESS */}
        {tab === "progress" && (
          <div style={{ padding: 20 }}>
            {isTrial ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                  <p style={{ fontSize: 22, fontWeight: 800, color: DK, margin: 0 }}>Δοκιμαστική Περίοδος</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => setSub("notifs")} aria-label="Ειδοποιήσεις" style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: 30 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                      {notifsUnread > 0 && (<span style={{ position: "absolute", top: -4, right: -4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{notifsUnread > 9 ? "9+" : notifsUnread}</span>)}
                    </button>
                    <button onClick={() => setFs(f => f >= 2 ? 0 : f + 1)} style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", color: DK, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{fs > 0 ? "A−" : "A+"}</button>
                  </div>
                </div>
                <p style={{ fontSize: 14, color: MG, margin: "0 0 14px" }}>Ημέρα {trialDay} από {trialDays}</p>

                {/* Countdown — πορτοκαλί gradient */}
                <C s={{ background: `linear-gradient(135deg,${O},#D06A0E)`, border: "none", marginBottom: 16, textAlign: "center", padding: "22px 20px" }}>
                  <p style={{ margin: "0 0 4px", fontSize: 48, fontWeight: 800, color: WH, letterSpacing: -1 }}>{trialExpired ? 0 : trialDaysLeft}</p>
                  <p style={{ margin: "0 0 14px", fontSize: 14, color: "rgba(255,255,255,0.8)", fontWeight: 500 }}>
                    {trialExpired ? "Η δοκιμή ολοκληρώθηκε" : trialDaysLeft === 1 ? "ημέρα απομένει" : "ημέρες απομένουν"}
                  </p>
                  <div style={{ background: "rgba(255,255,255,0.18)", borderRadius: 10, height: 4, overflow: "hidden" }}>
                    <div style={{ width: `${trialProgress}%`, height: "100%", background: WH, borderRadius: 10, transition: "width 0.5s" }} />
                  </div>
                </C>

                {/* Dynamic guidance based on trialDays duration + trialDay */}
                {(() => {
                  let phaseLabel = "";
                  let title = "";
                  let body = "";
                  let tip = "";

                  if (trialDays <= 7) {
                    // 7 ΗΜΕΡΕΣ (Fast Experience)
                    if (trialDay <= 3) {
                      phaseLabel = "Προσαρμογή";
                      title = "Περίοδος προσαρμογής";
                      body = "Οι πρώτες ημέρες είναι περίοδος προσαρμογής. Είναι φυσιολογικό να ακούτε νέους ήχους που μπορεί να σας φαίνονται έντονοι ή διαφορετικοί.";
                      tip = "Φορέστε τα ακουστικά όσο περισσότερο μπορείτε μέσα στην ημέρα και δώστε χρόνο στον εγκέφαλο να προσαρμοστεί.";
                    } else {
                      phaseLabel = "Σταθεροποίηση";
                      title = "Η ακοή σας σταθεροποιείται";
                      body = "Η ακοή σας αρχίζει να σταθεροποιείται. Οι ήχοι θα σας φαίνονται πιο φυσικοί και η κατανόηση της ομιλίας θα βελτιώνεται.";
                      tip = "Αν κάτι σας ενοχλεί ή δεν ακούγεται σωστά, σημειώστε το για να το συζητήσουμε.";
                    }
                  } else if (trialDays <= 14) {
                    // 14 ΗΜΕΡΕΣ (Standard Experience)
                    if (trialDay <= 5) {
                      phaseLabel = "Προσαρμογή";
                      title = "Περίοδος προσαρμογής";
                      body = "Οι πρώτες ημέρες είναι περίοδος προσαρμογής. Νέοι ήχοι μπορεί να σας φαίνονται έντονοι ή ασυνήθιστοι — αυτό είναι φυσιολογικό.";
                      tip = "Φοράτε τα ακουστικά καθημερινά ώστε ο εγκέφαλος να προσαρμοστεί πιο γρήγορα.";
                    } else if (trialDay <= 10) {
                      phaseLabel = "Εξοικείωση";
                      title = "Η εμπειρία γίνεται πιο φυσική";
                      body = "Η εμπειρία αρχίζει να γίνεται πιο φυσική. Η κατανόηση της ομιλίας βελτιώνεται, ιδιαίτερα σε ήρεμα περιβάλλοντα.";
                      tip = "Παρατηρήστε αν κάποιοι ήχοι δεν σας φαίνονται σωστοί.";
                    } else {
                      phaseLabel = "Αξιολόγηση";
                      title = "Ολοκληρωμένη εικόνα";
                      body = "Έχετε πλέον μια πιο ολοκληρωμένη εικόνα της ακοής σας. Εστιάστε στην καθημερινότητά σας — συζητήσεις, τηλεόραση, εξωτερικοί χώροι.";
                      tip = "Καταγράψτε οτιδήποτε θέλετε να βελτιώσουμε.";
                    }
                  } else if (trialDays <= 21) {
                    // 21 ΗΜΕΡΕΣ (Advanced Experience)
                    if (trialDay <= 7) {
                      phaseLabel = "Προσαρμογή";
                      title = "Περίοδος προσαρμογής";
                      body = "Οι ήχοι μπορεί να φαίνονται έντονοι — αυτό είναι φυσιολογικό. Η συνεχής χρήση οδηγεί σε καλύτερη προσαρμογή.";
                      tip = "Φοράτε τα ακουστικά σταθερά καθημερινά, ακόμα και αν κάτι σας φαίνεται διαφορετικό.";
                    } else if (trialDay <= 14) {
                      phaseLabel = "Βελτίωση";
                      title = "Η ακοή σας βελτιώνεται αισθητά";
                      body = "Η ακοή σας βελτιώνεται αισθητά. Η ομιλία γίνεται πιο καθαρή και η καθημερινότητα πιο άνετη.";
                      tip = "Αρχίστε να εντοπίζετε λεπτομέρειες που θέλετε να διορθώσουμε.";
                    } else {
                      phaseLabel = "Σταθεροποίηση";
                      title = "Η εμπειρία σταθεροποιείται";
                      body = "Η εμπειρία σταθεροποιείται. Τώρα μπορείτε να αξιολογήσετε πιο ουσιαστικά την απόδοση των ακουστικών.";
                      tip = "Καταγράψτε συγκεκριμένες παρατηρήσεις για βελτιστοποίηση.";
                    }
                  } else {
                    // 28 ΗΜΕΡΕΣ (Premium Experience)
                    if (trialDay <= 7) {
                      phaseLabel = "Προσαρμογή";
                      title = "Προσαρμογή σε νέους ήχους";
                      body = "Προσαρμογή σε νέους ήχους. Είναι φυσιολογικό να φαίνονται έντονοι στην αρχή.";
                      tip = "Φοράτε τα σταθερά και δώστε χρόνο στον εγκέφαλο.";
                    } else if (trialDay <= 14) {
                      phaseLabel = "Εξοικείωση";
                      title = "Η ακοή σας γίνεται πιο φυσική";
                      body = "Η ακοή σας γίνεται πιο φυσική. Η κατανόηση της ομιλίας βελτιώνεται σταδιακά.";
                      tip = "Δοκιμάστε τα σε τηλεόραση, συζήτηση και τηλέφωνο.";
                    } else if (trialDay <= 21) {
                      phaseLabel = "Σταθεροποίηση";
                      title = "Αρχίζετε να νιώθετε πιο άνετα";
                      body = "Αρχίζετε να αισθάνεστε άνετα σε περισσότερες καταστάσεις. Εστιάστε σε πραγματικές συνθήκες καθημερινότητας.";
                      tip = "Βγείτε σε παρέες, καφέ, θορυβώδη περιβάλλοντα.";
                    } else {
                      phaseLabel = "Αξιολόγηση";
                      title = "Πλήρης εμπειρία χρήσης";
                      body = "Πλήρης εμπειρία χρήσης. Τώρα μπορείτε να αξιολογήσετε συνολικά την απόδοση.";
                      tip = "Καταγράψτε τυχόν λεπτομέρειες για τελική βελτιστοποίηση.";
                    }
                  }

                  return (
                    <C s={{ marginBottom: 16 }}>
                      <div style={{ display: "inline-block", background: OL, borderRadius: 999, padding: "4px 12px", marginBottom: 12 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: O, letterSpacing: 0.3 }}>{phaseLabel}</span>
                      </div>
                      <p style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 800, color: DK, lineHeight: 1.3 }}>
                        {title}
                      </p>
                      <p style={{ margin: "0 0 14px", fontSize: 15, color: DK2, lineHeight: 1.8 }}>
                        {body}
                      </p>
                      <div style={{ padding: "12px 14px", background: LG, borderRadius: 12 }}>
                        <p style={{ margin: 0, fontSize: 14, color: DK, lineHeight: 1.7 }}>
                          {tip}
                        </p>
                      </div>
                      <p style={{ margin: "14px 0 0", fontSize: 13, color: MG, lineHeight: 1.5, fontStyle: "italic" }}>
                        Αν κάτι σας δυσκολεύει, μπορείτε να μιλήσετε με τον ειδικό σας.
                      </p>
                    </C>
                  );
                })()}

                {/* Questionnaire CTA */}
                {isCheckpoint ? (
                  <Btn onClick={() => setSub("quiz")} s={{ marginBottom: 12 }}>Πες μας πώς ακούτε σήμερα</Btn>
                ) : (
                  <C s={{ marginBottom: 12, opacity: 0.85 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {Ic.chk(MG, 18)}
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: "0 0 2px", fontSize: 13, fontWeight: 700, color: DK }}>Σύντομη αξιολόγηση</p>
                        <p style={{ margin: 0, fontSize: 12, color: MG }}>
                          {nextCheckpoint
                            ? `Το επόμενο ερωτηματολόγιο ανοίγει στην Ημέρα ${nextCheckpoint}`
                            : "Θα σας ζητήσουμε σύντομη αξιολόγηση στην επόμενη φάση"}
                        </p>
                      </div>
                    </div>
                  </C>
                )}

                {/* Support section — correct hierarchy */}
                <div style={{ marginBottom: 16 }}>
                  <p style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: DK }}>Χρειάζεστε βοήθεια;</p>
                  <p style={{ margin: "0 0 14px", fontSize: 13, color: MG, lineHeight: 1.5 }}>
                    Έχετε απορία ή κάτι δεν ακούγεται σωστά;
                  </p>
                  {/* 1. PRIMARY — Κλείσε ραντεβού */}
                  <button onClick={() => { setSub("newappt"); setNewAReason(""); }} style={{
                    width: "100%", padding: "16px 20px", borderRadius: 16,
                    border: "none", background: "linear-gradient(135deg, #F97316, #EA580C)", color: WH,
                    fontSize: 15, fontWeight: 700, cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(249,115,22,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    marginBottom: 10, fontFamily: "inherit",
                  }}>
                    {Ic.cal(WH, 16)} Κλείσε ραντεβού
                  </button>
                  {/* 2. SECONDARY — Μίλα με τον ειδικό */}
                  <button onClick={() => setSub("chat")} style={{
                    width: "100%", padding: "16px 20px", borderRadius: 16,
                    border: "2px solid #F97316", background: WH, color: "#F97316", position: "relative",
                    fontSize: 15, fontWeight: 700, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    marginBottom: 10, fontFamily: "inherit",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}>
                    {Ic.chat("#F97316", 16)} Μίλα με τον ειδικό{chatUnread > 0 && <div style={{position:"absolute",top:8,right:12,width:10,height:10,borderRadius:"50%",background:"#C62828",border:"2px solid white"}} />}
                  </button>
                  {/* 3. TERTIARY — Ψηφιακός βοηθός */}
                  <button onClick={() => setSub("ai")} style={{
                    width: "100%", padding: "13px 16px", borderRadius: 14,
                    border: "none", background: "#F5F5F5", color: DK2,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    fontFamily: "inherit",
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
                    </svg>
                    Ρωτήστε τον Ορφέα
                  </button>
                </div>
              </>
            ) : (
              <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: DK, margin: 0 }}>Η Πρόοδός σας</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => setSub("notifs")} aria-label="Ειδοποιήσεις" style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: 30 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {notifsUnread > 0 && (<span style={{ position: "absolute", top: -4, right: -4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{notifsUnread > 9 ? "9+" : notifsUnread}</span>)}
                </button>
                <button onClick={() => setFs(f => f >= 2 ? 0 : f + 1)} style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", color: DK, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{fs > 0 ? "A−" : "A+"}</button>
              </div>
            </div>
            <p style={{ fontSize: 14, color: MG, margin: "0 0 14px" }}>Βήμα-βήμα προς πιο καθαρή ακοή</p>

            {/* Κεντρική κάρτα προόδου — πορτοκαλί + λευκά γράμματα */}
            <C s={{ background: `linear-gradient(135deg,${O},#D06A0E)`, border: "none", marginBottom: 12, textAlign: "center" }}>
              {/* Badge πάνω από το νούμερο */}
              <div style={{ display: "inline-block", background: "rgba(255,255,255,0.22)", borderRadius: 999, padding: "4px 12px", marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: WH, letterSpacing: 0.3 }}>
                  {ds < 7 ? "Καλή αρχή" : ds < 21 ? "Συνεχίζετε σωστά" : "Χτίζετε σταθερή συνήθεια"}
                </span>
              </div>
              <p style={{ margin: "0 0 4px", fontSize: 48, fontWeight: 800, color: WH, lineHeight: 1 }}>{dur.big}</p>
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "rgba(255,255,255,0.85)" }}>{dur.unit}</p>
              {dur.sub && (
                <p style={{ margin: "0 0 8px", fontSize: 11, color: "rgba(255,255,255,0.7)" }}>{dur.sub}</p>
              )}
              <p style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: WH }}>
                {ds < 3 ? "Ξεκινάτε σήμερα!"
                  : ds < 7 ? "Πολύ καλή αρχή!"
                  : ds < 14 ? "Ο εγκέφαλος ήδη προσαρμόζεται"
                  : "Η καθημερινή χρήση φέρνει σταθερή βελτίωση"}
              </p>
              <div style={{ background: "rgba(255,255,255,0.25)", borderRadius: 10, height: 6, overflow: "hidden" }}>
                <div style={{ width: `${Math.min((ds / 30) * 100, 100)}%`, height: "100%", background: WH, borderRadius: 10 }} />
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "rgba(255,255,255,0.8)" }}>Στόχος: άνετη και σταθερή καθημερινή χρήση</p>
            </C>

            {/* Επόμενος στόχος */}
            <C s={{ border: `1.5px solid #e0e0e0`, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                {Ic.prog(O, 18)}
                <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: MG, letterSpacing: 0.5 }}>ΕΠΟΜΕΝΟΣ ΣΤΟΧΟΣ</p>
              </div>
              <p style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 800, color: DK, lineHeight: 1.3 }}>
                {ds < 7 ? "Φοράτε τα κάθε μέρα, έστω και για λίγες ώρες"
                  : ds < 14 ? "Δοκιμάστε τα σε τηλεόραση και τηλέφωνο"
                  : ds < 28 ? "Φοράτε τα και σε θορυβώδη περιβάλλοντα"
                  : "Συνεχίστε τη σταθερή καθημερινή χρήση"}
              </p>
              <p style={{ margin: "0 0 10px", fontSize: 14, color: DK2, lineHeight: 1.6 }}>
                {ds < 7
                  ? "Η ακοή σας θα βελτιώνεται μέρα με την ημέρα. Ο εγκέφαλος αρχίζει σταδιακά να συνηθίζει τα νέα ακουστικά ερεθίσματα και να ξεχωρίζει καλύτερα τις φωνές και τους ήχους γύρω σας."
                  : ds < 14
                  ? "Ο εγκέφαλος έχει αρχίσει να προσαρμόζεται. Δοκιμάστε τα ακουστικά σε διαφορετικά περιβάλλοντα — θα εντοπίσετε πού χρειάζεστε ίσως μικρές ρυθμίσεις."
                  : ds < 28
                  ? "Περάσατε το κρίσιμο διάστημα προσαρμογής. Τώρα τα ακουστικά πρέπει να νιώθουν πιο φυσικά — δοκιμάστε τα σε πιο απαιτητικές καταστάσεις."
                  : "Έχετε χτίσει μια σταθερή συνήθεια χρήσης. Συνεχίστε να παρακολουθείτε τις εντυπώσεις σας και ενημερώστε μας αν χρειάζεται ρύθμιση."}
              </p>
              <div style={{ padding: "10px 12px", background: OL, borderRadius: 10 }}>
                <p style={{ margin: 0, fontSize: 13, color: DK }}>
                  <b style={{ color: O }}>Μικρό βήμα για σήμερα:</b>{" "}
                  {ds < 7 ? "Φορέστε τα σταθερά στο σπίτι, στην τηλεόραση και σε ήρεμες συζητήσεις."
                    : ds < 14 ? "Ανοίξτε την τηλεόραση σε φυσιολογική ένταση και προσπαθήστε να παρακολουθήσετε μια συζήτηση."
                    : ds < 28 ? "Βγείτε έξω — σε καφέ ή με παρέα — και παρατηρήστε πώς ξεχωρίζετε τις φωνές."
                    : "Κρατήστε σταθερή ρουτίνα χρήσης και σημειώστε τυχόν δυσκολίες για την επόμενη ρύθμιση."}
                </p>
              </div>
            </C>

            {/* Μικρή ενθάρρυνση */}
            <C s={{ border: `1.5px solid #e0e0e0`, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                {Ic.star(O, 18)}
                <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: MG, letterSpacing: 0.5 }}>ΜΙΚΡΗ ΝΙΚΗ</p>
              </div>
              <p style={{ margin: 0, fontSize: 14, color: DK, lineHeight: 1.6 }}>
                Κάθε μέρα που τα φοράτε είναι ένα βήμα μπροστά. Ακόμα και αν στην αρχή κάποια πράγματα σας φαίνονται ασυνήθιστα, η σταθερότητα στη χρήση οδηγεί συνήθως σε πιο φυσική ακρόαση και μεγαλύτερη άνεση.
              </p>
            </C>

            {/* v1.3.41 — Old checkpoint-gated questionnaire card removed.
                The Smart Quiz Prompt at the top of the home page (between
                hero and daily guidance) now handles questionnaire access
                with adaptive intensity instead of hiding it on non-checkpoint
                days. */}
            <Btn onClick={() => setSub("prog")} primary={false}>Συνέχισε το πρόγραμμα αποκατάστασης</Btn>

            {/* Support — 3 levels */}
            <div style={{ marginTop: 16 }}>
              <p style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: DK }}>Χρειάζεστε βοήθεια;</p>
              <button onClick={() => { setSub("newappt"); setNewAReason(""); }} style={{
                width: "100%", padding: "16px 20px", borderRadius: 16,
                border: "none", background: "linear-gradient(135deg, #F97316, #EA580C)", color: WH,
                fontSize: 15, fontWeight: 700, cursor: "pointer",
                boxShadow: "0 4px 14px rgba(249,115,22,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                marginBottom: 10, fontFamily: "inherit",
              }}>
                {Ic.cal(WH, 16)} Κλείσε ραντεβού
              </button>
              <button onClick={() => setSub("chat")} style={{
                width: "100%", padding: "16px 20px", borderRadius: 16,
                border: "2px solid #F97316", background: WH, color: "#F97316", position: "relative",
                fontSize: 15, fontWeight: 700, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                marginBottom: 10, fontFamily: "inherit",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}>
                {Ic.chat("#F97316", 16)} Μίλα με τον ειδικό{chatUnread > 0 && <div style={{position:"absolute",top:8,right:12,width:10,height:10,borderRadius:"50%",background:"#C62828",border:"2px solid white"}} />}
              </button>
              <button onClick={() => setSub("ai")} style={{
                width: "100%", padding: "13px 16px", borderRadius: 14,
                border: "none", background: "#F5F5F5", color: DK2,
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                fontFamily: "inherit",
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
                Ρωτήστε τον Ορφέα
              </button>
            </div>
              </>
            )}
          </div>
        )}
        {/* SHOP */}
        {tab === "shop" && (
          <div style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: DK, margin: 0 }}>Κατάστημα</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => setSub("notifs")} aria-label="Ειδοποιήσεις" style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: 30 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {notifsUnread > 0 && (<span style={{ position: "absolute", top: -4, right: -4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{notifsUnread > 9 ? "9+" : notifsUnread}</span>)}
                </button>
                <button onClick={() => setFs(f => f >= 2 ? 0 : f + 1)} style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", color: DK, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{fs > 0 ? "A−" : "A+"}</button>
              </div>
            </div>
            <p style={{ fontSize: 12, color: MG, margin: "0 0 14px" }}>
              Αναλώσιμα για τα ακουστικά σας
            </p>
            {ds > 25 && (
              <C
                s={{
                  border: `1.5px solid #e0e0e0`,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {Ic.shield(O, 22)}
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 13,
                        fontWeight: 700,
                        color: DK,
                      }}
                    >
                      Ίσως χρειάζεστε νέα φίλτρα
                    </p>
                    <p style={{ margin: "2px 0 0", fontSize: 11, color: MG }}>
                      Αλλαγή κάθε μήνα
                    </p>
                  </div>
                </div>
              </C>
            )}
            {SHOP.map((p, i) => (
              <C
                key={i}
                onClick={() => window.open(p.url, "_blank")}
                s={{ cursor: "pointer", marginBottom: 12, padding: "16px 18px" }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{ width: 56, height: 56, borderRadius: 14, background: LG, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {p.icon(DK, 26)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <p style={{ margin: 0, fontSize: 16, fontWeight: 800, color: DK }}>{p.l}</p>
                      <span style={{ fontSize: 10, fontWeight: 700, color: WH, background: DK, borderRadius: 6, padding: "3px 8px", letterSpacing: 0.3 }}>{p.badge}</span>
                    </div>
                    <p style={{ margin: "0 0 6px", fontSize: 13, color: MG, lineHeight: 1.4 }}>{p.sub}</p>
                    <p style={{ margin: "0 0 4px", fontSize: 12, color: DK2 }}>{p.when}</p>
                    <p style={{ margin: 0, fontSize: 12, color: O, fontWeight: 700 }}>
                      {p.l === "Μπαταρίες" && batteryProfile ? `Τύπος ${batteryProfile.batteryType} • περίπου κάθε ${batteryProfile.packDays} ημέρες` : p.hint}
                    </p>
                  </div>
                  <div style={{ background: O, borderRadius: 10, padding: "10px 14px", flexShrink: 0, alignSelf: "center" }}>
                    <span style={{ fontSize: 14, fontWeight: 800, color: WH }}>Αγορά</span>
                  </div>
                </div>
              </C>
            ))}
            {/* LOYALTY CARD — μόνο για battery users */}
            {batteryProfile && profId && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: MG, margin: "0 0 10px", letterSpacing: 0.5 }}>ΚΑΡΤΑ ΜΠΑΤΑΡΙΩΝ</p>
                <div style={{
                  background: `linear-gradient(135deg, #EF801A 0%, #D06A0E 60%, #B85A08 100%)`,
                  borderRadius: 20, padding: 20, position: "relative", overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(239,128,26,0.35)",
                }}>
                  {/* Decorative circles */}
                  <div style={{ position: "absolute", top: -30, right: -20, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
                  <div style={{ position: "absolute", bottom: -20, left: -20, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />

                  {/* Top row: title + QR */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                      <p style={{ margin: "0 0 2px", fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.75)", letterSpacing: 1 }}>EARMEDICAL</p>
                      <p style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 800, color: WH }}>Κάρτα Μπαταριών</p>
                      <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 800, color: WH }}>{nm}</p>
                      {/* Battery type badge */}
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.15)", borderRadius: 20, padding: "3px 10px" }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: batteryProfile.batteryColor, border: "2px solid rgba(255,255,255,0.6)" }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: WH }}>Νο{batteryProfile.batteryType} — {batteryProfile.batteryColorName}</span>
                      </div>
                    </div>
                    {/* QR */}
                    <div style={{ background: WH, borderRadius: 10, padding: 4, flexShrink: 0, marginLeft: 12 }}>
                      <QRCanvas value={`earmedical:${profId}`} size={72} />
                    </div>
                  </div>

                  {/* Stamps row */}
                  <div style={{ display: "flex", gap: 8, marginTop: 14, marginBottom: 8 }}>
                    {Array.from({ length: clinicId === "katerini" ? 2 : 5 }).map((_, i) => {
                      const stampsNeeded = clinicId === "katerini" ? 2 : 5;
                      const cur = loyaltyStamps % stampsNeeded || (loyaltyStamps > 0 && loyaltyStamps % stampsNeeded === 0 ? stampsNeeded : loyaltyStamps % stampsNeeded);
                      const filled = i < cur;
                      return (
                        <div key={i} style={{
                          width: 38, height: 38, borderRadius: 10,
                          background: filled ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.2)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: "2px solid rgba(255,255,255,0.4)"
                        }}>
                          {filled ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF801A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          ) : (
                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.5)" }} />
                          )}
                        </div>
                      );
                    })}
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: "rgba(255,255,255,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: "2px dashed rgba(255,255,255,0.5)"
                    }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
                      </svg>
                    </div>
                  </div>

                  {/* Progress text */}
                  <p style={{ margin: 0, fontSize: 11, color: "rgba(255,255,255,0.85)" }}>
                    {loyaltyStamps % (clinicId === "katerini" ? 2 : 5) === 0 && loyaltyStamps > 0
                      ? "Δικαιούστε δωρεάν μπαταρίες!"
                      : `${loyaltyStamps % (clinicId === "katerini" ? 2 : 5)}/${clinicId === "katerini" ? 2 : 5} σφραγίδες • ${(clinicId === "katerini" ? 2 : 5) - (loyaltyStamps % (clinicId === "katerini" ? 2 : 5))} ακόμα για δώρο`}
                  </p>
                </div>
              </div>
            )}

            <p style={{ fontSize: 10, color: MG, textAlign: "center" }}>
              earmedical.gr • Αποστολή σε όλη την Ελλάδα
            </p>
          </div>
        )}
        {/* MENU */}
        {tab === "more" && (
          <div style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ fontSize: 20, fontWeight: 800, color: DK, margin: 0 }}>Μενού</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => setSub("notifs")} aria-label="Ειδοποιήσεις" style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", cursor: "pointer", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", height: 30 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  {notifsUnread > 0 && (<span style={{ position: "absolute", top: -4, right: -4, background: "#E53935", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 99, minWidth: 16, height: 16, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{notifsUnread > 9 ? "9+" : notifsUnread}</span>)}
                </button>
                <button onClick={() => setFs(f => f >= 2 ? 0 : f + 1)} style={{ background: LG, border: "none", borderRadius: 10, padding: "6px 10px", color: DK, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>{fs > 0 ? "A−" : "A+"}</button>
              </div>
            </div>

            {/* Ο λογαριασμός μου */}
            <p style={{ fontSize: 11, fontWeight: 700, color: MG, margin: "0 0 8px", letterSpacing: 0.5 }}>Ο ΛΟΓΑΡΙΑΣΜΟΣ ΜΟΥ</p>
            {[
              { l: "Προφίλ μου", t: () => setSub("profile"), ic: Ic.chk(DK, 18) },
              { l: "Προγραμμάτισε Ραντεβού", t: () => setSub("newappt"), ic: Ic.cal(DK, 18) },
            ].map((x, i) => (
              <C key={"m1"+i} onClick={x.t} s={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: OL, display: "flex", alignItems: "center", justifyContent: "center" }}>{x.ic}</div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: DK }}>{x.l}</span>
                  </div>
                  {Ic.rt(MG, 16)}
                </div>
              </C>
            ))}

            {/* Η αποκατάστασή μου */}
            <p style={{ fontSize: 11, fontWeight: 700, color: MG, margin: "16px 0 8px", letterSpacing: 0.5 }}>Η ΑΠΟΚΑΤΑΣΤΑΣΉ ΜΟΥ</p>
            {[
              ...(!isTrial ? [{ l: "Πρόγραμμα Αποκατάστασης", t: () => setSub("prog"), ic: Ic.ear(DK, 18) }] : []),
              { l: "Ερωτηματολόγιο", t: () => setSub("quiz"), ic: Ic.chk(DK, 18) },
              { l: "Videos / Οδηγίες", t: () => window.open("https://youtube.com/@earmedical", "_blank"), ic: Ic.prog(DK, 18) },
            ].map((x, i) => (
              <C key={"m2"+i} onClick={x.t} s={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: LG, display: "flex", alignItems: "center", justifyContent: "center" }}>{x.ic}</div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: DK }}>{x.l}</span>
                  </div>
                  {Ic.rt(MG, 16)}
                </div>
              </C>
            ))}

            {/* Επικοινωνία */}
            <p style={{ fontSize: 11, fontWeight: 700, color: MG, margin: "16px 0 8px", letterSpacing: 0.5 }}>ΕΠΙΚΟΙΝΩΝΙΑ</p>
            {[
              { l: "Μίλα με τον ειδικό", t: () => setSub("chat"), ic: Ic.ear(DK, 18), badge: chatUnread },
              { l: "Κλείσε ραντεβού", t: () => { setSub("newappt"); setNewAReason(""); }, ic: Ic.cal(DK, 18) },
              { l: "Κάλεσε το κέντρο", t: () => window.open(`tel:${clinic.t}`), ic: Ic.ph(DK, 18) },
              { l: "Μίλα με το κέντρο", t: ch, ic: Ic.chat(DK, 18) },
            ].map((x, i) => (
              <C key={"mc"+i} onClick={x.t} s={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: LG, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                      {x.ic}
                      {x.badge > 0 && (
                        <div style={{
                          position: "absolute", top: -4, right: -4,
                          width: 18, height: 18, borderRadius: "50%",
                          background: "#C62828", color: WH,
                          fontSize: 10, fontWeight: 800,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          border: "2px solid " + WH,
                        }}>{x.badge}</div>
                      )}
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: DK }}>{x.l}</span>
                  </div>
                  {Ic.rt(MG, 16)}
                </div>
              </C>
            ))}

            {/* Clinic open/closed status */}
            {(() => {
              const st = isClinicOpen(clinic);
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", marginBottom: 4, background: st.open ? "#F0F7F0" : "#FFF5F5", borderRadius: 10 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: st.open ? "#2E7D32" : "#C62828", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: st.open ? "#2E7D32" : "#C62828", fontWeight: 600 }}>
                    {clinic.c}: {st.text}
                  </span>
                  {!st.open && st.next && <span style={{ fontSize: 11, color: MG }}> • Ανοίγει {st.next}</span>}
                </div>
              );
            })()}

            {/* Η αποκατάστασή μου */}
            <p style={{ fontSize: 11, fontWeight: 700, color: MG, margin: "16px 0 8px", letterSpacing: 0.5 }}>Η ΑΠΟΚΑΤΑΣΤΑΣΉ ΜΟΥ</p>
            {[
              { l: "Ορφέας", t: () => setSub("ai"), ic: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 014 4v4a4 4 0 01-8 0V6a4 4 0 014-4z"/><path d="M19 10v1a7 7 0 01-14 0v-1"/><line x1="12" y1="19" x2="12" y2="22"/></svg> },
              ...(!isTrial ? [{ l: "Πρόγραμμα Αποκατάστασης", t: () => setSub("prog"), ic: Ic.ear(DK, 18) }] : []),
              { l: "Ερωτηματολόγιο", t: () => setSub("quiz"), ic: Ic.chk(DK, 18) },
              { l: "Videos / Οδηγίες", t: () => window.open("https://youtube.com/@earmedical", "_blank"), ic: Ic.prog(DK, 18) },
              { l: "Άμεση Βοήθεια", t: () => setSub("sos"), ic: Ic.al(DK, 18) },
            ].map((x, i) => (
              <C key={"m2"+i} onClick={x.t} s={{ marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 12, background: LG, display: "flex", alignItems: "center", justifyContent: "center" }}>{x.ic}</div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: DK }}>{x.l}</span>
                  </div>
                  {Ic.rt(MG, 16)}
                </div>
              </C>
            ))}

            {/* Κοινότητα */}
            <p style={{ fontSize: 11, fontWeight: 700, color: MG, margin: "16px 0 8px", letterSpacing: 0.5 }}>ΚΟΙΝΌΤΗΤΑ</p>
            {[
              {
                l: "Στείλε στην Οικογένεια",
                t: () => {
                  const text = "Φοράω ακουστικά βαρηκοΐας.\n\nTips:\n- Μίλα μου καθαρά, όχι δυνατά\n- Κοίτα με όταν μιλάς\n- Σε θόρυβο πλησίασε\n- Κάνε υπομονή!\n\n— Earmedical";
                  shareContent(text, "Tips για την οικογένεια");
                },
                ic: Ic.snd(DK, 18),
              },
              {
                l: "Σύστησέ μας (100€ έκπτωση)",
                t: () => {
                  const text = "Δωρεάν έλεγχος ακοής στην Earmedical!\nΑνέφερε το όνομά μου: 100€ έκπτωση.\nΤηλ: 210 360 8005\nearmedical.gr";
                  shareContent(text, "Earmedical — 100€ έκπτωση");
                },
                ic: Ic.snd(DK, 18),
              },
              {
                l: "Αξιολογήστε μας στο Google",
                t: () => setSub("review"),
                ic: Ic.star(DK, 18),
              },
            ].map((x, i) => (
              <C key={i} onClick={x.t}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: LG, display: "flex", alignItems: "center", justifyContent: "center" }}>{x.ic}</div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: DK }}>{x.l}</span>
                  </div>
                  {Ic.rt(MG, 16)}
                </div>
              </C>
            ))}

            {/* Εταιρείες */}
            <p style={{ fontSize: 11, fontWeight: 700, color: MG, margin: "16px 0 8px", letterSpacing: 0.5 }}>ΕΤΑΙΡΕΙΕΣ</p>
            {[
              { l: "Earmedical — Κέντρα Ακοής", sub: "Εξειδικευμένη αποκατάσταση ακοής", t: () => window.open("https://www.earmedical.gr", "_blank") },
              { l: "Coselgi by Earmedical", sub: "Τεχνολογίες ακουστικών βαρηκοΐας", t: () => window.open("https://coselgi.earmedical.gr", "_blank") },
            ].map((x, i) => (
              <C key={"et"+i} onClick={x.t} s={{ marginBottom: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 700, color: DK }}>{x.l}</p>
                    <p style={{ margin: 0, fontSize: 12, color: MG }}>{x.sub}</p>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </div>
              </C>
            ))}
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: MG,
                margin: "6px 0 8px",
                letterSpacing: 0.5,
              }}
            >
              ΚΕΝΤΡΑ ΑΚΟΗΣ
            </p>
            {LOCS.map((l, i) => {
              const status = isClinicOpen(l);
              return (
              <C key={i}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {Ic.pin(O, 16)}
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: DK }}>{l.c}</span>
                      <br />
                      <span style={{ fontSize: 10, color: MG }}>{l.a}</span>
                    </div>
                  </div>
                  <a href={`tel:${l.t}`} style={{ padding: "5px 10px", background: OL, borderRadius: 8, color: O, fontWeight: 700, fontSize: 11, textDecoration: "none" }}>
                    {l.f}
                  </a>
                </div>
                {/* Ωράριο + status */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, padding: "6px 10px", background: status.open ? "#F0F7F0" : "#FFF5F5", borderRadius: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: status.open ? "#2E7D32" : "#C62828", flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: status.open ? "#2E7D32" : "#C62828", fontWeight: 600 }}>{status.text}</span>
                  {!status.open && status.next && <span style={{ fontSize: 10, color: MG, marginLeft: 4 }}>• Ανοίγει {status.next}</span>}
                </div>
                <p style={{ fontSize: 10, color: MG, margin: "0 0 8px", lineHeight: 1.5 }}>{l.hoursText}</p>
                <button
                  onClick={() => window.open(l.map, "_blank")}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 6, padding: "8px 0", background: LG, borderRadius: 10,
                    color: DK, fontWeight: 600, fontSize: 11, border: "none",
                    cursor: "pointer", width: "100%",
                  }}
                >
                  {Ic.pin(O, 12)} Google Maps
                </button>
              </C>
              );
            })}

            {/* Reset */}
            <div style={{ marginTop: 24, padding: "16px 0", borderTop: "1px solid #eee" }}>
              {/* Install app — τέρμα κάτω, πριν το reset */}
              {!isAppInstalled && (
                <button
                  onClick={handleInstallApp}
                  style={{
                    width: "100%", padding: "14px", borderRadius: 14,
                    border: `1.5px solid ${O}40`, background: WH,
                    color: O, fontSize: 14, fontWeight: 700,
                    cursor: "pointer", marginBottom: 12,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v13M5 9l7-7 7 7"/>
                    <path d="M3 15v4a2 2 0 002 2h14a2 2 0 002-2v-4"/>
                  </svg>
                  Εγκατάσταση εφαρμογής
                </button>
              )}
              <button
                onClick={() => {
                  if (window.confirm("Θέλετε σίγουρα αποσύνδεση;")) {
                    authLogout();
                  }
                }}
                style={{
                  width: "100%", padding: "12px", borderRadius: 12,
                  border: "1.5px solid #e0e0e0", background: WH,
                  color: DK, fontSize: 13, fontWeight: 600,
                  cursor: "pointer", marginTop: 8,
                }}
              >
                Αποσύνδεση
              </button>
              <button
                onClick={() => {
                  if (window.confirm("Θέλετε σίγουρα να κάνετε επαναφορά; Θα χαθούν τα τοπικά δεδομένα και θα ξεκινήσετε από την αρχή.")) {
                    localStorage.clear();
                    window.location.reload();
                  }
                }}
                style={{
                  width: "100%", padding: "12px", borderRadius: 12,
                  border: "1.5px solid #C6282840", background: WH,
                  color: "#C62828", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", marginTop: 8,
                }}
              >
                Επαναφορά εφαρμογής
              </button>
              <p style={{ margin: "6px 0 0", fontSize: 10, color: MG, textAlign: "center" }}>
                Τα δεδομένα σας παραμένουν στη βάση μας
              </p>
              {/* ═══════════════════════════════════════════════════════════ */}
              {/*  ΟΔΗΓΙΕΣ ΧΡΗΣΗΣ — Help section                              */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid #f0f0f0" }}>
                <button
                  onClick={() => {
                    setExpandedGuideSection(0);
                    setSub("guide");
                  }}
                  style={{
                    width: "100%", padding: "13px 14px", borderRadius: 12,
                    border: "1px solid #eee", background: WH,
                    color: DK, fontSize: 13, fontWeight: 600,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: `${O}15`, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
                    </svg>
                  </div>
                  <span style={{ flex: 1, textAlign: "left" }}>Οδηγίες Χρήσης</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>

              {/* ═══════════════════════════════════════════════════════════ */}
              {/*  ΝΟΜΙΚΑ — Privacy Policy + Διαγραφή Λογαριασμού             */}
              {/*  Google Play / Apple App Store Compliance                   */}
              {/* ═══════════════════════════════════════════════════════════ */}
              <div style={{ marginTop: 16, paddingTop: 0 }}>

                {/* Ρυθμίσεις Ειδοποιήσεων (v1.3.13) */}
                <button
                  onClick={() => setSub("settings")}
                  style={{
                    width: "100%", padding: "13px 14px", borderRadius: 12,
                    border: "1px solid #eee", background: WH,
                    color: DK, fontSize: 13, fontWeight: 600,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10,
                    marginBottom: 8,
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: `${O}15`, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                      <path d="M13.73 21a2 2 0 01-3.46 0"/>
                    </svg>
                  </div>
                  <span style={{ flex: 1, textAlign: "left" }}>Ρυθμίσεις Ειδοποιήσεων</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>

                {/* Πολιτική Απορρήτου */}
                <button
                  onClick={() => window.open("https://www.earmedical.gr/privacy-policy/", "_blank", "noopener,noreferrer")}
                  style={{
                    width: "100%", padding: "13px 14px", borderRadius: 12,
                    border: "1px solid #eee", background: WH,
                    color: DK, fontSize: 13, fontWeight: 600,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 10,
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: `${O}15`, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={O} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </div>
                  <span style={{ flex: 1, textAlign: "left" }}>Πολιτική Απορρήτου</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>

                {/* Διαγραφή Λογαριασμού */}
                <button
                  onClick={() => {
                    setShowDeleteModal(true);
                    setDeleteConfirmText("");
                    setDeleteError("");
                  }}
                  style={{
                    width: "100%", padding: "13px 14px", borderRadius: 12,
                    border: "1px solid #eee", background: WH,
                    color: DK, fontSize: 13, fontWeight: 600,
                    cursor: "pointer", marginTop: 10,
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 10,
                    background: "#FFEBEE", display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C62828" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                    </svg>
                  </div>
                  <span style={{ flex: 1, textAlign: "left" }}>Διαγραφή Λογαριασμού</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>

              <p style={{ margin: "12px 0 0", fontSize: 10, color: MG, textAlign: "center", letterSpacing: 0.5 }}>
                Earmedical App v{process.env.REACT_APP_VERSION || '?'}
              </p>
            </div>
          </div>
        )}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/*  DELETE ACCOUNT MODAL                                          */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {showDeleteModal && (
          <div
            onClick={() => !deleteLoading && setShowDeleteModal(false)}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.55)",
              display: "flex", alignItems: "flex-end", justifyContent: "center",
              zIndex: 9999, padding: 0,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: WH,
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
                width: "100%", maxWidth: 480,
                maxHeight: "92vh", overflowY: "auto",
                padding: "20px 22px 32px",
                boxShadow: "0 -8px 30px rgba(0,0,0,0.15)",
                animation: "slideUp 0.3s ease-out",
              }}
            >
              {/* Drag handle */}
              <div style={{
                width: 40, height: 4, background: "#ddd",
                borderRadius: 4, margin: "0 auto 18px",
              }} />

              {/* Icon */}
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: "#FFEBEE", display: "flex",
                alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px",
              }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#C62828" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                  <line x1="10" y1="11" x2="10" y2="17"/>
                  <line x1="14" y1="11" x2="14" y2="17"/>
                </svg>
              </div>

              <h2 style={{
                margin: "0 0 6px", fontSize: 20, fontWeight: 800,
                color: DK, textAlign: "center",
              }}>
                Διαγραφή Λογαριασμού
              </h2>
              <p style={{
                margin: "0 0 18px", fontSize: 13, color: MG,
                textAlign: "center", lineHeight: 1.45,
              }}>
                Αυτή η ενέργεια είναι μόνιμη και δεν αναιρείται
              </p>

              {/* Body text */}
              <div style={{
                background: "#FAFAFA",
                borderRadius: 14, padding: 16, marginBottom: 14,
              }}>
                <p style={{ margin: "0 0 10px", fontSize: 13, color: DK, fontWeight: 700 }}>
                  Με την επιβεβαίωση θα διαγραφούν:
                </p>
                <ul style={{ margin: "0 0 14px", paddingLeft: 18, fontSize: 12.5, color: DK, lineHeight: 1.7 }}>
                  <li>Τα προσωπικά σας στοιχεία (όνομα, τηλέφωνο, email, διεύθυνση)</li>
                  <li>Το ιστορικό συνομιλιών με τον ακοοπροθετιστή</li>
                  <li>Οι ειδοποιήσεις και τα check-ins</li>
                  <li>Τα στοιχεία σύνδεσής σας</li>
                </ul>

                {/* Legal disclaimer — KRITIKO */}
                <div style={{
                  background: "#FFF8E1",
                  borderLeft: `3px solid ${O}`,
                  padding: "10px 12px",
                  borderRadius: 6,
                  marginTop: 4,
                }}>
                  <p style={{ margin: 0, fontSize: 11.5, color: DK, lineHeight: 1.55 }}>
                    <strong>Σημείωση:</strong> Η διαγραφή αφορά τα δεδομένα της εφαρμογής. Δεν αντικαθιστά ιατρικά, λογιστικά ή νόμιμα αρχεία (π.χ. ραντεβού, παραστατικά) που μπορεί να απαιτείται να διατηρηθούν σύμφωνα με την ισχύουσα νομοθεσία.
                  </p>
                </div>
              </div>

              {/* Confirmation input */}
              <label style={{
                display: "block", fontSize: 12, color: DK,
                marginBottom: 8, fontWeight: 600,
              }}>
                Πληκτρολογήστε <strong style={{ color: "#C62828" }}>ΔΙΑΓΡΑΦΗ</strong> για επιβεβαίωση:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                disabled={deleteLoading}
                placeholder="ΔΙΑΓΡΑΦΗ"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                style={{
                  width: "100%", padding: "13px 14px", fontSize: 14,
                  border: `1.5px solid ${deleteConfirmText === "ΔΙΑΓΡΑΦΗ" ? "#C62828" : "#ddd"}`,
                  borderRadius: 10, boxSizing: "border-box", outline: "none",
                  fontFamily: "inherit", transition: "border 0.2s",
                  letterSpacing: 1,
                }}
              />

              {/* Error */}
              {deleteError && (
                <div style={{
                  background: "#FFEBEE", color: "#C62828",
                  padding: "10px 12px", borderRadius: 8, marginTop: 10,
                  fontSize: 12, fontWeight: 600,
                }}>
                  {deleteError}
                </div>
              )}

              {/* Buttons */}
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteLoading}
                  style={{
                    flex: 1, padding: "13px", borderRadius: 12,
                    border: "1.5px solid #ddd", background: WH,
                    color: DK, fontSize: 14, fontWeight: 600,
                    cursor: deleteLoading ? "not-allowed" : "pointer",
                    opacity: deleteLoading ? 0.5 : 1,
                  }}
                >
                  Ακύρωση
                </button>
                <button
                  onClick={authDeleteAccount}
                  disabled={deleteLoading || deleteConfirmText !== "ΔΙΑΓΡΑΦΗ"}
                  style={{
                    flex: 1.3, padding: "13px", borderRadius: 12,
                    border: "none",
                    background: (deleteLoading || deleteConfirmText !== "ΔΙΑΓΡΑΦΗ") ? "#FFCDD2" : "#C62828",
                    color: WH, fontSize: 14, fontWeight: 700,
                    cursor: (deleteLoading || deleteConfirmText !== "ΔΙΑΓΡΑΦΗ") ? "not-allowed" : "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  {deleteLoading ? "Διαγραφή..." : "Διαγραφή Οριστικά"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
      {/* FAB — πιο απαλό, μικρότερο */}
      {/* NAV */}
      <div
        style={{
          display: "flex",
          background: WH,
          borderTop: "none",
          boxShadow: "0 -1px 8px rgba(0,0,0,0.04)",
          // v1.3.25 — Δύο sources για το bottom inset:
          //   --em-safe-bottom : injected από MainActivity.java (Android
          //     Capacitor). Δίνει τη ΠΡΑΓΜΑΤΙΚΗ τιμή του gesture/3-button
          //     bar από WindowInsets. Στο PWA browser είναι κενό.
          //   env(safe-area-inset-bottom) : Chrome/Safari PWA mode.
          // Χρησιμοποιούμε var() με fallback στο env() για να καλύψουμε
          // και τις δύο διαδρομές. Προσθέτουμε +6px breathing room ώστε
          // τα labels να μη αγγίζουν το system bar.
          paddingBottom: "var(--em-nav-pad-bottom)",
          paddingTop: 4,
        }}
      >
        {tabs.map((t) => {
          const isOrfeas = !!t.orfeas;
          // v1.3.17/1.3.56 — chat (ειδικός) ενεργό όταν sub==="chat"· Ορφέας όταν
          // sub==="ai"· τα υπόλοιπα όταν tab===id χωρίς ανοιχτή sub-view.
          const isActive = t.id === "chat" ? sub === "chat" : (isOrfeas ? sub === "ai" : (tab === t.id && !sub));
          return (
          <button
            key={t.id}
            onClick={() => {
              // v1.3.17 — Το chat (ειδικός) είναι sub-view του home, όχι top-level tab.
              if (t.id === "chat") {
                setTab("home");
                setSub("chat");
                setCheckin(null);
                return;
              }
              // v1.3.56 — Ο Ορφέας (AI) είναι το κυρίαρχο κεντρικό tab· ανοίγει ως
              // sub-view του home (ai), όχι top-level tab.
              if (isOrfeas) {
                setTab("home");
                setSub("ai");
                setCheckin(null);
                return;
              }
              setTab(t.id);
              setSub(null);
              setCheckin(null);
            }}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              padding: "3px 0 4px",
              border: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            {isOrfeas ? (
              // v1.3.59 — Κυρίαρχο κεντρικό κουμπί: πορτοκαλί κύκλος με μικρόφωνο, ΜΕΣΑ στη
              // μπάρα (χωρίς αρνητικό margin που ξεχείλιζε/κόβονταν σε iPhone). Ίδιο ύψος slot
              // (30px) με τα υπόλοιπα → ευθυγραμμισμένα labels. Δεσπόζει μέσω χρώματος/σκιάς.
              <>
                <div style={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "linear-gradient(135deg, #FB923C 0%, #EA580C 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(234,88,12,0.40)",
                  }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={WH} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
                      <path d="M19 11v1a7 7 0 0 1-14 0v-1"/>
                      <line x1="12" y1="19" x2="12" y2="22"/>
                    </svg>
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: O, letterSpacing: 0.2 }}>{t.l}</span>
              </>
            ) : (
              <>
                <div style={{ position: "relative", height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {t.ic(isActive)}
                  {/* v1.3.17/1.3.56 — Unread badge του ειδικού: αφού ο ειδικός δεν είναι
                      πλέον tab στη μπάρα, πέφτει στο «Μενού». */}
                  {((t.id === "chat") || (t.id === "more" && !tabs.some(x => x.id === "chat"))) && chatUnread > 0 && (
                    <div style={{
                      position: "absolute", top: 0, right: -6,
                      width: 10, height: 10, borderRadius: "50%",
                      background: "#C62828",
                      border: "2px solid " + WH,
                    }} />
                  )}
                </div>
                <span
                  style={{
                    // v1.3.24 — Bigger + darker inactive labels.
                    fontSize: 11,
                    fontWeight: isActive ? 700 : 600,
                    color: isActive ? O : DK2,
                    letterSpacing: 0.2,
                  }}
                >
                  {t.l}
                </span>
              </>
            )}
          </button>
          );
        })}
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* v1.3.58 — ORFEAS MOMENT OVERLAY (μόνο κρίσιμα· 1/μέρα)        */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {momentOverlayOpen && orfeasMoment && (
        <div onClick={() => setMomentOverlayOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(20,20,22,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 480, background: BG, borderRadius: "20px 20px 0 0", padding: "20px 20px calc(20px + var(--em-nav-pad-bottom))", boxShadow: "0 -8px 30px rgba(0,0,0,0.25)", animation: "emSheetIn 0.35s ease both" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
              <button onClick={() => setMomentOverlayOpen(false)} aria-label="Κλείσιμο" style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex" }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={MG} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            {momentBody(orfeasMoment)}
          </div>
          <style>{`@keyframes emSheetIn{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:none}}`}</style>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* OFFERS FULL-SCREEN VIEW                                      */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {offerScreen && (
        <div style={{
          position: "fixed", inset: 0, background: BG, zIndex: 9999,
          display: "flex", flexDirection: "column",
          animation: "slideUp 0.25s ease-out",
          overflowY: "auto",
          zoom: 1 + fs * 0.15,
        }}>
          {/* Header */}
          <div style={{
            padding: "16px 20px", background: WH, borderBottom: "1px solid #eee",
            display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10,
          }}>
            <button
              onClick={() => { setOfferScreen(false); setOfferThankYou(false); }}
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                padding: 4, display: "flex", alignItems: "center",
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={DK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: DK, flex: 1 }}>
              {isSupport ? "Προτάσεις για εσάς" : (isTrial ? "Οι προτάσεις σας" : "Νέες επιλογές")}
            </h2>
          </div>

          {/* Thank you screen */}
          {offerThankYou && (
            <div style={{
              padding: 40, textAlign: "center", flex: 1,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: "50%", background: "#E8F5E9",
                display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20,
                animation: "earCheckPop 0.4s cubic-bezier(0.34,1.56,0.64,1)",
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 800, color: DK }}>
                Έγινε! Προχωράμε μαζί
              </h2>
              <p style={{ margin: "0 0 8px", fontSize: 15, color: DK, maxWidth: 340, lineHeight: 1.5 }}>
                Η επιλογή σας στάλθηκε στον ακοοπροθετιστή σας.
              </p>
              <p style={{ margin: "0 0 24px", fontSize: 14, color: MG, maxWidth: 340, lineHeight: 1.5 }}>
                Θα επικοινωνήσει σύντομα μαζί σας για τα επόμενα βήματα.
              </p>
              <button
                onClick={() => { setOfferScreen(false); setOfferThankYou(false); }}
                style={{
                  background: O, color: WH, border: "none",
                  padding: "14px 32px", borderRadius: 14, fontSize: 15, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Επιστροφή στην Αρχική
              </button>
            </div>
          )}

          {/* Offers list */}
          {!offerThankYou && (
            <div style={{ padding: "16px 16px 32px" }}>
              {activeSentOffers.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: MG }}>
                  Δεν υπάρχουν ενεργές προτάσεις αυτή τη στιγμή.
                </div>
              )}
              {activeSentOffers.map((offer, oi) => {
                const items = offer.items || [];
                const recommendedItem = items.find(it => it.is_recommended);
                // Sort so recommended comes first
                const sortedItems = [...items].sort((a, b) => {
                  if (a.is_recommended && !b.is_recommended) return -1;
                  if (!a.is_recommended && b.is_recommended) return 1;
                  return 0;
                });

                return (
                  <div key={offer.id}>
                    {oi === 0 && (
                      <p style={{
                        margin: "4px 4px 16px", fontSize: 14, color: DK, lineHeight: 1.5,
                      }}>
                        Ο ακοοπροθετιστής σας ετοίμασε {items.length === 1 ? "την ακόλουθη πρόταση" : `τις ακόλουθες ${items.length} επιλογές`}. Διαβάστε τα και επιλέξτε αυτή που σας ταιριάζει καλύτερα.
                      </p>
                    )}

                    {sortedItems.map((it, idx) => {
                      const packageLabel = {
                        pair: "Ζευγάρι (2 ακουστικά)",
                        single_left: "Μονό — Αριστερό αυτί",
                        single_right: "Μονό — Δεξί αυτί",
                      }[it.package_type] || "Ζευγάρι";
                      const isExpanded = offerExpandedItem === it.id;
                      const features = it.features || [];
                      // Show top 3 benefits as simplified bullets
                      const topFeatures = isExpanded ? features : features.slice(0, 3);

                      return (
                        <div
                          key={it.id}
                          style={{
                            background: WH,
                            borderRadius: 16,
                            padding: 16,
                            marginBottom: 14,
                            border: it.is_recommended ? `2px solid ${O}` : "1px solid #eee",
                            boxShadow: it.is_recommended ? `0 4px 16px ${O}20` : "0 2px 8px rgba(0,0,0,0.04)",
                            position: "relative",
                          }}
                        >
                          {it.is_recommended && (
                            <div style={{
                              position: "absolute", top: -11, left: 16,
                              background: O, color: WH, padding: "4px 10px",
                              borderRadius: 10, fontSize: 11, fontWeight: 800,
                              letterSpacing: 0.5,
                            }}>ΠΡΟΤΕΙΝΟΜΕΝΗ ΕΠΙΛΟΓΗ</div>
                          )}

                          {/* Title */}
                          <div style={{ marginTop: it.is_recommended ? 8 : 0 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: DK, lineHeight: 1.2 }}>
                              {it.brand} {it.model}
                            </div>
                            <div style={{ fontSize: 12, color: MG, marginTop: 2 }}>
                              {packageLabel}
                              {it.rechargeable ? " • Επαναφορτιζόμενο" : ""}
                            </div>
                            {it.badge && !it.is_recommended && (
                              <span style={{
                                display: "inline-block", marginTop: 6,
                                background: "#F5F0FE", color: "#5B2BA5",
                                padding: "3px 10px", borderRadius: 10,
                                fontSize: 11, fontWeight: 700,
                              }}>{it.badge}</span>
                            )}
                          </div>

                          {/* Patient note */}
                          {it.patient_note && (
                            <p style={{
                              margin: "10px 0 0", fontSize: 13, color: DK,
                              lineHeight: 1.5, fontStyle: "italic",
                              background: "#FFF8F0", padding: "8px 10px", borderRadius: 8,
                              borderLeft: `3px solid ${O}`,
                            }}>
                              "{it.patient_note}"
                            </p>
                          )}

                          {/* Simplified features (top 3) */}
                          {topFeatures.length > 0 && (
                            <ul style={{
                              margin: "12px 0 0", padding: "0 0 0 18px",
                              fontSize: 13, color: DK, lineHeight: 1.7,
                            }}>
                              {topFeatures.map((f, fi) => (
                                <li key={fi}>{f}</li>
                              ))}
                            </ul>
                          )}

                          {/* Show more / AI button */}
                          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                            {features.length > 3 && (
                              <button
                                onClick={() => setOfferExpandedItem(isExpanded ? null : it.id)}
                                style={{
                                  background: "transparent", border: "none", color: O,
                                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                                  padding: "4px 0", fontFamily: "inherit",
                                }}
                              >
                                {isExpanded ? "Λιγότερα ▲" : `Δείτε περισσότερα (${features.length - 3} ακόμα) ▼`}
                              </button>
                            )}
                            <button
                              onClick={() => askAiAboutModel(offer.id, it.id, it.brand, it.model, it, items)}
                              style={{
                                background: "#F5F0FE", border: "none", color: "#5B2BA5",
                                fontSize: 12, fontWeight: 700, cursor: "pointer",
                                padding: "6px 12px", borderRadius: 10, fontFamily: "inherit",
                                display: "inline-flex", alignItems: "center", gap: 6,
                                marginLeft: "auto",
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B2BA5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                              </svg>
                              Τι διαφορά έχει;
                            </button>
                          </div>

                          {/* ━━━ Pricing with anchor framing ━━━ */}
                          {(() => {
                            const hasDiscount = (it.discount || 0) > 0;
                            const hasSubsidy = (it.subsidy_amount || 0) > 0;
                            const hasAnyBenefit = hasDiscount || hasSubsidy;
                            const copay = it.copay || 0;
                            const retail = it.retail_price || 0;

                            return (
                              <div style={{
                                marginTop: 14, padding: "14px 16px",
                                background: "#F0F9F0", borderRadius: 12,
                                border: hasAnyBenefit ? "1.5px solid #C8E6C9" : "none",
                              }}>
                                {/* Anchor price (only if there's a benefit) */}
                                {hasAnyBenefit && (
                                  <div style={{
                                    fontSize: 13, color: MG, marginBottom: 2,
                                    textDecoration: "line-through",
                                  }}>
                                    Από €{retail.toLocaleString('el-GR')}
                                  </div>
                                )}

                                {/* Main price */}
                                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                                  <div style={{ fontSize: 11, color: "#558B2F", fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>
                                    {hasAnyBenefit ? "Ποσό που θα πληρώσετε" : "Τελική τιμή"}
                                  </div>
                                </div>
                                <div style={{ fontSize: 30, fontWeight: 800, color: "#2E7D32", lineHeight: 1.1, marginTop: 2, marginBottom: 6 }}>
                                  €{copay.toLocaleString('el-GR')}
                                </div>

                                {/* Savings badges */}
                                {hasAnyBenefit && (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                                    {hasDiscount && (
                                      <span style={{
                                        background: "#FFF3E0", padding: "4px 10px", borderRadius: 10,
                                        fontSize: 11, fontWeight: 700, color: "#E65100",
                                      }}>
                                        ✓ Έκπτωση −€{it.discount}
                                      </span>
                                    )}
                                    {hasSubsidy && (
                                      <span style={{
                                        background: "#E3F2FD", padding: "4px 10px", borderRadius: 10,
                                        fontSize: 11, fontWeight: 700, color: "#1565C0",
                                      }}>
                                        ✓ ΕΟΠΥΥ −€{it.subsidy_amount}
                                      </span>
                                    )}
                                  </div>
                                )}

                                {/* Trust line under price */}
                                <div style={{
                                  fontSize: 11, color: "#558B2F", marginTop: 6,
                                  paddingTop: 8, borderTop: "1px solid #C8E6C9",
                                  display: "flex", alignItems: "center", gap: 6,
                                }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                  Περιλαμβάνει πλήρη εφαρμογή & ρυθμίσεις
                                </div>
                              </div>
                            );
                          })()}

                          {/* ━━━ Confidence Builder ━━━ */}
                          <div style={{
                            marginTop: 12, padding: "12px 14px",
                            background: "#F8F9FA", borderRadius: 10,
                            border: "1px solid #E0E0E0",
                          }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: DK, letterSpacing: 0.3, marginBottom: 8, textTransform: "uppercase" }}>
                              Η δέσμευσή μας
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12, color: DK, lineHeight: 1.4 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                                <span>Δοκιμαστική περίοδος</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                                <span>Δωρεάν ρυθμίσεις</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                                <span>Υποστήριξη από ειδικό</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                                <span>Επαλήθευση REM</span>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
                            <button
                              onClick={() => chooseOfferItem(offer.id, it.id, 'interested')}
                              disabled={offerSubmitting}
                              style={{
                                background: it.is_recommended ? O : "#2E7D32",
                                color: WH, border: "none",
                                padding: "16px", borderRadius: 14,
                                fontSize: 15, fontWeight: 800, cursor: "pointer",
                                fontFamily: "inherit",
                                opacity: offerSubmitting ? 0.6 : 1,
                                boxShadow: it.is_recommended ? `0 4px 14px ${O}40` : "0 4px 14px rgba(46,125,50,0.3)",
                              }}
                            >
                              {offerSubmitting ? "Αποστολή..." : "✓ Επιλέγω αυτή την πρόταση"}
                            </button>
                            <button
                              onClick={() => chooseOfferItem(offer.id, it.id, 'questions')}
                              disabled={offerSubmitting}
                              style={{
                                background: WH, color: DK,
                                border: "1.5px solid #ddd",
                                padding: "12px", borderRadius: 14,
                                fontSize: 14, fontWeight: 700, cursor: "pointer",
                                fontFamily: "inherit",
                                opacity: offerSubmitting ? 0.6 : 1,
                                display: "flex", flexDirection: "column", alignItems: "center",
                              }}
                            >
                              <span>Μιλήστε με τον ειδικό σας τώρα</span>
                              <span style={{ fontSize: 11, fontWeight: 500, color: MG, marginTop: 2 }}>
                                Απάντηση συνήθως σε λίγα λεπτά
                              </span>
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {/* Neutral exit — "Δεν είμαι έτοιμος ακόμα" */}
                    <div style={{ textAlign: "center", padding: "8px 0 20px" }}>
                      <button
                        onClick={() => chooseOfferItem(offer.id, '', 'not_ready')}
                        disabled={offerSubmitting}
                        style={{
                          background: "transparent", border: "none",
                          color: MG, fontSize: 13, cursor: "pointer",
                          padding: "8px 16px", fontFamily: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        Δεν είμαι έτοιμος/η ακόμα
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* AI EXPLANATION MODAL                                         */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {offerAiModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) { setOfferAiModal(null); setOfferAiAnswer(""); } }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000,
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            zoom: 1 + fs * 0.15,
          }}
        >
          <div style={{
            background: WH, borderRadius: "20px 20px 0 0",
            width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto",
            padding: 20, animation: "slideUp 0.25s ease-out",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: DK }}>
                {offerAiModal.brand} {offerAiModal.model}
              </h3>
              <button
                onClick={() => { setOfferAiModal(null); setOfferAiAnswer(""); }}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  fontSize: 24, color: MG, padding: 4, lineHeight: 1,
                }}
              >×</button>
            </div>
            <div style={{
              fontSize: 12, color: "#5B2BA5", fontWeight: 700,
              background: "#F5F0FE", padding: "8px 12px", borderRadius: 10, marginBottom: 14,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B2BA5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              Ορφέας
            </div>
            {offerAiLoading && (
              <div style={{ padding: 20, textAlign: "center", color: MG, fontSize: 14 }}>
                <div style={{ fontSize: 12, opacity: 0.8 }}>Ετοιμάζω απάντηση...</div>
              </div>
            )}
            {!offerAiLoading && offerAiAnswer && (
              <div style={{
                fontSize: 14, color: DK, lineHeight: 1.6, whiteSpace: "pre-wrap",
              }}>{offerAiAnswer}</div>
            )}
            <div style={{ marginTop: 20, padding: "12px", background: "#FFF8F0", borderRadius: 10, fontSize: 12, color: MG, lineHeight: 1.5 }}>
              Η τελική πρόταση γίνεται πάντα από τον ακοοπροθετιστή σας μετά από εξατομικευμένη εκτίμηση.
            </div>
          </div>
        </div>
      )}

      <style>{`*,*::before,*::after{box-sizing:border-box}input,select,textarea,button{box-sizing:border-box;max-width:100%;font-family:inherit}input,select,textarea{display:block;width:100%;font-size:16px}input[type="text"],input[type="tel"],input[type="email"],input[type="date"],input[type="number"],select{height:48px;border-radius:14px;border:1.5px solid #e5e5e5;padding:0 14px;font-size:16px;outline:none;transition:border-color 0.15s}input:focus,select:focus,textarea:focus{border-color:#EF801A}body{margin:0;-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}button{-webkit-tap-highlight-color:transparent}@keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}@keyframes earOfferPulse{0%,100%{box-shadow:0 4px 20px rgba(239,128,26,0.5)}50%{box-shadow:0 6px 28px rgba(239,128,26,0.75)}}@keyframes earCheckPop{0%{transform:scale(0);opacity:0}60%{transform:scale(1.15);opacity:1}100%{transform:scale(1);opacity:1}}@keyframes earEq{0%,100%{transform:scaleY(.32)}50%{transform:scaleY(1)}}`}</style>
    </div>
  );
}

/*
═══════════════════════════════════════════════════
  ΟΔΗΓΙΕΣ ΕΝΕΡΓΟΠΟΙΗΣΗΣ PUSH NOTIFICATIONS
═══════════════════════════════════════════════════

  ΒΗΜΑ 1: Δημιούργησε λογαριασμό στο https://onesignal.com (δωρεάν)

  ΒΗΜΑ 2: Δημιούργησε νέο App → Web Push → βάλε:
    - Site URL: https://app.earmedical.gr
    - Site Name: Earmedical

  ΒΗΜΑ 3: Αντίγραψε το App ID και βάλε το στη μεταβλητή
    ONESIGNAL_APP_ID πάνω στον κώδικα

  ΒΗΜΑ 4: Δημιούργησε αρχείο /public/OneSignalSDKWorker.js με περιεχόμενο:
    importScripts('https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.sw.js');

  ΒΗΜΑ 5: Πρόσθεσε στο manifest.json:
    {
      "name": "Earmedical",
      "short_name": "Earmedical",
      "display": "standalone",
      "start_url": "/",
      "icons": [{ "src": "/icon-192.png", "sizes": "192x192", "type": "image/png" }]
    }

  ΒΗΜΑ 6: Deploy & test!
    - Android: push notifications αμέσως
    - iOS: μόνο αν ο χρήστης κάνει "Add to Home Screen" (iOS 16.4+)

  ΣΤΕΛΝΕ NOTIFICATIONS ΑΠΟ:
    - OneSignal Dashboard → Messages → New Push
    - Segments: clinic=athens, clinic=larisa, κλπ.
    - Tags: brand, model, fitting_date

  ΠΑΡΑΔΕΙΓΜΑΤΑ NOTIFICATIONS:
    - "Αύριο η 2η Ρύθμιση στις 10:00 — Earmedical Αθήνας"
    - "Tip ημέρας: Φοράτε τα ακουστικά τουλάχιστον 6 ώρες σήμερα"
    - "Ήρθε η ώρα για Service! Προγραμματίστε ραντεβού."
═══════════════════════════════════════════════════
*/